import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent as ReactDragEvent, type PointerEvent as ReactPointerEvent } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Search, MapPin, Compass, Footprints, Bike, Zap, Globe2, Star, Loader2, Sparkles, Clock, Wand2, Navigation, X, Headphones, Bookmark, Crown, Lock, UtensilsCrossed, Car, Bus, ParkingCircle, Wallet, Ticket, Maximize2, Minimize2, ChevronUp, ChevronDown, Volume2, VolumeX, CalendarPlus, Calendar as CalendarIcon, StickyNote, Route as RouteIcon, FileDown, BedDouble, Phone, Globe, ExternalLink, GripVertical, Coffee, Shuffle, Share2, Plane, Luggage } from "lucide-react";
import { buildIcs, downloadIcs } from "@/lib/ics";
import { exportItineraryPdf, type PdfDaySection, type PdfItem, type PdfTemplate } from "@/lib/itinerary-pdf";
import { dayRouteUrl } from "@/lib/pdf-day-plan";
import { parseDistanceMeters, estimateCostEUR, formatKm, formatDurationMin } from "@/lib/route-cost";
import { buildBudget, summarizeTravelLegs } from "@/lib/route-snapshot";
import {
  buildItinerarySnapshot,
  snapshotIcsEvents,
  snapshotPdfDays,
  snapshotRoutePoints,
  type ItinerarySnapshot,
  type SnapshotEntryInput,
} from "@/lib/itinerary-snapshot";
import { publishRouteDraft, type RouteDraft } from "@/lib/route-publication";
import {
  compositionKey,
  freezeComposition,
  legMinutesAreMaterial,
  routeLegsAreMaterial,
  withFrozenMinutes,
  type FrozenComposition,
} from "@/lib/route-composition-freeze";
import {
  createLogicalIdRegistry,
  logisticsLogicalKey,
  mealLogicalKey,
  stopLogicalKey,
} from "@/lib/logical-entry-id";
import { resolveLegMode, type TravelModeId } from "@/lib/travel-modes";
import { createGenerationIdFactory } from "@/lib/route-generation-id";


import { isFoodCandidate } from "@/lib/meal-candidate-validation";
import { isEveningEligible } from "@/lib/evening-suitability";
import { expectedDayRouteLegs } from "@/lib/map-day-routing";

import { useVoiceNavigation } from "@/hooks/useVoiceNavigation";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { LANGUAGES, T, formatPrice, INTERESTS, type LangCode, type InterestId } from "@/lib/i18n";
import { EMPTY_LOCALE, locationFromCoords, type Locale } from "@/lib/locations";
import { comparableCityName } from "@/lib/city-endonym";
import { cn } from "@/lib/utils";

import { GoogleMapView, type ResolvedStop, type RouteLeg, type PickedPoi, type MapVenue } from "@/components/GoogleMapView";
import { NavigationReadinessBar } from "@/components/NavigationReadinessBar";
import { PlacesAutocompleteInput, type PlaceResult } from "@/components/PlacesAutocompleteInput";
import {
  destinationConfirmationKey,
  resolvePreCreateConfirmation,
  type DestinationSource,
  type PreCreateConfirmation,
} from "@/lib/pre-create-confirmation";
import { classifyGeoScope, needsRegionalPlanner } from "@/lib/geo-scope";
import { savePlanHandoff, buildPlanHandoff, scopeChoiceCopy } from "@/lib/plan-handoff";
import { parseMultiDestinationPlan } from "@/lib/pro/multi-destination";
import {
  detectFoodExperience,
  detectNamedRouteExperience,
  foodInterestSelected,
} from "@/lib/experience-types";



import { hasUsableLocation } from "@/lib/places-autocomplete-policy";
import { StopSearchInput, type PoiResult } from "@/components/StopSearchInput";
import {
  NO_HOTEL_SELECTED,
  clearHotelSelection,
  excludeHotelFromStops,
  hotelNightlyPrice,
  hotelPlaceIdOf,
  hotelSelectionSignature,
  isHotelSelected,
  selectSuggestedHotel,
  type HotelSelection,
} from "@/lib/hotel-selection";
import { generateStops, recommendRestaurant, recommendHotel, generateStopDetails, suggestExtraStops, type AIStop, type AIRestaurant, type AIHotel, type AIStopDetails } from "@/lib/ai-stops.functions";
import { parseTripPrompt, buildDeterministicPromptResult, canUseDeterministicPromptResult } from "@/lib/ai-prompt.functions";
import { demoFn } from "@/lib/demo-mode";
import { demoStops } from "@/lib/demo-fixtures";
import { computeFoodIntent } from "@/lib/food-intent";
import {
  assessTripRequest,
  extractDestinationCandidate,
  mergeClarificationAnswer,
  technicalFailureMessage,
  type MissingField,
} from "@/lib/trip-request-readiness";

import {
  detectGastronomyRequirement,
  gastronomyRecoveryCopy,
  gastronomySearchCascade,
  type GastronomyRequirement,
} from "@/lib/gastronomy-requirement";
import { specialtyPromptDirective, specialtyStopQuery } from "@/lib/local-specialties";
import { specialtyCardLine } from "@/lib/local-specialty";
import { categoryFocusDisplayLabel } from "@/lib/category-focus-labels";

import { extractDestinationFromPrompt } from "@/lib/destination-extraction";
import { resolveDestinationPrecedence } from "@/lib/destination-precedence";
import { canonicalCityName, preferSearchableCityName } from "@/lib/city-endonym";
import { isCityLevelResult, pickCityGeocodeResult } from "@/lib/city-destination";
import { isAreaDestination } from "@/lib/area-destination";
import { generationGuardCopy } from "@/lib/generation-guard-copy";



import { parseNegativePreferences, isCategoryExcluded } from "@/lib/negative-preferences";
import { detectBreakfastIntent } from "@/lib/breakfast-intent";
import { resolveMealPreference, mealPreferenceForFocus, stripFoodExclusions, nextMealStateForRequest, type MealPreference } from "@/lib/meal-preference";
import { unresolvedCategoryMessage } from "@/lib/unresolved-category-policy";
import { detectCuisineIntent } from "@/lib/cuisine-intent";
import { extractExplicitLocality } from "@/lib/geo-identity";
import { cuisineScarceMessage } from "@/lib/cuisine-scarce-message";
import { detectRestaurantsOnlyRequest, extractRequestLocality } from "@/lib/route-final-validation";
import { detectMealIntent } from "@/lib/meal-intent";
import { parseIntradayWindow } from "@/lib/intraday-window";
import { checkRequiredPlacesAvailability } from "@/lib/place-availability.functions";
import {
  pinnedAreaIncludingPoint,
  pinnedPointWithinCap,
  type PinnedConstraintSelection,
  type PinnedSelectionRef,
} from "@/lib/pinned-constraint-selection";
import { formatAlternativeSlot, formatSlotTime } from "@/lib/place-availability";
import { resolveTechnicalFailureOutcome } from "@/lib/availability-precheck";
import {
  failureReasonGroup,
  failureReasonMessage,
  logRequestFailure,
  type RequestFailureReason,
} from "@/lib/request-failure-reason";
import {
  buildExpandedArea,
  expandedAreaLabel,
  nearbyLocalityText,
  type ExpandedArea,
} from "@/lib/locality-scope";
import { availabilityCardActions } from "@/lib/availability-actions";


import {
  detectDiscoveryRequest,
  discoveryTitle,
  discoveryDisclaimer,
  type DiscoveryRequest,
} from "@/lib/discovery-intent";
import type { RankedDiscoveryPlace } from "@/lib/discovery-ranking";
import { discoverPlaces } from "@/lib/discovery.functions";
import { detectInspirationRequest, inspirationCopy } from "@/lib/inspiration-intent";
import {
  suggestDestinations,
  type InspirationBudget,
  type InspirationDestination,
  type InspirationVibe,
} from "@/lib/inspiration.functions";
import { InspirationCards, inspirationCardsCopy } from "@/components/InspirationCards";

import type { GeographicScope } from "@/lib/trip-intent";
import { extractBasicTripRequest } from "@/lib/trip-intent";
import {
  accommodationClarificationCopy,
  accommodationSectionTitle,
  accommodationUnavailableCopy,
  detectAccommodationRequest,
} from "@/lib/place-term-classification";
import { tripPlanningWindow } from "@/lib/trip-duration-policy";
import { asksForDurationAdvice, suggestTripDuration } from "@/lib/duration-suggestion";
import {
  buildTripPreferences,
  interestsFromPreferences,
} from "@/lib/trip-preferences";
import { forwardGeocodeLocation, reverseGeocodeLocation, findDestinationCandidates } from "@/lib/location-geocoding.functions";
import {
  applyDestinationChoice,
  homonymCopy,
  resolveHomonyms,
  type DestinationOption,
} from "@/lib/homonym-destinations";
import { decideDestinationRouting, isAreaCategory } from "@/lib/destination-class";


import {
  GEOLOCATION_OPTIONS,
  approximateLocationMessage,
  canApplyDetectedFix,
  describeGeolocationError,
  shouldConfirmLocation,
} from "@/lib/geolocation-policy";

// quota now enforced server-side inside generateStops

// Modo de teste (sem custos): respostas locais para as chamadas pagas.
const DEMO_NULL = null as never;
const DEMO_NEARBY = { results: [] } as never;
const DEMO_DETAILS = {
  history: "Descrição de demonstração — modo de teste, sem chamadas de IA.",
  funFacts: ["Este conteúdo é fixo e local."],
  historicalFigures: [],
  bestTimeToVisit: "Manhã",
} as never;
import { getAnonId } from "@/lib/anon-id";
import { QuotaLimitModal } from "@/components/QuotaLimitModal";
import { useLang } from "@/hooks/use-lang";
import { currencyForCountry } from "@/lib/currency";
import { convertAmount, formatPriceDual } from "@/lib/fx";
import { getTransitPassUrl } from "@/lib/transit-passes";
import { MobilityTicketCard } from "@/components/MobilityTicketCard";
import { getMobilityResources } from "@/lib/mobility/mobility.functions";
import type { MobilityGroup } from "@/lib/mobility/teaser";
import { buildPdfMobilitySection, relevantGroupsForTransport } from "@/lib/pdf-mobility";

import { usePremium } from "@/lib/premium";
import { PremiumModal } from "@/components/PremiumModal";
import { buildSchedule, clipStopsForDeparture, formatTime, DEFAULT_MEAL_PREFS, isLogisticsMeal, type MealPrefs, type TimedEntry } from "@/lib/schedule";
import {
  parseTravelLogistics,
  lastDayVisitEndMinutes,
  LOGISTICS_SKIP_MARKER,
  LANDING_BAGGAGE_MIN,
  AIRPORT_TRANSFER_DEFAULT_MIN,
  HOTEL_CHECKIN_MIN,
  type TravelLogistics,
} from "@/lib/arrival-window";
import { noLogisticsNotice } from "@/lib/travel-logistics-chain";
import { buildDayWindows } from "@/lib/day-windows";
import type { OpeningPeriod } from "@/lib/place-availability";
import { validateScheduleWithDayCoverage } from "@/lib/day-coverage";
import { dropGeographicOutliers } from "@/lib/route-outliers";
import { findStopsOutsideOpeningHours } from "@/lib/scheduled-hours-guard";

import { resolveRequestOrigin } from "@/lib/request-origin";
import {
  decideGenerationStart,
  readyToGenerateCopy,
  type RequestPhase,
  type SubmitTrigger,
} from "@/lib/request-readiness";
import { getMealDefaults } from "@/lib/admin.functions";
import { getPlaceContactInfo, getStaticRouteMapDataUrl, findNearbyRestaurants, type PlaceContactInfo, type NearbyRestaurant } from "@/lib/maps.functions";

import { useAuth } from "@/hooks/use-auth";
import { shareItinerary } from "@/lib/share-link";
import { saveItinerary, consumePendingItinerary, PENDING_ITINERARY_EVENT, type SavedItinerary } from "@/lib/saved-itineraries";
import { attachResolvedVenuesToSavedStops } from "@/lib/saved-route-venues";
import { orderLog } from "@/lib/debug-order";
import { toast } from "sonner";
import { DiscoveryCardActions } from "@/components/DiscoveryCardActions";
import { track, identify } from "@/lib/analytics";
import { WeatherBadge } from "@/components/WeatherBadge";
import { TimeInput } from "@/components/TimeInput";
import { LogisticsClarification } from "@/components/LogisticsClarification";
import { safeLocalStorageGet, safeLocalStorageRemove, safeLocalStorageSet } from "@/lib/safe-browser";
import { buildAudioGuideScript } from "@/lib/audio-guide-script";
import { itinerarySessionSignature, scopedBreakKey } from "@/lib/itinerary-session";


const MEAL_CHOICE_LABELS: Record<'restaurant' | 'free_break' | 'none', Record<string, string>> = {"restaurant": {"pt": "Incluir pausas e sugestões de restaurantes", "en": "Include breaks and restaurant suggestions", "es": "Incluir pausas y sugerencias de restaurantes", "fr": "Inclure pauses et suggestions de restaurants", "it": "Includere pause e suggerimenti di ristoranti", "de": "Pausen und Restaurantvorschläge einschließen"}, "free_break": {"pt": "Apenas pausa livre (sem restaurantes)", "en": "Free break only (no restaurants)", "es": "Solo pausa libre (sin restaurantes)", "fr": "Pause libre seulement (sans restaurants)", "it": "Solo pausa libera (senza ristoranti)", "de": "Nur freie Pause (ohne Restaurants)"}, "none": {"pt": "Não incluir refeições nem restaurantes", "en": "No meals or restaurants", "es": "No incluir comidas ni restaurantes", "fr": "Aucun repas ni restaurant", "it": "Nessun pasto né ristorante", "de": "Keine Mahlzeiten oder Restaurants"}};


export const Route = createFileRoute("/")({
  component: Index,
  head: () => ({
    meta: [
      { property: "og:url", content: "https://www.routify-tour.com/" },
    ],
    links: [
      { rel: "canonical", href: "https://www.routify-tour.com/" },
    ],
  }),
});

type Mode = "walk" | "scooter" | "bike" | "car" | "transit";

type DisplayStop = {
  name: string;
  category: string;
  blurb: string;
  priceLocal: number;
  rating: number;
  minutes?: number;
  photoUrl?: string;
  placeId?: string;
  lat?: number;
  lng?: number;
  day?: number;
  specialtyLabel?: string;
  specialtyEvidence?: string;
  specialtyEvidenceLevel?: "product" | "category" | "none";
  specialtyAlternative?: boolean;
  /** Horários habituais do Places (barreira final do horário). */
  openingPeriods?: OpeningPeriod[] | null;
  /** Experiência noturna confirmada. */
  evening?: boolean;
  /** Paragem obrigatória: nunca removida pela barreira final. */
  required?: boolean;
  /** Tipos estruturados do Places — única base para classificar alojamento. */
  placeTypes?: string[];
  placePrimaryType?: string;
  /** Origem estruturada do candidato (ex.: "hotel_suggestion"). */
  source?: string;
  kind?: string;
};

/** Linha de especialidade do cartão (produto só com prova específica). */
function specialtyLineFor(stop: DisplayStop, language: string): string | null {
  if (!stop.specialtyEvidenceLevel) return null;
  return specialtyCardLine(
    {
      level: stop.specialtyEvidenceLevel,
      label: stop.specialtyLabel,
      reason: stop.specialtyEvidence,
      alternative: stop.specialtyAlternative,
    },
    categoryFocusDisplayLabel("sweets", language),
    language,
  );
}

type ManualStop = { name: string; lat: number; lng: number; placeId?: string; photoUrl?: string; address?: string };

type MealBreak = Extract<TimedEntry, { type: "break" }>;
type RoutePoint = { name: string; lat?: number; lng?: number; placeId?: string; googleMapsUri?: string };

const mealBreakKeyFor = (b: MealBreak, scope: string) => scopedBreakKey(scope, b.meal, b.afterStopIdx);

const sameNumberArray = (a: number[], b: number[]) => a.length === b.length && a.every((v, i) => v === b[i]);
const sameRouteLegs = (a: RouteLeg[], b: RouteLeg[]) =>
  a.length === b.length &&
  a.every((leg, i) => {
    const other = b[i];
    return leg.from === other.from && leg.to === other.to && leg.minutes === other.minutes && leg.distance === other.distance &&
      leg.steps.length === other.steps.length && leg.steps.every((step, idx) => step === other.steps[idx]);
  });



/** Chave estável de um âmbito (país/região/corredor) para memorizar escolhas. */
const scopeKeyOf = (name: string) =>
  (name ?? "").normalize("NFD").replace(/[\u0300-\u036f]/gu, "").toLowerCase().trim();

function Index() {
  const { lang, setLang } = useLang();
  const [query, setQuery] = useState("");
  // Sem cidade sugerida por defeito: o chip só aparece depois de resolvermos
  // mesmo o destino (nunca "Roteiro em Paris" herdado).
  const [locale, setLocale] = useState<Locale>(EMPTY_LOCALE);

  /** Localização escolhida à mão nunca é substituída por deteção automática. */
  const manualLocationRef = useRef(false);
  /** Origem do destino ativo — só user_selected/prompt_named dispensam confirmação. */
  const destinationSourceRef = useRef<DestinationSource>("none");
  /** Chave (cidade+coordenadas+origem) já confirmada; muda → volta a perguntar. */
  const confirmedDestinationKeyRef = useRef<string | null>(null);
  const [cityConfirm, setCityConfirm] = useState<PreCreateConfirmation | null>(null);
  const [cityConfirmTarget, setCityConfirmTarget] = useState<"prompt" | "generate">("prompt");

  const [geoNotice, setGeoNotice] = useState<string | null>(null);

  const [mode, setMode] = useState<Mode>("walk");
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiStops, setAiStops] = useState<AIStop[] | null>(null);
  const [routeConstraintWarning, setRouteConstraintWarning] = useState<string | null>(null);
  const stayDecisionRef = useRef<null | "overnight" | "rest">(null);
  const [stayClarify, setStayClarify] = useState<{ basePrompt: string } | null>(null);
  const [stayRequested, setStayRequested] = useState(false);
  // A escolha "cidade" vale só para o âmbito onde foi feita (ex.: "Nacional 2").
  // Um pedido novo com outro país/região volta a propor o Pro.
  const scopeDecisionRef = useRef<null | { key: string; decision: "city" | "pro" }>(null);
  const [scopeChoice, setScopeChoice] = useState<
    | {
        basePrompt: string;
        scopeName: string;
        scopeKind: "country" | "region" | "multi_city";
        countryCode?: string;
        /** Rota conhecida (N2, Route 66, …) que motivou o handoff. */
        routeTemplateId?: string;
        /** Corredor curado correspondente, quando existe. */
        routeCorridorId?: string;
        routeDays?: number;
        routeTransport?: "walk" | "bike" | "car" | "transit";

        routeCities?: string[];
      }
    | null
  >(null);

  /**
   * Estado canónico do hotel: só existe hotel quando a pessoa o escolheu.
   * Nenhuma sugestão, texto ou heurística pode marcar hotel como selecionado.
   */
  const [hotelSelection, setHotelSelection] = useState<HotelSelection>(NO_HOTEL_SELECTED);
  const [stayHotels, setStayHotels] = useState<AIHotel[] | "loading" | "none" | null>(null);


  const [aiLoading, setAiLoading] = useState(false);
  const [legMinutes, setLegMinutes] = useState<number[]>([]);
  const [durationIdx, setDurationIdx] = useState<number>(4); // default ~3h
  const [interests, setInterests] = useState<InterestId[]>([]);
  const [notes, setNotes] = useState("");
  const [planMode, setPlanMode] = useState<"ai" | "manual">("ai");
  const [manualStops, setManualStops] = useState<ManualStop[]>([]);
  const [optimizeManualOrder, setOptimizeManualOrder] = useState(true);
  // Pick-on-map mode: when on, tapping a Google POI adds it as a stop.
  const [pickMode, setPickMode] = useState(false);
  const [pickCount, setPickCount] = useState(0);

  // True when current itinerary was restored from a saved route: preserve the
  // saved order (no Google Directions waypoint optimization) so stop names
  // don't shuffle on the map between renders.
  const [loadedFromSaved, setLoadedFromSaved] = useState(false);
  // True once the user manually reorders stops in AI mode: disables Google
  // Directions waypoint optimization so the names don't swap back on the map.
  const [userReorderedAi, setUserReorderedAi] = useState(false);
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [resolvedMap, setResolvedMap] = useState<Record<string, ResolvedStop>>({});
  const [routeLegs, setRouteLegs] = useState<RouteLeg[]>([]);

  /**
   * Identidade da rota ativa.
   * - `routeInstanceId`: uma geração pedida pela pessoa;
   * - `generationId`: criado de novo em CADA clique em "Criar rota" (duas
   *   gerações iguais têm IDs diferentes);
   * - `modeRevision`: sobe apenas quando a pessoa muda de modo de transporte;
   * - `logicalIds`: registo de identidades lógicas estáveis dessa geração.
   */
  /** Texto ORIGINAL do pedido, imune a merges de clarificação. */
  const originalRequestRef = useRef<string>("");
  const routeInstanceIdRef = useRef<string>("route-0");
  const generationIdRef = useRef<string>("gen-0");
  const modeRevisionRef = useRef(0);
  const logicalIdsRef = useRef(createLogicalIdRegistry());
  const [routeIdentity, setRouteIdentity] = useState({
    routeInstanceId: "route-0",
    generationId: "gen-0",
    modeRevision: 0,
  });

  /** Novo pedido da pessoa: nova instância, novo `generationId`, IDs limpos. */
  const generationIdFactoryRef = useRef(createGenerationIdFactory());
  const beginRouteGeneration = useCallback(() => {
    const identity = generationIdFactoryRef.current.next();
    routeInstanceIdRef.current = identity.routeInstanceId;
    generationIdRef.current = identity.generationId;
    modeRevisionRef.current = 0;
    logicalIdsRef.current.reset();
    const next = {
      routeInstanceId: identity.routeInstanceId,
      generationId: identity.generationId,
      modeRevision: 0,
    };
    setRouteIdentity(next);
    return next;
  }, []);


  /**
   * Mudança de modo de transporte DENTRO da mesma rota: mesma
   * `routeInstanceId`, mesmos locais/IDs lógicos; sobe apenas `modeRevision`,
   * o que invalida qualquer resposta assíncrona do modo anterior.
   */
  const changeTravelMode = useCallback((next: TravelModeId) => {
    setMode((cur) => {
      if (cur === next) return cur;
      modeRevisionRef.current += 1;
      setRouteIdentity((id) => ({ ...id, modeRevision: modeRevisionRef.current }));
      return next;
    });
  }, []);

  /**
   * Rota PUBLICADA (única fonte visível): agenda, cartões, mapa, navegação,
   * resumo, orçamento, PDF e ICS leem exclusivamente esta revisão.
   */
  const publishedRouteRef = useRef<ItinerarySnapshot | null>(null);
  const [publishedRoute, setPublishedRoute] = useState<ItinerarySnapshot | null>(null);
  const routeSnapshot = publishedRoute;



  const [routePolyline, setRoutePolyline] = useState<string | null>(null);
  const [navigationActive, setNavigationActive] = useState(false);
  const [activeStopIdx, setActiveStopIdx] = useState(0);
  const [userPos, setUserPos] = useState<{ lat: number; lng: number } | null>(null);
  // Auto-GPS can detect the city for convenience, but it must not make a
  // planned itinerary start "right now / from me" unless the user explicitly
  // asks for GPS/current-location behaviour.
  const [useGpsAsStart, setUseGpsAsStart] = useState(false);
  const [namedRouteStart, setNamedRouteStart] = useState<{ name: string; lat: number; lng: number } | null>(null);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [userHeading, setUserHeading] = useState<number | null>(null);
  const [draggingStopName, setDraggingStopName] = useState<string | null>(null);
  const [dragOverStopIdx, setDragOverStopIdx] = useState<number | null>(null);
  const [dragGhost, setDragGhost] = useState<{ x: number; y: number; name: string } | null>(null);
  const [detailStop, setDetailStop] = useState<DisplayStop | null>(null);
  const [autoArrived, setAutoArrived] = useState(false);
  const [navigationLeg, setNavigationLeg] = useState<RouteLeg | null>(null);
  const [navigationRouteLegs, setNavigationRouteLegs] = useState<RouteLeg[]>([]);
  const [navFullscreen, setNavFullscreen] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  // Planning default: do not use the current clock time. Users can change it
  // manually if they are doing the route immediately.
  const [startTime, setStartTime] = useState<string>("09:00");
  const [startDate, setStartDate] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  });
  const [arriveBy, setArriveBy] = useState<string>("");
  // Meal preferences: defaults from admin, optional per-user override in localStorage.
  // `activePrefs` is what schedule uses; `pendingPrefs` is what the user is editing.
  const [activePrefs, setActivePrefs] = useState<MealPrefs>(DEFAULT_MEAL_PREFS);
  const [pendingPrefs, setPendingPrefs] = useState<MealPrefs>(DEFAULT_MEAL_PREFS);
  const [mealPrefsOpen, setMealPrefsOpen] = useState(false);
  const getMealDefaultsFn = useServerFn(getMealDefaults);

  const navigate = useNavigate();
  const [premiumOpen, setPremiumOpen] = useState(false);
  const [pendingMode, setPendingMode] = useState<Mode | null>(null);
  const [speakingKey, setSpeakingKey] = useState<string | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveTitle, setSaveTitle] = useState("");
  const [saving, setSaving] = useState(false);
  // Personal notes per stop (session-only). Key = stop name.
  const [stopNotes, setStopNotes] = useState<Record<string, string>>({});
  const [openNoteFor, setOpenNoteFor] = useState<string | null>(null);
  const [restaurants, setRestaurants] = useState<Record<string, AIRestaurant | null | "loading">>({});
  const [hotels, setHotels] = useState<Record<string, AIHotel | null | "loading">>({});
  const mealRequestTokensRef = useRef(new Map<string, symbol>());
  const [failedPhotoKeys, setFailedPhotoKeys] = useState<Set<string>>(() => new Set());
  const [hotelAlts, setHotelAlts] = useState<Record<string, AIHotel[]>>({});
  // Gastronomia pedida explicitamente → requisito real do pedido.
  const [gastronomy, setGastronomy] = useState<GastronomyRequirement>({ required: false, kinds: [], constraints: [] });
  // Raio alargado por pausa, usado pela recuperação gratuita ("Alargar a pesquisa").
  const [mealRadiusBoost, setMealRadiusBoost] = useState<Record<string, number>>({});

  const [stopDetailsMap, setStopDetailsMap] = useState<Record<string, AIStopDetails | "loading" | "error">>({});
  const [placeContactMap, setPlaceContactMap] = useState<Record<string, PlaceContactInfo | "loading">>({});
  const fetchStopDetails = demoFn(useServerFn(generateStopDetails), () => DEMO_DETAILS);
  const fetchPlaceContact = demoFn(useServerFn(getPlaceContactInfo), () => DEMO_NULL);
  const fetchNearbyRestos = demoFn(useServerFn(findNearbyRestaurants), () => DEMO_NEARBY);
  // Open restaurants modal anchored to a meal-break key
  const [restoModalKey, setRestoModalKey] = useState<string | null>(null);
  const [restoModalLoading, setRestoModalLoading] = useState(false);
  const [restoModalResults, setRestoModalResults] = useState<NearbyRestaurant[]>([]);

  const runPromptParse = demoFn(
    useServerFn(parseTripPrompt),
    (opts) =>
      ({
        parsed: buildDeterministicPromptResult(
          (opts as { data: { prompt: string; language?: string } }).data.prompt,
          (opts as { data: { language?: string } }).data.language ?? "pt",
        ),
        error: null,
      }) as never,
  );
  const [aiPrompt, setAiPrompt] = useState("");
  /** Texto do pedido que gerou a rota atual (o textarea é limpo após gerar). */
  const [lastRequestText, setLastRequestText] = useState("");
  // Espelho síncrono: a geração corre no mesmo tick do setState do pedido.
  const lastRequestTextRef = useRef("");
  // Última decisão de intenção de comida (espelho client-side do cálculo do
  // servidor): alimenta "Sugerir mais sítios" — nunca sugere restaurantes
  // quando o pedido não os pediu.
  const lastFoodIntentRef = useRef(false);

  useEffect(() => {
    lastRequestTextRef.current = lastRequestText;
  }, [lastRequestText]);

  // Fonte única de verdade das refeições: escolha manual feita DEPOIS de
  // analisar o pedido atual > intenção do texto atual > configuração default.
  // Default neutro do rádio: "free_break" NUNCA cria paragem/pesquisa de
  // comida. "restaurant" só depois de uma ação explícita (garantia A).
  const [mealChoice, setMealChoice] = useState<MealPreference>("free_break");
  /**
   * Texto do pedido em vigor quando o utilizador tocou no rádio. Se o texto
   * mudar, a escolha deixa de ser "manual para este pedido" e o texto novo
   * volta a mandar — a menos que ele volte a escolher manualmente.
   */
  const [mealChoiceForText, setMealChoiceForText] = useState<string | null>(null);
  const mealChoiceExplicit = mealChoiceForText !== null && mealChoiceForText === lastRequestText;
  /**
   * Foco exclusivo de categoria do pedido em curso (ex.: "sweets"). É reposto
   * a cada pedido novo, para não haver fuga de estado entre pesquisas.
   */
  const [sessionCategoryFocus, setSessionCategoryFocus] = useState<string | null>(null);
  const mealPreferenceFromRequest = useMemo(
    () =>
      resolveMealPreference({
        choice: mealChoice,
        choiceExplicit: mealChoiceExplicit,
        eatMinutes: activePrefs.eatMinutes,
        requestText: [lastRequestText, notes].join(" "),
      }),
    [mealChoice, mealChoiceExplicit, activePrefs.eatMinutes, lastRequestText, notes],
  );
  /**
   * Propriedade única e vinculativa usada por horário, Places, PDF e partilha.
   * Um itinerário com foco exclusivo de doçaria nunca tem refeições.
   */
  const mealPreferenceBase: MealPreference = mealPreferenceForFocus(
    sessionCategoryFocus,
    mealPreferenceFromRequest,
  );
  // Pedido só de restaurantes: a própria lista é a refeição — nunca acrescentar
  // uma pausa genérica com um restaurante fora da cozinha/localidade pedidas.
  const mealPreference: MealPreference = detectRestaurantsOnlyRequest(
    [lastRequestText, notes].filter(Boolean).join(" "),
  )
    ? "none"
    : mealPreferenceBase;

  const restaurantsExcluded = mealPreference !== "restaurant";

  const [aiPromptLoading, setAiPromptLoading] = useState(false);
  /** Visita por paragem devolvida pelo motor de duração (null = valor base). */
  const [planVisitMinutes, setPlanVisitMinutes] = useState<number | null>(null);

  const [clarification, setClarification] = useState<{ missing: MissingField; question: string; basePrompt: string; city?: string; logistics?: TravelLogistics } | null>(null);
  /**
   * Pedido completo à espera do clique final. Responder a uma clarificação
   * apenas escreve aqui: nunca chama IA/Places/Routes nem consome crédito.
   */
  const [pendingGeneration, setPendingGeneration] = useState<{ text: string } | null>(null);
  const [requestPhase, setRequestPhase] = useState<RequestPhase>("idle");
  const [clarifyAnswer, setClarifyAnswer] = useState("");
  const [clarifyStartTime, setClarifyStartTime] = useState("13:00");
  const [clarifyEndTime, setClarifyEndTime] = useState("16:00");
  const [clarifyDayOffset, setClarifyDayOffset] = useState(0);
  /**
   * Locais pedidos que não cabem na janela indicada. Enquanto este bloco
   * existir não há geração de IA nem consumo de créditos: o utilizador escolhe
   * outro horário, outro dia ou alargar a zona.
   */
  const [availabilityBlock, setAvailabilityBlock] = useState<{
    basePrompt: string;
    area: string;
    window: string;
    dayOffset: number;
    /** Assinatura do pedido original devolvida pelo servidor (nunca geometria). */
    requestSignature: string | null;
    /** Área alargada proposta a partir dos candidatos realmente encontrados. */
    suggestedArea: ExpandedArea | null;
    /** A pessoa escolheu manter só a localidade exata: não repetir o pedido. */
    keepStrict?: boolean;
    entries: Array<{
      label: string;
      address: string | null;
      locality: string | null;
      reason: RequestFailureReason;
      message: string;
      /** Identidade estável da restrição (para "Escolher este local"). */
      constraintId: string | null;
      alternatives: Array<{ label: string; dayOffset: number; start: string; end: string }>;
      /** Candidatos reais encontrados, em linguagem normal (sem códigos). */
      candidates: Array<{
        placeId: string | null;
        /** Referência assinada pelo servidor: "Escolher este local" envia isto. */
        selectionRef: string | null;
        name: string;
        address: string | null;
        locality: string | null;
        lat: number | null;
        lng: number | null;
        types: string[];
        nearby: boolean;
        distanceMeters: number | null;
      }>;
    }>;
  } | null>(null);

  /**
   * Ambiguidade real de localidade (vários sítios com o mesmo nome, ou só o
   * município encontrado). Enquanto existir não há pesquisa, IA nem crédito:
   * a pessoa escolhe primeiro qual é o sítio.
   */
  const [localityChoice, setLocalityChoice] = useState<{
    basePrompt: string;
    requested: string;
    options: Array<{
      id?: string | null;
      label: string;
      countryCode: string;
      lat: number;
      lng: number;
    }>;
  } | null>(null);
  /** Localidade já escolhida pela pessoa: manda sobre o texto e a geocodificação. */
  const chosenLocalityRef = useRef<{
    id?: string | null;
    label: string;
    countryCode: string;
    lat: number;
    lng: number;
  } | null>(null);




  /**
   * Área alargada ATIVA neste pedido. Nunca é um booleano: guarda a zona
   * concreta ("zona próxima de Leça da Palmeira (Perafita)") e o raio máximo,
   * para a pesquisa nunca voltar ao raio largo antigo.
   */
  const expandedAreaRef = useRef<ExpandedArea | null>(null);
  const [activeExpandedArea, setActiveExpandedArea] = useState<ExpandedArea | null>(null);
  const applyExpandedArea = (area: ExpandedArea | null) => {
    expandedAreaRef.current = area;
    setActiveExpandedArea(area);
  };

  /**
   * Seleções fixas ("Escolher este local") deste pedido. O sessionStorage é
   * apenas visual: o servidor re-confirma placeId, coordenadas, tipos e
   * restrição antes de gerar.
   */
  const PINNED_STORAGE_KEY = "routify:pinnedSelections";
  const [pinnedSelections, setPinnedSelections] = useState<PinnedConstraintSelection[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.sessionStorage.getItem(PINNED_STORAGE_KEY);
      const parsed = raw ? (JSON.parse(raw) as unknown) : [];
      return Array.isArray(parsed) ? (parsed as PinnedConstraintSelection[]) : [];
    } catch {
      return [];
    }
  });
  useEffect(() => {
    try {
      if (pinnedSelections.length) {
        window.sessionStorage.setItem(PINNED_STORAGE_KEY, JSON.stringify(pinnedSelections));
      } else {
        window.sessionStorage.removeItem(PINNED_STORAGE_KEY);
      }
    } catch {
      /* sessão sem storage */
    }
  }, [pinnedSelections]);

  /** Confirmação visível depois de "Escolher este local": substitui o cartão. */
  const [pinnedConfirmation, setPinnedConfirmation] = useState<{
    name: string;
    address: string | null;
    constraintLabel: string;
    constraintId: string;
    basePrompt: string;
  } | null>(null);
  const pinnedContinueRef = useRef(false);
  /**
   * Escolha rejeitada pelo servidor com motivo concreto: nunca se volta ao
   * cartão de candidatos em silêncio.
   */
  const [pinnedRejected, setPinnedRejected] = useState<{
    basePrompt: string;
    reason: string;
  } | null>(null);
  /**
   * Escassez de visita em modo genérico: não há locais de visita suficientes
   * e NUNCA se preenche com restaurantes. O cartão dá quatro opções.
   */
  const [visitScarcity, setVisitScarcity] = useState<{
    city: string;
    found: number;
    target: number;
    minimum: number;
    /** Mínimo de segurança por dia (3/2/1), na ordem dos dias. */
    minimumViableStopsPerDay?: number[];
    /** Paragens finais por dia, na ordem dos dias. */
    stopsPerDay?: number[];
    /** Alvo de ritmo (soma dos alvos diários) — orienta, nunca bloqueia. */
    targetStops?: number;
    coveredMinutes: number;
    requestedMinutes: number;
    basePrompt: string;
    /** Dias efetivamente usados nesta execução. */
    durationDays: number;
    /** Zona realmente usada e respetivo raio. */
    areaLabel: string;
    areaRadiusMeters: number;
    /** Falso = falha técnica de recolha, não escassez da zona. */
    collectionComplete: boolean;
    /** Candidatos elegíveis na área (contexto quando a falha é de montagem). */
    eligibleCount?: number;
    cause:
      | "collection_incomplete"
      | "area_scarcity"
      | "collector_fault"
      | "route_assembly_failure"
      | "schedule_unavailable";
  } | null>(null);
  /**
   * Falha técnica estruturada: substitui QUALQUER toast genérico depois de o
   * destino já estar resolvido. Nunca sugere trocar de cidade.
   */
  const [technicalFailure, setTechnicalFailure] = useState<{
    origin: string;
    generationRequestId: string;
    traceId?: string;
  } | null>(null);
  /** Nova tentativa em curso: impede duplo toque e dupla reserva de crédito. */
  const [technicalRetryBusy, setTechnicalRetryBusy] = useState(false);
  /**
   * Contexto EXATO do pedido enviado ao servidor, criado imediatamente antes
   * da chamada. "Tentar novamente" repete só isto — nunca o contexto do
   * cartão de escassez, que pode pertencer a outro pedido.
   */
  const generationRetryContextRef = useRef<{
    generationRequestId: string;
    run: () => void;
  } | null>(null);
  /**
   * Ação em curso do cartão de escassez ("Alargar a zona" / "Reduzir
   * duração"): garante carregamento visível e "Tentar novamente" em falha.
   */
  const [scarcityAction, setScarcityAction] = useState<{
    kind: "widen_area" | "shorten_duration";
    phase: "running" | "error";
    /** Zona/duração pedidas por esta ação (mostradas no cartão). */
    areaRadiusMeters?: number;
    days?: number;
  } | null>(null);
  /** Escolha explícita de redução de duração ("2 dias → 1 dia"). */
  const [durationChoice, setDurationChoice] = useState<{ fromDays: number; toDays: number } | null>(null);
  /** Aviso persistente: "Roteiro ajustado para 1 dia". */
  const [durationAdjustedDays, setDurationAdjustedDays] = useState<number | null>(null);
  /** Nota de roteiro mais leve: rota válida abaixo do alvo de ritmo. */
  const [itineraryPaceNotice, setItineraryPaceNotice] = useState<{
    target: number;
    stops: number;
  } | null>(null);
  /** Rota completada com destaques da cidade porque os interesses deram poucos locais. */
  const [interestFillNotice, setInterestFillNotice] = useState(false);
  /** Modo Aldeia: roteiro calmo só dentro de uma localidade pequena. */
  const [calmSmallPlaceNotice, setCalmSmallPlaceNotice] = useState<{
    area: string;
    stops: number;
    areaRadiusMeters: number;
  } | null>(null);
  // Reserva de locais já validados na geração (para "Trocar sítio", custo 0).
  const [spareStops, setSpareStops] = useState<AIStop[]>([]);
  const [swapFor, setSwapFor] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  /** Contexto da última submissão (necessário para reescrever/reduzir). */
  const visitScarcityCtxRef = useRef<{
    generateArgs: Parameters<typeof runGenerate>[0] | null;
  } | null>(null);

  /** Contexto validado da continuação fixa (assinatura, seleção e área). */
  const pinnedContinuationRef = useRef<{
    basePrompt: string;
    requestSignature: string;
    pinned: PinnedSelectionRef[];
    area: ExpandedArea | null;
  } | null>(null);
  /** Último pedido já interpretado: a continuação NUNCA torna a fazer parse. */
  const lastParsedPromptRef = useRef<import("@/lib/ai-prompt.functions").ParsedTripPrompt | null>(null);
  /** Última assinatura devolvida pelo servidor (contexto validado). */
  const lastAvailabilitySignatureRef = useRef<string | null>(null);
  /** Cópia do cartão de candidatos para "Alterar escolha" após rejeição. */
  const lastAvailabilityBlockRef = useRef<typeof availabilityBlock>(null);

  function choosePinnedCandidate(
    block: NonNullable<typeof availabilityBlock>,
    entry: NonNullable<typeof availabilityBlock>["entries"][number],
    cand: NonNullable<typeof availabilityBlock>["entries"][number]["candidates"][number],
  ) {
    if (!block.requestSignature || !entry.constraintId || !cand.placeId) return;
    // A escolha é uma referência assinada pelo servidor: sem ela não há
    // seleção — o cartão atual nunca teve este candidato.
    if (!cand.selectionRef) {
      setPinnedRejected({
        basePrompt: block.basePrompt,
        reason: "invalid_selection_ref",
      });
      return;
    }
    // O centro/identidade da localidade pedida mantém-se; só o raio cresce.
    const center =
      activeExpandedArea?.center ??
      (Number.isFinite(locale.lat) && Number.isFinite(locale.lng)
        ? { lat: locale.lat, lng: locale.lng }
        : null);
    const point =
      typeof cand.lat === "number" && typeof cand.lng === "number"
        ? { lat: cand.lat, lng: cand.lng }
        : null;
    if (!center || !point || !pinnedPointWithinCap(center, point)) {
      // Além do teto desta etapa: não fixar — seria preciso um NOVO
      // alargamento explícito confirmado pela pessoa.
      toast.info(
        lang === "pt"
          ? "Esse local fica além do alargamento atual. Alarga a zona primeiro para o poderes escolher."
          : "That place is beyond the current widening step. Widen the area first to choose it.",
      );
      return;
    }
    const area = pinnedAreaIncludingPoint({
      requestedLocality: block.area,
      center,
      point,
      placeLocality: cand.locality,
      currentRadiusMeters: activeExpandedArea?.radiusMeters ?? null,
      language: lang,
    });
    const selection: PinnedConstraintSelection = {
      requestSignature: block.requestSignature,
      constraintId: entry.constraintId,
      placeId: cand.placeId,
      // Referência assinada pelo servidor: a revalidação no servidor usa ISTO.
      selectionRef: cand.selectionRef,
      name: cand.name,
      address: cand.address,
      locality: cand.locality,
      lat: cand.lat,
      lng: cand.lng,
      types: cand.types,
      confirmedArea: area,
    };
    // Lista calculada AGORA (nunca estado React pendente): é o que a
    // continuação envia ao servidor.
    const nextPinned = [
      ...pinnedSelections.filter((s) => s.constraintId !== selection.constraintId),
      selection,
    ];
    setPinnedSelections(nextPinned);
    // A área ativa passa a ser a área alargada confirmada que inclui a escolha.
    applyExpandedArea(area);
    // Contexto validado da continuação: assinatura + seleção + área.
    pinnedContinuationRef.current = {
      basePrompt: block.basePrompt,
      requestSignature: block.requestSignature,
      pinned: nextPinned.map(({ requestSignature, constraintId, placeId, selectionRef }) => ({
        requestSignature,
        constraintId,
        placeId,
        selectionRef,
      })),
      area,
    };
    // Cópia do cartão de candidatos para "Alterar escolha".
    lastAvailabilityBlockRef.current = block;
    // O cartão de disponibilidade fica guardado (para "Alterar escolha") mas é
    // substituído pela confirmação — NÃO há revalidação nem nova submissão aqui.
    setPinnedRejected(null);
    setPinnedConfirmation({
      name: cand.name,
      address: cand.address,
      constraintLabel: entry.label,
      constraintId: selection.constraintId,
      basePrompt: block.basePrompt,
    });
  }

  function continueWithPinned() {
    if (pinnedContinueRef.current) return;
    const ctx = pinnedContinuationRef.current;
    const base = ctx?.basePrompt ?? pinnedConfirmation?.basePrompt;
    // Sem contexto validado (ou de outro pedido): rejeição explícita, nunca
    // um novo submit normal nem novo parse do texto.
    if (!ctx || !base || ctx.requestSignature !== lastAvailabilitySignatureRef.current) {
      setPinnedConfirmation(null);
      setPinnedRejected({ basePrompt: base ?? "", reason: "signature_mismatch" });
      return;
    }
    pinnedContinueRef.current = true;
    setPinnedConfirmation(null);
    setAvailabilityBlock(null);
    // Uma única continuação explícita: seleção fixa e área expandida passadas
    // como ARGUMENTOS — sem depender de estado React, sem novo parse.
    void (async () => {
      try {
        await submitAiPrompt(base, {
          continueSession: true,
          continuation: { pinned: ctx.pinned, area: ctx.area },
        });
      } finally {
        pinnedContinueRef.current = false;
      }
    })();
  }

  function changePinnedChoice() {
    const conf = pinnedConfirmation;
    if (conf) {
      setPinnedSelections((prev) => prev.filter((s) => s.constraintId !== conf.constraintId));
    }
    pinnedContinuationRef.current = null;
    setPinnedConfirmation(null);
    // O cartão de candidatos volta a aparecer sem limpar o pedido.
  }

  /** Falha técnica na verificação de horários: bloqueia a geração até o
   *  utilizador escolher "Tentar novamente" ou "Continuar sem confirmar". */
  const [availabilityTechnical, setAvailabilityTechnical] = useState<{
    basePrompt: string;
  } | null>(null);

  /** Restrições obrigatórias do pedido sem local válido: cartão contextual
   *  (nunca um toast genérico) com a opção de alargar a zona. */
  const [constraintBlock, setConstraintBlock] = useState<{
    labels: string[];
    area: string;
    /** Já estamos numa zona alargada? Então não há mais alargamento silencioso. */
    expandedLabel: string | null;
    onWiden: (() => void) | null;
  } | null>(null);


  /** Modo Descoberta: ranking de recomendações, sem rota nem horários. */
  const [discovery, setDiscovery] = useState<
    | {
        title: string;
        disclaimer: string;
        request: DiscoveryRequest;
        places: RankedDiscoveryPlace[];
        loading: boolean;
      }
    | null
  >(null);

  /** Modo Inspiração: "não sei para onde ir de férias" → 3 perguntas → destinos. */
  const [inspiration, setInspiration] = useState<
    | {
        phase: "questions" | "loading" | "results";
        days: number | null;
        vibe: InspirationVibe[];
        budget: InspirationBudget | null;
        destinations: InspirationDestination[];
      }
    | null
  >(null);
  const [savedDiscovery, setSavedDiscovery] = useState<Set<string>>(new Set());
  /** Seleção temporária de locais de descoberta que já entraram na rota. */
  const [discoveryInRoute, setDiscoveryInRoute] = useState<Set<string>>(new Set());
  useEffect(() => {
    let alive = true;
    import("@/lib/saved-places")
      .then((m) => m.listSavedPlaces())
      .then((list) => {
        if (alive) setSavedDiscovery(new Set(list.map((p) => p.place_id)));
      })
      .catch(() => {
        /* histórico opcional */
      });
    return () => {
      alive = false;
    };
  }, []);

  /** Escolha gratuita entre locais com o mesmo nome (Caracal RO vs. MX). */
  const [homonymChoice, setHomonymChoice] = useState<
    { name: string; basePrompt: string; options: DestinationOption[] } | null
  >(null);
  const [homonymLoading, setHomonymLoading] = useState(false);
  const homonymResolvedRef = useRef<string | null>(null);
  /** Identidade canónica escolhida, reutilizada durante toda esta tentativa. */
  const resolvedDestinationRef = useRef<DestinationOption | null>(null);
  /** Token do pedido de texto em curso: respostas tardias não escrevem estado. */
  const submitTokenRef = useRef(0);

  /**
   * Um único alvo acessível por decisão. A identidade depende do conteúdo da
   * decisão, nunca do render, para evitar saltos enquanto o cartão não muda.
   */
  const activeDecision = useMemo(() => {
    if (pinnedConfirmation) {
      return {
        kind: "pinned-confirmation",
        id: `pinned-confirmation:${pinnedConfirmation.constraintId}:${pinnedConfirmation.name}`,
      };
    }
    if (pinnedRejected) {
      return { kind: "pinned-rejected", id: `pinned-rejected:${pinnedRejected.basePrompt}:${pinnedRejected.reason}` };
    }
    if (localityChoice) {
      return { kind: "locality-choice", id: `locality-choice:${localityChoice.requested}:${localityChoice.options.map((o) => o.id ?? o.label).join("|")}` };
    }
    if (homonymChoice) {
      return { kind: "homonym-choice", id: `homonym-choice:${homonymChoice.name}:${homonymChoice.options.map((o) => o.id).join("|")}` };
    }
    if (availabilityBlock) {
      return { kind: "availability", id: `availability:${availabilityBlock.requestSignature ?? availabilityBlock.basePrompt}:${availabilityBlock.entries.map((e) => `${e.constraintId ?? e.label}:${e.reason}`).join("|")}` };
    }
    if (availabilityTechnical) {
      return { kind: "availability-technical", id: `availability-technical:${availabilityTechnical.basePrompt}` };
    }
    if (constraintBlock) {
      return { kind: "constraint-block", id: `constraint-block:${constraintBlock.area}:${constraintBlock.labels.join("|")}:${constraintBlock.expandedLabel ?? ""}` };
    }
    if (visitScarcity) {
      return { kind: "visit-scarcity", id: `visit-scarcity:${visitScarcity.city}:${visitScarcity.basePrompt}` };
    }
    if (clarification) {
      return { kind: "clarification", id: `clarification:${clarification.basePrompt}:${clarification.missing}:${clarification.question}` };
    }
    return null;
  }, [
    availabilityBlock,
    availabilityTechnical,
    clarification,
    constraintBlock,
    homonymChoice,
    localityChoice,
    pinnedConfirmation,
    pinnedRejected,
    visitScarcity,
  ]);
  const lastFocusedDecisionIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!activeDecision || lastFocusedDecisionIdRef.current === activeDecision.id) return;
    const title = document.getElementById(`decision-title-${activeDecision.kind}`);
    if (!title) return;
    lastFocusedDecisionIdRef.current = activeDecision.id;
    title.focus({ preventScroll: true });
    title.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      block: "center",
    });
  }, [activeDecision]);


  // GPS confirmation dialog when the prompt implies "I'm here now" but no GPS
  const [gpsPromptOpen, setGpsPromptOpen] = useState(false);
  const [gpsPromptLocating, setGpsPromptLocating] = useState(false);
  const pendingGenerateRef = useRef<Parameters<typeof runGenerate>[0] | null>(null);
  const { isPremium, tier: effectiveTier, loading: premiumLoading } = usePremium();
  const { isAuthenticated, user: authUser } = useAuth();
  const t = T[lang];
  const MM = {
    ai: { pt: "Sugerir com IA", en: "AI suggests", it: "Suggerisci con l'IA", es: "Sugerir con IA", fr: "Suggestions IA", de: "KI schlägt vor", ja: "AIにおまかせ" },
    manual: { pt: "Escolho eu", en: "I pick the stops", it: "Scelgo io", es: "Elijo yo", fr: "Je choisis", de: "Ich wähle", ja: "自分で選ぶ" },
    addPlaces: { pt: "Adiciona os sítios que queres visitar", en: "Add the places you want to visit", it: "Aggiungi i luoghi che vuoi visitare", es: "Añade los sitios que quieres visitar", fr: "Ajoute les lieux à visiter", de: "Füge die Orte hinzu, die du besuchen willst", ja: "行きたい場所を追加" },
    poiPh: { pt: "Procura um lugar (ex.: Torre de Belém)", en: "Search a place (e.g. Eiffel Tower)", it: "Cerca un luogo (es.: Colosseo)", es: "Busca un lugar (ej.: Sagrada Família)", fr: "Cherche un lieu (ex. : Tour Eiffel)", de: "Ort suchen (z. B. Brandenburger Tor)", ja: "場所を検索（例：東京タワー）" },
    optimize: { pt: "Otimizar ordem automaticamente", en: "Optimize order automatically", it: "Ottimizza l'ordine automaticamente", es: "Optimizar orden automáticamente", fr: "Optimiser l'ordre automatiquement", de: "Reihenfolge automatisch optimieren", ja: "順番を自動最適化" },
    yourStops: { pt: "As tuas paragens em", en: "Your stops in", it: "Le tue tappe a", es: "Tus paradas en", fr: "Tes arrêts à", de: "Deine Stopps in", ja: "あなたの目的地：" },
    emptyTitle: { pt: "Ainda não adicionaste paragens", en: "No stops added yet", it: "Nessuna tappa aggiunta", es: "Aún no has añadido paradas", fr: "Aucun arrêt ajouté", de: "Noch keine Stopps hinzugefügt", ja: "まだ目的地がありません" },
    emptyHint: { pt: "Pesquisa lugares no painel e monta a tua própria rota com tempos.", en: "Search places in the panel and build your own timed route.", it: "Cerca luoghi nel pannello e crea il tuo itinerario con gli orari.", es: "Busca lugares en el panel y crea tu propia ruta con tiempos.", fr: "Cherche des lieux dans le panneau et crée ta propre route minutée.", de: "Suche Orte im Panel und baue deine eigene Route mit Zeiten.", ja: "パネルで場所を検索して自分のルートを作ろう。" },
    duplicate: { pt: "Esse lugar já está na rota", en: "That place is already in the route", it: "Questo luogo è già nell'itinerario", es: "Ese lugar ya está en la ruta", fr: "Ce lieu est déjà dans la route", de: "Dieser Ort ist schon in der Route", ja: "その場所はすでに追加済みです" },
    arriveBy: { pt: "Chegar até às", en: "Arrive by", it: "Arrivare entro le", es: "Llegar antes de las", fr: "Arriver avant", de: "Ankunft bis", ja: "到着時刻" },
    leaveBy: { pt: "Para chegares a horas, sai às", en: "To arrive on time, leave at", it: "Per arrivare in orario, parti alle", es: "Para llegar a tiempo, sal a las", fr: "Pour arriver à l'heure, pars à", de: "Um pünktlich anzukommen, geh los um", ja: "間に合うには出発時刻：" },
    useAsStart: { pt: "Usar como hora de partida", en: "Use as start time", it: "Usa come orario di partenza", es: "Usar como hora de salida", fr: "Définir comme départ", de: "Als Startzeit verwenden", ja: "出発時刻に設定" },
    notEnough: { pt: "⚠️ Não dá tempo — a rota demora mais do que isso. Tira paragens ou escolhe outra hora.", en: "⚠️ Not enough time — the route takes longer. Remove stops or pick another time.", it: "⚠️ Tempo insufficiente — l'itinerario richiede più tempo. Togli tappe o scegli un altro orario.", es: "⚠️ No da tiempo — la ruta tarda más. Quita paradas o elige otra hora.", fr: "⚠️ Pas assez de temps — la route est plus longue. Retire des arrêts ou change l'heure.", de: "⚠️ Zu wenig Zeit — die Route dauert länger. Entferne Stopps oder wähle eine andere Zeit.", ja: "⚠️ 時間が足りません。停車地を減らすか時刻を変更してください。" },
    marginNote: { pt: "inclui 10 min de margem de segurança", en: "includes a 10 min safety buffer", it: "include 10 min di margine", es: "incluye 10 min de margen", fr: "inclut 10 min de marge", de: "inkl. 10 Min Puffer", ja: "10分の余裕を含む" },
    arriveByHint: { pt: "Diz a que horas queres terminar e dizemos-te a que horas tens de sair.", en: "Tell us when you want to finish and we tell you when to leave.", it: "Dicci a che ora vuoi finire e ti diciamo quando partire.", es: "Dinos a qué hora quieres terminar y te decimos a qué hora salir.", fr: "Dis-nous quand tu veux finir et on te dit quand partir.", de: "Sag uns, wann du fertig sein willst, und wir sagen dir, wann du losmusst.", ja: "終了したい時刻を入力すると出発時刻をお知らせします。" },
    arriveByLocked: { pt: "Desbloquear com Premium", en: "Unlock with Premium", it: "Sblocca con Premium", es: "Desbloquear con Premium", fr: "Débloquer avec Premium", de: "Mit Premium freischalten", ja: "プレミアムで解除" },
    arriveByNeedsRoute: { pt: "Adiciona paragens primeiro para calcular.", en: "Add stops first to calculate.", it: "Aggiungi prima delle tappe per calcolare.", es: "Añade paradas primero para calcular.", fr: "Ajoute des arrêts pour calculer.", de: "Füge zuerst Stopps hinzu.", ja: "先に目的地を追加してください。" },
    planTitle: { pt: "O teu plano", en: "Your plan", it: "Il tuo piano", es: "Tu plan", fr: "Ton plan", de: "Dein Plan", ja: "プラン" },
    depart: { pt: "Sair", en: "Depart", it: "Partenza", es: "Salir", fr: "Départ", de: "Abfahrt", ja: "出発" },
    arriveAt: { pt: "Chegada a", en: "Arrive at", it: "Arrivo a", es: "Llegar a", fr: "Arrivée à", de: "Ankunft an", ja: "到着" },
    leaveStop: { pt: "Saída", en: "Leave", it: "Partenza", es: "Salida", fr: "Départ", de: "Abfahrt", ja: "出発" },
    transit: { pt: "Deslocação", en: "Travel", it: "Spostamento", es: "Trayecto", fr: "Trajet", de: "Fahrt", ja: "移動" },
    finalArrival: { pt: "Chegada final", en: "Final arrival", it: "Arrivo finale", es: "Llegada final", fr: "Arrivée finale", de: "Endankunft", ja: "最終到着" },
    mealBudget: { pt: "Orçamento máx. por refeição", en: "Max budget per meal", it: "Budget massimo per pasto", es: "Presupuesto máx. por comida", fr: "Budget max par repas", de: "Max. Budget pro Mahlzeit", ja: "1食あたりの予算上限" },
    mealBudgetHint: { pt: "Sugerimos restaurantes que cabem neste valor por pessoa.", en: "We'll suggest restaurants that fit this per-person budget.", it: "Suggeriamo ristoranti che rientrano in questo budget a persona.", es: "Sugerimos restaurantes que entren en este presupuesto por persona.", fr: "On suggère des restaurants dans ce budget par personne.", de: "Wir schlagen Restaurants in diesem Budget pro Person vor.", ja: "1人あたりこの予算に収まるお店を提案します。" },
    mealBudgetLocked: { pt: "Desbloquear orçamento Premium", en: "Unlock budget with Premium", it: "Sblocca il budget con Premium", es: "Desbloquear con Premium", fr: "Débloquer avec Premium", de: "Mit Premium freischalten", ja: "プレミアムで解除" },
    premiumTitle: { pt: "Funcionalidades Premium", en: "Premium features", it: "Funzionalità Premium", es: "Funciones Premium", fr: "Fonctionnalités Premium", de: "Premium-Funktionen", ja: "プレミアム機能" },
    premiumFeatArrive: { pt: "Planeador “chegar a horas”", en: "“Arrive on time” planner", it: "Pianificatore «arrivare in orario»", es: "Planificador “llegar a tiempo”", fr: "Planificateur « arriver à l'heure »", de: "„Pünktlich ankommen“-Planer", ja: "「時間通り到着」プランナー" },
    premiumFeatBudget: { pt: "Orçamento máx. por refeição", en: "Max budget per meal", it: "Budget massimo per pasto", es: "Presupuesto máx. por comida", fr: "Budget max par repas", de: "Max. Budget pro Mahlzeit", ja: "1食あたりの予算上限" },
    premiumFeatModes: { pt: "Modos Carro & Transportes", en: "Car & Transit modes", it: "Modalità Auto e Trasporti", es: "Modos Coche y Transporte", fr: "Modes Voiture & Transports", de: "Auto- & ÖPNV-Modi", ja: "車・公共交通モード" },
    premiumFeatResto: { pt: "Restaurantes recomendados", en: "Recommended restaurants", it: "Ristoranti consigliati", es: "Restaurantes recomendados", fr: "Restaurants recommandés", de: "Empfohlene Restaurants", ja: "おすすめレストラン" },
    premiumUnlock: { pt: "Desbloquear Premium", en: "Unlock Premium", it: "Sblocca Premium", es: "Desbloquear Premium", fr: "Débloquer Premium", de: "Premium freischalten", ja: "プレミアムを解除" },
    addStop: { pt: "Adicionar paragem", en: "Add a stop", it: "Aggiungi una tappa", es: "Añadir parada", fr: "Ajouter un arrêt", de: "Stopp hinzufügen", ja: "目的地を追加" },
    suggestMore: { pt: "Sugerir mais sítios", en: "Suggest more places", it: "Suggerisci altri luoghi", es: "Sugerir más lugares", fr: "Suggérer plus de lieux", de: "Mehr Orte vorschlagen", ja: "もっと提案" },
    suggesting: { pt: "A pensar...", en: "Thinking...", it: "Sto pensando...", es: "Pensando...", fr: "Réflexion...", de: "Denke nach...", ja: "考え中..." },
    optimizeNow: { pt: "Otimizar ordem", en: "Optimize order", it: "Ottimizza l'ordine", es: "Optimizar orden", fr: "Optimiser l'ordre", de: "Reihenfolge optimieren", ja: "順番を最適化" },
    optimizedToast: { pt: "Ordem otimizada por proximidade.", en: "Order optimized by proximity.", it: "Ordine ottimizzato per vicinanza.", es: "Orden optimizado por proximidad.", fr: "Ordre optimisé par proximité.", de: "Reihenfolge nach Nähe optimiert.", ja: "近さで順番を最適化しました。" },
    optimizeNeedsCoords: { pt: "Aguarda que o mapa termine de carregar e tenta de novo.", en: "Wait for the map to finish loading and try again.", it: "Attendi il caricamento della mappa e riprova.", es: "Espera a que el mapa termine de cargar e inténtalo de nuevo.", fr: "Attends que la carte finisse de charger et réessaie.", de: "Warte bis die Karte geladen ist und versuche es erneut.", ja: "地図の読み込みを待ってから再試行してください。" },
    suggestModalTitle: { pt: "Sítios extra que talvez gostes", en: "Extra places you might like", it: "Altri luoghi che potrebbero piacerti", es: "Sitios extra que podrían gustarte", fr: "Lieux supplémentaires à découvrir", de: "Weitere Orte, die dir gefallen könnten", ja: "おすすめの追加スポット" },
    suggestModalEmpty: { pt: "Sem mais sugestões agora. Tenta de novo daqui a pouco.", en: "No more suggestions right now. Try again shortly.", it: "Nessun altro suggerimento al momento. Riprova tra poco.", es: "Sin más sugerencias ahora. Inténtalo de nuevo en un momento.", fr: "Pas d'autres suggestions pour le moment.", de: "Aktuell keine weiteren Vorschläge.", ja: "現在、これ以上の提案はありません。" },
    addBtn: { pt: "Adicionar", en: "Add", it: "Aggiungi", es: "Añadir", fr: "Ajouter", de: "Hinzufügen", ja: "追加" },
    added: { pt: "Adicionado", en: "Added", it: "Aggiunto", es: "Añadido", fr: "Ajouté", de: "Hinzugefügt", ja: "追加済" },
    stopAdded: { pt: "Paragem adicionada.", en: "Stop added.", it: "Tappa aggiunta.", es: "Parada añadida.", fr: "Arrêt ajouté.", de: "Stopp hinzugefügt.", ja: "目的地を追加しました。" },
  } as const;
  const mapSectionRef = useRef<HTMLDivElement>(null);
  const itineraryRef = useRef<HTMLDivElement>(null);
  const pendingScrollRef = useRef(false);
  const aiPromptAlternativesRef = useRef<Array<{ city: string; country: string; countryCode: string; lat: number; lng: number }>>([]);
  /** Destino resolvido não é cidade (ilha/província/região) — usa âmbito regional. */
  const areaDestinationRef = useRef(false);

  const [maxMealBudget, setMaxMealBudget] = useState<string>("");
  const lastGpsCommitRef = useRef<{ lat: number; lng: number; at: number } | null>(null);
  const lastHeadingCommitRef = useRef(0);
  const dragStopIndexRef = useRef<number | null>(null);
  const nativeDragActiveRef = useRef<boolean>(false);
  const pointerDragRef = useRef<{ fromIdx: number; targetIdx: number; name: string } | null>(null);
  const autoScrollRafRef = useRef<number | null>(null);
  const autoScrollSpeedRef = useRef(0);
  const loadedFromSavedRef = useRef(false);
  const userReorderedAiRef = useRef(false);
  const autoNowStartRef = useRef(false);

  // Presets covering 30 min → 1 week
  const DURATIONS = [0.5, 1, 1.5, 2, 3, 4, 6, 8, 12, 24, 48, 72, 96, 120, 144, 168];
  /** Duração por defeito de um pedido novo que nada diga (nunca herdar dias). */
  const DEFAULT_DURATION_HOURS = 3;

  const FIRST_MULTIDAY_IDX = DURATIONS.findIndex((d) => d >= 24);
  // Sugestão gratuita de duração: só quando falta a duração e o utilizador
  // perguntou explicitamente quantos dias precisa.
  const durationSuggestion =
    clarification && clarification.missing === "duration" && clarification.city && asksForDurationAdvice(clarification.basePrompt)
      ? suggestTripDuration(clarification.city, lang)
      : null;
  const duration = DURATIONS[durationIdx];
  const planningWindow = tripPlanningWindow(duration);
  const isMultiDay = planningWindow.isMultiDay;
  const numDays = planningWindow.totalDays;
  const activeRouteHours = planningWindow.activeHours;
  const durationLabel = (() => {
    if (duration < 1) return `${Math.round(duration * 60)} min`;
    if (duration < 24) return `${duration} ${t.hours}`;
    const d = Math.round(duration / 24);
    if (d === 7) return lang === "pt" ? "1 semana" : lang === "fr" ? "1 semaine" : lang === "es" ? "1 semana" : lang === "de" ? "1 Woche" : lang === "ja" ? "1週間" : "1 week";
    const dayWord = lang === "pt" ? (d > 1 ? "dias" : "dia") : lang === "fr" ? "j" : lang === "es" ? (d > 1 ? "días" : "día") : lang === "de" ? (d > 1 ? "Tage" : "Tag") : lang === "ja" ? "日" : (d > 1 ? "days" : "day");
    return `${d} ${dayWord}`;
  })();


  const fetchAI = demoFn(
    useServerFn(generateStops),
    (opts) =>
      ({
        stops: demoStops((opts as { data?: { requestedDays?: number } })?.data?.requestedDays ?? 1),
      }) as never,
  );
  const fetchExtra = demoFn(useServerFn(suggestExtraStops), () => ({ stops: [] }) as never);
  const fetchReverseGeocode = useServerFn(reverseGeocodeLocation);
  const fetchForwardGeocode = useServerFn(forwardGeocodeLocation);

  const [extraSuggestions, setExtraSuggestions] = useState<AIStop[] | null>(null);
  const [extraLoading, setExtraLoading] = useState(false);
  const [extraOpen, setExtraOpen] = useState(false);
  const [addedExtra, setAddedExtra] = useState<Set<string>>(new Set());

  const reqIdRef = useRef(0);
  const skipNextGenerateRef = useRef(false);
  const generationInFlightRef = useRef(false);

  /**
   * Sessão de itinerário. Cada clique em "Criar rota" com um pedido diferente
   * abre uma sessão nova: o resultado anterior é apagado por inteiro e só o
   * pedido mais recente pode escrever no estado (respostas tardias de um
   * destino anterior são descartadas).
   */
  const [itinerarySession, setItinerarySession] = useState(0);
  const itinerarySessionRef = useRef(0);
  const sessionSignatureRef = useRef<string>("");
  const isCurrentSession = useCallback(
    (session: number) => session === itinerarySessionRef.current,
    [],
  );
  const routeScopeKey = useMemo(
    () => `${itinerarySession}:${sessionSignatureRef.current}`,
    [itinerarySession],
  );
  const mealBreakKey = useCallback(
    (b: MealBreak) => mealBreakKeyFor(b, routeScopeKey),
    [routeScopeKey],
  );


  // Quota-limit modal state
  const [quotaModal, setQuotaModal] = useState<{ open: boolean; tier: "anon" | "free" | "premium"; reason?: "anon_limit" | "free_limit" | "premium_limit" | "premium_monthly_limit"; resetInMinutes: number }>({
    open: false, tier: "anon", resetInMinutes: 60,
  });

  // Analytics: session start
  useEffect(() => {
    track("session_started");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load meal-prefs: admin defaults from server + optional per-user override from localStorage.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let base: MealPrefs = DEFAULT_MEAL_PREFS;
      try {
        const remote = await getMealDefaultsFn();
        if (remote) base = { ...DEFAULT_MEAL_PREFS, ...remote };
      } catch { /* fallback */ }
      let final: MealPrefs = base;
      try {
        const raw = safeLocalStorageGet("routify-meal-prefs");
        if (raw) final = { ...base, ...JSON.parse(raw) };
      } catch { /* ignore */ }
      if (cancelled) return;
      setActivePrefs(final);
      setPendingPrefs(final);
      // A preferência de refeições NÃO é persistida: cada pedido novo
      // recalcula-a a partir do texto atual e da escolha manual desse pedido.

    })();
    return () => { cancelled = true; };
  }, [getMealDefaultsFn]);


  // Analytics: identify when user logs in
  useEffect(() => {
    if (authUser?.id) identify(authUser.id, { email: authUser.email });
  }, [authUser?.id, authUser?.email]);

  // Lazily fetch phone/website/price-level when the user opens a stop detail.
  // Cached by placeId — no AI credits, only a single Places "Details" call.
  useEffect(() => {
    const pid = detailStop?.placeId;
    if (!pid) return;
    if (placeContactMap[pid]) return;
    setPlaceContactMap((m) => ({ ...m, [pid]: "loading" }));
    fetchPlaceContact({ data: { placeId: pid } })
      .then((info) => setPlaceContactMap((m) => ({ ...m, [pid]: info })))
      .catch(() => setPlaceContactMap((m) => {
        const next = { ...m }; delete next[pid]; return next;
      }));
  }, [detailStop?.placeId, fetchPlaceContact, placeContactMap]);


  /**
   * Abre uma sessão nova de itinerário: apaga TODO o resultado transitório do
   * pedido anterior (paragens, mapa, percurso, refeições, alojamento,
   * recomendações, navegação). Nunca faz merge — só "Adicionar à rota" junta
   * paragens a um itinerário existente.
   */
  function startNewItinerarySession(signature: string) {
    itinerarySessionRef.current += 1;
    sessionSignatureRef.current = signature;
    setItinerarySession(itinerarySessionRef.current);

    setAiStops(null);
    setSpareStops([]);
    setSwapFor(null);
    setManualStops([]);
    setRemoved(new Set());
    setResolvedMap({});
    setRouteLegs([]);
    setRoutePolyline(null);
    setLegMinutes([]);
    setNavigationLeg(null);
    setNavigationRouteLegs([]);
    setNavigationActive(false);
    setActiveStopIdx(0);
    setRestaurants({});
    setHotels({});
    setHotelAlts({});
    setMealRadiusBoost({});
    setStopDetailsMap({});
    setDetailStop(null);
    setStayHotels(null);
    setStayRequested(false);
    setExtraSuggestions(null);
    setExtraOpen(false);
    setAddedExtra(new Set());
    setDiscovery(null);
    setRouteConstraintWarning(null);
    setPlanVisitMinutes(null);
    setLoadedFromSaved(false);


    setUserReorderedAi(false);
    loadedFromSavedRef.current = false;
    userReorderedAiRef.current = false;
    
  }

  async function runGenerate(opts?: {
    silent?: boolean;
    /** Ação "Ver menos paragens por dia" do cartão de escassez de visita. */
    reducedStopsPerDay?: boolean;
    locale?: Locale;
    duration?: number;
    /** Dias escritos pelo utilizador; impedem a IA de aumentar a viagem. */
    requestedDays?: number;
    interests?: InterestId[];
    notes?: string;
    /** Texto do pedido em curso (usado para experiências gastronómicas). */
    requestText?: string;
    /** Interesses detetados no texto deste pedido (não afetam o estado visual dos chips). */
    textInterests?: InterestId[];
    /** Interesses explicitamente selecionados nos chips deste pedido. */
    explicitInterests?: InterestId[];

    mode?: Mode;
    /** Modo local de um pedido híbrido; o modo principal continua entre zonas. */
    localMode?: Mode;
    startTime?: string;
    mealPrefs?: MealPrefs;
    mealPreference?: MealPreference;

    useGpsAsStart?: boolean;
    userPosition?: { lat: number; lng: number } | null;
    exhaustive?: boolean;
    categoryFocus?: string;
    requestedStopCategory?: string;
    requestedStopQuery?: string;
    categoryFocusQuery?: string;
    startPlace?: string;
    namedStartPosition?: { name: string; lat: number; lng: number } | null;
    namedRoute?: string;
    requiredPlaces?: string[];
    requiredStopConstraints?: import("@/lib/required-constraints").RequiredStopConstraint[];
    /** Paragens fixas escolhidas pela pessoa; o servidor re-confirma. */
    pinnedStops?: Array<{ constraintId: string; placeId: string }>;
    /** Área alargada confirmada: concreta, nomeada e com raio limitado. */
    expandedArea?: ExpandedArea | null;
    geographicScope?: GeographicScope;
    /** Identidade escolhida pelo utilizador; nunca pode ser re-resolvida. */
    canonicalDestination?: DestinationOption;
    /** Origem da continuação (cartão de escassez) — registada no trace. */
    continuation?: "initial" | "widen_area" | "shorten_duration" | "retry";
  }) {
    if (skipNextGenerateRef.current) {
      skipNextGenerateRef.current = false;
      return;
    }
    // Uma só geração de cada vez: duplo toque em "Tentar novamente" não pode
    // originar duas chamadas nem duas reservas de crédito.
    if (generationInFlightRef.current) {
      setTechnicalRetryBusy(false);
      return;
    }
    generationInFlightRef.current = true;
    setConstraintBlock(null);
    setVisitScarcity(null);
    setTechnicalFailure(null);


    const generationLocale = opts?.locale ?? locale;
    const generationDuration = opts?.duration ?? duration;
    const generationInterests = opts?.interests ?? interests;
    const generationTextInterests = opts?.textInterests ?? [];
    const generationExplicitInterests = opts?.explicitInterests ?? [];
    const rawGenerationNotes = opts?.notes ?? notes;
    const generationMode = opts?.mode ?? mode;
    const generationLocalMode = opts?.localMode;
    const generationStartTime = opts?.startTime ?? startTime;
    // Sem refeições, o tempo reservado para pausas volta para as visitas.
    const basePrefs = opts?.mealPrefs ?? activePrefs;
    // Foco exclusivo deste pedido; um pedido sem foco limpa o anterior.
    const generationCategoryFocus = opts?.categoryFocus ?? null;
    setSessionCategoryFocus(generationCategoryFocus);
    // A preferência do pedido em curso (calculada antes do setState) manda.
    const requestTextForMeal = [
      opts?.requestText ?? lastRequestTextRef.current ?? "",
      opts?.notes ?? notes ?? "",
    ].filter(Boolean).join(" ");
    // Pedido só de restaurantes: a lista é a refeição — nada de pausa genérica
    // com um restaurante fora da cozinha/localidade pedidas.
    const generationMealPreference =
      generationCategoryFocus === "sweets" ||
      generationCategoryFocus === "breakfast" ||
      detectRestaurantsOnlyRequest(requestTextForMeal)
        ? "none"
        : (opts?.mealPreference ?? mealPreference);
    const generationPrefs =
      generationMealPreference === "none" ? { ...basePrefs, eatMinutes: 0 } : basePrefs;

    // Gastronomia específica ("pastel de nata numa pastelaria típica") é uma
    // EXPERIÊNCIA pedida, não uma refeição: sobrevive a `mealPreference: none`
    // e só cai quando o próprio pedido nega esses estabelecimentos.
    const specialtySourceText = [
      opts?.requestText ?? lastRequestTextRef.current ?? "",
      rawGenerationNotes ?? "",
      opts?.notes ?? notes ?? "",
    ]
      .filter(Boolean)
      .join(" ");
    const generationSpecialties = detectFoodExperience(specialtySourceText).specialties;
    // Cozinha explícita ("restaurantes japoneses"): condição vinculativa do
    // pedido — o servidor só aceita locais provados dessa cozinha.
    const generationCuisine = detectCuisineIntent(specialtySourceText)?.requested;
    // Localidade escrita pelo utilizador ("Leça da Palmeira"): vinculativa em
    // todos os locais da rota. Sem resultados, perguntamos antes de alargar.
    const generationExplicitLocality =
      extractExplicitLocality(specialtySourceText) ??
      extractRequestLocality(specialtySourceText) ??
      undefined;




    const specialtyDirective = specialtyPromptDirective(
      generationSpecialties,
      [opts?.locale?.city ?? locale.city].filter(Boolean) as string[],
    );
    // O limite do servidor é 1200 caracteres: cortar aqui evita que uma
    // diretiva gastronómica longa faça o pedido inteiro falhar na validação.
    const generationNotes = (specialtyDirective
      ? `${rawGenerationNotes ?? ""}\n${specialtyDirective}`.trim()
      : rawGenerationNotes
    )?.slice(0, 1200);

    // Logística de chegada declarada no pedido ("aterro às 14h no aeroporto",
    // "chego ao hotel às 15h"): enviada ao servidor para o dia 1 perder os
    // minutos de desembarque/bagagem/transferência/check-in antes da 1.ª
    // visita. "Criar sem logística" não envia nada.
    const generationLogisticsSource = [
      opts?.requestText ?? lastRequestTextRef.current ?? "",
      rawGenerationNotes ?? "",
    ].filter(Boolean).join(" ");
    const generationLogistics = parseTravelLogistics(generationLogisticsSource);
    const generationArrivalLogistics =
      generationLogistics.skipLogistics ||
      !generationLogistics.arrival ||
      generationLogistics.arrival.timeMinutes == null
        ? undefined
        : {
            kind: generationLogistics.arrival.kind,
            timeMinutes: generationLogistics.arrival.timeMinutes,
            // Sem hotel escolhido não há check-in: hotel mencionado ≠ hotel selecionado.
            includeCheckin: false,

          };

    // Espelho client-side da decisão vinculativa do servidor (mesma função
    // pura, mesmos sinais deste pedido): chips/texto/notas. Guarda o sinal
    // para "Sugerir mais sítios" — nunca derivado de refeições ou horários.
    lastFoodIntentRef.current = computeFoodIntent({
      requestText: opts?.requestText ?? lastRequestTextRef.current ?? "",
      notes: rawGenerationNotes ?? null,
      explicitInterests: generationExplicitInterests.length
        ? generationExplicitInterests
        : (opts?.interests ?? interests),
    }).foodStopIntent;



    const generationUseGpsAsStart = opts?.useGpsAsStart ?? useGpsAsStart;
    const generationUserPos = opts?.userPosition === undefined ? userPos : opts.userPosition;
    const generationRequestedStopCategory = opts?.requestedStopCategory;
    // Especialidade pedida sem estabelecimento nomeado ("provar um doce
    // típico"): força a pesquisa de uma pastelaria/café real na localidade.
    const generationRequestedStopQuery =
      opts?.requestedStopQuery ??
      // Com foco exclusivo de categoria, a pesquisa do foco já cobre a
      // especialidade: uma pesquisa secundária só serviria para gerar avisos.
      (!opts?.requestedStopCategory && !opts?.categoryFocusQuery && !opts?.categoryFocus
        ? specialtyStopQuery(generationSpecialties, opts?.locale?.city ?? locale.city)
        : undefined);

    const generationCategoryFocusQuery = opts?.categoryFocusQuery;
    const generationStartPlace = opts?.startPlace;

    const generationNamedStartPosition = opts?.namedStartPosition ?? null;
    const generationNamedRoute = opts?.namedRoute;
    const generationRequiredPlaces = opts?.requiredPlaces ?? [];
    const generationRequiredStopConstraints = opts?.requiredStopConstraints ?? [];
    const generationPinnedStops = opts?.pinnedStops ?? [];
    const generationExpandedArea = opts?.expandedArea ?? expandedAreaRef.current ?? null;
    // Um destino que não é cidade (ilha/província/região, ex.: Bali) tem de
    // usar âmbito regional; senão a validação estrita por localidade rejeita
    // tudo e a rota sai vazia.
    const generationGeographicScope =
      opts?.geographicScope ?? (areaDestinationRef.current ? ("regional" as GeographicScope) : undefined);

    const generationRequestId = globalThis.crypto?.randomUUID?.()
      ?? `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;

    const myReq = ++reqIdRef.current;

    // Assinatura do pedido: destino canónico (fornecedor + id + país +
    // coordenadas), duração, interesses e modo. Se mudou, este é um pedido
    // NOVO: a rota anterior é apagada já, para nunca ser mostrada como se
    // pertencesse a este pedido nem se misturar com o resultado novo.
    const nextSignature = itinerarySessionSignature({
      provider: generationLocale.provider,
      providerPlaceId: generationLocale.providerPlaceId,
      countryCode: generationLocale.countryCode,
      city: generationLocale.city,
      lat: generationLocale.lat,
      lng: generationLocale.lng,
      durationHours: generationDuration,
      interests: generationInterests,
      mode: generationMode,
    });
    if (nextSignature !== sessionSignatureRef.current) {
      startNewItinerarySession(nextSignature);
    }
    const mySession = itinerarySessionRef.current;
    setAiLoading(true);

    // Garantir que nenhum valor não-finito chega ao servidor: lat/lng inválidas
    // ou duração corrompida originam mensagem clara no cliente sem gastar crédito.
    const payloadFiniteCheck = (() => {
      if (!Number.isFinite(generationLocale.lat) || !Number.isFinite(generationLocale.lng)) {
        return { ok: false, field: lang === "pt" ? "destino" : "destination" } as const;
      }
      if (!Number.isFinite(generationDuration) || generationDuration <= 0) {
        return { ok: false, field: lang === "pt" ? "duração" : "duration" } as const;
      }
      if (generationUserPos && (!Number.isFinite(generationUserPos.lat) || !Number.isFinite(generationUserPos.lng))) {
        return { ok: false, field: lang === "pt" ? "localização atual" : "current location" } as const;
      }
      if (generationNamedStartPosition && (!Number.isFinite(generationNamedStartPosition.lat) || !Number.isFinite(generationNamedStartPosition.lng))) {
        return { ok: false, field: lang === "pt" ? "ponto de partida" : "starting point" } as const;
      }
      return { ok: true } as const;
    })();
    if (!payloadFiniteCheck.ok) {
      setAiLoading(false);
      setTechnicalRetryBusy(false);
      generationInFlightRef.current = false;
      toast.error(
        lang === "pt"
          ? `O campo “${payloadFiniteCheck.field}” tem um valor inválido. Verifica e tenta novamente.`
          : `The “${payloadFiniteCheck.field}” field has an invalid value. Please check and try again.`,
      );
      return;
    }

    // Contexto do pedido em curso: o cartão de escassez reutiliza estes
    // argumentos para "menos paragens" e "incluir gastronomia".
    visitScarcityCtxRef.current = { generateArgs: opts ?? {} };
    // Contexto EXATO desta geração (pedido, área, duração, interesses,
    // seleções fixas e ação atual), criado antes da chamada ao servidor.
    const retryArgs = { ...(opts ?? {}) };
    generationRetryContextRef.current = {
      generationRequestId,
      run: () => { void runGenerate(retryArgs); },
    };
    setTechnicalFailure(null);
    fetchAI({
      data: {
        generationRequestId,
        city: generationLocale.city,
        country: generationLocale.country,
        // Generate and validate stops around the requested city centre. The
        // map can still start directions from the user's GPS position when
        // they are nearby, but AI validation must never drift to another city.
        lat: generationLocale.lat,
        lng: generationLocale.lng,
        language: lang,
        currency: generationLocale.currency,
        durationHours: generationDuration,
        requestedDays: opts?.requestedDays,
        mode: generationMode,
        localMode: generationLocalMode,
        interests: generationInterests,
        requestText: opts?.requestText ?? lastRequestTextRef.current ?? "",
        textInterests: generationTextInterests,
        explicitInterests: generationExplicitInterests,
        reducedStopsPerDay: opts?.reducedStopsPerDay || undefined,
        notes: generationNotes,
        anonId: getAnonId(),
        requestId: generationRequestId,
        startTime: generationStartTime || undefined,
        mealPrefs: generationPrefs,
        userLat: generationUseGpsAsStart ? generationUserPos?.lat : undefined,
        userLng: generationUseGpsAsStart ? generationUserPos?.lng : undefined,
        exhaustive: opts?.exhaustive || undefined,
        categoryFocus: opts?.categoryFocus as any,
        requestedStopCategory: generationRequestedStopCategory as any,
        requestedStopQuery: generationRequestedStopQuery,
        categoryFocusQuery: generationCategoryFocusQuery,
        startPlace: generationStartPlace,
        startLat: generationNamedStartPosition?.lat,
        startLng: generationNamedStartPosition?.lng,
        namedRoute: generationNamedRoute,
        requiredPlaces: generationRequiredPlaces,
        requiredStopConstraints: generationRequiredStopConstraints,
        pinnedStops: generationPinnedStops,
        expandedArea: generationExpandedArea,
        geographicScope: generationGeographicScope,
        arrivalLogistics: generationArrivalLogistics,
        cuisine: generationCuisine,
        explicitLocality: generationExplicitLocality,
        generationContinuation: opts?.continuation ?? "initial",
      },
    })
      .then((r) => {
        if (myReq !== reqIdRef.current || !isCurrentSession(mySession)) return;
        if (r.error === "generation_in_progress" || r.error === "generation_temporarily_limited") {
          const guard = "costGuard" in r && r.costGuard && !r.costGuard.ok ? r.costGuard : null;
          const secs = guard ? Math.max(1, guard.retryAfterSeconds) : 30;
          const wait = secs >= 120 ? `${Math.ceil(secs / 60)} min` : `${secs} s`;
          toast.info(generationGuardCopy(guard?.reason ?? "unavailable", wait, lang));

          setAiLoading(false);
          return;
        }
        if (r.error === "quota_exceeded" && "quota" in r && r.quota && !r.quota.ok) {
          track("quota_limit_hit", { tier: r.quota.tier, reason: r.quota.reason });
          track("paywall_view", { source: "quota_limit", tier: r.quota.tier });
          setQuotaModal({ open: true, tier: r.quota.tier, reason: r.quota.reason, resetInMinutes: r.quota.resetInMinutes });
          setAiLoading(false);
          return;
        }
        if (
          r.error === "required_constraints_unmet" &&
          "missingRequiredConstraints" in r &&
          Array.isArray(r.missingRequiredConstraints)
        ) {
          const labels = (r.missingRequiredConstraints as unknown[]).filter(
            (v): v is string => typeof v === "string" && v.trim().length > 0,
          );
          const scopeName = generationExplicitLocality ?? generationLocale.city;
          const alreadyExpanded = generationExpandedArea;
          // NUNCA descartar as restrições obrigatórias: alargar a zona é
          // procurar melhor, não gerar uma rota só com sushi.
          const widenArea: ExpandedArea | null = alreadyExpanded
            ? null
            : {
                label: expandedAreaLabel(scopeName, null, lang),
                localities: [scopeName],
                ...(Number.isFinite(generationLocale.lat) && Number.isFinite(generationLocale.lng)
                  ? { center: { lat: generationLocale.lat, lng: generationLocale.lng } }
                  : {}),
                radiusMeters: 6000,
              };
          setConstraintBlock({
            labels,
            area: scopeName,
            expandedLabel: alreadyExpanded?.label ?? null,
            onWiden: widenArea
              ? () => {
                  setConstraintBlock(null);
                  applyExpandedArea(widenArea);
                  void runGenerate({ ...opts, expandedArea: widenArea });
                }
              : null,
          });
          setAiLoading(false);
          return;
        }

        if (r.stops.length) {
          const missingRequiredPlaces =
            "missingRequiredPlaces" in r && Array.isArray(r.missingRequiredPlaces)
              ? r.missingRequiredPlaces.filter((name): name is string => typeof name === "string" && name.trim().length > 0)
              : [];
          const unresolvedStartPlace =
            "unresolvedStartPlace" in r && typeof r.unresolvedStartPlace === "string"
              ? r.unresolvedStartPlace.trim()
              : "";
          const requiredPlacesExceedTime =
            "requiredPlacesExceedTime" in r && r.requiredPlacesExceedTime === true;
          const unresolvedRequestedStopCategory =
            "unresolvedRequestedStopCategory" in r && typeof r.unresolvedRequestedStopCategory === "string"
              ? r.unresolvedRequestedStopCategory
              : "";
          const constraintMessages: string[] = [];

          if (missingRequiredPlaces.length) {
            constraintMessages.push(
              lang === "pt" ? `Não foi possível validar e incluir: ${missingRequiredPlaces.join(", ")}. Confirma o nome ou escolhe o local no mapa.`
              : lang === "es" ? `No fue posible validar e incluir: ${missingRequiredPlaces.join(", ")}. Confirma el nombre o elige el lugar en el mapa.`
              : lang === "fr" ? `Impossible de valider et d'inclure : ${missingRequiredPlaces.join(", ")}. Vérifie le nom ou choisis le lieu sur la carte.`
              : lang === "de" ? `Folgende Orte konnten nicht geprüft und aufgenommen werden: ${missingRequiredPlaces.join(", ")}. Prüfe den Namen oder wähle den Ort auf der Karte.`
              : lang === "ja" ? `次の場所を確認してルートに追加できませんでした：${missingRequiredPlaces.join("、")}。名前を確認するか、地図上で選択してください。`
              : `We couldn't validate and include: ${missingRequiredPlaces.join(", ")}. Check the name or choose the place on the map.`,
            );
          }

          if (unresolvedRequestedStopCategory) {
            constraintMessages.push(
              unresolvedCategoryMessage(unresolvedRequestedStopCategory, lang),
            );
          }

          const cuisineScarce =
            "cuisineScarce" in r && r.cuisineScarce && typeof r.cuisineScarce === "object"
              ? (r.cuisineScarce as { cuisine: string; area: string })
              : null;
          if (cuisineScarce) {
            constraintMessages.push(
              cuisineScarceMessage(cuisineScarce.cuisine, cuisineScarce.area, lang),
            );
          }



          if (unresolvedStartPlace) {
            constraintMessages.push(
              lang === "pt" ? `Não foi possível localizar o ponto de partida "${unresolvedStartPlace}". A rota foi criada sem essa origem.`
              : lang === "es" ? `No fue posible localizar el punto de partida "${unresolvedStartPlace}". La ruta se creó sin ese origen.`
              : lang === "fr" ? `Impossible de localiser le point de départ « ${unresolvedStartPlace} ». L'itinéraire a été créé sans ce départ.`
              : lang === "de" ? `Der Startpunkt „${unresolvedStartPlace}“ konnte nicht gefunden werden. Die Route wurde ohne diesen Start erstellt.`
              : lang === "ja" ? `出発地点「${unresolvedStartPlace}」を特定できなかったため、その地点なしでルートを作成しました。`
              : `We couldn't locate the starting point "${unresolvedStartPlace}". The route was created without that origin.`,
            );
          }

          if (requiredPlacesExceedTime) {
            constraintMessages.push(
              lang === "pt" ? "Os locais obrigatórios excedem o tempo disponível. Foram preservados; aumenta a duração ou remove um deles."
              : lang === "es" ? "Los lugares obligatorios superan el tiempo disponible. Se conservaron; aumenta la duración o elimina uno."
              : lang === "fr" ? "Les lieux obligatoires dépassent le temps disponible. Ils ont été conservés ; augmente la durée ou supprime l'un d'eux."
              : lang === "de" ? "Die Pflichtorte überschreiten die verfügbare Zeit. Sie wurden beibehalten; erhöhe die Dauer oder entferne einen davon."
              : lang === "ja" ? "必須の場所が利用可能な時間を超えています。場所は維持されているため、所要時間を延ばすか、いずれかを削除してください。"
              : "The required places exceed the available time. They were preserved; increase the duration or remove one.",
            );
          }

          const constraintWarning = constraintMessages.join(" ");
          setRouteConstraintWarning(constraintWarning || null);
          if (constraintWarning) toast.warning(constraintWarning, { duration: 12000 });

          // Nota de roteiro mais leve: rota válida, mas abaixo do alvo de
          // ritmo (modo genérico). Aviso de tempo livre, nunca bloqueio.
          const paceNotice =
            "itineraryPaceNotice" in r && r.itineraryPaceNotice && typeof r.itineraryPaceNotice === "object"
              ? (r.itineraryPaceNotice as { target: number; stops: number })
              : null;
          setItineraryPaceNotice(paceNotice);
          setInterestFillNotice(!!("interestFillNotice" in r && r.interestFillNotice));
          setCalmSmallPlaceNotice(
            "calmSmallPlaceNotice" in r && r.calmSmallPlaceNotice && typeof r.calmSmallPlaceNotice === "object"
              ? (r.calmSmallPlaceNotice as { area: string; stops: number; areaRadiusMeters: number })
              : null,
          );
          setSpareStops("spareStops" in r && Array.isArray(r.spareStops) ? (r.spareStops as AIStop[]) : []);
          setSwapFor(null);

          orderLog("index.generate", "AI itinerary generated → loadedFromSaved=false", { stops: r.stops.map((s) => s.name) });
          loadedFromSavedRef.current = false;
          userReorderedAiRef.current = false;
          setStayHotels(null);
          // Nova geração pedida pela pessoa: novo `generationId` (nunca derivado
          // dos locais) e registo de identidades lógicas limpo.
          beginRouteGeneration();
          setAiStops(r.stops);
          // Rota criada com sucesso: só agora o campo de texto é limpo.
          setAiPrompt("");

          // Duração pedida como contrato: o servidor devolve a visita já
          // alongada para preencher a janela quando faltava tempo.
          setPlanVisitMinutes(
            "visitMinutes" in r && typeof r.visitMinutes === "number" && r.visitMinutes > 0
              ? r.visitMinutes
              : null,
          );

          setNamedRouteStart("start" in r && r.start ? r.start : generationNamedStartPosition);
          setLoadedFromSaved(false);
          setUserReorderedAi(false);
          // Atomically reset transient enrichments now that new stops arrived.
          setRemoved(new Set());
          setResolvedMap({});
          setRouteLegs([]);
          setRoutePolyline(null);
          setNavigationLeg(null);
          setRestaurants({});
          setHotels({});
          setStopDetailsMap({});
          setLegMinutes([]);
          track("itinerary_generated", { city: generationLocale.city, country: generationLocale.country, mode: generationMode, durationHours: generationDuration, stops: r.stops.length, authenticated: isAuthenticated });
          // If the AI-prompt path resolved to an ambiguous city name and the
          // itinerary came back unusually short, offer same-name alternatives
          // in other countries so the user can switch with one click.
          const alts = aiPromptAlternativesRef.current;
          if (alts.length && r.stops.length < 3) {
            const alt = alts[0];
            toast(
              lang === "pt"
                ? `Poucas atrações em ${generationLocale.city}, ${generationLocale.country}. Querias ${alt.city}, ${alt.country}?`
                : `Few attractions in ${generationLocale.city}, ${generationLocale.country}. Did you mean ${alt.city}, ${alt.country}?`,
              {
                action: {
                  label: lang === "pt" ? "Mudar" : "Switch",
                  onClick: () => {
                    const cur = currencyForCountry(alt.countryCode);
                    const altLocale: Locale = {
                      city: alt.city, country: alt.country, countryCode: alt.countryCode,
                      lat: alt.lat, lng: alt.lng,
                      currency: cur.currency, currencySymbol: cur.symbol,
                      language: lang, stops: [],
                    };
                    setLocale(altLocale);
                    aiPromptAlternativesRef.current = alts.slice(1);
                    pendingScrollRef.current = true;
                    setTimeout(() => { void runGenerate({ locale: altLocale }); }, 50);
                  },
                },
                duration: 12000,
              },
            );
          }
          aiPromptAlternativesRef.current = [];
        } else {
          const alts = aiPromptAlternativesRef.current;
          if (alts.length) {
            const alt = alts[0];
            toast.error(
              lang === "pt"
                ? `Sem rota em ${generationLocale.city}, ${generationLocale.country}. Querias ${alt.city}, ${alt.country}?`
                : `No route in ${generationLocale.city}, ${generationLocale.country}. Did you mean ${alt.city}, ${alt.country}?`,
              {
                action: {
                  label: lang === "pt" ? "Tentar" : "Try",
                  onClick: () => {
                    const cur = currencyForCountry(alt.countryCode);
                    const altLocale: Locale = {
                      city: alt.city, country: alt.country, countryCode: alt.countryCode,
                      lat: alt.lat, lng: alt.lng,
                      currency: cur.currency, currencySymbol: cur.symbol,
                      language: lang, stops: [],
                    };
                    setLocale(altLocale);
                    aiPromptAlternativesRef.current = alts.slice(1);
                    pendingScrollRef.current = true;
                    setTimeout(() => { void runGenerate({ locale: altLocale }); }, 50);
                  },
                },
                duration: 15000,
              },
            );
            aiPromptAlternativesRef.current = [];
          } else {
            const scarcity =
              r && typeof r === "object" && "visitScarcity" in r && r.visitScarcity
                ? (r.visitScarcity as {
                    city: string;
                    found: number;
                    target: number;
                    minimum: number;
                    minimumViableStopsPerDay?: number[];
                    stopsPerDay?: number[];
                    targetStops?: number;
                    coveredMinutes: number;
                    requestedMinutes: number;
                    durationDays?: number;
                    areaLabel?: string;
                    areaRadiusMeters?: number;
                    collectionComplete?: boolean;
                    eligibleCount?: number;
                    cause?:
                      | "collection_incomplete"
                      | "area_scarcity"
                      | "collector_fault"
                      | "route_assembly_failure"
                      | "schedule_unavailable";
                  })
                : null;
            if (scarcity) {
              // Escassez de visita: cartão com quatro opções — nunca toast
              // genérico e nunca preencher com restaurantes.
              setScarcityAction(null);
              setVisitScarcity({
                city: scarcity.city,
                found: scarcity.found,
                target: scarcity.target,
                minimum: scarcity.minimum,
                minimumViableStopsPerDay: scarcity.minimumViableStopsPerDay,
                stopsPerDay: scarcity.stopsPerDay,
                targetStops: scarcity.targetStops,
                coveredMinutes: scarcity.coveredMinutes,
                requestedMinutes: scarcity.requestedMinutes,
                basePrompt: opts?.requestText ?? lastRequestTextRef.current ?? "",
                durationDays: scarcity.durationDays ?? Math.max(1, Math.round(generationDuration / 24)),
                areaLabel: scarcity.areaLabel ?? scarcity.city,
                areaRadiusMeters: scarcity.areaRadiusMeters ?? 0,
                collectionComplete: scarcity.collectionComplete !== false,
                eligibleCount: scarcity.eligibleCount,
                cause: scarcity.cause ?? "area_scarcity",
              });
            } else {

              // Localidade/cozinha vinculativas sem locais válidos: perguntar
              // antes de alargar, nunca mostrar locais de outra zona.
              const scarce =
                r && typeof r === "object" && "cuisineScarce" in r && r.cuisineScarce
                  ? (r.cuisineScarce as { cuisine: string; area: string })
                  : null;
              if (scarce) {
                toast.error(cuisineScarceMessage(scarce.cuisine, scarce.area, lang));
              } else if (r.error === "needs_locality_choice") {
                // Decisão de produto (localidade ambígua), não falha técnica.
                toast.error(
                  failureReasonMessage("needs_locality_choice", lang, {
                    area: generationExplicitLocality ?? generationLocale.city,
                  }),
                );
              } else {
                // Destino já resolvido: qualquer resultado não interpretável é
                // falha técnica estruturada, nunca "tenta outra cidade".
                const tf =
                  r && typeof r === "object" && "technicalFailure" in r && r.technicalFailure
                    ? (r.technicalFailure as { origin: string; generationRequestId: string; traceId?: string })
                    : null;
                setTechnicalFailure(tf ?? { origin: "missing_response", generationRequestId });
              }
            }
          }
        }
      })
      .catch((e) => {
        if (myReq !== reqIdRef.current || !isCurrentSession(mySession)) return;
        console.error("Itinerary generation failed", e);
        // Rede/timeout/pedido que nem chegou ao servidor: sem traceId, o
        // cartão usa o identificador criado pelo cliente.
        setTechnicalFailure({ origin: "client_error", generationRequestId });
      })
      .finally(() => {
        setTechnicalRetryBusy(false);
        generationInFlightRef.current = false;
        if (myReq === reqIdRef.current && isCurrentSession(mySession)) setAiLoading(false);
      });
  }

  /**
   * Passos de alargamento da zona a partir do raio REALMENTE usado. O centro
   * mantém-se sempre no destino pedido; só o raio cresce, até 25 km.
   */
  const WIDEN_MAX_RADIUS_M = 25000;
  function nextWidenRadiusMeters(usedRadiusMeters: number): number | null {
    const base = Number.isFinite(usedRadiusMeters) && usedRadiusMeters > 0 ? usedRadiusMeters : 3000;
    if (base >= WIDEN_MAX_RADIUS_M) return null;
    return Math.min(WIDEN_MAX_RADIUS_M, Math.round(base * 2));
  }

  /** Marca o início de uma ação do cartão (carregamento visível + retoma). */
  const scarcityActionRunRef = useRef<{ started: boolean } | null>(null);

  /** "Alargar a zona": nova área concreta, mesmo pedido, nova execução real. */
  function widenScarcityArea(radiusMeters?: number) {
    const args = visitScarcityCtxRef.current?.generateArgs;
    const radius = radiusMeters ?? nextWidenRadiusMeters(visitScarcity?.areaRadiusMeters ?? 0) ?? 0;
    if (!args || !radius) return;
    const scopeName = visitScarcity?.city || locale.displayCity || locale.city;
    const widenArea: ExpandedArea = {
      label: expandedAreaLabel(scopeName, null, lang),
      localities: [scopeName],
      // O centro é SEMPRE a localidade pedida — nunca um local escolhido.
      center: { lat: locale.lat, lng: locale.lng },
      radiusMeters: radius,
    };
    applyExpandedArea(widenArea);
    const nextArgs = { ...args, expandedArea: widenArea, continuation: "widen_area" as const };
    visitScarcityCtxRef.current = { generateArgs: nextArgs };
    scarcityActionRunRef.current = { started: false };
    setScarcityAction({ kind: "widen_area", phase: "running", areaRadiusMeters: radius });
    void runGenerate(nextArgs);
  }

  /** "Reduzir duração": escolha explícita, duração vinculativa na continuação. */
  function applyShorterDuration(toDays: number) {
    const args = visitScarcityCtxRef.current?.generateArgs;
    if (!args || toDays < 1) return;
    const nextDuration = toDays * 24;
    // A duração escolhida entra nos argumentos da continuação (e por isso na
    // assinatura da sessão): o texto original "2 dias" não volta a sobrepor-se.
    const nextArgs = { ...args, duration: nextDuration, requestedDays: toDays, continuation: "shorten_duration" as const };
    visitScarcityCtxRef.current = { generateArgs: nextArgs };
    scarcityActionRunRef.current = { started: false };
    setDurationAdjustedDays(toDays);
    setScarcityAction({ kind: "shorten_duration", phase: "running", days: toDays });
    void runGenerate(nextArgs);
  }

  // Fim de uma ação do cartão: rota, novo cartão ou falha técnica com
  // "Tentar novamente" — nunca ficar visualmente no mesmo estado.
  useEffect(() => {
    const run = scarcityActionRunRef.current;
    if (!run) return;
    if (aiLoading) {
      run.started = true;
      return;
    }
    if (!run.started) return;
    scarcityActionRunRef.current = null;
    const gotRoute = (aiStops?.length ?? 0) > 0;
    const gotCard = visitScarcity !== null;
    if (gotRoute || gotCard) {
      setScarcityAction(null);
      requestAnimationFrame(() => {
        if (gotCard) document.getElementById("decision-title-visit-scarcity")?.scrollIntoView({ behavior: "smooth", block: "center" });
        else mapSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } else {
      setScarcityAction((cur) => (cur ? { ...cur, phase: "error" } : cur));
    }
  }, [aiLoading, aiStops, visitScarcity]);


  // NOTE: routes are generated ONLY when the user clicks the "Generate" button
  // (no auto-generation on city/lang/duration/mode change — that wastes AI credits).

  /** Chave do destino ativo (cidade + coordenadas + origem). */
  function currentDestinationKey(target?: { city: string; lat: number; lng: number }) {
    const l = target ?? locale;
    return destinationConfirmationKey({
      city: l.city,
      lat: l.lat,
      lng: l.lng,
      source: destinationSourceRef.current,
    });
  }

  /** Regista de onde veio a cidade ativa; geolocalização nunca é explícita. */
  function setDestinationSource(
    source: DestinationSource,
    target?: { city: string; lat: number; lng: number },
  ) {
    destinationSourceRef.current = source;
    confirmedDestinationKeyRef.current =
      source === "user_selected" || source === "prompt_named"
        ? currentDestinationKey(target)
        : null;
    setCityConfirm(null);
  }

  /** Marca a cidade/coordenadas atuais como confirmadas pelo utilizador. */
  function confirmActiveDestination() {
    confirmedDestinationKeyRef.current = currentDestinationKey();
    setCityConfirm(null);
  }

  /** "Escolher outra cidade": só foca o campo, não gera nem gasta. */
  function changeCityFromConfirm() {
    setCityConfirm(null);
    manualLocationRef.current = true;
    setGeoNotice(null);
    document.querySelector<HTMLInputElement>("input[data-places-autocomplete]")?.focus();
  }

  /**
   * Limpa o rascunho de destino do pedido anterior. A identidade resolvida
   * (fornecedor + id + país + coordenadas) vive apenas dentro da sessão do
   * pedido que a produziu: nunca transita para o clique seguinte em
   * "Criar rota", mesmo que o nome escrito seja o mesmo.
   */
  function resetDestinationDraft() {
    resolvedDestinationRef.current = null;
    homonymResolvedRef.current = null;
    aiPromptAlternativesRef.current = [];
    areaDestinationRef.current = false;
    setHomonymChoice(null);
  }

  /**
   * Clique em "Criar itinerário": a confirmação é intercetada ANTES de
   * reservar crédito, chamar IA/Maps, criar generation_attempt ou ligar loading.
   */
  function requestGenerate() {
    const confirmation = resolvePreCreateConfirmation(
      {
        source: destinationSourceRef.current,
        city: locale.city,
        confirmedKey: confirmedDestinationKeyRef.current,
        currentKey: currentDestinationKey(),
      },
      lang,
    );
    if (confirmation) {
      setCityConfirmTarget("generate");
      setCityConfirm(confirmation);
      return;
    }
    setCityConfirm(null);
    // Destino manual = pedido novo: a identidade escolhida antes não conta.
    resetDestinationDraft();
    void runGenerate();
  }

  function cityConfirmPanel(onConfirm: () => void) {
    if (!cityConfirm) return null;
    return (
      <div
        data-testid="city-confirm"
        className="mt-2 rounded-2xl border border-border/70 bg-muted/40 p-4"
      >
        <p className="text-sm font-semibold text-foreground">{cityConfirm.message}</p>
        <div className="mt-2 flex flex-wrap gap-2">
          <button
            type="button"
            data-testid="city-confirm-yes"
            onClick={onConfirm}
            className="rounded-xl bg-foreground px-4 py-2 text-sm font-semibold text-background"
          >
            {cityConfirm.confirmLabel}
          </button>
          <button
            type="button"
            data-testid="city-confirm-change"
            onClick={changeCityFromConfirm}
            className="rounded-xl border border-border bg-background px-4 py-2 text-sm font-semibold hover:bg-accent"
          >
            {cityConfirm.changeLabel}
          </button>
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground">{cityConfirm.hint}</p>
      </div>
    );
  }



  function pickFromPlace(p: PlaceResult) {
    // Never accept a destination without a usable name + coordinates: it would
    // let the user spend quota/credits on an itinerary we cannot build.
    if (!hasUsableLocation({ name: p.city, city: p.city, lat: p.lat, lng: p.lng })) return;
    const cur = currencyForCountry(p.countryCode);

    // A escolha manual tem prioridade: nenhuma deteção automática a substitui.
    manualLocationRef.current = true;
    setDestinationSource("user_selected", { city: p.city, lat: p.lat, lng: p.lng });
    setGeoNotice(null);

    setUseGpsAsStart(false);
    setNamedRouteStart(null);
    setLocale({

      city: p.city,
      country: p.country,
      countryCode: p.countryCode,
      lat: p.lat,
      lng: p.lng,
      currency: cur.currency,
      currencySymbol: cur.symbol,
      language: lang,
      stops: [],
    });
    setQuery("");
    setError(null);
  }

  function formatDateInput(d: Date) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }

  function formatTimeInput(d: Date) {
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  }

  // Safari-safe date construction from separate date/time strings. Never parse
  // locale-formatted strings like "DD/MM/YYYY HH:mm" through new Date().
  function buildStartDateTime(dateStr: string, timeStr: string): Date | null {
    const [year, month, day] = dateStr.split("-").map((n) => Number.parseInt(n, 10));
    const [h, m] = timeStr.split(":").map((n) => Number.parseInt(n, 10));
    if (
      !Number.isFinite(year) ||
      !Number.isFinite(month) ||
      !Number.isFinite(day) ||
      !Number.isFinite(h) ||
      !Number.isFinite(m)
    ) {
      return null;
    }
    const d = new Date(0);
    d.setFullYear(year, month - 1, day);
    d.setHours(h, m, 0, 0);
    if (Number.isNaN(d.getTime())) return null;
    return d;
  }

  function setStartToNow() {
    const now = new Date();
    const date = formatDateInput(now);
    const time = formatTimeInput(now);
    setStartDate(date);
    setStartTime(time);
    autoNowStartRef.current = true;
    return time;
  }

  function resetClarifyInputs() {
    setClarifyAnswer("");
    setClarifyStartTime("13:00");
    setClarifyEndTime("16:00");
    setClarifyDayOffset(0);
    
  }

  function buildTimeClarificationAnswer(): string {
    const start = clarifyStartTime || "13:00";
    const end = clarifyEndTime || "16:00";
    const day = clarifyDayOffset === 1 ? "amanhã" : clarifyDayOffset === 2 ? "depois de amanhã" : "hoje";
    return `${day} das ${start} às ${end}`;
  }


  function getBrowserPosition(timeout = 8000): Promise<{ lat: number; lng: number }> {
    return new Promise((resolve, reject) => {
      if (!("geolocation" in navigator)) {
        reject(new Error("geolocation_unavailable"));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
        reject,
        { enableHighAccuracy: true, timeout, maximumAge: 30000 },
      );
    });
  }

  function resetAutoNowStartForPlanning() {
    if (!autoNowStartRef.current) return startTime;
    autoNowStartRef.current = false;
    setStartDate(formatDateInput(new Date()));
    setStartTime("09:00");
    return "09:00";
  }

  async function localeFromCoordsValue(
    lat: number,
    lng: number,
    fallback?: { city?: string; country?: string; countryCode?: string },
  ): Promise<Locale> {
    const nearest = locationFromCoords(lat, lng);
    try {
      const resolved = await fetchReverseGeocode({
        data: { lat, lng, language: lang },
      });
      const city = resolved?.city || fallback?.city || nearest.city;
      const country = resolved?.country || fallback?.country || nearest.country;
      const cc = (
        resolved?.countryCode ||
        fallback?.countryCode ||
        nearest.countryCode ||
        ""
      ).toUpperCase();
      if (city && cc) {
        const cur = currencyForCountry(cc);
        return {
          city,
          country,
          countryCode: cc,
          lat,
          lng,
          currency: cur.currency,
          currencySymbol: cur.symbol,
          language: lang,
          stops: [],
        };
      }
    } catch (error) {
      console.warn("Reverse geocoding failed", error);
    }
    const cc = (fallback?.countryCode || nearest.countryCode || "").toUpperCase();
    const cur = currencyForCountry(cc);
    return {
      ...nearest,
      city: fallback?.city || nearest.city,
      country: fallback?.country || nearest.country,
      countryCode: cc,
      lat,
      lng,
      currency: cur.currency,
      currencySymbol: cur.symbol,
      language: lang,
      stops: [],
    };
  }

  async function resolveNamedStartPosition(
    startPlace: string,
    tripLocale: Locale,
  ): Promise<{ name: string; lat: number; lng: number } | null> {
    const normalizedPlace = startPlace.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const maxDistance = /\b(?:aeroporto|airport|aeropuerto|aeroport|flughafen)\b/.test(normalizedPlace)
      ? 120000
      : 60000;

    // Resolve named origins on the server first. This gives us the configured
    // Nominatim User-Agent and Google fallback, and avoids city-specific
    // coordinate exceptions.
    try {
      const resolved = await fetchForwardGeocode({
        data: {
          city: [startPlace, tripLocale.city].filter(Boolean).join(", ").slice(0, 120),
          country: tripLocale.country,
          language: lang,
        },
      });
      if (resolved) {
        const distance = distanceMeters(resolved, { lat: tripLocale.lat, lng: tripLocale.lng });
        if (distance <= maxDistance) {
          return { name: startPlace, lat: resolved.lat, lng: resolved.lng };
        }
      }
    } catch (error) {
      console.warn("Server named-origin geocoding failed", error);
    }

    // Browser lookup remains as a secondary fallback for providers that return
    // several candidates; choose the closest valid one to the requested city.
    try {
      const q = encodeURIComponent([startPlace, tripLocale.city, tripLocale.country].filter(Boolean).join(", "));
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=5&accept-language=${lang}&q=${q}`,
      );
      const items = (await res.json()) as Array<{ lat?: string; lon?: string; display_name?: string }>;
      const candidates = items
        .map((item) => ({
          name: startPlace,
          lat: Number.parseFloat(item.lat ?? ""),
          lng: Number.parseFloat(item.lon ?? ""),
        }))
        .filter((item) => Number.isFinite(item.lat) && Number.isFinite(item.lng))
        .map((item) => ({
          ...item,
          distance: distanceMeters(item, { lat: tripLocale.lat, lng: tripLocale.lng }),
        }))
        .filter((item) => item.distance <= maxDistance)
        .sort((a, b) => a.distance - b.distance);
      if (candidates[0]) {
        const { name, lat, lng } = candidates[0];
        return { name, lat, lng };
      }
    } catch (error) {
      console.warn("Named route origin geocoding failed", error);
    }

    return null;
  }

  const discoveryCopy = {
    openMaps: { pt: "Abrir no Maps", en: "Open in Maps", es: "Abrir en Maps", fr: "Ouvrir dans Maps", it: "Apri in Maps", de: "In Maps öffnen", ja: "Mapsで開く" }[lang],
    save: { pt: "Guardar", en: "Save", es: "Guardar", fr: "Enregistrer", it: "Salva", de: "Speichern", ja: "保存" }[lang],
    saved: { pt: "Guardado", en: "Saved", es: "Guardado", fr: "Enregistré", it: "Salvato", de: "Gespeichert", ja: "保存済" }[lang],
    addToRoute: { pt: "Adicionar à rota", en: "Add to route", es: "Añadir a la ruta", fr: "Ajouter à l'itinéraire", it: "Aggiungi al percorso", de: "Zur Route", ja: "ルートに追加" }[lang],
    buildRoute: { pt: "Criar rota com estas escolhas", en: "Create a route with these picks", es: "Crear ruta con estas opciones", fr: "Créer un itinéraire avec ces choix", it: "Crea un percorso con queste scelte", de: "Route mit dieser Auswahl", ja: "この選択でルート作成" }[lang],
    noData: { pt: "Não há dados suficientes para recomendar com confiança. Tenta indicar uma zona mais específica.", en: "There isn't enough data to recommend confidently. Try a more specific area.", es: "No hay datos suficientes para recomendar con confianza.", fr: "Pas assez de données pour recommander avec confiance.", it: "Dati insufficienti per una raccomandazione affidabile.", de: "Nicht genug Daten für eine verlässliche Empfehlung.", ja: "十分なデータがありません。" }[lang],
    opening: { pt: "A abrir…", en: "Opening…", es: "Abriendo…", fr: "Ouverture…", it: "Apertura…", de: "Wird geöffnet…", ja: "開いています…" }[lang],
    openedMaps: { pt: "Aberto no Maps", en: "Opened in Maps", es: "Abierto en Maps", fr: "Ouvert dans Maps", it: "Aperto in Maps", de: "In Maps geöffnet", ja: "Mapsで開きました" }[lang],
    inRoute: { pt: "Na rota", en: "In route", es: "En la ruta", fr: "Dans l'itinéraire", it: "Nel percorso", de: "In Route", ja: "ルートに追加済" }[lang],
    added: { pt: "Adicionado à rota", en: "Added to route", es: "Añadido a la ruta", fr: "Ajouté à l'itinéraire", it: "Aggiunto al percorso", de: "Zur Route hinzugefügt", ja: "ルートに追加しました" }[lang],
    removed: { pt: "Removido da rota", en: "Removed from route", es: "Eliminado de la ruta", fr: "Retiré de l'itinéraire", it: "Rimosso dal percorso", de: "Aus Route entfernt", ja: "ルートから削除" }[lang],
    routeCounter: { pt: "Rota atual", en: "Current route", es: "Ruta actual", fr: "Itinéraire actuel", it: "Percorso attuale", de: "Aktuelle Route", ja: "現在のルート" }[lang],
    actionError: { pt: "Não foi possível concluir. Tenta novamente.", en: "Couldn't complete. Please try again.", es: "No se pudo completar. Inténtalo de nuevo.", fr: "Action impossible. Réessaie.", it: "Operazione non riuscita. Riprova.", de: "Nicht möglich. Bitte erneut versuchen.", ja: "完了できませんでした。" }[lang],
    savedToPlaces: { pt: "Guardado nos teus locais", en: "Saved to your places", es: "Guardado en tus lugares", fr: "Enregistré dans tes lieux", it: "Salvato nei tuoi luoghi", de: "In deinen Orten gespeichert", ja: "あなたの場所に保存しました" }[lang],
    removedFromPlaces: { pt: "Removido dos teus locais", en: "Removed from your places", es: "Eliminado de tus lugares", fr: "Retiré de tes lieux", it: "Rimosso dai tuoi luoghi", de: "Aus deinen Orten entfernt", ja: "あなたの場所から削除しました" }[lang],
  };


  /** Guarda/remove o local na área persistente "Locais guardados" da conta. */
  async function toggleSavedDiscoveryPlace(place: RankedDiscoveryPlace) {
    const wasSaved = savedDiscovery.has(place.placeId);
    const { savePlace, removeSavedPlace } = await import("@/lib/saved-places");
    if (wasSaved) {
      await removeSavedPlace(place.placeId);
      setSavedDiscovery((cur) => {
        const next = new Set(cur);
        next.delete(place.placeId);
        return next;
      });
      toast.message(discoveryCopy.removedFromPlaces);
      return;
    }
    await savePlace({
      place_id: place.placeId,
      name: place.name,
      category: place.types?.[0] ?? null,
      address: place.address ?? null,
      zone: place.zone ?? null,
      rating: place.rating ?? null,
      lat: place.lat ?? null,
      lng: place.lng ?? null,
      maps_url: place.googleMapsUri ?? null,
    });
    setSavedDiscovery((cur) => new Set(cur).add(place.placeId));
    toast.success(discoveryCopy.savedToPlaces);
  }


  function addDiscoveryPlaceToRoute(place: RankedDiscoveryPlace) {
    addExtraStop({
      name: place.name,
      category: place.types[0] ?? "point_of_interest",
      blurb: place.reasons.join(" · "),
      priceLocal: 0,
      rating: place.rating ?? 0,
      placeId: place.placeId,
      lat: place.lat,
      lng: place.lng,
    });
  }

  /** Alterna a seleção do local na rota (apenas seleção; sem horários novos). */
  function toggleDiscoveryPlaceInRoute(place: RankedDiscoveryPlace) {
    if (discoveryInRoute.has(place.placeId)) {
      setAiStops((cur) => (cur ?? []).filter((s) => s.name.toLowerCase() !== place.name.toLowerCase()));
      setAddedExtra((cur) => {
        const next = new Set(cur);
        next.delete(place.name);
        return next;
      });
      setDiscoveryInRoute((cur) => {
        const next = new Set(cur);
        next.delete(place.placeId);
        return next;
      });
      toast.success(discoveryCopy.removed);
      return;
    }
    addDiscoveryPlaceToRoute(place);
    setDiscoveryInRoute((cur) => new Set(cur).add(place.placeId));
    toast.success(discoveryCopy.added);
  }

  function createRouteFromDiscovery() {
    if (!discovery || discovery.places.length === 0) return;
    const names = discovery.places.slice(0, 4).map((p) => p.name).join(", ");
    const zone = discovery.request.area ?? discovery.places[0]?.zone ?? "";
    const isSweets = discovery.request.category === "sweets";
    const prompt =
      lang === "pt"
        ? isSweets
          ? `Quero uma rota${zone ? ` em ${zone}` : ""} só de doces típicos que inclua ${names}`
          : `Quero uma rota${zone ? ` em ${zone}` : ""} que inclua ${names}`
        : isSweets
          ? `I want a route${zone ? ` in ${zone}` : ""} only traditional sweets including ${names}`
          : `I want a route${zone ? ` in ${zone}` : ""} including ${names}`;
    setDiscovery(null);
    setAiPrompt(prompt);
    void submitAiPrompt(prompt);
  }

  /** Descoberta: ranking sem rota, sem horários e sem quota de itinerário. */
  async function runDiscovery(request: DiscoveryRequest, text: string) {
    setClarification(null);
    setScopeChoice(null);
    setError(null);
    setDiscovery({
      title: discoveryTitle(request, lang),
      disclaimer: discoveryDisclaimer(lang),
      request,
      places: [],
      loading: true,
    });
    let origin = userPos;
    if (request.nearMe && !origin && typeof navigator !== "undefined" && "geolocation" in navigator) {
      origin = await new Promise<{ lat: number; lng: number } | null>((resolve) => {
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
          () => resolve(null),
          { timeout: 8000 },
        );
      });
      if (origin) setUserPos(origin);
    }
    try {
      const result = await discoverPlaces({
        data: {
          searchQuery: request.searchQuery,
          specialty: request.specialty,
          area: request.area ?? (request.nearMe ? null : locale.city || null),
          category: request.category,
          nearMe: request.nearMe,
          ...(origin ? { lat: origin.lat, lng: origin.lng } : {}),
          language: lang,
          cuisine: detectCuisineIntent(text)?.requested ?? null,
          dish: request.dish ?? null,
          sweetDish: request.sweetDish ?? false,

        },
      });
      if (result.cuisineScarce) {
        const { cuisine, area } = result.cuisineScarce;
        const where = area ?? locale.city;
        toast.info(
          lang === "pt" ? `Não encontrámos restaurantes ${cuisine} verificados em ${where}. Queres alargar a pesquisa às localidades vizinhas?`
          : lang === "es" ? `No encontramos restaurantes ${cuisine} verificados en ${where}. ¿Quieres ampliar la búsqueda a las localidades cercanas?`
          : lang === "fr" ? `Aucun restaurant ${cuisine} vérifié à ${where}. Élargir la recherche aux localités voisines ?`
          : lang === "de" ? `Keine geprüften ${cuisine} Restaurants in ${where}. Suche auf Nachbarorte ausweiten?`
          : `No verified ${cuisine} restaurants in ${where}. Want to widen the search to nearby areas?`,
        );
      }
      setDiscovery((cur) => (cur ? { ...cur, places: result.places, loading: false } : cur));

    } catch (err) {
      console.warn("Discovery search failed", err);
      setDiscovery((cur) => (cur ? { ...cur, places: [], loading: false } : cur));
    }
    setLastRequestText(text);
  }

  /** Inspiração: o botão "Sugerir destinos" é a ÚNICA porta para a chamada
   *  paga (1 crédito) — responder às perguntas nunca gera nada. */
  async function runInspiration() {
    if (!inspiration || inspiration.phase === "loading") return;
    const days = inspiration.days ?? 5;
    setInspiration({ ...inspiration, phase: "loading" });
    try {
      const result = await suggestDestinations({
        data: {
          days,
          vibe: inspiration.vibe,
          budget: inspiration.budget,
          language: lang,
          anonId: getAnonId(),
        },
      });
      if (result.error === "quota_exceeded" && result.quota) {
        setQuotaModal({
          open: true,
          tier: result.quota.tier,
          reason: result.quota.reason,
          resetInMinutes: result.quota.resetInMinutes,
        });
        setInspiration({ ...inspiration, phase: "questions" });
        return;
      }
      if (result.error !== null || result.destinations.length === 0) {
        toast.error(inspirationCopy(lang).noResults);
        setInspiration({ ...inspiration, phase: "questions" });
        return;
      }
      setInspiration({ ...inspiration, phase: "results", destinations: result.destinations });
    } catch (e) {
      console.warn("Inspiration failed", e);
      toast.error(inspirationCopy(lang).noResults);
      setInspiration({ ...inspiration, phase: "questions" });
    }
  }

  /** "Criar roteiro aqui": sai do modo Inspiração e entra no fluxo normal de
   *  rota com o destino escolhido — nenhuma geração acontece sem o botão. */
  function createRouteFromInspiration(d: InspirationDestination) {
    const days = inspiration?.days ?? d.idealDays;
    // O estilo escolhido (praia, natureza, ...) é parte do pedido: sem ele o
    // motor monta um roteiro genérico e ignora aquilo que a pessoa pediu.
    const VIBE_TERMS: Record<InspirationVibe, Partial<Record<string, string>> & { en: string }> = {
      beach: { en: "beaches", pt: "praias", es: "playas", fr: "plages", it: "spiagge", de: "Strände" },
      nature: { en: "nature and landscapes", pt: "natureza e paisagens", es: "naturaleza y paisajes", fr: "nature et paysages", it: "natura e paesaggi", de: "Natur und Landschaften" },
      city: { en: "city sights and culture", pt: "cidade e cultura", es: "ciudad y cultura", fr: "ville et culture", it: "città e cultura", de: "Stadt und Kultur" },
      adventure: { en: "adventure and outdoor activities", pt: "aventura e atividades ao ar livre", es: "aventura y actividades al aire libre", fr: "aventure et plein air", it: "avventura e attività all'aperto", de: "Abenteuer und Outdoor" },
      relax: { en: "relaxing spots", pt: "sítios calmos para descansar", es: "sitios tranquilos", fr: "endroits calmes", it: "luoghi tranquilli", de: "ruhige Orte" },
    };
    const vibes = inspiration?.vibe ?? [];
    const wanted = vibes.map((v) => VIBE_TERMS[v][lang] ?? VIBE_TERMS[v].en);
    const wantedList = wanted.join(lang === "pt" ? " e " : lang === "es" ? " y " : lang === "fr" ? " et " : lang === "it" ? " e " : lang === "de" ? " und " : " and ");
    const focus = wantedList
      ? lang === "pt"
        ? `, quero sobretudo ${wantedList}`
        : lang === "es"
          ? `, quiero sobre todo ${wantedList}`
          : lang === "fr"
            ? `, je veux surtout ${wantedList}`
            : lang === "it"
              ? `, voglio soprattutto ${wantedList}`
              : lang === "de"
                ? `, vor allem ${wantedList}`
                : `, I mainly want ${wantedList}`
      : "";
    const prompt =
      (lang === "pt"
        ? `Vou a ${d.name}, ${d.country} durante ${days} dias`
        : lang === "es"
          ? `Voy a ${d.name}, ${d.country} durante ${days} días`
          : lang === "fr"
            ? `Je vais à ${d.name}, ${d.country} pendant ${days} jours`
            : lang === "it"
              ? `Vado a ${d.name}, ${d.country} per ${days} giorni`
              : lang === "de"
                ? `Ich fahre nach ${d.name}, ${d.country} für ${days} Tage`
                : `I'm going to ${d.name}, ${d.country} for ${days} days`) + focus;
    setInspiration(null);

    // O destino do cartão já vem validado: fixa-se diretamente (cidade +
    // coordenadas), sem deixar o resolvedor adivinhar outra vez pelo texto.
    if (typeof d.lat === "number" && typeof d.lng === "number") {
      manualLocationRef.current = true;
      setDestinationSource("user_selected", { city: d.name, lat: d.lat, lng: d.lng });
      setUseGpsAsStart(false);
      setNamedRouteStart(null);
      setLocale((prev) => ({
        ...prev,
        city: d.name,
        country: d.country,
        countryCode: prev.countryCode,
        lat: d.lat as number,
        lng: d.lng as number,
        language: lang,
        stops: [],
      }));
    }
    setAiPrompt(prompt);
    void submitAiPrompt(prompt);
  }

  // Fecho da máquina de estados: generating → published | failed.
  useEffect(() => {
    if (aiPromptLoading) return;
    setRequestPhase((prev) => (prev === "generating" ? (error ? "failed" : "published") : prev));
  }, [aiPromptLoading, error]);

  async function submitAiPrompt(
    promptOverride?: string,
    submitOpts?: {
      continueSession?: boolean;
      skipAvailabilityCheck?: boolean;
      /**
       * Continuação EXPLÍCITA após "Continuar a criar rota": seleção fixa e
       * área expandida chegam como argumentos (nunca estado React pendente) e
       * o texto NUNCA volta a ser interpretado — usa-se o parse validado.
       */
      continuation?: { pinned: PinnedSelectionRef[]; area: ExpandedArea | null };
      /**
       * Origem da submissão. `clarification_answer` NUNCA gera: guarda o
       * pedido em `pendingGeneration` e espera pelo botão "Criar itinerário".
       */
      trigger?: SubmitTrigger;
    },

  ) {
    const text = (promptOverride ?? aiPrompt).trim();
    if (!text || aiPromptLoading) return;
    // Guarda os chips selecionados para ESTE pedido antes de os limpar. Os
    // chips são estado visual: começam vazios, só entram se a pessoa tocar, e
    // resetam para o pedido seguinte.
    const explicitInterestsForThisRequest = interests;
    // Um pedido novo limpa sempre os blocos de disponibilidade anteriores.
    setAvailabilityBlock(null);
    if (!submitOpts?.continueSession) {
      // Pedido novo escrito pela pessoa: limpa ações e ajustes anteriores.
      setScarcityAction(null);
      setDurationChoice(null);
      setDurationAdjustedDays(null);
      scarcityActionRunRef.current = null;
    }

    setAvailabilityTechnical(null);
    setConstraintBlock(null);
    setLocalityChoice(null);
    setPinnedRejected(null);
    // O alargamento pertence a UM pedido: um pedido novo volta à zona exata.
    if (!submitOpts?.continueSession) applyExpandedArea(null);
    // As seleções fixas também pertencem a UM pedido: um pedido novo limpa-as.
    if (!submitOpts?.continueSession) {
      setPinnedSelections([]);
      setPinnedConfirmation(null);
      pinnedContinuationRef.current = null;
      lastParsedPromptRef.current = null;
      lastAvailabilitySignatureRef.current = null;
      lastAvailabilityBlockRef.current = null;
    }
    // A localidade escolhida também pertence a UM pedido.
    if (!submitOpts?.continueSession) chosenLocalityRef.current = null;


    // Pedido novo por texto: apaga sempre a identidade resolvida do pedido
    // anterior. Escolher um candidato é continuação da MESMA sessão e passa
    // `continueSession`, para não limpar a identidade acabada de escolher.
    if (!submitOpts?.continueSession) {
      resetDestinationDraft();
      // Os chips do pedido anterior não contaminam este pedido novo.
      setInterests([]);
      // O chip "Roteiro em X" do pedido anterior não pode contaminar este:
      // quando o novo texto nomeia outra cidade, limpamos o destino visual.
      const namedNow = extractDestinationFromPrompt(text);
      if (
        namedNow.explicit &&
        namedNow.city &&
        locale.city &&
        comparableCityName(namedNow.city) !== comparableCityName(locale.city)
      ) {
        setLocale(EMPTY_LOCALE);
        setDestinationSource("none", EMPTY_LOCALE);
      }
    }

    // Pedido novo escrito pela pessoa: o "pronto a gerar" anterior morre aqui.
    if ((submitOpts?.trigger ?? "user_submit") === "user_submit") {
      setPendingGeneration(null);
      setRequestPhase("clarifying");
    }
    const mySubmit = ++submitTokenRef.current;
    // Um pedido novo redefine o texto original; uma continuação (clarificação)
    // preserva-o para que a origem nunca nasça de uma resposta.
    if (!submitOpts?.continueSession || !originalRequestRef.current) originalRequestRef.current = text;
    setLastRequestText(text);

    // Modo Inspiração: "não sei para onde ir de férias". Só entra quando NÃO
    // há destino explícito — um destino escrito no texto é sempre rota.
    const inspirationRequest = detectInspirationRequest(text);
    if (inspirationRequest) {
      // Forma forte de indecisão ganha sempre: um "destino" extraído de uma
      // frase destas é sempre falso ("Mas Não Sei").
      const dest = inspirationRequest.strong ? null : extractDestinationFromPrompt(text);
      if (!dest || !(dest.explicit && dest.city)) {
        setClarification(null);
        setScopeChoice(null);
        setDiscovery(null);
        setInspiration({
          phase: "questions",
          days: inspirationRequest.days,
          vibe: [],
          budget: null,
          destinations: [],
        });
        return;
      }
    }

    // Modo Descoberta (aditivo): pedido de recomendação/comparação, não rota.
    // Não consome quota de geração nem altera qualquer fluxo existente.
    const discoveryRequest = detectDiscoveryRequest(text);
    if (discoveryRequest) {
      // Cidade escrita no próprio texto manda sempre sobre a zona inferida
      // ("melhor doce tradicional de Viana do Castelo" → Viana do Castelo).
      if (!discoveryRequest.nearMe) {
        const dest = extractDestinationFromPrompt(text);
        if (dest.explicit && dest.city) discoveryRequest.area = dest.city;
      }
      setInspiration(null);
      await runDiscovery(discoveryRequest, text);
      return;
    }
    setDiscovery(null);
    setInspiration(null);


    // Pedido novo = estado novo: a escolha manual do pedido anterior e a sua
    // preferência (positiva ou negativa) nunca transitam. Recalcula-se a
    // intenção a partir do texto atual e sincroniza-se rádio + resumo.
    const mealState = nextMealStateForRequest({
      text,
      eatMinutes: activePrefs.eatMinutes,
      defaultEatMinutes: DEFAULT_MEAL_PREFS.eatMinutes,
    });
    setMealChoiceForText(null);
    setMealChoice(mealState.choice);
    if (mealState.eatMinutes !== activePrefs.eatMinutes) {
      setActivePrefs((p) => ({ ...p, eatMinutes: mealState.eatMinutes }));
      setPendingPrefs((p) => ({ ...p, eatMinutes: mealState.eatMinutes }));
    }
    const requestMealPreference = mealState.preference;


    // Viagem com distribuição por destinos ("2 dias em Atenas, 5 em Creta e 2
    // em Santorini"): o Premium nunca gera meia rota nem gasta crédito —
    // encaminha imediatamente para o Pro com o contrato completo.
    const multiDestination = parseMultiDestinationPlan(text);
    if (multiDestination) {
      setClarification(null);
      setScopeChoice(null);
      savePlanHandoff({
        prompt: text,
        scopeName: multiDestination.stops.map((stop) => stop.destination).join(" · "),
        scopeKind: "multi_city",
        days: multiDestination.totalDays,
        cities: multiDestination.stops.map((stop) => stop.destination),
        transport: "transit",
        multiDestinationPlan: multiDestination,
      });
      toast.info(
        lang === "pt"
          ? `Viagem com ${multiDestination.stops.length} destinos e ${multiDestination.totalDays} dias — isto é um roteiro Pro. Não gastaste crédito.`
          : `Trip with ${multiDestination.stops.length} destinations and ${multiDestination.totalDays} days — this is a Pro itinerary. No credit used.`,
      );
      void navigate({ to: "/pro" });
      return;
    }

    // Homónimos são resolvidos antes da própria validação de duração. Assim um
    // pedido curto como "Lagos" pergunta primeiro QUAL Lagos, sem IA, Maps ou
    // crédito. País/região escritos no pedido resolvem diretamente.
    const earlyDestination = extractDestinationFromPrompt(text);
    const earlyHomonymName = earlyDestination.explicit ? earlyDestination.city : undefined;
    if (earlyHomonymName && homonymResolvedRef.current !== earlyHomonymName.toLowerCase()) {
      try {
        setHomonymLoading(true);
        const rawCandidates = await findDestinationCandidates({
          data: { name: earlyHomonymName, language: lang },
        });
        const decision = resolveHomonyms(rawCandidates, earlyHomonymName, text);
        // Resposta tardia de um pedido já substituído nunca escreve estado.
        if (mySubmit !== submitTokenRef.current) return;
        if (decision.status === "ask" && decision.options.length > 1) {
          setHomonymChoice({ name: earlyHomonymName, basePrompt: text, options: decision.options });
          return;
        }
        if (decision.status === "resolved") resolvedDestinationRef.current = decision.option;
      } catch (error) {
        console.warn("Homonym lookup failed", error);
      } finally {
        setHomonymLoading(false);
      }
      homonymResolvedRef.current = earlyHomonymName.toLowerCase();
    }
    setHomonymChoice(null);


    // Free pre-validation: never spend AI / Maps / credits on an incomplete
    // request — ask one short question instead. Usa o fuso do dispositivo até
    // termos o destino geocodificado; para Leça da Palmeira num telemóvel em
    // Portugal resulta Europe/Lisbon.
    const assessment = assessTripRequest(text, lang, new Date(), Intl.DateTimeFormat().resolvedOptions().timeZone);
    if (assessment.status === "unsupported") {
      setClarification(null);
      toast.error(assessment.message);
      return;
    }

    // Ilha, província, região grande ou país NÃO são cidade. Com vários dias e
    // sem base concreta, o roteiro é Pro — encaminhamos antes de gastar
    // crédito, IA ou Maps.
    const resolvedIdentity = resolvedDestinationRef.current;
    if (resolvedIdentity?.category && isAreaCategory(resolvedIdentity.category)) {
      const routing = decideDestinationRouting({
        category: resolvedIdentity.category,
        days: assessment.facts.days ?? 1,
      });
      if (routing.target === "pro") {
        setClarification(null);
        setScopeChoice(null);
        setHomonymChoice(null);
        savePlanHandoff({
          prompt: text,
          scopeName: resolvedIdentity.label,
          scopeKind: resolvedIdentity.category === "country" ? "country" : "region",
          days: assessment.facts.days ?? 1,
          cities: [],
          transport: "transit",
        });
        toast.info(
          lang === "pt"
            ? `${resolvedIdentity.name} não é uma cidade — é uma área ampla. Este roteiro é Pro. Não gastaste crédito.`
            : `${resolvedIdentity.name} is an area, not a city — this is a Pro itinerary. No credit used.`,
        );
        void navigate({ to: "/pro" });
        return;
      }
    }

    // Estrada/percurso nomeado, país, região ou viagem intercidades: nunca
    // perguntar "que cidade?" — propor o Pro com o contrato completo. Vem
    // ANTES de qualquer pergunta para não herdar cidades de pedidos antigos.
    const regionalScopeKey =
      assessment.status === "regional" ? scopeKeyOf(assessment.regional.name) : null;
    if (
      assessment.status === "regional" &&
      !(scopeDecisionRef.current?.decision === "city" && scopeDecisionRef.current.key === regionalScopeKey)
    ) {
      const regional = assessment.regional;
      setClarification(null);
      resetClarifyInputs();
      setScopeChoice({
        basePrompt: text,
        scopeName: regional.name,
        scopeKind: regional.scopeKind,
        countryCode: regional.countryCode,
        routeTemplateId: regional.templateId,
        routeCorridorId: regional.corridorId,
        routeDays: regional.minDays,
        routeTransport:
          regional.transport === "walk" ||
          regional.transport === "bike" ||
          regional.transport === "car" ||
          regional.transport === "transit"
            ? regional.transport
            : regional.transport === "motorcycle"
              ? "car"
              : undefined,
        routeCities: regional.routeCities,
      });
      return;
    }
    if (assessment.status === "needs_info") {
      setClarification({
        missing: assessment.missing,
        question: assessment.question,
        basePrompt: text,
        city: assessment.facts.city ?? undefined,
        logistics: assessment.facts.logistics,
      });
      resetClarifyInputs();
      // Se já sabemos o dia pretendido, pré-seleciona-o nos botões de clarificação.
      if (assessment.facts.dayOffset != null) setClarifyDayOffset(assessment.facts.dayOffset);
      if (assessment.facts.startMinutes != null) {
        const h = Math.floor(assessment.facts.startMinutes / 60);
        const m = assessment.facts.startMinutes % 60;
        setClarifyStartTime(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
      }
      if (assessment.facts.endMinutes != null) {
        const h = Math.floor(assessment.facts.endMinutes / 60);
        const m = assessment.facts.endMinutes % 60;
        setClarifyEndTime(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
      }
      return;
    }
    setClarification(null);
    setScopeChoice(null);
    // Janela do próprio dia ("agora até às 18 horas"): a duração vem daqui e
    // nunca de dias herdados de um pedido anterior.
    const intradayWindow = parseIntradayWindow(text);
    const intradayHours = assessment.facts.intraday ? assessment.facts.durationHours : undefined;


    // Alojamento: "um local para dormir" é uma categoria, não um local
    // obrigatório. Antes de gastar IA/Maps/créditos, esclarecer se é dormida
    // ou apenas descanso durante o dia.
    const basicRequest = extractBasicTripRequest(text);
    const stay = detectAccommodationRequest(text, basicRequest.durationHours);
    // "sem hotéis" é uma recusa explícita: a palavra "hotel" no texto não pode
    // valer como pedido de alojamento. Sem este cruzamento, o bloco "Onde
    // ficar" aparecia (e pagava chamadas) contra a vontade da pessoa.
    const stayExcluded = isCategoryExcluded(parseNegativePreferences(text), "hotel");
    if (!stayExcluded && stay.wanted && stay.ambiguous && !stayDecisionRef.current) {
      setStayClarify({ basePrompt: text });
      return;
    }
    setStayClarify(null);
    setStayRequested(!stayExcluded && stay.wanted && stayDecisionRef.current !== "rest");





    // Confirmação da cidade (sem custo): só quando o destino não foi escolhido
    // pelo utilizador nem indicado no texto do pedido.
    // Destino nomeado no texto: extração local antes de qualquer IA/Maps.
    const namedDestination = extractDestinationFromPrompt(text);
    const promptNamesDestination = namedDestination.explicit || Boolean(extractDestinationCandidate(text));
    setGastronomy(detectGastronomyRequirement(text));

    const confirmation = resolvePreCreateConfirmation(
      {
        source: promptNamesDestination ? "prompt_named" : destinationSourceRef.current,
        city: locale.city,
        confirmedKey: confirmedDestinationKeyRef.current,
        currentKey: currentDestinationKey(),
      },
      lang,
    );
    if (confirmation) {
      setCityConfirmTarget("prompt");
      setCityConfirm(confirmation);
      return;
    }
    setCityConfirm(null);

    // Barreira única antes de QUALQUER trabalho pago (IA, Places, Routes,
    // Photos, crédito, overlay, generationId). Respostas de clarificação
    // param aqui: o pedido fica pronto e espera pelo clique explícito.
    const startDecision = decideGenerationStart({
      trigger: submitOpts?.trigger ?? "user_submit",
      alreadyGenerating: aiPromptLoading,
    });
    if (startDecision.action !== "generate") {
      setPendingGeneration({ text });
      setRequestPhase(startDecision.phase);
      return;
    }
    setPendingGeneration(null);
    setRequestPhase("generating");

    setAiPromptLoading(true);
    setError(null);
    try {
      let p: import("@/lib/ai-prompt.functions").ParsedTripPrompt | null = null;

      // Continuação explícita ("Continuar a criar rota"): o texto NUNCA é
      // interpretado outra vez. Reutiliza-se o parse já validado para que a
      // assinatura, a localidade, a janela e as restrições sejam estáveis —
      // um novo parse podia produzir uma assinatura diferente e invalidar a
      // própria seleção fixa.
      if (submitOpts?.continuation) {
        p = lastParsedPromptRef.current ? { ...lastParsedPromptRef.current } : null;
        if (!p) {
          setPinnedRejected({ basePrompt: text, reason: "signature_mismatch" });
          return;
        }
      }

      // Bypass local: pedidos com destino + janela + restrições de paragens
      // podem ser resolvidos sem IA, evitando timeouts do gateway e
      // garantindo que a validação de disponibilidade corre antes da geração.
      const localParsed = p ? null : buildDeterministicPromptResult(
        text,
        lang,
        Intl.DateTimeFormat().resolvedOptions().timeZone,
        new Date(),
      );
      if (canUseDeterministicPromptResult(localParsed)) {
        p = localParsed;
      } else if (!p) {
        const r = await runPromptParse({ data: { prompt: text, language: lang, anonId: getAnonId() } });
        if (r.rateLimited) {
          if (r.tier === "anon") {
            toast.error(lang === "pt" ? "Limite diário atingido. Cria conta para mais pedidos." : "Daily limit reached. Sign in for more requests.");
          } else if (r.tier === "free") {
            toast.error(lang === "pt" ? "Limite diário grátis atingido. Passa a Premium para mais." : "Free daily limit reached. Upgrade to Premium for more.");
            setPremiumOpen(true);
          } else {
            toast.error(lang === "pt" ? "Limite diário atingido. Tenta amanhã." : "Daily limit reached. Try again tomorrow.");
          }
          return;
        }
        p = r.parsed;
      }
      if (p && !p.city && namedDestination.city) {
        // O destino local não pode perder-se entre pré-validação e geração.
        p.city = namedDestination.city;
      }
      if (!p) {
        toast.error(technicalFailureMessage(lang));
        return;
      }

      // Precedência do destino: um destino escrito ("quero ir a X", "vou a X")
      // prevalece SEMPRE sobre GPS, IP, cache e sobre o sinal da IA. O GPS
      // continua a servir para distância e "Navegar até ao ponto de partida".
      const destinationDecision = resolveDestinationPrecedence({
        prompt: text,
        parsedCity: p.city,
        parsedStartFromUserLocation: p.startFromUserLocation,
        currentCity: locale.city,
        currentCityUserSelected: destinationSourceRef.current === "user_selected",
        userChoseCurrentLocation: useGpsAsStart,
      });
      if (destinationDecision.source === "prompt_explicit" && destinationDecision.city) {
        p.city = destinationDecision.city;
        p.startFromUserLocation = false;
      } else if (destinationDecision.city) {
        // "Estou em X": X é a localidade a explorar, mesmo quando o GPS falha.
        p.city = destinationDecision.city;
      }


      // Apply city/country/coords — but always re-geocode via Nominatim to
      // avoid the model's unreliable lat/lng guesses (it sometimes returns
      // coordinates for the wrong city entirely, e.g. Caracal → Călăraşi).
      // Also use Nominatim's resolved country as authoritative, and keep any
      // alternative same-name cities in other countries as a fallback the
      // user can switch to if the itinerary comes back nearly empty.
      aiPromptAlternativesRef.current = [];
      let nextLocale = locale;
      let nextDuration = duration;
      // Os chips visualmente já foram limpos no início do submit; usamos a
      // captura feita antes do reset como os interesses EXPLÍCITOS deste pedido.
      let nextInterests = explicitInterestsForThisRequest;
      let nextMode = mode;
      let nextNotes = notes;
      const wantsGpsStart = destinationDecision.useCurrentLocation;

      let nextStartTime = startTime;
      let gpsStartPosition = userPos;
      let namedStartPosition: { name: string; lat: number; lng: number } | null = null;

      if (wantsGpsStart) {
        nextStartTime = setStartToNow();
        if (!gpsStartPosition) {
          try {
            gpsStartPosition = await getBrowserPosition();
            setUserPos(gpsStartPosition);
          } catch {
            gpsStartPosition = null;
          }
        }
      }

      if (wantsGpsStart && gpsStartPosition) {
        nextLocale = await localeFromCoordsValue(gpsStartPosition.lat, gpsStartPosition.lng, {
          city: p.city,
          country: p.country,
          countryCode: p.countryCode,
        });
        setUseGpsAsStart(true);
        setLocale(nextLocale);
        setDestinationSource("prompt_named", nextLocale);

        setQuery("");
      } else if (!wantsGpsStart) {
        if (intradayWindow) {
          // Pedido do próprio dia: começa agora, não às 09:00 de amanhã.
          nextStartTime = setStartToNow();
        } else {
          nextStartTime = resetAutoNowStartForPlanning();
        }
        if (!intradayWindow && p.startTime && /^\d{1,2}:\d{2}$/.test(p.startTime)) {
          nextStartTime = p.startTime;
          setStartTime(p.startTime);
        } else if (!intradayWindow && (p.durationHours ?? 0) >= 24) {
          // A day-based future plan with no stated time starts in daylight.
          // Never inherit a stale/manual late-night value from a previous route.
          nextStartTime = "09:00";
          setStartTime("09:00");
          autoNowStartRef.current = false;
        }
      }


      if (p.city && !(wantsGpsStart && gpsStartPosition)) {
        const selectedDestination = resolvedDestinationRef.current;
        if (selectedDestination) {
          const cur = currencyForCountry(selectedDestination.countryCode);
          nextLocale = {
            city: selectedDestination.name,
            country: selectedDestination.country,
            countryCode: selectedDestination.countryCode,
            lat: selectedDestination.lat,
            lng: selectedDestination.lng,
            currency: cur.currency,
            currencySymbol: cur.symbol,
            language: lang,
            stops: [],
          };
          aiPromptAlternativesRef.current = [];
          areaDestinationRef.current = false;
          setUseGpsAsStart(false);
          setLocale(nextLocale);
          setDestinationSource("user_selected", nextLocale);
          setQuery("");
        } else {
        let lat = typeof p.lat === "number" ? p.lat : NaN;
        let lng = typeof p.lng === "number" ? p.lng : NaN;
        let resolvedCity = p.city;
        let resolvedDisplayCity: string | undefined;
        let resolvedProvider: "osm" | "google" | undefined;
        let resolvedProviderId: string | undefined;
        let resolvedCountry = p.country || "";
        let resolvedCC = (p.countryCode || "").toUpperCase();
        areaDestinationRef.current = false;

        try {
          const q = encodeURIComponent([canonicalCityName(p.city), p.country].filter(Boolean).join(", "));
          const res = await fetch(
            `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=5&addressdetails=1&namedetails=1&accept-language=${lang}&q=${q}`,
          );
          const arr = (await res.json()) as Array<{
            lat: string; lon: string; display_name?: string;
            addresstype?: string; type?: string; class?: string;
            namedetails?: Record<string, string>;
            address?: { country?: string; country_code?: string; city?: string; town?: string; village?: string; municipality?: string; county?: string; state?: string };
          }>;
          // Cidade nomeada no pedido: só aceitamos resultados ao nível
          // localidade. País/estado/região nunca são destino de rota urbana.
          if (mySubmit !== submitTokenRef.current) return;
          const picked = pickCityGeocodeResult(arr, p.city);
          if (picked) {
            const best = picked.item as (typeof arr)[number];
            areaDestinationRef.current = isAreaDestination({
              addresstype: best.addresstype,
              type: best.type,
              category: best.class,
              address: best.address,
            });
            lat = picked.lat;
            lng = picked.lng;

            resolvedCountry = picked.country || resolvedCountry;
            resolvedCC = picked.countryCode || resolvedCC;
            resolvedCity = picked.city;
            resolvedDisplayCity = picked.displayName;
            resolvedProvider = "osm";
            resolvedProviderId = picked.providerPlaceId;

            // Collect up to 2 alternatives in *different countries*
            const seen = new Set<string>([resolvedCC]);
            for (let i = 0; i < arr.length && aiPromptAlternativesRef.current.length < 2; i++) {
              if (arr[i] === best) continue;
              const a = arr[i].address || {};
              const cc = (a.country_code || "").toUpperCase();
              if (!cc || seen.has(cc)) continue;
              if (!isCityLevelResult(arr[i])) continue;
              seen.add(cc);
              aiPromptAlternativesRef.current.push({
                city: preferSearchableCityName(
                  a.city || a.town || a.village || a.municipality || p.city,
                  arr[i].namedetails?.["name:en"] || arr[i].namedetails?.name,
                ),
                country: a.country || "",
                countryCode: cc,
                lat: parseFloat(arr[i].lat),
                lng: parseFloat(arr[i].lon),
              });
            }
          }
        } catch { /* use server-side fallback below */ }
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          try {
            const resolved = await fetchForwardGeocode({
              data: {
                city: p.city,
                country: p.country || undefined,
                language: lang,
              },
            });
            if (resolved) {
              lat = resolved.lat;
              lng = resolved.lng;
              resolvedCity = resolved.city;
              resolvedDisplayCity = resolved.displayName || resolvedDisplayCity;
              resolvedProvider = resolved.provider || resolvedProvider;
              resolvedProviderId = resolved.providerPlaceId || resolvedProviderId;
              resolvedCountry = resolved.country;
              resolvedCC = resolved.countryCode;
            }
          } catch (error) {
            console.warn("Server-side city geocoding failed", error);
          }
        }
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          const cur = currencyForCountry(resolvedCC);
          nextLocale = {
            city: resolvedCity,
            displayCity:
              resolvedDisplayCity && resolvedDisplayCity !== resolvedCity
                ? resolvedDisplayCity
                : undefined,
            provider: resolvedProvider,
            providerPlaceId: resolvedProviderId,
            country: resolvedCountry,
            countryCode: resolvedCC,
            lat,
            lng,
            currency: cur.currency,
            currencySymbol: cur.symbol,
            language: lang,
            stops: [],
          };
          setUseGpsAsStart(false);
          setLocale(nextLocale);
          setDestinationSource("prompt_named", nextLocale);

          setQuery("");
        } else {
          // Último recurso gratuito: em vez de um erro genérico, mostramos os
          // candidatos reais do fluxo de homónimos para o utilizador escolher.
          try {
            const rawCandidates = await findDestinationCandidates({
              data: { name: p.city, language: lang },
            });
            const decision = resolveHomonyms(rawCandidates, p.city, text);
            if (decision.status === "ask" && decision.options.length > 0) {
              homonymResolvedRef.current = null;
              setHomonymChoice({ name: p.city, basePrompt: text, options: decision.options });
              return;
            }
          } catch (error) {
            console.warn("Destination candidate fallback failed", error);
          }
          // Cidade explícita que não resolve: nunca cair para outro destino
          // (nem para o da sessão anterior). Limpamos só o rascunho visual —
          // rotas já guardadas ficam intactas — e não gastamos crédito.
          resetDestinationDraft();
          setLocale(EMPTY_LOCALE);
          setDestinationSource("none", EMPTY_LOCALE);
          toast.error(
            lang === "pt"
              ? `Não consegui localizar «${p.city}». Confirma o nome ou escolhe outra cidade.`
              : `Couldn't locate “${p.city}”. Check the name or pick another city.`,
          );
          return;

        }

        }
      }
      // Ponto de partida SÓ do texto original: respostas de clarificação
      // ("16", "JFK") nunca podem virar uma morada de origem.
      const origin = resolveRequestOrigin(
        { originalRequest: originalRequestRef.current || text, mergedRequest: text },
        p.startPlace,
      );
      if (origin.rejected) {
        console.info("[origin] rejeitado (veio de clarificação):", origin.rejected);
      }
      if (origin.startPlace) {
        namedStartPosition = await resolveNamedStartPosition(origin.startPlace, nextLocale);
        setNamedRouteStart(namedStartPosition);
      } else {
        setNamedRouteStart(null);
      }

      // Duração: a janela do próprio dia manda; depois os DIAS escritos no
      // texto (vinculativos, a IA nunca os pode aumentar); depois a IA; e, se
      // o pedido novo nada disser, volta-se ao valor por defeito.
      const hoursOnlyRequest =
        typeof assessment.facts.durationHours === "number" && assessment.facts.durationHours < 24;
      const explicitDaysHours =
        !assessment.facts.intraday && !hoursOnlyRequest && assessment.facts.days && assessment.facts.days > 0
          ? assessment.facts.days * 24
          : undefined;
      const requestedHours = intradayHours
        ?? explicitDaysHours
        ?? (hoursOnlyRequest
          ? assessment.facts.durationHours!
          : typeof p.durationHours === "number" && Number.isFinite(p.durationHours)
            ? (intradayWindow ? Math.min(p.durationHours, 12) : p.durationHours)
            : assessment.facts.durationHours ?? DEFAULT_DURATION_HOURS);

      {
        let bestIdx = 0;
        let bestDiff = Infinity;
        DURATIONS.forEach((d, i) => {
          const diff = Math.abs(d - requestedHours);
          if (diff < bestDiff) { bestDiff = diff; bestIdx = i; }
        });
        setDurationIdx(bestIdx);
        nextDuration = DURATIONS[bestIdx];
      }

      // Os chips visuais NUNCA são alterados pelo parser: `nextInterests` já
      // contém os chips explicitamente selecionados antes do reset. Os
      // interesses detetados no texto são calculados separadamente e enviados
      // como `textInterests` sem tocar no estado visual.
      // Mode
      if (p.mode && (["walk", "scooter", "bike", "car", "transit"] as const).includes(p.mode as Mode)) {
        setMode(p.mode as Mode);
        nextMode = p.mode as Mode;
      }
      // Notes (preserve "include restaurant" hint)
      const extra = p.includeRestaurant && requestMealPreference === "restaurant"
        ? (lang === "pt" ? "Inclui sugestão de restaurante." : "Include a restaurant suggestion.")
        : "";
      const mergedRaw = [p.notes?.trim() || "", extra].filter(Boolean).join(" ").slice(0, 300);
      // Cada pesquisa nova substitui integralmente as notas: uma preferência
      // (positiva ou negativa) de um pedido anterior nunca transita.
      const merged = requestMealPreference === "restaurant" ? stripFoodExclusions(mergedRaw) : mergedRaw;
      setNotes(merged);
      nextNotes = merged;


      // Exhaustive intent ("todos os pontos turísticos", "lista completa", etc.)
      // → bump the duration slider so the schedule can host all the stops the
      // AI returns (multi-day). Otherwise a comprehensive request stays capped
      // by the current slider value.
      const wantsExhaustive = !!p.exhaustive;
      if (wantsExhaustive && nextDuration < 24) {
        // 48h ≈ 2 days: enough room for most small/medium cities. The server
        // will further expand up to 7 days as needed based on stop count.
        const bumpTo = 48;
        const bumpIdx = DURATIONS.findIndex((d) => d === bumpTo);
        if (bumpIdx >= 0) {
          setDurationIdx(bumpIdx);
          nextDuration = bumpTo;
        }
      }

      setPlanMode("ai");
      // O texto do pedido NÃO é limpo aqui: se a verificação bloquear ou a
      // geração falhar, a pessoa tem de continuar a ver o que escreveu. Só é
      // limpo quando a rota é criada com sucesso ou num pedido novo.

      // Scroll to itinerary panel once stops render
      pendingScrollRef.current = true;

      // Contrato de preferências deste pedido: interesses efetivos são a união
      // dos interesses detetados no texto com os chips explicitamente tocados,
      // respeitando exclusões escritas. O estado visual dos chips NUNCA é
      // alterado por este cálculo.
      const requestPreferences = buildTripPreferences({
        text,
        formInterests: nextInterests,
        mealChoice: requestMealPreference,
        // O rádio sincronizado por nextMealStateForRequest não é escolha
        // explícita: só mealChoiceForText (toque real) conta.
        mealChoiceExplicit: mealChoiceForText !== null && mealChoiceForText === text,
        eatMinutes: activePrefs.eatMinutes,
      });
      const textInterests = interestsFromPreferences(
        buildTripPreferences({ text, formInterests: [] }),
        [],
      ).filter((id): id is InterestId =>
        (INTERESTS as readonly { id: string }[]).some((it) => it.id === id),
      );
      const effectiveInterests = interestsFromPreferences(requestPreferences, []).filter(
        (id): id is InterestId =>
          (INTERESTS as readonly { id: string }[]).some((it) => it.id === id),
      );

      // O parse validado deste pedido fica guardado: uma continuação
      // explícita nunca torna a interpretar o texto.
      lastParsedPromptRef.current = p;

      // Numa continuação, a seleção fixa e a área vêm como ARGUMENTOS —
      // nunca de estado React que ainda possa estar pendente.
      const effectivePinned: PinnedSelectionRef[] =
        submitOpts?.continuation?.pinned ??
        pinnedSelections.map(({ requestSignature, constraintId, placeId, selectionRef }) => ({
          requestSignature,
          constraintId,
          placeId,
          selectionRef,
        }));
      const effectiveArea: ExpandedArea | null = submitOpts?.continuation
        ? submitOpts.continuation.area
        : expandedAreaRef.current;

      const generateArgs = {
        requestText: text,
        locale: nextLocale,
        duration: nextDuration,
        requestedDays:
          !assessment.facts.intraday && !hoursOnlyRequest && assessment.facts.days && assessment.facts.days > 0
            ? assessment.facts.days
            : undefined,
        interests: effectiveInterests,
        textInterests,
        explicitInterests: nextInterests,
        notes: nextNotes,
        mode: nextMode,
        localMode:
          p.localMode && (["walk", "scooter", "bike", "car", "transit"] as const).includes(p.localMode as Mode)
            ? (p.localMode as Mode)
            : undefined,
        startTime: nextStartTime,
        mealPrefs:
          requestMealPreference === "restaurant" && activePrefs.eatMinutes <= 0
            ? { ...activePrefs, eatMinutes: DEFAULT_MEAL_PREFS.eatMinutes }
            : activePrefs,
        mealPreference: requestMealPreference,

        exhaustive: wantsExhaustive,
        categoryFocus: p.categoryFocus,
        requestedStopCategory: p.requestedStopCategory,
        requestedStopQuery: p.requestedStopQuery,
        categoryFocusQuery: p.categoryFocusQuery,
        startPlace: p.startPlace,
        namedStartPosition,
        namedRoute: p.namedRoute,
        requiredPlaces: p.requiredPlaces,
        requiredStopConstraints: p.requiredStopConstraints,
        // Paragens fixas escolhidas pela pessoa: entram na geração como
        // obrigatórias (o servidor re-confirma via Place Details).
        pinnedStops: effectivePinned.map(({ constraintId, placeId }) => ({ constraintId, placeId })),
        geographicScope: p.geographicScope,
        canonicalDestination: resolvedDestinationRef.current ?? undefined,
      };

      // Locais explicitamente pedidos + janela horária explícita: verificar os
      // horários habituais devolvidos pelo Google Places ANTES de gerar. Esta
      // verificação não consome créditos Routify nem inicia geração por IA.
      const hasRequiredStops =
        (p.requiredPlaces?.length ?? 0) > 0 || (p.requiredStopConstraints?.length ?? 0) > 0;
      if (
        !submitOpts?.skipAvailabilityCheck &&
        hasRequiredStops &&
        assessment.facts.startMinutes != null &&
        assessment.facts.endMinutes != null &&
        nextLocale.city
      ) {
        const dayOffset = assessment.facts.dayOffset ?? 0;
        const target = new Date();
        target.setDate(target.getDate() + dayOffset);
        // Etiqueta escrita pela pessoa ("Leça da Palmeira"): nunca é
        // substituída pelo município resolvido pela geocodificação
        // ("Matosinhos"), que fica apenas como metadado de proximidade.
        const requestedLocalityLabel =
          extractExplicitLocality(text) ?? extractRequestLocality(text) ?? nextLocale.city;
        const municipalityMeta =
          nextLocale.city && nextLocale.city !== requestedLocalityLabel ? [nextLocale.city] : [];
        try {
          const availability = await checkRequiredPlacesAvailability({
            data: {
              places: p.requiredPlaces?.slice(0, 6) ?? [],
              constraints: p.requiredStopConstraints?.slice(0, 6) ?? [],
              // Texto original + seleções fixas: o servidor deriva a assinatura
              // e re-confirma cada placeId (nunca confia no cliente).
              requestText: text,
              pinnedSelections: effectivePinned,
              city: nextLocale.city,
              requestedLocality: requestedLocalityLabel,
              requestedLocalityLabel,
              nearbyLocalities: municipalityMeta,
              expandedArea: effectiveArea,
              chosenLocality: chosenLocalityRef.current,
              lat: Number.isFinite(nextLocale.lat) ? nextLocale.lat : undefined,
              lng: Number.isFinite(nextLocale.lng) ? nextLocale.lng : undefined,
              language: lang,
              day: target.getDay(),
              startMinutes: assessment.facts.startMinutes,
              endMinutes: assessment.facts.endMinutes,
            },
          });
          // A assinatura devolvida pelo servidor passa a ser a assinatura
          // validada deste pedido (a continuação compara com ela).
          lastAvailabilitySignatureRef.current = availability.requestSignature ?? null;
          // Seleções de um pedido diferente (assinatura distinta) são limpas:
          // a assinatura nunca inclui área/seleções, por isso alargar a zona ou
          // escolher um local MANTÉM as seleções deste pedido.
          if (availability.requestSignature) {
            const signature = availability.requestSignature;
            setPinnedSelections((prev) =>
              prev.filter((s) => s.requestSignature === signature),
            );
          }
          // Outcome explícito de cada seleção fixa: uma rejeição mostra SEMPRE
          // o motivo concreto — nunca se volta ao mesmo cartão em silêncio.
          const rejectedPinned = (availability.pinnedOutcomes ?? []).find((o) => !o.accepted);
          if (rejectedPinned) {
            // Falhas técnicas são recuperáveis: a escolha assinada e a área
            // confirmada ficam intactas para o botão "Tentar novamente".
            if (rejectedPinned.reason !== "technical_failure") {
              setPinnedSelections((prev) =>
                prev.filter((s) => s.constraintId !== rejectedPinned.constraintId),
              );
              pinnedContinuationRef.current = null;
            }
            setPinnedConfirmation(null);
            setPinnedRejected({
              basePrompt: text,
              reason: rejectedPinned.reason ?? "technical_failure",
            });
            return;
          }
          if (availability.localityChoice?.options?.length) {
            // Ambiguidade real: escolher primeiro, sem pesquisa nem crédito.
            setLocalityChoice({
              basePrompt: text,
              requested: availability.localityChoice.requested || requestedLocalityLabel,
              options: availability.localityChoice.options.slice(0, 6),
            });
            return;
          }
          if (availability.checked && availability.blocked) {
            const windowLabel = `${formatSlotTime(assessment.facts.startMinutes)}–${formatSlotTime(assessment.facts.endMinutes)}`;
            // Se o servidor não sugeriu área (por não haver "nearby"), ainda
            // assim damos uma zona concreta e limitada a partir do candidato
            // mais próximo: nunca voltamos ao raio largo antigo.
            const fallbackArea =
              availability.suggestedArea ??
              buildExpandedArea({
                requestedLocality: requestedLocalityLabel,
                candidates: availability.results
                  .flatMap((r) => r.candidates)
                  .filter((c) => c.name)
                  .map((c) => ({ locality: c.locality, distanceMeters: c.distanceMeters })),
                ...(Number.isFinite(nextLocale.lat) && Number.isFinite(nextLocale.lng)
                  ? { center: { lat: nextLocale.lat, lng: nextLocale.lng } }
                  : {}),
                language: lang,
              });
            const newBlock = {
              basePrompt: text,
              area: requestedLocalityLabel,
              window: windowLabel,
              dayOffset,
              requestSignature: availability.requestSignature ?? null,
              suggestedArea: expandedAreaRef.current ? null : fallbackArea,
              entries: availability.results
                .filter((r) => r.reason && r.reason !== "technical_failure")
                .map((r) => ({
                  // O rótulo é a RESTRIÇÃO original ("um kartódromo") —
                  // nunca o nome de um candidato ("Kart Center de Matosinhos").
                  label: r.constraintLabel ?? r.name ?? r.query,
                  address: r.address,
                  locality: r.locality ?? null,
                  reason: r.reason!,
                  constraintId: r.constraintId ?? null,
                  message: failureReasonMessage(r.reason!, lang, {
                    placeName: r.name ?? r.query,
                    area: requestedLocalityLabel,
                    window: windowLabel,
                    ...(r.locality ? { placeLocality: r.locality } : {}),
                  }),
                  alternatives: r.alternatives.map((slot) => ({
                    label: formatAlternativeSlot(slot, lang),
                    dayOffset: slot.dayOffset,
                    start: formatSlotTime(slot.startMinutes),
                    end: formatSlotTime(slot.endMinutes),
                  })),
                  candidates: r.candidates
                    .filter((c) => !!c.name)
                    .slice(0, 8)
                    .map((c) => ({
                      placeId: c.placeId,
                      // Referência assinada pelo servidor para ESTE candidato:
                      // a escolha devolve isto ao servidor, nunca um placeId livre.
                      selectionRef: c.selectionRef ?? null,
                      name: c.name!,
                      address: c.address,
                      locality: c.locality,
                      lat: c.lat,
                      lng: c.lng,
                      types: c.types,
                      nearby: c.localityMatch === "nearby",
                      distanceMeters: c.distanceMeters,
                    })),
                })),
            };
            // Cópia para "Alterar escolha" mostrar os mesmos candidatos.
            lastAvailabilityBlockRef.current = newBlock;
            setAvailabilityBlock(newBlock);
            return;
          }
        } catch (error) {
          // Falha técnica na verificação: com locais obrigatórios e janela
          // explícita NUNCA continua automaticamente para a geração — bloqueia
          // até o utilizador escolher "Tentar novamente" ou "Continuar sem
          // confirmar horários". Sem crédito gasto nem IA chamada.
          console.warn("availability precheck failed", error);
          logRequestFailure("submit_ai_prompt", "technical_failure", { stage: "availability" });
          const outcome = resolveTechnicalFailureOutcome({
            requiredPlacesCount: (p.requiredPlaces?.length ?? 0) + (p.requiredStopConstraints?.length ?? 0),
            hasExplicitWindow: true,
          });
          if (outcome === "block_technical") {
            setAvailabilityTechnical({ basePrompt: text });
            return;
          }
        }
      }

      // "I'm here now" intent → try to start from user GPS

      if (wantsGpsStart) {
        if (gpsStartPosition) {
          setUseGpsAsStart(true);
          void runGenerate({ ...generateArgs, useGpsAsStart: true, userPosition: gpsStartPosition });
        } else {
          // Ask user to enable location; keep args pending
          pendingGenerateRef.current = generateArgs;
          setGpsPromptOpen(true);
        }
      } else {
        setUseGpsAsStart(false);
        void runGenerate({ ...generateArgs, useGpsAsStart: false });
      }


    } catch (e) {
      console.error(e);
      toast.error(technicalFailureMessage(lang));
    } finally {
      setAiPromptLoading(false);
    }
  }

  async function reverseToLocale(lat: number, lng: number) {
    setLocale(await localeFromCoordsValue(lat, lng));
  }

  function useGPS() {
    setError(null);
    setGeoNotice(null);
    if (!("geolocation" in navigator)) { setError(t.notFound); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (p) => {
        // Clique explícito no GPS: passa a ser a fonte ativa.
        manualLocationRef.current = false;
        setUseGpsAsStart(true);
        setNamedRouteStart(null);
        setUserPos({ lat: p.coords.latitude, lng: p.coords.longitude });
        const nextLocale = await localeFromCoordsValue(p.coords.latitude, p.coords.longitude);
        setLocale(nextLocale);
        const fix = {
          source: "gps" as const,
          accuracyMeters: typeof p.coords.accuracy === "number" ? p.coords.accuracy : null,
        };
        const approximate = shouldConfirmLocation(fix);
        // A cidade obtida por GPS NUNCA é um destino explícito: exige confirmação.
        setDestinationSource(
          approximate ? "geolocation_approximate" : "geolocation_precise",
          nextLocale,
        );
        setGeoNotice(approximate ? approximateLocationMessage(nextLocale.city) : null);

        setLocating(false);
      },
      (err) => {
        setLocating(false);
        const status = describeGeolocationError(err);
        setError(
          status === "denied"
            ? (lang === "pt"
                ? "Acesso à localização recusado. Escreve a tua cidade na pesquisa."
                : "Location access denied. Type your city in the search box.")
            : status === "timeout"
              ? (lang === "pt"
                  ? "Demorou demasiado a obter a localização. Tenta de novo ou escreve a cidade."
                  : "Getting your location took too long. Try again or type your city.")
              : t.notFound,
        );
      },
      GEOLOCATION_OPTIONS,
    );

  }

  // Auto-detect on first mount (skipped if loading a saved itinerary)
  const didAutoLocate = useRef(false);
  // Open Premium modal when navigated with ?premium=1
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("premium") === "1") {
      setPremiumOpen(true);
      params.delete("premium");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
  }, []);

  // Apply a pending (saved) itinerary: restore stops, hydrate photo cache,
  // restore mode/time/duration. Reusable so we can react to new pending
  // itineraries without depending on a route remount.
  const applyPendingItinerary = useCallback((pending: SavedItinerary) => {
    orderLog("index.applyPendingItinerary", "loading saved itinerary", {
      title: pending.title,
      city: pending.city,
      stops: pending.stops.map((s) => s.name),
    });
    skipNextGenerateRef.current = true;
    loadedFromSavedRef.current = true;
    userReorderedAiRef.current = false;
    setLoadedFromSaved(true);
    setUserReorderedAi(false);
    setNamedRouteStart(null);
    setPlanMode("ai");
    setManualStops([]);
    setInterests([]);
    setAddedExtra(new Set());
    setExtraSuggestions(null);
    const firstWithCoords = pending.stops.find(
      (s) => typeof s.lat === "number" && typeof s.lng === "number",
    );
    setLocale((prev) => ({
      ...prev,
      city: pending.city,
      country: pending.country ?? prev.country,
      countryCode:
        pending.country && pending.country !== prev.country ? "" : prev.countryCode,
      lat: firstWithCoords?.lat ?? prev.lat,
      lng: firstWithCoords?.lng ?? prev.lng,
      stops: [],
    }));
    if (typeof firstWithCoords?.lat === "number" && typeof firstWithCoords.lng === "number") {
      void reverseToLocale(firstWithCoords.lat, firstWithCoords.lng);
    }
    beginRouteGeneration();
    setAiStops(pending.stops.map((s) => ({
      name: s.name,
      category: s.category,
      blurb: s.blurb,
      priceLocal: s.priceLocal,
      rating: s.rating,
      day: s.day,
    })));
    // Rebuild resolvedMap from saved coords/photos so images appear
    // immediately. Replace (not merge) — stops from the previous itinerary
    // must not leak into the new one.
    const hydrated: Record<string, ResolvedStop> = {};
    for (const s of pending.stops) {
      if (typeof s.lat === "number" && typeof s.lng === "number") {
        // Hidrata photoUrl guardado para mostrar imediatamente. Os URLs do
        // Google (lh3.googleusercontent.com) são estáveis; se algum falhar
        // a carregar, o GoogleMapView re-resolve via placeId.
        hydrated[s.name] = {
          name: s.name,
          lat: s.lat,
          lng: s.lng,
          placeId: s.placeId,
          photoUrl: s.photoUrl,
        };
      }
    }
    setResolvedMap(hydrated);
    setRemoved(new Set());
    setRouteLegs([]);
    setRoutePolyline(null);
    setNavigationLeg(null);
    setLegMinutes([]);
    setRestaurants({});
    setHotels({});
    setStopDetailsMap({});
    setDetailStop(null);
    if (["walk", "scooter", "bike", "car", "transit"].includes(pending.mode)) {
      setMode(pending.mode as Mode);
    }
    if (pending.start_time) setStartTime(pending.start_time);
    if (typeof pending.duration_hours === "number") {
      let bestIdx = 4;
      let bestDiff = Infinity;
      DURATIONS.forEach((d, i) => {
        const diff = Math.abs(d - (pending.duration_hours ?? 0));
        if (diff < bestDiff) { bestDiff = diff; bestIdx = i; }
      });
      setDurationIdx(bestIdx);
    }
    toast.success(`"${pending.title}" carregada`);
    pendingScrollRef.current = true;
  }, []);

  useEffect(() => {
    if (didAutoLocate.current) return;
    didAutoLocate.current = true;

    // 1) If user just clicked "Open" on a saved route, restore it.
    const pending = consumePendingItinerary();
    if (pending) {
      applyPendingItinerary(pending);
      return;
    }

    // 2) Otherwise: GPS auto-detect (alta precisão; nunca sobrepõe escolha manual).
    if (!("geolocation" in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      async (p) => {
        const detected = {
          lat: p.coords.latitude,
          lng: p.coords.longitude,
          accuracyMeters: typeof p.coords.accuracy === "number" ? p.coords.accuracy : null,
          timestamp: p.timestamp ?? Date.now(),
          source: "browser" as const,
        };
        if (!canApplyDetectedFix({ manual: manualLocationRef.current ? detected : null, detected })) {
          return;
        }
        setUseGpsAsStart(false);
        setUserPos({ lat: detected.lat, lng: detected.lng });
        const nextLocale = await localeFromCoordsValue(detected.lat, detected.lng);
        if (manualLocationRef.current) return;
        setLocale(nextLocale);
        const approximate = shouldConfirmLocation(detected);
        setDestinationSource(
          approximate ? "geolocation_approximate" : "geolocation_precise",
          nextLocale,
        );
        setGeoNotice(approximate ? approximateLocationMessage(nextLocale.city) : null);

      },
      () => { /* keep default */ },
      GEOLOCATION_OPTIONS,
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // React to subsequent saved-itinerary opens without requiring a remount
  // (e.g. opening another route while the home page is already mounted).
  useEffect(() => {
    const handler = () => {
      const pending = consumePendingItinerary();
      if (pending) applyPendingItinerary(pending);
    };
    window.addEventListener(PENDING_ITINERARY_EVENT, handler);
    return () => window.removeEventListener(PENDING_ITINERARY_EVENT, handler);
  }, [applyPendingItinerary]);



  function toggleInterest(id: InterestId) {
    setInterests((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  }

  // Build display stops: only AI-generated stops (no mock fallback); filter removed; merge resolved info
  const baseDisplayStops: DisplayStop[] = useMemo(() => {
    // legMinutes[i] is travel time from stop i → stop i+1.
    // We want `minutes` on a stop to mean "minutes to REACH this stop from the previous one",
    // so shift by one and set stop 0 to 0 (no inbound leg).
    if (planMode === "manual") {
      return manualStops.map((s, i) => ({
        name: s.name,
        category: s.address ?? "",
        blurb: s.address ?? "",
        priceLocal: 0,
        rating: 0,
        minutes: i === 0 ? 0 : legMinutes[i - 1],
        photoUrl: s.photoUrl,
        placeId: s.placeId,
        lat: s.lat,
        lng: s.lng,
      }));
    }
    const base: DisplayStop[] = aiStops && aiStops.length ? aiStops.map((s) => ({ ...s })) : [];
    return base
      .filter((s) => !removed.has(s.name))
      .map((s, i) => {
        const r = resolvedMap[s.name];
        return {
          ...s,
          minutes: i === 0 ? 0 : (legMinutes[i - 1] ?? s.minutes),
          photoUrl: r?.photoUrl ?? s.photoUrl,
          placeId: r?.placeId ?? s.placeId,
          lat: r?.lat ?? s.lat,
          lng: r?.lng ?? s.lng,
        };
      });
  }, [aiStops, locale, lang, mode, legMinutes, removed, resolvedMap, planMode, manualStops]);

  /**
   * Bloco de pequeno-almoço/brunch do pedido em curso. Só existe em pedidos
   * de rota que mencionam pequeno-almoço sem serem exclusivos: nesse caso o
   * foco exclusivo já filtra todas as paragens.
   */
  const breakfastBlock = useMemo(() => {
    if (sessionCategoryFocus === "breakfast") return null;
    const intent = detectBreakfastIntent(lastRequestText ?? "");
    if (!intent || intent.mode !== "route") return null;
    return intent.window;
  }, [lastRequestText, sessionCategoryFocus]);

  const breakfastMealType = useMemo(() => {
    const intent = detectBreakfastIntent(lastRequestText ?? "");
    return intent?.kind === "brunch" ? "brunch" : "breakfast";
  }, [lastRequestText]);

  // ---- Schedule (start time + lunch/dinner/breakfast breaks) ----
  // Logística de chegada no dia 1 ("aterro às 14h no aeroporto, com hotel
  // escolhido"): a agenda só começa depois do desembarque/bagagem/
  // transferência/check-in. Partida declarada ("voo às 20h") corta as
  // paragens do último dia depois da margem do aeroporto.
  /**
   * Contexto temporal canónico: janelas por dia, logística e a ORDEM
   * cronológica das paragens. A ordem validada é aplicada ao próprio array de
   * paragens — a lista, o mapa, o PDF e o ICS passam a ver sempre a mesma
   * sequência, sem nunca ligar horários por posição original do array.
   *
   * Hotel mencionado no texto NÃO é hotel selecionado: sem `hotelPlaceId` não
   * existe check-in, check-out nem bagagem inventados.
   */
  /**
   * Composição congelada: a barreira final (que reordena e remove paragens)
   * recebe sempre os MESMOS minutos enquanto a geração não muda. Assim, uma
   * atualização de tempos de viagem nunca troca uma paragem por outra.
   */
  const frozenCompositionRef = useRef<FrozenComposition | null>(null);
  const scheduleContext = useMemo(() => {
    const visit = planVisitMinutes ?? (mode === "walk" ? 35 : mode === "car" || mode === "transit" ? 45 : 20);
    // Paragens geograficamente impossíveis (dezenas de km fora do conjunto)
    // consumiam o dia inteiro em deslocação: saem antes de qualquer horário.
    const areaStops = dropGeographicOutliers(baseDisplayStops);

    // Hotel realmente escolhido pela pessoa (estado canónico), nunca texto.
    const hotelPlaceId: string | undefined = hotelPlaceIdOf(hotelSelection);
    const hotelSelected = Boolean(hotelPlaceId);
    const logistics = parseTravelLogistics(lastRequestText ?? "", { hotelSelected });
    // "Criar sem logística" remove os BLOCOS inventados (desembarque, bagagem,
    // transferência, check-in), nunca a hora declarada: "chego às 15h" continua
    // a mandar no início do dia 1.
    const arrival =
      !logistics.skipLogistics && logistics.arrival && logistics.arrival.timeMinutes != null
        ? {
            kind: logistics.arrival.kind,
            timeMinutes: logistics.arrival.timeMinutes,
            // Check-in só existe com hotel realmente escolhido.
            includeCheckin: hotelSelected,
          }
        : null;
    /** Hora declarada de chegada, mesmo sem logística (só limita o dia 1). */
    const declaredArrivalMinutes =
      logistics.arrival && logistics.arrival.timeMinutes != null ? logistics.arrival.timeMinutes : null;
    const totalDays = baseDisplayStops.reduce((max, s) => Math.max(max, s.day ?? 1), 1);
    const [startH, startM] = String(startTime || "09:00").split(":").map((n) => Number.parseInt(n, 10));
    const startTimeMinutes = (Number.isFinite(startH) ? startH : 9) * 60 + (Number.isFinite(startM) ? startM : 0);
    // A hora de chegada nunca é o horário normal dos dias seguintes: quando o
    // início global veio da própria chegada, os dias 2..N usam 09:00.
    const inheritedFromArrival =
      declaredArrivalMinutes != null && declaredArrivalMinutes === startTimeMinutes;
    const defaultStartMinutes = inheritedFromArrival ? 9 * 60 : startTimeMinutes;
    const arrivalOverhead = arrival
      ? (arrival.kind === "airport" ? LANDING_BAGGAGE_MIN + AIRPORT_TRANSFER_DEFAULT_MIN : 0) +
        (arrival.includeCheckin ? HOTEL_CHECKIN_MIN : 0)
      : 0;
    const cutoff = lastDayVisitEndMinutes(logistics);
    // Sem logística, o dia 1 começa exatamente à hora declarada (overhead 0).
    const day1StartMinutes =
      arrival != null
        ? arrival.timeMinutes + arrivalOverhead
        : declaredArrivalMinutes != null
          ? declaredArrivalMinutes
          : undefined;
    const dayWindows = buildDayWindows({
      totalDays,
      defaultStartMinutes,
      day1StartMinutes,
      lastDayEndMinutes: cutoff ?? undefined,
    });
    // Minutos por dia que NÃO são visitas (refeições, chegada, check-out): a
    // barreira final tem de os reservar, senão a agenda real transborda a
    // janela do dia e produz paragens de madrugada.
    const eatMinutes = Math.max(0, Number(activePrefs?.eatMinutes ?? DEFAULT_MEAL_PREFS.eatMinutes) || 0);
    const mealsOff = mealPreference === "none" || eatMinutes <= 0;
    const reservedMinutesByDay: Record<number, number> = {};
    for (let day = 1; day <= totalDays; day++) {
      let reserved = mealsOff ? 0 : eatMinutes * 2;
      if (day === 1 && breakfastBlock) reserved += 60;
      // O overhead de chegada JÁ está no início da janela do dia 1
      // (`day1StartMinutes`). Reservá-lo outra vez encurtava o dia pelos dois
      // lados e apagava o Dia 1 inteiro.
      reservedMinutesByDay[day] = reserved;
    }
    // Composição congelada por geração: a barreira final não pode ser sensível
    // ao trânsito do momento, senão a rota publicada muda sozinha.
    const key = compositionKey({
      generationId: routeIdentity.generationId,
      modeRevision: routeIdentity.modeRevision,
      mode,
      startTime,
      requestText: lastRequestText ?? "",
      hotelSignature: hotelSelectionSignature(hotelSelection),
      mealPreference,
      visitMinutes: visit,
      stops: areaStops.map((s) => ({ name: s.name, day: s.day ?? 1 })),
    });
    frozenCompositionRef.current = freezeComposition(frozenCompositionRef.current, key, areaStops);
    const barrierStops = withFrozenMinutes(frozenCompositionRef.current, areaStops);

    // Barreira final: reordena dentro do dia e remove apenas opcionais que
    // não cabem na janela ou estão fechadas nesse dia da semana.
    const validated = validateScheduleWithDayCoverage({
      stops: barrierStops.map((s, i) => ({
        id: String(i),
        day: s.day ?? 1,
        visitMinutes: visit,
        transitMinutes: s.minutes,
        openingPeriods: s.openingPeriods ?? null,
        required: s.required,
        evening: s.evening,
        placeTypes: s.placeTypes,
        placePrimaryType: s.placePrimaryType,
        category: s.category,
        name: s.name,
      })),

      dayWindows,
      defaultVisitMinutes: visit,
      reservedMinutesByDay,
    });

    const stops = validated.stops
      .map((s) => areaStops[Number.parseInt(s.id, 10)])
      .filter(Boolean) as DisplayStop[];
    return {
      visit,
      hotelSelected,
      logistics,
      arrival,
      dayWindows,
      cutoff,
      stops: stops.length ? stops : areaStops,
    };

  }, [baseDisplayStops, startTime, mode, planVisitMinutes, lastRequestText, activePrefs, mealPreference, breakfastBlock, hotelSelection, routeIdentity]);

  /**
   * Paragens já em ordem cronológica — única lista usada em toda a app.
   * O hotel escolhido nunca é uma visita: é excluído por `placeId`.
   */
  const displayStops = useMemo(() => {
    const kept = excludeHotelFromStops(scheduleContext.stops, hotelSelection);
    // Ordem canónica única: dia crescente e, dentro do dia, a ordem da rota.
    // Sem isto a agenda repetia cabeçalhos ("Dia 1, Dia 2, Dia 1…").
    return kept
      .map((s, i) => ({ s, i }))
      .sort((a, b) => (Math.trunc(a.s.day ?? 1) - Math.trunc(b.s.day ?? 1)) || a.i - b.i)
      .map(({ s }) => s);
  }, [scheduleContext.stops, hotelSelection]);

  // ---- Schedule (start time + lunch/dinner/breakfast breaks) ----
  // A agenda é construída SEMPRE a partir da ordem canónica acima; qualquer
  // inserção (refeição, chegada, check-out, aeroporto) é depois reordenada
  // cronologicamente dentro de `buildSchedule`.
  const scheduleWithArrival: TimedEntry[] = useMemo(() => {
    if (!displayStops.length) return [];
    const { visit, arrival, dayWindows, cutoff, hotelSelected } = scheduleContext;
    const buildFor = (list: DisplayStop[]) =>
      buildSchedule({
        startTime,
        visitMinutes: visit,
        stops: list.map((s) => ({
          name: s.name,
          transitMinutes: s.minutes,
          day: s.day,
          ...(s.evening && isEveningEligible(s) ? { evening: true } : {}),
        })),
        mealPrefs: activePrefs,
        mealPreference,
        breakfast: breakfastBlock,
        arrival,
        dayWindows,
        departure:
          cutoff != null &&
          !scheduleContext.logistics.skipLogistics &&
          !scheduleContext.logistics.departure?.kindUnknown
            ? {
                blockStartMinutes: cutoff,
                includeCheckout: hotelSelected,
                isAirport: !!scheduleContext.logistics.departure?.isAirport,
                ...(scheduleContext.logistics.departure?.timeMinutes != null
                  ? { departureTimeMinutes: scheduleContext.logistics.departure.timeMinutes }
                  : {}),
              }
            : null,
      });

    // A agenda real inclui refeições e logística: só aqui se sabe a hora
    // verdadeira de cada visita. Uma visita opcional agendada com o local
    // fechado é removida e a agenda é reconstruída.
    let indices = displayStops.map((_, i) => i);
    let built = buildFor(indices.map((i) => displayStops[i]));
    for (let pass = 0; pass < 2; pass++) {
      const bad = new Set(
        findStopsOutsideOpeningHours(built, indices.map((i) => displayStops[i])),
      );
      if (!bad.size) break;
      const next = indices.filter((_, pos) => !bad.has(pos));
      if (!next.length || next.length === indices.length) break;
      indices = next;
      built = buildFor(indices.map((i) => displayStops[i]));
    }
    const remapped: TimedEntry[] = built.map((e) =>
      e.type === "stop"
        ? { ...e, idx: indices[e.idx] ?? e.idx }
        : { ...e, afterStopIdx: e.afterStopIdx >= 0 ? indices[e.afterStopIdx] ?? e.afterStopIdx : e.afterStopIdx },
    );

    return cutoff != null ? clipStopsForDeparture(remapped, cutoff) : remapped;
  }, [displayStops, scheduleContext, startTime, activePrefs, mealPreference, breakfastBlock]);


  const schedule = scheduleWithArrival;


  // During guided navigation, resolved meal and hotel venues become real route
  // points in their scheduled position. Outside navigation, cards and editing
  // continue to use only the user's attraction stops.
  const navigationStops: DisplayStop[] = useMemo(() => {
    if (!schedule.length) return displayStops;
    const points: DisplayStop[] = [];
    for (const entry of schedule) {
      if (entry.type === "stop") {
        const stop = displayStops[entry.idx];
        if (stop) points.push(stop);
        continue;
      }
      const key = mealBreakKey(entry);
      const venue = entry.meal === "hotel" ? hotels[key] : restaurants[key];
      if (!venue || venue === "loading" || typeof venue.lat !== "number" || typeof venue.lng !== "number") continue;
      points.push({
        name: venue.name,
        category: entry.meal === "hotel"
          ? (lang === "pt" ? "Hotel" : "Hotel")
          : (lang === "pt" ? "Restaurante" : "Restaurant"),
        blurb: venue.blurb,
        priceLocal: venue.priceLocal ?? 0,
        rating: 0,
        lat: venue.lat,
        lng: venue.lng,
        placeId: venue.placeId,
        day: displayStops[entry.afterStopIdx]?.day,
      });
    }
    return points.length ? points : displayStops;
  }, [schedule, displayStops, restaurants, hotels, lang]);

  const guidedStops = navigationActive ? navigationStops : displayStops;

  // Only start from the user's GPS when they explicitly used the GPS button or
  // started navigation. Auto-detecting their city must not change a planned
  // future route into an "agora, desde onde estou" route.
  const itineraryAnchor = useMemo(() => {
    const withCoords = displayStops.filter((s) => typeof s.lat === "number" && typeof s.lng === "number");
    if (!withCoords.length) {
      // Sem destino resolvido, o mapa fica no utilizador (ou numa vista neutra)
      // — nunca numa cidade de exemplo.
      if (Number.isFinite(locale.lat) && Number.isFinite(locale.lng)) {
        return { lat: locale.lat, lng: locale.lng };
      }
      return userPos ? { lat: userPos.lat, lng: userPos.lng } : { lat: 39.5, lng: -8 };
    }
    return {
      lat: withCoords.reduce((sum, s) => sum + (s.lat ?? 0), 0) / withCoords.length,
      lng: withCoords.reduce((sum, s) => sum + (s.lng ?? 0), 0) / withCoords.length,
    };
  }, [displayStops, locale.lat, locale.lng, userPos]);


  const routeStart = useMemo(() => {
    if (namedRouteStart) {
      const d = distanceMeters(namedRouteStart, itineraryAnchor);
      if (d < 50000) return namedRouteStart;
    }
    if (!useGpsAsStart || !userPos) return null;
    const d = distanceMeters(userPos, itineraryAnchor);
    return d < 50000 ? userPos : null;
  }, [namedRouteStart, useGpsAsStart, userPos, itineraryAnchor]);

  const routeNumberOffset = routeStart ? 1 : 0;

  const activeStop = guidedStops[activeStopIdx] ?? guidedStops[0];
  const nextStop = guidedStops[activeStopIdx + 1];
  // navLeg computed inline in render based on activeStopIdx
  const progressPct = guidedStops.length > 1 ? ((activeStopIdx + 1) / guidedStops.length) * 100 : 100;

  useEffect(() => {
    setActiveStopIdx((cur) => Math.min(cur, Math.max(guidedStops.length - 1, 0)));
    if (guidedStops.length === 0) { setNavigationActive(false); setNavFullscreen(false); }
  }, [guidedStops.length]);

  // After loading a saved route, scroll to the itinerary panel as soon as it
  // exists in the DOM. Mobile layouts take several frames to settle, so we
  // retry instead of using a single setTimeout.
  useEffect(() => {
    if (!pendingScrollRef.current) return;
    if (displayStops.length === 0) return;
    let attempts = 0;
    const tick = () => {
      const el = itineraryRef.current ?? mapSectionRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        const alreadyNearTarget = rect.top >= 0 && rect.top < window.innerHeight * 0.28;
        if (!alreadyNearTarget) {
          el.scrollIntoView({ behavior: "auto", block: "start" });
        }
        pendingScrollRef.current = false;
        return;
      }
      if (++attempts < 20) window.setTimeout(tick, 100);
      else pendingScrollRef.current = false;
    };
    window.setTimeout(tick, 220);
  }, [displayStops.length]);

  // Lock body scroll while the fullscreen navigator is open
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (navigationActive && navFullscreen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [navigationActive, navFullscreen]);

  function startInAppNavigation() {
    setUseGpsAsStart(true);
    setActiveStopIdx(0);
    setNavigationRouteLegs([]);
    setNavigationActive(true);
    setNavFullscreen(true);
    setAutoArrived(false);
    setNavigationLeg(null);
    if (!userPos && "geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (p) => {
          setUserPos({ lat: p.coords.latitude, lng: p.coords.longitude });
          if (typeof p.coords.heading === "number" && !Number.isNaN(p.coords.heading)) setUserHeading(p.coords.heading);
        },
        () => setError(t.awaitingGPS),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
      );
    }
    window.setTimeout(() => {
      mapSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  }

  // Haversine distance in meters
  function distanceMeters(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
    const R = 6371000;
    const toRad = (d: number) => (d * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(x));
  }

  // Watch user position only during live navigation. Committing every raw GPS
  // tick makes mobile maps feel heavy and can spam route recalculations.
  useEffect(() => {
    if (!navigationActive) return;
    if (!("geolocation" in navigator)) return;
    const id = navigator.geolocation.watchPosition(
      (p) => {
        const next = { lat: p.coords.latitude, lng: p.coords.longitude };
        const now = Date.now();
        const last = lastGpsCommitRef.current;
        if (!last || distanceMeters(last, next) > 6 || now - last.at > 2500) {
          lastGpsCommitRef.current = { ...next, at: now };
          setUserPos(next);
        }
        if (typeof p.coords.heading === "number" && !Number.isNaN(p.coords.heading)) {
          setUserHeading(p.coords.heading);
        }
      },
      () => { /* ignore */ },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 },
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [navigationActive]);

  // Device compass heading fallback (when not moving, GPS heading is null)
  useEffect(() => {
    if (!navigationActive) return;
    const handler = (e: DeviceOrientationEvent) => {
      const now = Date.now();
      if (now - lastHeadingCommitRef.current < 900) return;
      const anyE = e as any;
      const h = typeof anyE.webkitCompassHeading === "number"
        ? anyE.webkitCompassHeading
        : (e.alpha != null ? 360 - e.alpha : null);
      if (h != null && !Number.isNaN(h)) {
        lastHeadingCommitRef.current = now;
        setUserHeading(h);
      }
    };
    window.addEventListener("deviceorientationabsolute" as any, handler as any, true);
    window.addEventListener("deviceorientation", handler, true);
    return () => {
      window.removeEventListener("deviceorientationabsolute" as any, handler as any, true);
      window.removeEventListener("deviceorientation", handler, true);
    };
  }, [navigationActive]);

  const distanceToActive = useMemo(() => {
    if (!userPos || !activeStop?.lat || !activeStop?.lng) return null;
    return distanceMeters(userPos, { lat: activeStop.lat, lng: activeStop.lng });
  }, [userPos, activeStop?.lat, activeStop?.lng]);

  useVoiceNavigation({
    enabled: voiceEnabled,
    lang,
    navigationActive,
    activeStopName: activeStop?.name,
    activeStopIdx,
    currentStepText: navigationLeg?.steps?.[0],
    distanceMeters: distanceToActive,
  });

  // Auto-advance when arriving within 40m
  useEffect(() => {
    if (!navigationActive || distanceToActive == null) return;
    if (distanceToActive < 40 && activeStopIdx < guidedStops.length - 1) {
      setAutoArrived(true);
      const tm = setTimeout(() => {
        setActiveStopIdx((cur) => Math.min(cur + 1, guidedStops.length - 1));
        setAutoArrived(false);
      }, 1800);
      return () => clearTimeout(tm);
    }
  }, [distanceToActive, navigationActive, activeStopIdx, guidedStops.length]);

  function removeStop(name: string) {
    if (planMode === "manual") {
      setManualStops((cur) => cur.filter((s) => s.name !== name));
    } else {
      setRemoved((cur) => {
        const n = new Set(cur);
        n.add(name);
        return n;
      });
    }
    setLegMinutes([]);
    setRouteLegs([]);
    setRoutePolyline(null);
    setNavigationLeg(null);
  }

  /** Alternativas próximas para uma paragem, a partir da reserva (sem custos). */
  function swapOptionsFor(stop: { name: string; lat?: number; lng?: number }): AIStop[] {
    const inRoute = new Set((aiStops ?? []).map((s) => s.name.toLowerCase()));
    const pool = spareStops.filter((c) => !inRoute.has(c.name.toLowerCase()));
    if (typeof stop.lat !== "number" || typeof stop.lng !== "number") return pool.slice(0, 3);
    const d = (c: AIStop) => {
      const dx = ((c.lng ?? 0) - stop.lng!) * Math.cos((stop.lat! * Math.PI) / 180);
      const dy = (c.lat ?? 0) - stop.lat!;
      return dx * dx + dy * dy;
    };
    return [...pool].sort((a, b) => d(a) - d(b)).slice(0, 3);
  }

  function swapStop(oldName: string, alt: AIStop) {
    let old: AIStop | undefined;
    setAiStops((cur) => {
      if (!cur) return cur;
      const idx = cur.findIndex((s) => s.name === oldName);
      if (idx < 0) return cur;
      old = cur[idx];
      const next = [...cur];
      next[idx] = { ...alt, day: cur[idx].day };
      return next;
    });
    setSpareStops((cur) => {
      const rest = cur.filter((c) => c.name !== alt.name);
      return old ? [old, ...rest] : rest;
    });
    setSwapFor(null);
    setLegMinutes([]);
    setRouteLegs([]);
    setRoutePolyline(null);
    setNavigationLeg(null);
    toast.success(lang === "pt" ? `Trocado por ${alt.name}` : `Swapped for ${alt.name}`);
  }

  function handleResolved(list: ResolvedStop[]) {
    setResolvedMap((cur) => {
      const next = { ...cur };
      let changed = false;
      for (const r of list) {
        const prev = next[r.name];
        if (!prev || prev.placeId !== r.placeId || prev.photoUrl !== r.photoUrl) {
          next[r.name] = r;
          changed = true;
        }
      }
      return changed ? next : cur;
    });
  }

  // Tempos de viagem: só uma variação REAL (>= 2 min ou >= 150 m) atualiza a
  // rota publicada. ±1 minuto de trânsito é ruído e fazia a página nunca
  // assentar ("A atualizar deslocações…" perpétuo).
  function handleDurations(next: number[]) {
    setLegMinutes((cur) =>
      sameNumberArray(cur, next) || (cur.length > 0 && !legMinutesAreMaterial(cur, next)) ? cur : next,
    );
  }

  function handleRouteLegs(next: RouteLeg[]) {
    setRouteLegs((cur) =>
      sameRouteLegs(cur, next) || (cur.length > 0 && !routeLegsAreMaterial(cur, next)) ? cur : next,
    );
  }

  function handleMapDurations(next: number[]) {
    if (!navigationActive) handleDurations(next);
  }

  function handleMapRouteLegs(next: RouteLeg[]) {
    if (navigationActive) {
      setNavigationRouteLegs((cur) => sameRouteLegs(cur, next) ? cur : next);
      return;
    }
    handleRouteLegs(next);
  }

  function handleOrder(orderedNames: string[]) {
    if (planMode === "manual") {
      setManualStops((cur) => {
        if (cur.length !== orderedNames.length) {
          orderLog("index.handleOrder", "manual: length mismatch — ignored", { cur: cur.map((s) => s.name), incoming: orderedNames });
          return cur;
        }
        const byName = new Map(cur.map((s) => [s.name, s] as const));
        const reordered = orderedNames.map((n) => byName.get(n)).filter(Boolean) as ManualStop[];
        if (reordered.length !== cur.length) {
          orderLog("index.handleOrder", "manual: name mismatch — ignored", { cur: cur.map((s) => s.name), incoming: orderedNames });
          return cur;
        }
        const same = reordered.every((s, i) => s.name === cur[i].name);
        if (!same) {
          orderLog("index.handleOrder", "manual: APPLYING new order", { from: cur.map((s) => s.name), to: orderedNames, loadedFromSaved });
        }
        return same ? cur : reordered;
      });
      return;
    }
    // Never let the map layer mutate the AI itinerary automatically. Google
    // Directions may return a different waypoint order a few seconds after the
    // cards render, which made stops 2/3 appear to swap without any user action.
    // AI routes are only reordered by explicit user actions: drag/up/down or the
    // "Otimizar ordem" button below.
    orderLog("index.handleOrder", "ai: Google order ignored — explicit reorder only", {
      incoming: orderedNames,
      loadedFromSaved: loadedFromSavedRef.current,
      userReorderedAi: userReorderedAiRef.current,
      isMultiDay,
    });
    return;
  }

  function addManualStop(p: PoiResult) {
    if (manualStops.some((s) => s.name === p.name)) {
      toast.info(MM.duplicate[lang]);
      return;
    }
    // First manual stop far from the current city centre (e.g. planning Lisbon
    // while the app still shows Paris)? Recentre the map/city on it.
    if (manualStops.length === 0) {
      const hasCentre = Number.isFinite(locale.lat) && Number.isFinite(locale.lng);
      const dist = hasCentre
        ? distanceMeters({ lat: locale.lat, lng: locale.lng }, { lat: p.lat, lng: p.lng })
        : Number.POSITIVE_INFINITY;
      if (dist > 50000 && p.address) {
        const parts = p.address.split(",").map((x) => x.trim()).filter(Boolean);
        const country = parts[parts.length - 1] ?? locale.country;
        const cityRaw = parts.length >= 2 ? parts[parts.length - 2] : "";
        const city = cityRaw.replace(/^[0-9][0-9\s-]*/, "").trim() || locale.city;
        setLocale((cur) => ({ ...cur, city, country, lat: p.lat, lng: p.lng, stops: [] }));
      }
    }

    setManualStops((cur) => [...cur, { name: p.name, lat: p.lat, lng: p.lng, placeId: p.placeId, photoUrl: p.photoUrl, address: p.address }]);
    setLegMinutes([]);
    setRouteLegs([]);
    setRoutePolyline(null);
    setNavigationLeg(null);
  }

  function moveManualStop(idx: number, dir: -1 | 1) {
    setOptimizeManualOrder(false);
    setManualStops((cur) => {
      const j = idx + dir;
      if (j < 0 || j >= cur.length) return cur;
      const next = [...cur];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
    setLegMinutes([]);
    setRouteLegs([]);
    setRoutePolyline(null);
    setNavigationLeg(null);
  }

  function resetRouteAfterReorder() {
    setLegMinutes([]);
    setRouteLegs([]);
    setRoutePolyline(null);
    setNavigationLeg(null);
  }

  function reorderManualStop(fromIdx: number, toIdx: number) {
    if (fromIdx === toIdx) return;
    setOptimizeManualOrder(false);
    setManualStops((cur) => {
      if (fromIdx < 0 || toIdx < 0 || fromIdx >= cur.length || toIdx >= cur.length) return cur;
      const next = [...cur];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
    resetRouteAfterReorder();
  }

  // Move a stop up/down in the AI itinerary. When the swap crosses a day
  // boundary the moved stop inherits the day of its new neighbor so the
  // grouping stays consistent.
  function moveAiStop(displayIdx: number, dir: -1 | 1) {
    userReorderedAiRef.current = true;
    setUserReorderedAi(true);
    const stops = displayStops;
    const targetIdx = displayIdx + dir;
    if (targetIdx < 0 || targetIdx >= stops.length) return;
    const movingName = stops[displayIdx].name;
    const neighborName = stops[targetIdx].name;
    const neighborDay = stops[targetIdx].day;
    setAiStops((cur) => {
      if (!cur) return cur;
      const mIdx = cur.findIndex((s) => s.name === movingName);
      if (mIdx < 0) return cur;
      const next = [...cur];
      const [moved] = next.splice(mIdx, 1);
      const nIdx = next.findIndex((s) => s.name === neighborName);
      if (nIdx < 0) {
        next.splice(mIdx, 0, moved);
        return cur;
      }
      const insertAt = dir < 0 ? nIdx : nIdx + 1;
      next.splice(insertAt, 0, { ...moved, day: neighborDay ?? moved.day });
      return next;
    });
    setLegMinutes([]);
    setRouteLegs([]);
    setRoutePolyline(null);
    setNavigationLeg(null);
  }

  function reorderAiStop(fromIdx: number, toIdx: number) {
    userReorderedAiRef.current = true;
    setUserReorderedAi(true);
    const stops = displayStops;
    if (fromIdx === toIdx || fromIdx < 0 || toIdx < 0 || fromIdx >= stops.length || toIdx >= stops.length) return;
    const movingName = stops[fromIdx].name;
    const targetName = stops[toIdx].name;
    const targetDay = stops[toIdx].day;
    setAiStops((cur) => {
      if (!cur) return cur;
      const sourceIdx = cur.findIndex((s) => s.name === movingName);
      if (sourceIdx < 0) return cur;
      const next = [...cur];
      const [moved] = next.splice(sourceIdx, 1);
      const targetIdx = next.findIndex((s) => s.name === targetName);
      if (targetIdx < 0) return cur;
      const adjustedTargetIdx = fromIdx < toIdx ? targetIdx + 1 : targetIdx;
      next.splice(adjustedTargetIdx, 0, { ...moved, day: targetDay ?? moved.day });
      return next;
    });
    resetRouteAfterReorder();
  }

  function reorderStop(fromIdx: number, toIdx: number) {
    if (planMode === "manual") reorderManualStop(fromIdx, toIdx);
    else reorderAiStop(fromIdx, toIdx);
  }

  function stopDragStart(e: ReactDragEvent<HTMLElement>, idx: number, name: string) {
    dragStopIndexRef.current = idx;
    nativeDragActiveRef.current = true;
    setDraggingStopName(name);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(idx));
  }

  function stopDragOver(e: ReactDragEvent<HTMLElement>) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    // Auto-scroll the window when the mouse is near the top/bottom edge.
    // Native HTML5 drag doesn't auto-scroll the page in Chromium on desktop,
    // so we replicate the touch behaviour for mouse users here.
    updateAutoScroll(e.clientY);
  }

  function stopDrop(e: ReactDragEvent<HTMLElement>, toIdx: number) {
    e.preventDefault();
    const fromData = Number(e.dataTransfer.getData("text/plain"));
    const fromIdx = Number.isFinite(fromData) ? fromData : dragStopIndexRef.current;
    if (fromIdx != null) reorderStop(fromIdx, toIdx);
    dragStopIndexRef.current = null;
    nativeDragActiveRef.current = false;
    stopAutoScroll();
    setDraggingStopName(null);
    setDragOverStopIdx(null);
  }

  function stopDragEnd(e?: ReactDragEvent<HTMLElement>) {
    dragStopIndexRef.current = null;
    nativeDragActiveRef.current = false;
    stopAutoScroll();
    setDraggingStopName(null);
    setDragOverStopIdx(null);
    const item = e?.currentTarget.closest('[data-route-stop="true"]') as HTMLElement | null;
    item?.setAttribute("draggable", "false");
  }

  function enableHandleDrag(e: ReactPointerEvent<HTMLElement>) {
    const item = e.currentTarget.closest('[data-route-stop="true"]') as HTMLElement | null;
    item?.setAttribute("draggable", "true");
  }

  function handleGripPointerDown(e: ReactPointerEvent<HTMLElement>, idx: number, name: string) {
    enableHandleDrag(e);
    if (e.pointerType === "mouse") return;
    const item = e.currentTarget.closest('[data-route-stop="true"]') as HTMLElement | null;
    pointerDragRef.current = { fromIdx: idx, targetIdx: idx, name };
    setDraggingStopName(name);
    setDragOverStopIdx(idx);
    setDragGhost({ x: e.clientX, y: e.clientY, name });
    e.currentTarget.setPointerCapture(e.pointerId);
    item?.setAttribute("draggable", "false");
    e.preventDefault();
  }

  function tickAutoScroll() {
    const speed = autoScrollSpeedRef.current;
    if (speed !== 0) {
      window.scrollBy(0, speed);
    }
    if ((pointerDragRef.current || nativeDragActiveRef.current) && autoScrollSpeedRef.current !== 0) {
      autoScrollRafRef.current = requestAnimationFrame(tickAutoScroll);
    } else {
      autoScrollRafRef.current = null;
    }
  }

  function updateAutoScroll(clientY: number) {
    const EDGE = 90;
    const MAX_SPEED = 18;
    const vh = window.innerHeight;
    let speed = 0;
    if (clientY < EDGE) {
      speed = -Math.ceil(((EDGE - clientY) / EDGE) * MAX_SPEED);
    } else if (clientY > vh - EDGE) {
      speed = Math.ceil(((clientY - (vh - EDGE)) / EDGE) * MAX_SPEED);
    }
    autoScrollSpeedRef.current = speed;
    if (speed !== 0 && autoScrollRafRef.current == null) {
      autoScrollRafRef.current = requestAnimationFrame(tickAutoScroll);
    }
  }

  function stopAutoScroll() {
    autoScrollSpeedRef.current = 0;
    if (autoScrollRafRef.current != null) {
      cancelAnimationFrame(autoScrollRafRef.current);
      autoScrollRafRef.current = null;
    }
  }

  function handleGripPointerMove(e: ReactPointerEvent<HTMLElement>) {
    const drag = pointerDragRef.current;
    if (!drag) return;
    updateAutoScroll(e.clientY);
    setDragGhost({ x: e.clientX, y: e.clientY, name: drag.name });
    const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
    const item = el?.closest('[data-route-stop="true"]') as HTMLElement | null;
    const nextIdx = item ? Number(item.dataset.stopIdx) : drag.targetIdx;
    if (Number.isFinite(nextIdx)) {
      drag.targetIdx = nextIdx;
      setDragOverStopIdx(nextIdx);
    }
    e.preventDefault();
  }

  function handleGripPointerUp(e: ReactPointerEvent<HTMLElement>) {
    const drag = pointerDragRef.current;
    if (drag) reorderStop(drag.fromIdx, drag.targetIdx);
    pointerDragRef.current = null;
    dragStopIndexRef.current = null;
    stopAutoScroll();
    setDraggingStopName(null);
    setDragOverStopIdx(null);
    setDragGhost(null);
    const item = e.currentTarget.closest('[data-route-stop="true"]') as HTMLElement | null;
    item?.setAttribute("draggable", "false");
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    e.preventDefault();
  }

  // Add a Google Place to the AI itinerary as a new stop. Appended to the
  // last day so it doesn't break day grouping.
  function addAiStop(p: PoiResult) {
    const existing = (aiStops ?? []).filter((s) => !removed.has(s.name));
    if (existing.some((s) => s.name.toLowerCase() === p.name.toLowerCase())) {
      toast.info(MM.duplicate[lang]);
      return;
    }
    const lastDay = existing.length ? existing[existing.length - 1].day : undefined;
    const newStop: AIStop = {
      name: p.name,
      category: lang === "pt" ? "Adicionado por ti" : lang === "es" ? "Añadido por ti" : lang === "fr" ? "Ajouté par toi" : lang === "de" ? "Von dir hinzugefügt" : lang === "ja" ? "あなたが追加" : "Added by you",
      blurb: p.address ?? "",
      priceLocal: 0,
      rating: 0,
      ...(lastDay != null ? { day: lastDay } : {}),
    };
    setAiStops((cur) => [...(cur ?? []), newStop]);
    setResolvedMap((cur) => ({
      ...cur,
      [p.name]: { name: p.name, placeId: p.placeId, photoUrl: p.photoUrl, lat: p.lat, lng: p.lng },
    }));
    setLegMinutes([]);
    setRouteLegs([]);
    setRoutePolyline(null);
    setNavigationLeg(null);
    toast.success(MM.stopAdded[lang]);
  }

  // Add an AI-suggested extra stop (from the "Suggest more" modal).
  // Called from <GoogleMapView> when the user taps a Google POI while pickMode is on.
  function handlePoiPicked(p: PickedPoi) {
    const poi: PoiResult = {
      name: p.name,
      lat: p.lat,
      lng: p.lng,
      placeId: p.placeId,
      address: p.address,
      photoUrl: p.photoUrl,
    };
    if (planMode === "manual" || !aiStops?.length) {
      if (planMode !== "manual") setPlanMode("manual");
      const exists = manualStops.some((s) => s.name === poi.name);
      addManualStop(poi);
      if (!exists) setPickCount((c) => c + 1);
    } else {
      const exists = (aiStops ?? []).some((s) => s.name.toLowerCase() === poi.name.toLowerCase());
      addAiStop(poi);
      if (!exists) setPickCount((c) => c + 1);
    }
  }



  function addExtraStop(s: AIStop) {

    const existing = (aiStops ?? []).filter((x) => !removed.has(x.name));
    if (existing.some((x) => x.name.toLowerCase() === s.name.toLowerCase())) {
      toast.info(MM.duplicate[lang]);
      return;
    }
    const lastDay = existing.length ? existing[existing.length - 1].day : undefined;
    const stop: AIStop = { ...s, ...(lastDay != null && s.day == null ? { day: lastDay } : {}) };
    setAiStops((cur) => [...(cur ?? []), stop]);
    setAddedExtra((cur) => new Set(cur).add(s.name));
    setLegMinutes([]);
    setRouteLegs([]);
    setRoutePolyline(null);
    setNavigationLeg(null);
  }

  async function openSuggestMore() {
    setExtraOpen(true);
    setExtraLoading(true);
    setExtraSuggestions(null);
    setAddedExtra(new Set());
    try {
      const existing = (aiStops ?? []).filter((s) => !removed.has(s.name)).map((s) => s.name);
      const r = await fetchExtra({
        data: {
          city: locale.city,
          country: locale.country,
          lat: locale.lat,
          lng: locale.lng,
          language: lang,
          currency: locale.currency,
          interests,
          excludeNames: existing,
          count: 6,
          anonId: getAnonId(),
          // Mesmo sinal da geração: sem intenção explícita de comida,
          // "Sugerir mais sítios" só acrescenta visitas (garantia B).
          foodIntent: lastFoodIntentRef.current,
        },
      });
      if (r.error === "quota_exceeded" && "quota" in r && r.quota && !r.quota.ok) {
        setExtraOpen(false);
        setQuotaModal({ open: true, tier: r.quota.tier, reason: r.quota.reason, resetInMinutes: r.quota.resetInMinutes });
        return;
      }
      setExtraSuggestions(r.stops ?? []);
    } catch (e) {
      console.error("suggestExtraStops failed", e);
      setExtraSuggestions([]);
    } finally {
      setExtraLoading(false);
    }
  }

  // Nearest-neighbor reorder by lat/lng, preserving day grouping when multi-day.
  function optimizeOrder() {
    const stops = displayStops;
    if (stops.length < 3) return;
    const hasCoords = (s: DisplayStop) => s.lat != null && s.lng != null;
    const withCoordsCount = stops.filter(hasCoords).length;
    if (withCoordsCount < 2) {
      // Não temos coordenadas suficientes (ex.: rota guardada antiga sem
      // lat/lng). Avisar o utilizador em vez de bloquear silenciosamente.
      toast.info(MM.optimizeNeedsCoords[lang]);
      return;
    }
    const reorderGroup = (group: DisplayStop[]): DisplayStop[] => {
      if (group.length <= 2) return group;
      // Reordena apenas as paragens com coordenadas; mantém as restantes
      // nas suas posições relativas no final do grupo.
      const withCoords = group.filter(hasCoords);
      const withoutCoords = group.filter((s) => !hasCoords(s));
      if (withCoords.length < 2) return group;
      const remaining = withCoords.slice(1);
      const out: DisplayStop[] = [withCoords[0]];
      while (remaining.length) {
        const last = out[out.length - 1];
        let bestIdx = 0;
        let bestD = Infinity;
        for (let i = 0; i < remaining.length; i++) {
          const d = distanceMeters(
            { lat: last.lat!, lng: last.lng! },
            { lat: remaining[i].lat!, lng: remaining[i].lng! },
          );
          if (d < bestD) { bestD = d; bestIdx = i; }
        }
        out.push(remaining.splice(bestIdx, 1)[0]);
      }
      return [...out, ...withoutCoords];
    };
    // Group by day (or single group if no days)
    const groups = new Map<number, DisplayStop[]>();
    stops.forEach((s) => {
      const d = s.day ?? 1;
      if (!groups.has(d)) groups.set(d, []);
      groups.get(d)!.push(s);
    });
    const orderedNames: string[] = [];
    Array.from(groups.keys()).sort((a, b) => a - b).forEach((d) => {
      orderedNames.push(...reorderGroup(groups.get(d)!).map((s) => s.name));
    });
    if (planMode === "ai") {
      // Rebuild aiStops as the reordered displayed stops (drops any soft-removed
      // names so the new order is not blocked by length mismatch in handleOrder).
      setAiStops((cur) => {
        if (!cur) return cur;
        const visible = cur.filter((s) => !removed.has(s.name));
        const byName = new Map(visible.map((s) => [s.name, s] as const));
        const next = orderedNames.map((n) => byName.get(n)).filter(Boolean) as AIStop[];
        return next.length === visible.length ? next : cur;
      });
      setRemoved(new Set());
    } else {
      handleOrder(orderedNames);
    }
    setLegMinutes([]);
    setRouteLegs([]);
    setRoutePolyline(null);
    setNavigationLeg(null);
    toast.success(MM.optimizedToast[lang]);
  }



  function mapsUrlFor(s: DisplayStop) {
    if (s.placeId) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.name)}&query_place_id=${s.placeId}`;
    }
    const q = encodeURIComponent(`${s.name} ${locale.city}`);
    return `https://www.google.com/maps/search/?api=1&query=${q}`;
  }

  function mapsUrlForVenue(v: RoutePoint) {
    if (v.googleMapsUri) return v.googleMapsUri;
    if (v.placeId) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(v.name)}&query_place_id=${v.placeId}`;
    }
    if (typeof v.lat === "number" && typeof v.lng === "number") {
      return `https://www.google.com/maps/search/?api=1&query=${v.lat},${v.lng}`;
    }
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${v.name} ${locale.city}`)}`;
  }

  /**
   * Pontos de rota (mapa e navegação) — vêm SEMPRE do snapshot canónico, na
   * mesma ordem de `scheduleEntryId` da agenda apresentada.
   */
  function orderedRoutePoints(includeVenues = true): RoutePoint[] {
    if (!includeVenues || !routeSnapshot) return displayStops;
    const points: RoutePoint[] = snapshotRoutePoints(routeSnapshot).map((p) => ({
      name: p.name,
      ...(p.lat != null ? { lat: p.lat } : {}),
      ...(p.lng != null ? { lng: p.lng } : {}),
      ...(p.placeId ? { placeId: p.placeId } : {}),
      ...(p.googleMapsUri ? { googleMapsUri: p.googleMapsUri } : {}),
    }));
    return points.some((p) => displayStops.some((s) => s.name === p.name)) ? points : displayStops;
  }

  function navigationUrlFor(_s: DisplayStop) {
    const stops = orderedRoutePoints(true);
    if (!stops.length) return "https://www.google.com/maps";
    const travelmode = mode === "walk" ? "walking" : mode === "car" ? "driving" : mode === "transit" ? "transit" : "bicycling";
    const coordOrName = (st: RoutePoint) =>
      st.lat != null && st.lng != null ? `${st.lat},${st.lng}` : `${st.name} ${locale.city}`;
    const destination = coordOrName(stops[stops.length - 1]);
    const params = new URLSearchParams({ api: "1", destination, travelmode });
    if (routeStart) params.set("origin", `${routeStart.lat},${routeStart.lng}`);
    else if (stops.length > 1) params.set("origin", coordOrName(stops[0]));
    const waypointStops = routeStart ? stops.slice(0, -1) : stops.slice(1, -1);
    if (waypointStops.length) {
      params.set("waypoints", waypointStops.map(coordOrName).join("|"));
    }
    const lastPlaceId = stops[stops.length - 1].placeId;
    if (lastPlaceId) params.set("destination_place_id", lastPlaceId);
    return `https://www.google.com/maps/dir/?${params.toString()}`;
  }

  // Departure time for traffic-aware routing (car mode): use the chosen start
  // time when it's still in the future today, otherwise "now".
  const mapDepartureTime = useMemo(() => {
    if (!startTime) return undefined;
    const now = new Date();
    const d = buildStartDateTime(startDate, startTime);
    if (!d) return undefined;
    if (d.getTime() > now.getTime()) return d;
    if (useGpsAsStart) return undefined;

    // A planned route is not an instruction to depart at the current clock
    // time. If its chosen date/time has passed, route it at the next occurrence
    // of that daytime hour instead of silently switching to "now".
    const daysBehind = Math.max(1, Math.ceil((now.getTime() - d.getTime()) / 86_400_000));
    d.setDate(d.getDate() + daysBehind);
    if (d.getTime() <= now.getTime()) d.setDate(d.getDate() + 1);
    return d;
  }, [startDate, startTime, useGpsAsStart]);

  // Total route duration in minutes (visits + transit + meal breaks), from start time to last departure.
  const totalRouteMinutes = useMemo(() => {
    if (!schedule.length || !startTime || isMultiDay) return null;
    const start = buildStartDateTime(startDate, startTime);
    if (!start) return null;
    let end = start.getTime();
    for (const e of schedule) end = Math.max(end, (e.type === "stop" ? e.leaveAt : e.endAt).getTime());
    return Math.max(0, Math.round((end - start.getTime()) / 60000));
  }, [schedule, startDate, startTime, isMultiDay]);

  /**
   * Hotel realmente escolhido pela pessoa (com identidade). Enquanto não
   * existe seleção, uma sugestão de alojamento nunca entra no orçamento nem
   * cria check-in.
   */
  const hotelIsSelected = isHotelSelected(hotelSelection);


  // Route totals: distance + duration come from EXACTLY the same legs, so the
  // summary can never show "53 m" together with "18h de deslocação".
  // Roteiro criado sem logística de chegada/partida: mostramos o aviso no topo
  // do resultado, para a pessoa perceber porque não há aeroporto nem check-in.
  const logisticsSkipped = useMemo(
    () => parseTravelLogistics(lastRequestText ?? "").skipLogistics === true,
    [lastRequestText],
  );

  /**
   * Resumo de deslocação: SEMPRE as pernas da revisão publicada. Enquanto uma
   * revisão nova é calculada, os últimos totais válidos MANTÊM-SE visíveis —
   * o cartão nunca desaparece e volta a aparecer (o "piscar" do resumo).
   */
  const lastTotalsRef = useRef<{ meters: number; min: number; cost: number } | null>(null);
  const routeTotals = useMemo(() => {
    const summary = routeSnapshot?.summary;
    if (!summary || summary.source === "none") return lastTotalsRef.current;
    const cost = estimateCostEUR(
      mode,
      summary.totalDistanceMeters,
      summary.totalTravelMinutes,
      summary.legCount,
    );
    const totals = { meters: summary.totalDistanceMeters, min: summary.totalTravelMinutes, cost, summary };
    lastTotalsRef.current = totals;
    return totals;
  }, [routeSnapshot, mode]);



  function handleExportIcs() {
    if (!displayStops.length || !startTime) {
      toast.error(lang === "pt" ? "Define a hora de início primeiro" : "Set a start time first");
      return;
    }
    // Mesmos eventos, ordem e horários da agenda: tudo vem do snapshot canónico.
    const events = routeSnapshot ? snapshotIcsEvents(routeSnapshot) : [];
    if (!events.length) {
      toast.error(lang === "pt" ? "Nada para exportar" : "Nothing to export");
      return;
    }
    const ics = buildIcs(`Routify · ${locale.city}`, events);
    downloadIcs(`routify-${locale.city.toLowerCase().replace(/\s+/g, "-")}.ics`, ics);
    toast.success(lang === "pt" ? "Calendário exportado" : "Calendar exported");
    track("calendar_exported", { city: locale.city, stops: events.length });
  }

  function buildFullRouteUrl(): string | undefined {
    const stops = orderedRoutePoints(true);
    if (stops.length < 2) return undefined;
    const travelmode = mode === "walk" ? "walking" : mode === "car" ? "driving" : mode === "transit" ? "transit" : "bicycling";
    const coordOrName = (s: RoutePoint) =>
      s.lat != null && s.lng != null ? `${s.lat},${s.lng}` : `${s.name} ${locale.city}`;
    const origin = routeStart ? `${routeStart.lat},${routeStart.lng}` : coordOrName(stops[0]);
    const destination = coordOrName(stops[stops.length - 1]);
    const middle = routeStart ? stops.slice(0, -1) : stops.slice(1, -1);
    const wps = middle.map(coordOrName).join("|");
    const p = new URLSearchParams({ api: "1", origin, destination, travelmode });
    if (wps) p.set("waypoints", wps);
    return `https://www.google.com/maps/dir/?${p.toString()}`;
  }

  const venueMarkers = useMemo<MapVenue[]>(() => {
    const markers: MapVenue[] = [];
    const seen = new Set<string>();
    for (const entry of schedule) {
      if (entry.type !== "break") continue;
      // Chegada/aeroporto/check-in não têm venue próprio: sem marcador no mapa.
      if (isLogisticsMeal(entry.meal)) continue;
      const key = mealBreakKey(entry);
      const venue = entry.meal === "hotel" ? hotels[key] : restaurants[key];
      if (!venue || venue === "loading" || typeof venue.lat !== "number" || typeof venue.lng !== "number") continue;
      const id = `${entry.meal}:${key}:${venue.placeId ?? venue.name}`;
      if (seen.has(id)) continue;
      seen.add(id);
      markers.push({
        id,
        name: venue.name,
        kind: entry.meal === "breakfast" ? "lunch" : entry.meal,
        lat: venue.lat,
        lng: venue.lng,
        day: displayStops[entry.afterStopIdx]?.day,
        googleMapsUri: venue.googleMapsUri ?? (venue.placeId
          ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue.name)}&query_place_id=${venue.placeId}`
          : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${venue.name} ${locale.city}`)}`),
      });
    }
    return markers;
  }, [schedule, restaurants, hotels, displayStops, locale.city]);

  async function handleExportPdf(template: PdfTemplate = "classic") {
    if (!displayStops.length) {
      toast.error(lang === "pt" ? "Nada para exportar" : "Nothing to export");
      return;
    }
    // O PDF contém exatamente as mesmas paragens, dias e horas da agenda:
    // tudo é derivado do snapshot canónico (mesma `routeRevision`).
    const items: PdfItem[] = [];
    const itemDays: number[] = [];
    if (routeSnapshot) {
      for (const section of snapshotPdfDays(routeSnapshot)) {
        let dayIndex = 0;
        for (const e of section.entries) {
          itemDays.push(section.day);
          if (e.kind === "stop") {
            dayIndex += 1;
            items.push({
              type: "stop",
              index: dayIndex,
              name: e.name,
              blurb: e.blurb,
              arrive: formatTime(e.startAt, lang),
              leave: formatTime(e.endAt, lang),
              note: e.note,
              mapsUrl: e.mapsUrl,
              lat: e.lat,
              lng: e.lng,
            });
          } else {
            items.push({
              type: "break",
              label: e.label,
              start: formatTime(e.startAt, lang),
              end: formatTime(e.endAt, lang),
              venue: e.venue,
            });
          }
        }
      }
    }
    if (!items.length) {
      displayStops.forEach((s, i) => {
        itemDays.push(Math.max(1, Math.trunc((s as { day?: number }).day ?? 1)));
        items.push({ type: "stop", index: i + 1, name: s.name, blurb: s.blurb, note: stopNotes[s.name], mapsUrl: mapsUrlFor(s), lat: s.lat, lng: s.lng });
      });
    }
    const modeLabel = mode === "walk" ? (lang === "pt" ? "A pé" : "Walking")
      : mode === "car" ? (lang === "pt" ? "Carro" : "Car")
      : mode === "transit" ? (lang === "pt" ? "Transporte público" : "Transit")
      : mode === "bike" ? (lang === "pt" ? "Bicicleta" : "Bicycle")
      : "Scooter";
    const dateLabel = new Date().toLocaleDateString(lang === "pt" ? "pt-PT" : lang, { day: "2-digit", month: "short", year: "numeric" });
    try {
      // One complete route per day: group the already-computed items by day and
      // fetch a dedicated static map for each. No AI call, no credit consumed,
      // no itinerary mutation and no invented coordinates.
      const dayNumbers = Array.from(new Set(itemDays)).sort((a, b) => a - b);
      const travelmode = mode === "walk" ? "walking" : mode === "car" ? "driving" : mode === "transit" ? "transit" : "bicycling";
      const pdfDays: PdfDaySection[] = [];
      for (const dayNumber of dayNumbers.length ? dayNumbers : [1]) {
        const dayItems = items.filter((_, i) => (itemDays[i] ?? 1) === dayNumber);
        const dayStops = dayItems.filter((i): i is PdfItem & { type: "stop" } => i.type === "stop");
        const mapPoints = dayStops
          .filter((s) => typeof s.lat === "number" && typeof s.lng === "number")
          .map((s) => ({ lat: s.lat as number, lng: s.lng as number }));
        const unmappedNames = dayStops
          .filter((s) => typeof s.lat !== "number" || typeof s.lng !== "number")
          .map((s) => s.name);
        const dayVenues = dayItems
          .filter((i) => i.type === "break" && i.venue?.lat != null && i.venue?.lng != null)
          .map((i) => {
            const b = i as PdfItem & { type: "break" };
            const kind = b.label.toLowerCase().includes("hotel") ? "hotel" : b.label.toLowerCase().includes("jantar") || b.label.toLowerCase().includes("dinner") ? "dinner" : "lunch";
            return { lat: b.venue!.lat as number, lng: b.venue!.lng as number, kind: kind as "lunch" | "dinner" | "hotel" };
          });

        let dayMap: string | undefined;
        if (mapPoints.length >= 2) {
          try {
            const map = await getStaticRouteMapDataUrl({
              data: {
                stops: mapPoints.slice(0, 25),
                ...(dayVenues.length ? { venues: dayVenues.slice(0, 20) } : {}),
                // Only reuse the stored polyline when it really is a single-day route.
                ...(dayNumbers.length === 1 && routePolyline ? { encodedPolyline: routePolyline } : {}),
              },
            });
            dayMap = map.dataUrl ?? undefined;
          } catch {
            dayMap = undefined;
          }
        }

        pdfDays.push({
          title: lang === "pt" ? `Dia ${dayNumber}` : `Day ${dayNumber}`,
          ...(dayMap ? { mapImageDataUrl: dayMap } : {}),
          ...(dayRouteUrl(mapPoints, travelmode) ? { routeUrl: dayRouteUrl(mapPoints, travelmode) } : {}),
          summary: `${dayStops.length} ${lang === "pt" ? "paragem(ns)" : "stop(s)"}`,
          ...(unmappedNames.length ? { unmappedNames } : {}),
          items: dayItems,
        });
      }

      // Mobilidade oficial: Free/Premium recebem avisos essenciais + teaser,
      // sem links Pro. Lê apenas o diretório curado (sem IA, sem Maps).
      let mobility: Awaited<ReturnType<typeof buildPdfMobilitySection>> = null;
      try {
        const res = await getMobilityResources({ data: { city: locale.city, transport: mode } });
        mobility = buildPdfMobilitySection({
          tier: "free",
          cities: [{ city: locale.city, groups: relevantGroupsForTransport(res.groups as MobilityGroup[], mode) }],
        });
      } catch {
        mobility = null;
      }

      await exportItineraryPdf({
        city: locale.city,
        country: locale.country,
        dateLabel,
        startTime: startTime || undefined,
        modeLabel,
        totalDistanceLabel: routeTotals?.meters ? formatKm(routeTotals.meters) : undefined,
        totalTravelLabel: routeTotals?.min ? formatDurationMin(routeTotals.min) : undefined,
        totalCostLabel: routeTotals?.cost ? `€${routeTotals.cost.toFixed(2)}` : undefined,
        items: [],
        days: pdfDays,
        ...(mobility
          ? {
              resources: {
                title: mobility.title,
                note: mobility.teaserLine ?? mobility.note ?? "",
                entries: mobility.entries.filter((e) => e.url || e.note),
              },
            }
          : {}),

        footer: "Routify · routify-tour.com",
        template,
        fullRouteUrl: buildFullRouteUrl(),
        copyBaseUrl: typeof window !== "undefined" ? `${window.location.origin}/copy` : "https://routify-tour.com/copy",
      });
      toast.success(lang === "pt" ? "PDF exportado" : "PDF exported");
      track("pdf_exported", { city: locale.city, stops: items.length, template });
      setPdfDialogOpen(false);
    } catch (err) {
      console.error("PDF export failed", err);
      toast.error(lang === "pt" ? "Falha ao exportar PDF" : "PDF export failed");
    }
  }

  // As horas NUNCA são recuperadas por índice: cada visita e cada bloco
  // trazem `startAt`/`endAt` na própria entrada canónica (`scheduleEntryId`)
  // da rota publicada — ver `agendaItems`.


  /**
   * Blocos (refeições, hotel, logística) com as horas da revisão PUBLICADA.
   * Antes da primeira publicação usa-se a agenda em rascunho.
   */
  const breaks = useMemo(
    () => schedule.filter((e): e is Extract<TimedEntry, { type: "break" }> => e.type === "break"),
    [schedule],
  );


  /** Etiqueta canónica de um bloco — igual na agenda, no PDF e no ICS. */
  function breakLabel(meal: string): string {
    if (meal === "arrival") return lang === "pt" ? "🏨 Check-in" : "🏨 Check-in";
    if (meal === "airport") return lang === "pt" ? "✈️ Desembarque e transferência" : "✈️ Landing & transfer";
    if (meal === "lunch") return lang === "pt" ? "Almoço" : "Lunch";
    if (meal === "dinner") return lang === "pt" ? "Jantar" : "Dinner";
    if (meal === "breakfast") return lang === "pt" ? "Pequeno-almoço" : "Breakfast";
    if (meal === "checkout") return lang === "pt" ? "Check-out" : "Check-out";
    if (meal === "departure_transfer") return lang === "pt" ? "🚕 Saída para o aeroporto" : "🚕 Transfer to airport";
    if (meal === "airport_margin") return lang === "pt" ? "🛫 Margem no aeroporto" : "🛫 Airport buffer";
    if (meal === "flight") return lang === "pt" ? "✈️ Voo de partida" : "✈️ Departure flight";
    if (meal === "departure") return lang === "pt" ? "🧳 Partida" : "🧳 Departure";
    return lang === "pt" ? "Hotel (dormir)" : "Hotel (overnight)";
  }

  /**
   * Orçamento canónico: o total é literalmente a soma das categorias visíveis.
   * Uma sugestão de alojamento não selecionada nunca entra no total.
   */
  const routeBudget = useMemo(() => {
    const attractions = displayStops.reduce((sum, s) => sum + (s.priceLocal || 0), 0);
    const meals = breaks.reduce((sum, b) => {
      if (b.meal === "hotel" || isLogisticsMeal(b.meal)) return sum;
      const r = restaurants[mealBreakKey(b)];
      return sum + (r && r !== "loading" ? r.priceLocal || 0 : 0);
    }, 0);
    // Só o hotel ESCOLHIDO entra no orçamento, e apenas com preço real.
    const nightly = hotelNightlyPrice(hotelSelection);
    const accommodationSelected = nightly > 0 ? nightly * Math.max(1, numDays - 1) : 0;
    const transportEUR =
      mode === "car" ? displayStops.length * 4 : mode === "transit" ? numDays * 7 : 0;
    const transport = transportEUR
      ? (convertAmount(transportEUR, "EUR", locale.currency) ?? transportEUR)
      : 0;
    return buildBudget({
      attractions,
      meals,
      transport,
      accommodationSelected,
      hotelSelected: hotelIsSelected,
    });
  }, [displayStops, breaks, restaurants, hotels, mode, numDays, locale.currency, hotelSelection, hotelIsSelected]);

  /**
   * Rascunho privado da rota. Enquanto está a ser recalculado, a página
   * continua a mostrar a última rota PUBLICADA — nunca uma mistura.
   */
  const routeRevisionRef = useRef({ signature: "", revision: 0 });
  /**
   * Índices APENAS para ações explícitas do utilizador (arrastar, remover,
   * editar). Nunca são fonte de dados visuais: os cartões vêm de
   * `publishedRoute.entryDataById`.
   */
  const stopIdxByEntryIdRef = useRef(new Map<string, number>());
  const breakByEntryIdRef = useRef(new Map<string, Extract<TimedEntry, { type: "break" }>>());
  const routeDraft = useMemo<RouteDraft | null>(() => {
    if (!schedule.length || !displayStops.length) return null;

    // Identidades lógicas estáveis desta geração: a mesma visita mantém o ID
    // mesmo que mude de dia, de hora ou de posição depois de um recálculo.
    const ids = logicalIdsRef.current;
    ids.beginPass();

    const entries: SnapshotEntryInput[] = [];
    const stopEntryIdByIdx = new Map<number, string>();
    const breakEntryIdByKey = new Map<string, string>();
    // Identidade → dado de trabalho. A agenda resolve cartões por
    // `scheduleEntryId`; nunca por índice de array.
    const stopIdxByEntryId = new Map<string, number>();
    const breakByEntryId = new Map<string, Extract<TimedEntry, { type: "break" }>>();
    for (const e of schedule) {
      if (e.type === "stop") {
        const s = displayStops[e.idx];
        if (!s) continue;
        const note = stopNotes[s.name];
        const logicalId = ids.idFor("stop", stopLogicalKey({ placeId: s.placeId, name: s.name }));
        stopEntryIdByIdx.set(e.idx, logicalId);
        stopIdxByEntryId.set(logicalId, e.idx);
        entries.push({
          kind: "stop",
          logicalId,
          ...(s.required ? { required: true } : {}),
          day: Math.max(1, Math.trunc(s.day ?? 1)),
          name: s.name,
          startAt: e.arriveAt,
          endAt: e.leaveAt,
          ...(s.blurb ? { blurb: s.blurb } : {}),
          ...(note ? { note } : {}),
          ...(typeof s.lat === "number" ? { lat: s.lat } : {}),
          ...(typeof s.lng === "number" ? { lng: s.lng } : {}),
          ...(s.placeId ? { placeId: s.placeId } : {}),
          mapsUrl: mapsUrlFor(s),
          // Cartão publicado: cópia imutável do estado visual no momento da
          // publicação. A UI nunca volta a `displayStops[idx]` para desenhar.
          card: { ...s, minutes: s.minutes ?? 0 },
        });
        continue;
      }
      if (e.type !== "break") continue;
      const key = mealBreakKey(e);
      const raw = e.meal === "hotel" ? hotels[key] : isLogisticsMeal(e.meal) ? undefined : restaurants[key];
      const v = raw && raw !== "loading" ? raw : undefined;
      const isLogistics = isLogisticsMeal(e.meal);
      const logicalId = isLogistics
        ? ids.idFor("logistics", logisticsLogicalKey(e.meal))
        : ids.idFor("meal", mealLogicalKey(e.meal));
      breakEntryIdByKey.set(key, logicalId);
      breakByEntryId.set(logicalId, e);
      entries.push({
        kind: "break",
        logicalId,
        day: Math.max(1, Math.trunc(e.day ?? displayStops[e.afterStopIdx]?.day ?? 1)),
        meal: e.meal,
        label: breakLabel(e.meal),
        startAt: e.startAt,
        endAt: e.endAt,
        // Bloco publicado: chave de refeição e dia fixados na publicação.
        card: { meal: e.meal, mealKey: key, day: e.day ?? null, afterStopIdx: e.afterStopIdx },
        ...(v
          ? {
              venue: {
                name: v.name,
                ...(v.blurb ? { blurb: v.blurb } : {}),
                ...(v.priceLocal ? { priceLabel: formatPrice(v.priceLocal, locale.currency, lang) } : {}),
                ...(typeof v.lat === "number" ? { lat: v.lat } : {}),
                ...(typeof v.lng === "number" ? { lng: v.lng } : {}),
                ...(v.placeId ? { placeId: v.placeId } : {}),
                ...(v.website ? { website: v.website } : {}),
                ...(v.phone ? { phone: v.phone } : {}),
                mapsUrl:
                  v.googleMapsUri ||
                  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${v.name} ${locale.city}`)}`,
              },
            }
          : {}),
      });
    }
    if (!entries.length) return null;
    stopIdxByEntryIdRef.current = stopIdxByEntryId;
    breakByEntryIdRef.current = breakByEntryId;
    // Pernas ligadas por IDs lógicos (nunca por índice) e com o modo pedido /
    // resolvido explícitos — sem fallback silencioso entre modos.
    const stopOrder = [...stopEntryIdByIdx.entries()].sort((a, b) => a[0] - b[0]).map(([, id]) => id);
    const requestedMode = mode as TravelModeId;
    const expectedLegs = expectedDayRouteLegs(displayStops);
    const completeRouteLegs = expectedLegs === routeLegs.length;
    const travelLegs = completeRouteLegs ? routeLegs.map((l, i) => {
      const distanceMeters = parseDistanceMeters(l.distance);
      const durationMinutes = l.minutes || 0;
      const providerMode = (l as { mode?: string }).mode ?? null;
      const { resolvedMode, status } = resolveLegMode(
        requestedMode,
        providerMode,
        distanceMeters > 0 && durationMinutes > 0,
      );
      const fromEntryId = stopOrder[l.fromIndex ?? i];
      const toEntryId = stopOrder[l.toIndex ?? (i + 1)];
      return {
        legId: fromEntryId && toEntryId ? `leg-${fromEntryId}__${toEntryId}` : `leg-${i}`,
        ...(fromEntryId ? { fromEntryId } : {}),
        ...(toEntryId ? { toEntryId } : {}),
        requestedMode,
        resolvedMode,
        status,
        distanceMeters,
        durationMinutes,
      };
    }) : [];
    const signature = JSON.stringify({
      city: locale.city,
      e: entries.map((x) => [
        x.logicalId,
        x.day,
        x.startAt.getTime(),
        x.endAt.getTime(),
        x.kind === "stop" ? x.name : `${x.label}:${x.venue?.placeId ?? x.venue?.name ?? ""}`,
      ]),
      l: travelLegs,
      b: routeBudget,
      h: hotelSelectionSignature(hotelSelection),
    });
    if (signature !== routeRevisionRef.current.signature) {
      routeRevisionRef.current = { signature, revision: routeRevisionRef.current.revision + 1 };
    }
    return {
      generationId: routeIdentity.generationId,
      modeRevision: routeIdentity.modeRevision,
      routeRevision: routeRevisionRef.current.revision,
      city: locale.city,
      entries,
      travelLegs,
      budget: routeBudget,
      // Estrutura de dias: vem das janelas por dia, não das paragens.
      dayWindows: [...scheduleContext.dayWindows],
      hotelSignature: hotelSelectionSignature(hotelSelection),
    } satisfies RouteDraft;
  }, [schedule, displayStops, restaurants, hotels, routeLegs, routeBudget, locale.city, locale.currency, lang, stopNotes, mode, routeIdentity, hotelSelection, scheduleContext.dayWindows]);


  /**
   * Publicação ATÓMICA. Agenda, cartões, mapa, navegação, resumo, orçamento,
   * PDF e ICS leem exclusivamente `routeSnapshot` — a última revisão válida.
   * Um rascunho incoerente ou uma resposta antiga nunca substituem a rota
   * visível.
   */
  // (estado declarado no topo do componente: `publishedRouteRef` / `publishedRoute`)
  /** Última revisão de rascunho já resolvida (publicada ou idêntica à publicada). */
  const [settledRevision, setSettledRevision] = useState(-1);

  useEffect(() => {
    if (!routeDraft) {
      publishedRouteRef.current = null;
      setPublishedRoute(null);
      setSettledRevision(-1);
      return;
    }
    const result = publishRouteDraft(publishedRouteRef.current, routeDraft, {
      generationId: routeDraft.generationId,
      modeRevision: routeDraft.modeRevision,
    });
    // `unchanged` = mesma rota visível: publicar outra vez criava um ciclo
    // infinito de render (Maximum update depth exceeded). Ainda assim o
    // rascunho fica RESOLVIDO — senão "A atualizar deslocações…" nunca fecha.
    if (result.status === "unchanged" || result.status === "published") {
      setSettledRevision((cur) => Math.max(cur, routeDraft.routeRevision));
    }
    if (result.status !== "published") return; // mantém a rota anterior intacta
    publishedRouteRef.current = result.snapshot;
    setPublishedRoute(result.snapshot);
  }, [routeDraft]);

  
  /**
   * Snapshot que a agenda mostra: a revisão PUBLICADA. Só no primeiro frame,
   * antes do efeito de publicação, se usa uma pré-visualização do rascunho.
   */
  const agendaSnapshot = publishedRoute;

  /** Uma geração em curso sem rota publicada mostra skeleton, nunca rascunho. */
  const agendaPending = routeDraft != null && publishedRoute == null;

  type AgendaBreak = Extract<TimedEntry, { type: "break" }> & { mealKey: string };
  type AgendaItem = {
    key: string;
    dayIndex: number;
    /** Cabeçalho do dia: emitido UMA vez por dia canónico. */
    showDayHeader: boolean;
    /** Cartão IMUTÁVEL publicado (nunca `displayStops[idx]`). */
    stop: DisplayStop | null;
    /** Índice apenas para handlers de edição — nunca para dados visuais. */
    index: number;
    times: { arriveAt: Date; leaveAt: Date } | null;
    breaksBefore: AgendaBreak[];
    breaksAfter: AgendaBreak[];
  };

  /**
   * ORDEM E CONTEÚDO CANÓNICOS DA AGENDA: `publishedRoute.days` +
   * `publishedRoute.entryDataById`. Dias, horas, fotos, refeições e hotel vêm
   * todos da mesma revisão publicada; nada é inferido de arrays de trabalho.
   */
  const agendaItems = useMemo<AgendaItem[]>(() => {
    const snap = agendaSnapshot;
    if (!snap) return [];
    const items: AgendaItem[] = [];
    for (const d of snap.days) {
      const ordered = [...d.entries].sort((a, b) => a.order - b.order);
      let pending: AgendaBreak[] = [];
      let headerPending = true;
      let lastStopItem: AgendaItem | null = null;
      for (const e of ordered) {
        const card = snap.entryDataById[e.scheduleEntryId];
        if (e.kind === "break") {
          const c = (card ?? {}) as Partial<AgendaBreak>;
          pending.push({
            type: "break",
            meal: e.meal as AgendaBreak["meal"],
            mealKey: c.mealKey ?? `${e.meal}:${e.day}`,
            day: e.day,
            afterStopIdx: c.afterStopIdx ?? -1,
            startAt: e.startAt,
            endAt: e.endAt,
          });
          continue;
        }
        if (!card) continue;
        const item: AgendaItem = {
          key: e.scheduleEntryId,
          dayIndex: d.dayIndex,
          showDayHeader: headerPending,
          stop: card as DisplayStop,
          index: stopIdxByEntryIdRef.current.get(e.logicalId ?? e.scheduleEntryId) ?? -1,
          times: { arriveAt: e.startAt, leaveAt: e.endAt },
          breaksBefore: pending,
          breaksAfter: [],
        };
        headerPending = false;
        pending = [];
        items.push(item);
        lastStopItem = item;
      }
      if (pending.length && lastStopItem) lastStopItem.breaksAfter = pending;
      if (!lastStopItem) {
        items.push({
          key: `day-${d.dayIndex}-empty`,
          dayIndex: d.dayIndex,
          showDayHeader: true,
          stop: null,
          index: -1,
          times: null,
          breaksBefore: pending,
          breaksAfter: [],
        });
      }
    }
    return items;
  }, [agendaSnapshot]);

  /** Uma revisão está a ser recalculada: mostramos "A atualizar deslocações…". */
  const routeUpdating = routeDraft != null && settledRevision < routeDraft.routeRevision;

  /**
   * CARTÃO ÚNICO DE PAUSA. Pequeno-almoço, almoço, jantar e dormida usam
   * EXATAMENTE o mesmo cartão, esteja a pausa antes ou depois de uma visita —
   * nunca há uma versão "compacta" sem sugestões de restaurante.
   */
  function renderBreakCard(bk: AgendaBreak, margin: string) {
    const isHotel = bk.meal === "hotel";
    const isBreakfast = bk.meal === "breakfast";
    const isLunch = bk.meal === "lunch";
    const isDinner = bk.meal === "dinner";
    if (isLogisticsMeal(bk.meal)) {
      const isAirport =
        bk.meal === "airport" ||
        bk.meal === "departure_transfer" ||
        bk.meal === "airport_margin" ||
        bk.meal === "flight";
      const LogisticsIcon = isAirport ? Plane : Luggage;
      return (
        <div key={`brk-${bk.meal}-${bk.startAt.getTime()}`} className={`${margin} overflow-hidden rounded-2xl border border-dashed border-brand/40 bg-brand/5 p-4`}>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand text-brand-foreground">
              <LogisticsIcon className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-brand">
                {bk.meal === "airport"
                  ? (lang === "pt" ? "Desembarque e transferência" : lang === "es" ? "Desembarque y traslado" : lang === "fr" ? "Atterrissage et transfert" : lang === "de" ? "Landung & Transfer" : lang === "it" ? "Atterraggio e trasferimento" : "Landing & transfer")
                  : bk.meal === "arrival"
                  ? (lang === "pt" ? "Chegada & check-in" : lang === "es" ? "Llegada y check-in" : lang === "fr" ? "Arrivée & check-in" : lang === "de" ? "Ankunft & Check-in" : lang === "it" ? "Arrivo e check-in" : "Arrival & check-in")
                  : breakLabel(bk.meal)}
              </div>
              <div className="text-sm font-semibold text-foreground">
                {formatTime(bk.startAt, lang)} – {formatTime(bk.endAt, lang)}
              </div>
            </div>
          </div>
        </div>
      );
    }
    // Pedido com exclusão ("sem restaurantes"): a pausa existe, mas nunca com
    // sugestão gastronómica.
    if (!isHotel && restaurantsExcluded) {
      return (
        <div key={`brk-${bk.meal}-${bk.startAt.getTime()}`} className={`${margin} overflow-hidden rounded-2xl border border-dashed border-border bg-muted/30 p-4`}>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <Clock className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {lang === "pt" ? "Pausa livre" : lang === "es" ? "Pausa libre" : lang === "fr" ? "Pause libre" : lang === "de" ? "Freie Pause" : "Free break"}
              </div>
              <div className="text-sm font-semibold text-foreground">
                {formatTime(bk.startAt, lang)} – {formatTime(bk.endAt, lang)}
              </div>
            </div>
          </div>
        </div>
      );
    }
    const restaurant = !isHotel ? restaurants[mealBreakKey(bk)] : undefined;
    const hotel = isHotel ? hotels[mealBreakKey(bk)] : undefined;
    // A etiqueta segue SEMPRE o tipo real do bloco.
    const label = isHotel
      ? (lang === "pt" ? "Dormir" : "Overnight stay")
      : isBreakfast
      ? (breakfastMealType === "brunch"
          ? "Brunch"
          : lang === "pt" ? "Pequeno-almoço" : lang === "es" ? "Desayuno" : lang === "fr" ? "Petit-déjeuner" : lang === "de" ? "Frühstück" : "Breakfast")
      : isLunch
      ? (lang === "pt" ? "Almoço" : lang === "es" ? "Almuerzo" : lang === "fr" ? "Déjeuner" : lang === "de" ? "Mittagessen" : "Lunch")
      : isDinner
      ? (lang === "pt" ? "Jantar" : lang === "es" ? "Cena" : lang === "fr" ? "Dîner" : lang === "de" ? "Abendessen" : "Dinner")
      : breakLabel(bk.meal);
    const BreakIcon = isHotel ? BedDouble : isBreakfast ? Coffee : UtensilsCrossed;
    const loadingMsg = isHotel
      ? (lang === "pt" ? "A procurar hotel…" : "Finding a hotel…")
      : (lang === "pt" ? "A procurar um sítio local…" : "Finding a local spot…");
    const emptyMsg = isHotel
      ? (lang === "pt" ? "Sem sugestão de hotel — tenta gerar novamente." : "No hotel suggestion right now — try refreshing.")
      : (lang === "pt" ? "Sem sugestão de restaurante — tenta gerar novamente." : "No restaurant suggestion right now — try refreshing.");
    // Com hotel escolhido, o bloco de dormida é SEMPRE o hotel selecionado.
    const data: { name: string; subtitle: string; blurb: string; priceLocal: number } | null =
      isHotel && hotelSelection.status === "selected"
        ? {
            name: hotelSelection.name,
            subtitle: lang === "pt" ? "Hotel selecionado" : "Selected hotel",
            blurb: hotelSelection.address ?? "",
            priceLocal: hotelSelection.nightlyPrice ?? 0,
          }
        : isHotel
        ? (hotel && hotel !== "loading"
            ? { name: hotel.name, subtitle: hotel.category, blurb: hotel.blurb, priceLocal: hotel.priceLocal }
            : null)
        : (restaurant && restaurant !== "loading"
            ? { name: restaurant.name, subtitle: restaurant.cuisine, blurb: restaurant.blurb, priceLocal: restaurant.priceLocal }
            : null);
    const status: "loading" | "empty" | "ready" = isHotel
      ? hotelSelection.status === "selected" ? "ready" : hotel === "loading" ? "loading" : !hotel ? "empty" : "ready"
      : restaurant === "loading" ? "loading" : !restaurant ? "empty" : "ready";
    return (
      <div key={`brk-${bk.meal}-${bk.startAt.getTime()}`} className={`${margin} overflow-hidden rounded-2xl border border-dashed border-brand/40 bg-brand/5 p-4`}>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand text-brand-foreground">
            <BreakIcon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-brand">
              {label}
              {isHotel && bk.day != null && ` · ${lang === "pt" ? "Noite" : "Night"} ${bk.day}`}
            </div>
            <div className="text-sm font-semibold text-foreground">
              {isHotel
                ? (lang === "pt" ? "Sugestão de alojamento — sem hora marcada" : "Accommodation suggestion — no scheduled time")
                : `${formatTime(bk.startAt, lang)} – ${formatTime(bk.endAt, lang)}`}
            </div>
          </div>
        </div>

        {planMode === "ai" && (status === "loading" ? (
          <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" /> {loadingMsg}
          </div>
        ) : status === "empty" || !data ? (
          <div className="mt-3 flex flex-col gap-2">
            <div className="text-xs text-muted-foreground">
              {!isHotel && gastronomy.required ? gastronomyRecoveryCopy(lang).message : emptyMsg}
            </div>
            {!isHotel && gastronomy.required && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    const key = mealBreakKey(bk);
                    setMealRadiusBoost((cur) => ({ ...cur, [key]: Math.min(4, (cur[key] ?? 1) * 1.8) }));
                    setRestaurants((cur) => {
                      const next = { ...cur };
                      delete next[key];
                      return next;
                    });
                  }}
                  className="inline-flex w-fit items-center gap-1 rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-brand-foreground"
                >
                  <Search className="h-3 w-3" /> {gastronomyRecoveryCopy(lang).widen}
                </button>
                <div className="text-[11px] text-muted-foreground">{gastronomyRecoveryCopy(lang).free}</div>
              </>
            )}
            {!isHotel && (
              <button
                type="button"
                onClick={() => openRestoModal(bk)}
                className="inline-flex w-fit items-center gap-1 text-xs font-medium text-brand hover:underline"
              >
                <UtensilsCrossed className="h-3 w-3" /> {gastronomy.required ? gastronomyRecoveryCopy(lang).pickArea : (lang === "pt" ? "Procurar restaurantes perto" : "Find restaurants nearby")}
              </button>
            )}
          </div>
        ) : (
          <div className="mt-3 rounded-xl border border-border bg-background p-3">
            <button
              type="button"
              onClick={() => { if (!isHotel) void openRestoModal(bk); }}
              className={`w-full text-left ${!isHotel ? "rounded-lg transition hover:bg-muted/40 focus:outline-none focus:ring-2 focus:ring-brand/40" : "cursor-default"}`}
              aria-label={!isHotel ? (lang === "pt" ? `Ver mais restaurantes perto de ${data.name}` : `See more restaurants near ${data.name}`) : undefined}
            >
              <div className="flex items-start justify-between gap-2 p-0.5">
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold">
                    {data.name}
                    {!isHotel && restaurant && restaurant !== "loading" && restaurant.openConfirmed && (
                      <span className="ml-2 rounded-full bg-emerald-500/10 px-2 py-0.5 align-middle text-[10px] font-semibold text-emerald-700 dark:text-emerald-400">
                        {lang === "pt" ? "Aberto" : "Open"}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{data.subtitle}</div>
                </div>
                <span className="shrink-0 rounded-full bg-brand/10 px-2 py-0.5 text-[11px] font-semibold text-brand">
                  {formatPriceDual(data.priceLocal, locale.currency, lang)}
                  {isHotel && (lang === "pt" ? " /noite" : " /night")}
                </span>
              </div>
              <p className="mt-1.5 px-0.5 text-xs text-foreground/80">{data.blurb}</p>
              {!isHotel && (
                <div className="mt-1.5 px-0.5 text-[11px] font-medium text-brand">
                  {lang === "pt" ? "Toca aqui para veres mais opções na zona" : "Tap here for more options nearby"}
                </div>
              )}
            </button>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <a
                href={!isHotel && restaurant && restaurant !== "loading" && restaurant.googleMapsUri
                  ? restaurant.googleMapsUri
                  : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${data.name} ${locale.city}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline"
              >
                <Navigation className="h-3 w-3" /> {lang === "pt" ? "Abrir no Maps" : "Open in Maps"}
              </a>
              {!isHotel && (
                <button
                  type="button"
                  onClick={() => openRestoModal(bk)}
                  className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline"
                >
                  <UtensilsCrossed className="h-3 w-3" /> {lang === "pt" ? "Ver mais restaurantes perto" : "More restaurants nearby"}
                </button>
              )}
            </div>
            {isHotel && hotel && hotel !== "loading" && (hotelAlts[mealBreakKey(bk)]?.length ?? 0) > 0 && (
              <div className="mt-3 border-t border-border pt-3">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {lang === "pt" ? "Alternativas na zona" : "Nearby alternatives"}
                </div>
                <div className="flex flex-col gap-1.5">
                  {(hotelAlts[mealBreakKey(bk)] ?? []).slice(0, 3).map((alt) => (
                    <button
                      key={alt.placeId ?? alt.name}
                      type="button"
                      onClick={() => {
                        const key = mealBreakKey(bk);
                        const currentMain = hotel;
                        setHotels((cur) => ({ ...cur, [key]: alt }));
                        setHotelAlts((cur) => {
                          const rest = (cur[key] ?? []).filter((h) => (h.placeId ?? h.name) !== (alt.placeId ?? alt.name));
                          return { ...cur, [key]: [currentMain, ...rest].slice(0, 3) };
                        });
                      }}
                      className="flex items-center justify-between gap-2 rounded-lg border border-border bg-background/60 px-2.5 py-1.5 text-left transition hover:bg-muted/50"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-xs font-medium">{alt.name}</div>
                        <div className="truncate text-[10px] text-muted-foreground">{alt.category}</div>
                      </div>
                      <span className="shrink-0 rounded-full bg-brand/10 px-1.5 py-0.5 text-[10px] font-semibold text-brand">
                        {formatPriceDual(alt.priceLocal, locale.currency, lang)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {isHotel && hotel === null && bk.day != null && (
              <div className="mt-3 border-t border-border pt-3">
                <button
                  type="button"
                  onClick={() => {
                    const key = mealBreakKey(bk);
                    setHotels((cur) => ({ ...cur, [key]: "loading" }));
                    fetchHotel({
                      data: {
                        city: locale.city,
                        country: locale.country,
                        language: lang,
                        currency: locale.currency,
                        lat: locale.lat,
                        lng: locale.lng,
                        radiusMeters: 6000,
                        anonId: getAnonId(),
                      },
                    })
                      .then((r) => {
                        const list = [r.hotel, ...(r.alternatives ?? [])].filter(Boolean) as AIHotel[];
                        setHotels((cur) => ({ ...cur, [key]: list[0] ?? null }));
                        setHotelAlts((cur) => ({ ...cur, [key]: list.slice(1, 4) }));
                      })
                      .catch(() => setHotels((cur) => ({ ...cur, [key]: null })));
                  }}
                  className="inline-flex w-fit items-center gap-1 rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-brand-foreground"
                >
                  <BedDouble className="h-3 w-3" /> {lang === "pt" ? `Encontrar hotel para a noite ${bk.day}` : `Find a hotel for night ${bk.day}`}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }



  function mealAnchorForBreak(b: MealBreak) {
    const prev =
      b.afterStopIdx < 0
        ? displayStops[0]
        : displayStops[b.afterStopIdx] ?? displayStops[b.afterStopIdx - 1] ?? displayStops[0];
    const next = displayStops[b.afterStopIdx + 1];
    const sameDayNext = prev && next && (prev.day ?? 1) === (next.day ?? 1);
    const hasNextCoords =
      sameDayNext &&
      typeof next.lat === "number" &&
      typeof next.lng === "number";
    return {
      // Search from the place where the traveler actually is when the meal
      // break starts. The next stop is a secondary scoring point, not a
      // straight-line midpoint that could land in a river or inaccessible area.
      lat: prev?.lat ?? (hasNextCoords ? (next.lat as number) : locale.lat),
      lng: prev?.lng ?? (hasNextCoords ? (next.lng as number) : locale.lng),
      nextLat: hasNextCoords ? (next.lat as number) : undefined,
      nextLng: hasNextCoords ? (next.lng as number) : undefined,
      nearStop: hasNextCoords ? `${prev?.name ?? ""} → ${next.name}` : prev?.name,
      radiusMeters: Math.round((mode === "walk" ? 650 : 1600) * (mealRadiusBoost[mealBreakKey(b)] ?? 1)),
    };
  }

  const mealFetchSignature = useMemo(
    () => breaks.map((b) => {
      const a = mealAnchorForBreak(b);
      return `${mealBreakKey(b)}:${Math.round((a.lat ?? 0) * 10000)}:${Math.round((a.lng ?? 0) * 10000)}:${Math.round((a.nextLat ?? 0) * 10000)}:${Math.round((a.nextLng ?? 0) * 10000)}:${a.radiusMeters}`;
    }).join("|"),
    [breaks, displayStops, mode, locale.lat, locale.lng, mealRadiusBoost],
  );


  // Fetch a recommended restaurant for each placed meal break
  const fetchRestaurant = demoFn(
    useServerFn(recommendRestaurant),
    () => ({ restaurant: null, diag: null }) as never,
  );
  const fetchHotel = demoFn(
    useServerFn(recommendHotel),
    () => ({ hotel: null, alternatives: [] }) as never,
  );

  // "Onde ficar": só quando o utilizador pediu alojamento sem nomear um hotel.
  useEffect(() => {
    if (!stayRequested || !aiStops?.length || stayHotels !== null) return;
    // Só a sessão atual pode escrever: uma resposta tardia do destino
    // anterior nunca pode aparecer na rota nova.
    const staySession = itinerarySessionRef.current;
    setStayHotels("loading");
    fetchHotel({
      data: {
        city: locale.city,
        country: locale.country,
        language: lang,
        currency: locale.currency,
        lat: locale.lat,
        lng: locale.lng,
        radiusMeters: 6000,
        anonId: getAnonId(),
      },
    })
      .then((r) => {
        if (!isCurrentSession(staySession)) return;
        const list = [r.hotel, ...(r.alternatives ?? [])].filter(Boolean) as AIHotel[];
        setStayHotels(list.length ? list.slice(0, 3) : "none");
      })
      .catch(() => { if (isCurrentSession(staySession)) setStayHotels("none"); });
  }, [stayRequested, aiStops, stayHotels, locale, lang, fetchHotel]);
  useEffect(() => {
    if (planMode === "manual") return;
    const mealNeeded = breaks.filter((b) => {
      // Chegada/aeroporto/check-in não são refeições: nunca há venue a buscar.
      if (isLogisticsMeal(b.meal)) return false;
      if (b.meal === "hotel") return false;
      if (restaurantsExcluded && b.meal !== "breakfast") return false;
      const key = mealBreakKey(b);
      const current = restaurants[key];
      if (current === undefined) return true;
      if (!current || current === "loading" || typeof current.lat !== "number" || typeof current.lng !== "number") return false;
      const anchor = mealAnchorForBreak(b);
      return distanceMeters({ lat: anchor.lat, lng: anchor.lng }, { lat: current.lat, lng: current.lng }) > anchor.radiusMeters + 250;
    });
    const hotelNeeded = breaks.filter((b) => b.meal === "hotel" && hotels[mealBreakKey(b)] === undefined);
    if (!mealNeeded.length && !hotelNeeded.length) return;
    const budgetNum = isPremium && maxMealBudget ? Number.parseFloat(maxMealBudget) : undefined;
    const validBudget = budgetNum && Number.isFinite(budgetNum) && budgetNum > 0 ? budgetNum : undefined;
    // Sessão do pedido em curso: respostas de um destino anterior são
    // descartadas em vez de escritas por cima do itinerário novo.
    const mealSession = itinerarySessionRef.current;
    const requestWithTimeout = <T,>(promise: Promise<T>, ms = 12_000): Promise<T> =>
      new Promise<T>((resolve, reject) => {
        const timer = window.setTimeout(() => reject(new Error("request_timeout")), ms);
        promise.then(resolve, reject).finally(() => window.clearTimeout(timer));
      });
    // Nenhuma atração pode virar restaurante: a sugestão tem de passar a
    // validação alimentar e não pode repetir o mesmo local no mesmo dia.
    const usedMealPlaceIdsByDay = new Map<string, Set<string>>();
    const dayKeyOf = (b: MealBreak) => {
      const at = b.startAt as Date | undefined;
      return at ? `${at.getFullYear()}-${at.getMonth()}-${at.getDate()}` : "d1";
    };
    const writeRestaurant = (k: string, value: AIRestaurant | null | "loading", b?: MealBreak) => {
      if (!isCurrentSession(mealSession)) return;
      let next = value;
      if (next && next !== "loading") {
        const stopMatch = displayStops.some(
          (s) => (s.placeId && next !== "loading" && s.placeId === (next as AIRestaurant).placeId),
        );
        const candidate = next as AIRestaurant;
        if (
          stopMatch ||
          !isFoodCandidate({ name: candidate.name, cuisine: candidate.cuisine, placeId: candidate.placeId })
        ) {
          next = null;
        } else if (b) {
          const dk = dayKeyOf(b);
          const used = usedMealPlaceIdsByDay.get(dk) ?? new Set<string>();
          const pid = (next as AIRestaurant).placeId;
          if (pid && used.has(pid)) next = null;
          else if (pid) {
            used.add(pid);
            usedMealPlaceIdsByDay.set(dk, used);
          }
        }
      }
      setRestaurants((cur) => ({ ...cur, [k]: next }));
    };

    const writeHotel = (k: string, value: AIHotel | null | "loading") => {
      if (!isCurrentSession(mealSession)) return;
      setHotels((cur) => ({ ...cur, [k]: value }));
    };
    mealNeeded.forEach((b) => {
      const key = mealBreakKey(b);
      const token = Symbol(key);
      mealRequestTokensRef.current.set(`restaurant:${key}`, token);
      writeRestaurant(key, "loading");
      const anchor = mealAnchorForBreak(b);
      // Seleção automática de recurso: usa a mesma pesquisa real do Google
      // Places do seletor manual e escolhe sozinha o melhor resultado perto da
      // rota. Nunca exige ação do utilizador.
      const runFallback = async () => {
        try {
          // Cascata determinística e económica ao longo da rota, sempre
          // executada (não só quando o pedido menciona gastronomia).
          const radiiToTry = gastronomySearchCascade(gastronomy, anchor.radiusMeters)
            .map((step) => step.radiusMeters)
            .filter((value, index, all) => all.indexOf(value) === index);
          let fallback = { results: [] as NearbyRestaurant[] };
          let searchRadius = anchor.radiusMeters;
          const diag: Array<{ radius: number; results: number; budget: boolean }> = [];
          for (const radius of radiiToTry) {
            searchRadius = radius;
            fallback = await requestWithTimeout(fetchNearbyRestos({
              data: { lat: anchor.lat, lng: anchor.lng, radiusMeters: radius, maxBudget: validBudget, generationId: generationIdRef.current },
            }));
            diag.push({ radius, results: fallback.results.length, budget: Boolean(validBudget) });
            if (fallback.results.length) break;
          }
          // Última tentativa: ignora o teto de orçamento antes de desistir.
          if (!fallback.results.length && validBudget) {
            searchRadius = radiiToTry[radiiToTry.length - 1] ?? anchor.radiusMeters;
            fallback = await requestWithTimeout(fetchNearbyRestos({
              data: { lat: anchor.lat, lng: anchor.lng, radiusMeters: searchRadius, generationId: generationIdRef.current },
            }));
            diag.push({ radius: searchRadius, results: fallback.results.length, budget: false });
          }
          const nextPoint =
            typeof anchor.nextLat === "number" && typeof anchor.nextLng === "number"
              ? { lat: anchor.nextLat, lng: anchor.nextLng }
              : null;
          const directToNext = nextPoint
            ? distanceMeters({ lat: anchor.lat, lng: anchor.lng }, nextPoint)
            : 0;
          const ranked = fallback.results
            .map((place) => {
              const fromCurrent = distanceMeters(
                { lat: anchor.lat, lng: anchor.lng },
                { lat: place.lat, lng: place.lng },
              );
              const toNext = nextPoint
                ? distanceMeters({ lat: place.lat, lng: place.lng }, nextPoint)
                : fromCurrent;
              const detour = nextPoint ? Math.max(0, fromCurrent + toNext - directToNext) : fromCurrent;
              const score =
                (place.rating ?? 3.5) * 100 +
                Math.log10((place.userRatingCount ?? 0) + 1) * 20 -
                detour / 5 -
                fromCurrent / 25;
              return { place, fromCurrent, score };
            })
            .sort((a, b) => b.score - a.score || a.fromCurrent - b.fromCurrent);
          // Prefere o que está mesmo em cima da rota; se nada ficar dentro do
          // raio, aceita o melhor resultado real em vez de não mostrar nada.
          const candidate =
            ranked.find(({ fromCurrent }) => fromCurrent <= searchRadius + 50)?.place ??
            ranked[0]?.place;
          if (import.meta.env.DEV) {
            console.info("[meal-search]", key, {
              center: { lat: anchor.lat, lng: anchor.lng },
              passes: diag,
              chosen: candidate?.name ?? null,
            });
          }
          if (!candidate) {
            writeRestaurant(key, null);
            return;
          }
          const priceByLevel: Record<number, number> = { 1: 12, 2: 22, 3: 45, 4: 80 };
          const estimatedPrice = candidate.priceLevel ? (priceByLevel[candidate.priceLevel] ?? 20) : 20;
          const restaurant: AIRestaurant = {
            name: candidate.name,
            cuisine: lang === "pt" ? "Restaurante" : "Restaurant",
            blurb: lang === "pt"
              ? `${candidate.name} é uma opção próxima desta parte do roteiro.`
              : `${candidate.name} is a nearby option for this part of the route.`,
            priceLocal: validBudget ? Math.min(estimatedPrice, validBudget) : estimatedPrice,
            placeId: candidate.placeId,
            googleMapsUri: candidate.googleMapsUri ?? undefined,
            lat: candidate.lat,
            lng: candidate.lng,
          };
          writeRestaurant(key, restaurant, b);
        } catch {
          writeRestaurant(key, null);
        }
      };

      requestWithTimeout(fetchRestaurant({
        data: {
          city: locale.city,
          country: locale.country,
          lat: anchor.lat,
          lng: anchor.lng,
          radiusMeters: anchor.radiusMeters,
          nextLat: anchor.nextLat,
          nextLng: anchor.nextLng,
          language: lang,
          currency: locale.currency,
          mealType: b.meal === "breakfast" ? breakfastMealType : (b.meal as "lunch" | "dinner"),
          // Janela + dia reais do bloco: o servidor só afirma "aberto" com
          // horários confirmados para ESTA janela (nunca rejeita por falta deles).
          ...(() => {
            if (b.meal === "breakfast" && breakfastBlock) return { mealWindow: breakfastBlock };
            // Janela padrão de almoço/jantar em minutos do dia ("HH:MM").
            const w = b.meal === "dinner" ? { start: "19:00", end: "21:30" } : { start: "12:00", end: "14:30" };
            return b.startAt ? { mealWindow: w, mealWeekday: (b.startAt as Date).getDay() } : {};
          })(),
          nearStop: anchor.nearStop,
          maxBudget: validBudget,
          anonId: getAnonId(),
        },
      }))
        .then(async (r) => {
          if (mealRequestTokensRef.current.get(`restaurant:${key}`) !== token) return;
          if (r.restaurant) {
            writeRestaurant(key, r.restaurant as AIRestaurant, b);
            return;
          }
          await runFallback();
        })
        // Falha técnica não é "sem restaurantes": tenta a pesquisa direta.
        .catch(() => runFallback())
        .finally(() => {
          if (mealRequestTokensRef.current.get(`restaurant:${key}`) !== token) return;
          setRestaurants((cur) => cur[key] === "loading" ? { ...cur, [key]: null } : cur);
        });

    });
    hotelNeeded.forEach((b) => {
      const key = mealBreakKey(b);
      const token = Symbol(key);
      mealRequestTokensRef.current.set(`hotel:${key}`, token);
      writeHotel(key, "loading");
      const lastStop = displayStops[b.afterStopIdx];
      // First stop of the NEXT day (the hotel break sits between day D and day D+1)
      const nextDayStop = displayStops
        .slice(b.afterStopIdx + 1)
        .find((s) => (s.day ?? 1) !== (lastStop?.day ?? 1));
      // Midpoint between end of day D and start of day D+1 — best neutral location for the hotel
      let midLat: number | undefined;
      let midLng: number | undefined;
      if (lastStop?.lat != null && lastStop?.lng != null) {
        if (nextDayStop?.lat != null && nextDayStop?.lng != null) {
          midLat = (lastStop.lat + nextDayStop.lat) / 2;
          midLng = (lastStop.lng + nextDayStop.lng) / 2;
        } else {
          midLat = lastStop.lat;
          midLng = lastStop.lng;
        }
      }
      requestWithTimeout(fetchHotel({
        data: {
          city: locale.city,
          country: locale.country,
          language: lang,
          currency: locale.currency,
          nearStop: lastStop?.name,
          lat: midLat,
          lng: midLng,
          radiusMeters: 3500,
          anonId: getAnonId(),
        },
      }))
        .then((r) => {
          if (mealRequestTokensRef.current.get(`hotel:${key}`) !== token) return;
          writeHotel(key, r.hotel ?? null);
          if (isCurrentSession(mealSession)) setHotelAlts((cur) => ({ ...cur, [key]: r.alternatives ?? [] }));
        })
        .catch(() => writeHotel(key, null))
        .finally(() => {
          if (mealRequestTokensRef.current.get(`hotel:${key}`) !== token) return;
          setHotels((cur) => cur[key] === "loading" ? { ...cur, [key]: null } : cur);
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breaks.length, mealFetchSignature, locale.city, lang, maxMealBudget, isPremium, restaurantsExcluded]);

  // Reset restaurants when city/language/budget/exclusions change
  useEffect(() => { setRestaurants({}); setHotels({}); setHotelAlts({}); setMealRadiusBoost({}); }, [locale.city, lang, maxMealBudget, restaurantsExcluded]);


  // Open nearby-restaurants modal anchored on the stop right before the meal break.
  async function openRestoModal(bk: MealBreak) {
    const anchor = mealAnchorForBreak(bk);
    const lat = anchor.lat ?? userPos?.lat;
    const lng = anchor.lng ?? userPos?.lng;
    if (typeof lat !== "number" || typeof lng !== "number") {
      toast.error(lang === "pt" ? "Sem coordenadas para procurar perto." : "No coordinates available.");
      return;
    }
    setRestoModalKey(mealBreakKey(bk));
    setRestoModalLoading(true);
    setRestoModalResults([]);
    try {
      const budgetNum = maxMealBudget ? Number.parseFloat(maxMealBudget) : undefined;
      const r = await fetchNearbyRestos({
        data: {
          lat,
          lng,
          radiusMeters: anchor.radiusMeters,
          maxBudget: budgetNum && Number.isFinite(budgetNum) && budgetNum > 0 ? budgetNum : undefined,
        },
      });
      setRestoModalResults(r.results ?? []);
    } catch (e) {
      console.error("findNearbyRestaurants failed", e);
      toast.error(lang === "pt" ? "Falha a procurar restaurantes." : "Failed to load restaurants.");
    } finally {
      setRestoModalLoading(false);
    }
  }



  // ---- Premium gate ----
  function requirePremium(action: () => void) {
    if (premiumLoading) {
      toast.message(lang === "pt" ? "A confirmar o Premium..." : "Checking Premium...");
      return;
    }
    if (isPremium) action();
    else setPremiumOpen(true);
  }

  // ---- Web Speech API audio guide ----
  function pickBestVoice(synth: SpeechSynthesis, langTag: string): SpeechSynthesisVoice | null {
    const voices = synth.getVoices();
    if (!voices.length) return null;
    const base = langTag.split("-")[0].toLowerCase();
    const matches = voices.filter((v) => v.lang?.toLowerCase().startsWith(base));
    if (!matches.length) return null;
    // Score: prefer exact lang tag, "natural/neural/online" voices, then Google/Microsoft/Apple premium voices.
    const score = (v: SpeechSynthesisVoice) => {
      let s = 0;
      const name = v.name.toLowerCase();
      if (v.lang.toLowerCase() === langTag.toLowerCase()) s += 10;
      if (/neural|natural|enhanced|premium|online|wavenet|studio/.test(name)) s += 8;
      if (/google/.test(name)) s += 4;
      if (/microsoft/.test(name)) s += 3;
      if (/siri|samantha|alex|karen|daniel|moira|kyoko/.test(name)) s += 3;
      if (!v.localService) s += 2; // cloud voices tend to be higher quality
      return s;
    };
    return matches.sort((a, b) => score(b) - score(a))[0] ?? matches[0];
  }

  function speakStop(key: string, text: string) {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) {
      toast.error(lang === "pt" ? "Este navegador não suporta guia áudio." : "This browser does not support audio guide.");
      return;
    }
    const synth = window.speechSynthesis;
    if (!text.trim()) {
      toast.error(lang === "pt" ? "Não há texto para ler." : "There is no text to read.");
      return;
    }
    if (speakingKey === key) {
      synth.cancel();
      setSpeakingKey(null);
      return;
    }
    synth.cancel();
    const langMap: Record<LangCode, string> = { en: "en-US", fr: "fr-FR", es: "es-ES", pt: "pt-PT", ja: "ja-JP", de: "de-DE", it: "it-IT" };
    const langTag = langMap[lang] ?? "en-US";
    const start = () => {
      if (speakingKey === key) return;
      const u = new SpeechSynthesisUtterance(text);
      u.lang = langTag;
      const voice = pickBestVoice(synth, langTag);
      if (voice) u.voice = voice;
      u.rate = 1;
      u.pitch = 1;
      u.onend = () => setSpeakingKey((cur) => (cur === key ? null : cur));
      u.onerror = () => setSpeakingKey((cur) => (cur === key ? null : cur));
      setSpeakingKey(key);
      synth.speak(u);
    };
    if (!synth.getVoices().length) {
      const handler = () => { synth.removeEventListener("voiceschanged", handler); start(); };
      synth.addEventListener("voiceschanged", handler);
      // Trigger voice loading in some browsers
      synth.getVoices();
      setTimeout(() => { synth.removeEventListener("voiceschanged", handler); start(); }, 700);
    } else {
      start();
    }
  }
  useEffect(() => {
    return () => { if (typeof window !== "undefined" && "speechSynthesis" in window) window.speechSynthesis.cancel(); };
  }, []);

  // Reuses enriched details already generated/cached for this stop — never triggers a new AI call.
  function audioScriptFor(stop: DisplayStop): string {
    const det = stopDetailsMap[stop.name];
    const details = det && det !== "loading" && det !== "error" ? det : null;
    return buildAudioGuideScript({ name: stop.name, blurb: stop.blurb, details, lang });
  }

  function handleListenStop(stop: DisplayStop, key: string) {
    requirePremium(() => speakStop(key, audioScriptFor(stop)));
  }

  function handleAudioGuideAll() {
    requirePremium(() => {
      const text = displayStops.map((s) => audioScriptFor(s)).join(" ");
      speakStop("__all__", text);
    });
  }

  async function loadStopDetails(stop: DisplayStop) {
    const key = stop.name;
    const existing = stopDetailsMap[key];
    if (existing && existing !== "error") return;
    setStopDetailsMap((m) => ({ ...m, [key]: "loading" }));
    try {
      const res = await fetchStopDetails({
        data: {
          name: stop.name,
          category: stop.category,
          city: locale.city,
          country: locale.country,
          language: lang,
          placeId: stop.placeId,
        },
      });
      if (res.details) setStopDetailsMap((m) => ({ ...m, [key]: res.details! }));
      else if (res.error === "premium_required") {
        setStopDetailsMap((m) => {
          const next = { ...m };
          delete next[key];
          return next;
        });
        setPremiumOpen(true);
      } else setStopDetailsMap((m) => ({ ...m, [key]: "error" }));
    } catch {
      setStopDetailsMap((m) => ({ ...m, [key]: "error" }));
    }
  }

  function handleSeeMore(stop: DisplayStop) {
    // Premium subscribers and credit-pack buyers (server decides) get details.
    if (isPremium || isAuthenticated) { void loadStopDetails(stop); return; }
    requirePremium(() => { void loadStopDetails(stop); });
  }

  function handleSaveItinerary() {
    if (!isAuthenticated) {
      toast.error("Faz login para guardar rotas");
      return;
    }
    if (!displayStops.length) {
      toast.error("Não há nada para guardar");
      return;
    }
    setSaveTitle(`${locale.city} · ${new Date().toLocaleDateString()}`);
    setSaveOpen(true);
  }

  function buildSavePayload(title: string): Parameters<typeof saveItinerary>[0] {
    return {
      title,
        city: locale.city,
        country: locale.country,
        lang,
        mode,
        start_time: startTime,
        duration_hours: duration,
        stops: attachResolvedVenuesToSavedStops({
          stops: displayStops.map((s) => ({
          name: s.name,
          category: s.category,
          blurb: s.blurb,
          priceLocal: s.priceLocal,
          rating: s.rating,
          minutes: s.minutes,
          day: s.day,
          lat: s.lat,
          lng: s.lng,
          placeId: s.placeId,
          photoUrl: s.photoUrl,
          })),
          breaks: schedule.filter((entry): entry is MealBreak => entry.type === "break"),
          restaurants,
          hotels,
        }),
        notes: notes || null,
        origin: isPremium ? "premium" : "free",
      };
  }

  const sharedRouteRef = useRef<{ sig: string; it: SavedItinerary } | null>(null);
  async function handleShareRoute() {
    if (!isAuthenticated) {
      toast.error(lang === "pt" ? "Faz login para partilhar rotas" : "Sign in to share routes");
      return;
    }
    if (!displayStops.length || sharing) return;
    setSharing(true);
    try {
      const sig = displayStops.map((s) => s.name).join("|");
      let it = sharedRouteRef.current?.sig === sig ? sharedRouteRef.current.it : null;
      if (!it) {
        it = await saveItinerary(buildSavePayload(`${locale.city} · ${new Date().toLocaleDateString()}`));
        sharedRouteRef.current = { sig, it };
      }
      const slug = await shareItinerary(it);
      sharedRouteRef.current = { sig, it: { ...it, share_slug: slug } };
    } catch {
      // shareItinerary já mostra o erro
    } finally {
      setSharing(false);
    }
  }

  async function confirmSaveItinerary() {
    const title = saveTitle.trim();
    if (!title) return;
    setSaving(true);
    try {
      await saveItinerary(buildSavePayload(title));

      toast.success("Rota guardada em 'As minhas rotas'");
      setSaveOpen(false);
    } catch (e) {
      toast.error((e as Error).message ?? "Erro a guardar");
    } finally {
      setSaving(false);
    }
  }



  return (
    <div className="min-h-screen overflow-x-hidden bg-background text-foreground">
      {/* Language switcher — subtle strip under the site header */}
      <div className="border-b border-border/60 bg-background/60">
        <div className="mx-auto flex max-w-6xl items-center justify-end gap-2 px-4 py-1.5 text-xs">
          <Globe2 className="h-3.5 w-3.5 text-muted-foreground" />
          <select
            value={lang}
            onChange={(e) => setLang(e.target.value as LangCode)}
            className="rounded-full bg-transparent px-2 py-1 font-medium text-muted-foreground outline-none transition hover:text-foreground focus:text-foreground"
            aria-label="Language"
          >
            {LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>{l.flag}  {l.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Editorial hero */}
      <section className="relative isolate">
        {/* Subtle travel-themed backdrop: dashed route lines + soft gradient blobs */}
        <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
          {/* Soft premium gradient wash */}
          <div className="absolute inset-0 bg-gradient-to-br from-sky-100/60 via-background to-emerald-50/50" />
          <div className="absolute -top-32 -right-24 h-[480px] w-[480px] rounded-full bg-sky-300/20 blur-3xl" />
          <div className="absolute top-1/3 -left-40 h-[420px] w-[420px] rounded-full bg-emerald-300/15 blur-3xl" />

          {/* Organic flight-route lines across the whole canvas */}
          <svg
            className="absolute inset-0 h-full w-full text-foreground opacity-[0.06]"
            viewBox="0 0 1200 1400"
            fill="none"
            preserveAspectRatio="xMidYMid slice"
          >
            {/* Route 1 — sweeping arc like a long-haul flight */}
            <path d="M-40 200 C 140 80, 340 400, 560 180 C 720 40, 900 320, 1240 120" stroke="currentColor" strokeWidth="1.3" strokeDasharray="2 10" strokeLinecap="round" />
            {/* Route 2 — gentle S-curve across the middle */}
            <path d="M-40 560 C 180 480, 280 720, 520 580 C 720 460, 880 780, 1240 620" stroke="currentColor" strokeWidth="1.1" strokeDasharray="2 12" strokeLinecap="round" />
            {/* Route 3 — winding lower arc */}
            <path d="M-40 960 C 200 860, 420 1120, 620 980 C 800 860, 1000 1060, 1240 940" stroke="currentColor" strokeWidth="1.1" strokeDasharray="2 12" strokeLinecap="round" />
            {/* Route 4 — diagonal flight across top-middle */}
            <path d="M-40 360 C 200 280, 400 120, 640 300 C 880 480, 1000 200, 1240 280" stroke="currentColor" strokeWidth="1" strokeDasharray="2 10" strokeLinecap="round" />
            {/* Route 5 — sweeping lower route */}
            <path d="M-40 1200 C 240 1100, 480 1320, 720 1180 C 960 1040, 1120 1240, 1240 1120" stroke="currentColor" strokeWidth="1" strokeDasharray="2 12" strokeLinecap="round" />
            {/* Route 6 — gentle mid-left arc */}
            <path d="M-40 760 C 180 640, 380 880, 560 740 C 740 600, 920 840, 1240 760" stroke="currentColor" strokeWidth="1" strokeDasharray="2 10" strokeLinecap="round" />
            {/* Connector to globe — dashed line from lower route toward bottom-right */}
            <path d="M1080 1020 C 1120 1080, 1160 1180, 1180 1320" stroke="currentColor" strokeWidth="1" strokeDasharray="2 10" strokeLinecap="round" />
            <circle cx="180" cy="140" r="2.5" fill="currentColor" />
            <circle cx="560" cy="180" r="2.5" fill="currentColor" />
            <circle cx="960" cy="220" r="2.5" fill="currentColor" />
            <circle cx="260" cy="540" r="2.5" fill="currentColor" />
            <circle cx="780" cy="500" r="2.5" fill="currentColor" />
            <circle cx="1080" cy="1020" r="2.5" fill="currentColor" />
          </svg>

          {/* Minimalist icons — margins only, very low opacity */}
          {/* Plane top-right */}
          <svg className="absolute top-16 right-[12%] h-16 w-16 text-foreground opacity-[0.07]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" />
          </svg>
          {/* Compass — pushed further up and right, away from headline */}
          <svg className="absolute top-6 right-[38%] h-16 w-16 sm:top-10 sm:right-[32%] sm:h-20 sm:w-20 text-foreground opacity-[0.06]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
            <line x1="12" y1="2" x2="12" y2="4" />
            <line x1="12" y1="20" x2="12" y2="22" />
            <line x1="2" y1="12" x2="4" y2="12" />
            <line x1="20" y1="12" x2="22" y2="12" />
          </svg>
          {/* Pin mid-right */}
          <svg className="absolute top-[38%] right-4 h-10 w-10 text-foreground opacity-[0.07]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          {/* Mountains mid-right */}
          <svg className="absolute top-[52%] right-[14%] h-20 w-20 text-foreground opacity-[0.06]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m8 3 4 8 5-5 5 15H2L8 3z" />
          </svg>
          {/* Bicycle mid-right lower */}
          <svg className="absolute top-[68%] right-[10%] h-16 w-16 text-foreground opacity-[0.07]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="5.5" cy="17.5" r="3.5" />
            <circle cx="18.5" cy="17.5" r="3.5" />
            <path d="M15 6a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm-3 11.5V14l-3-3 4-3 2 3h2" />
          </svg>
          {/* Camera top-left */}
          <svg className="absolute top-[46%] left-4 h-12 w-12 text-foreground opacity-[0.06]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
            <circle cx="12" cy="13" r="4" />
          </svg>
          {/* Suitcase bottom-left */}
          <svg className="absolute bottom-8 left-6 h-16 w-16 text-foreground opacity-[0.06]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="7" width="18" height="14" rx="2" />
            <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M3 13h18" />
          </svg>

          {/* Semi-transparent globe bottom-right — ~5% opacity, tied by dashed line */}
          <svg className="absolute -bottom-12 -right-12 h-[280px] w-[280px] text-sky-500 opacity-[0.05]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <ellipse cx="12" cy="12" rx="10" ry="4" />
            <ellipse cx="12" cy="12" rx="4" ry="10" />
            <line x1="2" y1="12" x2="22" y2="12" />
            <line x1="12" y1="2" x2="12" y2="22" />
          </svg>
        </div>
        <div className="mx-auto max-w-6xl px-5 pt-10 pb-2 sm:pt-14">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-foreground px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-background">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
            </span>
            {{ pt: "✨ IA para Viagens", en: "✨ AI Travel Planner", it: "✨ IA per Viaggi", es: "✨ IA para Viajes", fr: "✨ IA de Voyage", de: "✨ Reise-KI", ja: "✨ 旅行AI" }[lang]}
          </div>
          <h1 className="max-w-3xl text-balance text-[38px] font-extrabold leading-[1.05] tracking-tight text-foreground sm:text-[52px]">
            {t.tagline.split(" ").slice(0, -1).join(" ")}{" "}
            <span className="italic text-muted-foreground/60">
              {t.tagline.split(" ").slice(-1)[0]}
            </span>
          </h1>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            {{
              pt: "Cria roteiros únicos e personalizados em segundos — a IA trata dos detalhes.",
              en: "Create unique, tailor-made itineraries in seconds — AI takes care of the details.", it: "Crea itinerari unici e su misura in pochi secondi — all'IA i dettagli.",
              es: "Crea rutas únicas y personalizadas en segundos — la IA se ocupa de los detalles.",
              fr: "Crée des itinéraires uniques et personnalisés en quelques secondes — l'IA s'occupe des détails.",
              de: "Erstelle in Sekunden einzigartige, personalisierte Routen — die KI kümmert sich um die Details.",
              ja: "パーソナライズされた旅程を数秒で作成 — AIが細部まで整えます。",
            }[lang]}
          </p>

          {/* Value props — quick scan of what Routify does */}
          <div className="mt-6 flex flex-wrap gap-2">
            {[
              { icon: "🌍", label: { pt: "+200 países", en: "+200 countries", it: "+200 paesi", es: "+200 países", fr: "+200 pays", de: "+200 Länder", ja: "200以上の国" }[lang] },
              { icon: "🤖", label: { pt: "IA personalizada", en: "Personalized AI", it: "IA personalizzata", es: "IA personalizada", fr: "IA personnalisée", de: "Personalisierte KI", ja: "パーソナルAI" }[lang] },
              { icon: "🎧", label: { pt: "Guia áudio", en: "Audio guide", it: "Audioguida", es: "Guía audio", fr: "Guide audio", de: "Audio-Guide", ja: "オーディオガイド" }[lang] },
              { icon: "🧭", label: { pt: "Rotas Inteligentes", en: "Smart Routes", it: "Itinerari intelligenti", es: "Rutas Inteligentes", fr: "Itinéraires Malins", de: "Smarte Routen", ja: "スマートルート" }[lang] },
            ].map((v) => (
              <span key={v.label} className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-background/60 px-3 py-1.5 text-xs font-semibold text-foreground/80 backdrop-blur-sm">
                <span aria-hidden className="text-sm">{v.icon}</span>
                {v.label}
              </span>
            ))}
          </div>
        </div>

        <div className="mx-auto max-w-6xl px-5 pb-6 pt-8">
          <div className="rounded-[32px] border border-border/60 bg-card p-6 shadow-[var(--shadow-elevated)] sm:p-8">
            <div className="grid gap-6 lg:grid-cols-2 [&>*]:min-w-0">
              {/* AI prompt */}
              <div className="space-y-2">
                <label className="ml-1 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                  {{ pt: "Diz-nos o que queres", en: "Tell us what you want", it: "Dicci cosa vuoi", es: "Cuéntanos qué quieres", fr: "Dis-nous ce que tu veux", de: "Sag uns, was du willst", ja: "やりたいことを教えてください" }[lang]}
                </label>
                <div className="group relative rounded-2xl bg-muted/50 transition focus-within:bg-muted">
                  <Sparkles className="pointer-events-none absolute left-4 top-4 h-5 w-5 text-muted-foreground transition-colors group-focus-within:text-foreground" />
                  <textarea
                    value={aiPrompt}
                    onChange={(e) => {
                      stayDecisionRef.current = null;
                      setStayClarify(null);
                      setAiPrompt(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        void submitAiPrompt();
                      }
                    }}
                    rows={3}
                    placeholder={
                      {
                        pt: "Ex.: 'Vou ao Porto, tenho 4 horas, quero as principais atrações e um bom restaurante'",
                        en: "e.g. 'I'm in Porto, I have 4 hours, top sights and a great restaurant'", it: "es.: «Sono a Roma, ho 4 ore, i luoghi principali e un buon ristorante»",
                        es: "Ej.: 'Voy a Oporto, tengo 4 horas, principales atracciones y un buen restaurante'",
                        fr: "Ex. : 'Je vais à Porto, 4 heures, sites principaux et un bon restaurant'",
                        de: "Z. B. 'Porto, 4 Stunden, Top-Sehenswürdigkeiten und ein gutes Restaurant'",
                        ja: "例：『ポルトに4時間、主要観光地と良いレストラン』",
                      }[lang]
                    }
                    className="w-full resize-none rounded-2xl bg-transparent px-12 py-4 text-[15px] font-medium leading-relaxed text-foreground outline-none placeholder:font-normal placeholder:text-muted-foreground/70"
                  />
                </div>
                {cityConfirmTarget === "prompt" &&
                  cityConfirmPanel(() => {
                    confirmActiveDestination();
                    void submitAiPrompt();
                  })}

                <button
                  type="button"
                  data-testid="submit-prompt"
                  onClick={() => void submitAiPrompt()}
                  disabled={aiPromptLoading || !aiPrompt.trim()}
                  className="group relative mt-2 inline-flex w-full items-center justify-center gap-2 overflow-hidden rounded-2xl bg-gradient-to-br from-foreground via-foreground to-zinc-700 py-4 text-[15px] font-bold tracking-tight text-background shadow-lg shadow-foreground/20 transition-all hover:shadow-xl hover:shadow-foreground/30 hover:-translate-y-0.5 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
                >
                  <span aria-hidden className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/15 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                  {aiPromptLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                  {{ pt: "Criar rota", en: "Generate Itinerary", it: "Genera itinerario", es: "Generar Ruta", fr: "Générer l'itinéraire", de: "Route generieren", ja: "ルートを生成" }[lang]}
                  <span className="transition-transform group-hover:translate-x-0.5">→</span>
                </button>

                {scopeChoice && (() => {
                  const copy = scopeChoiceCopy(scopeChoice.scopeName, lang);
                  return (
                    <div className="mt-2 rounded-2xl border border-border/70 bg-muted/40 p-4">
                      <p className="text-sm font-semibold text-foreground">{copy.title}</p>
                      <p className="mt-1 text-sm text-muted-foreground">{copy.body}</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            // Contrato completo (dias, cidades, transporte
                            // interurbano, interesses) — não só o texto.
                            savePlanHandoff(
                              buildPlanHandoff({
                                prompt: scopeChoice.basePrompt,
                                scopeName: scopeChoice.scopeName,
                                scopeKind: scopeChoice.scopeKind,
                                countryCode: scopeChoice.countryCode,
                                transport:
                                  scopeChoice.routeTransport ??
                                  (mode === "walk" || mode === "bike" || mode === "car" ? mode : "transit"),
                                routeCities: scopeChoice.routeCities,
                                minDays: scopeChoice.routeDays,
                                corridorId: scopeChoice.routeCorridorId,
                                experience: scopeChoice.routeTemplateId ? scopeChoice.scopeName : undefined,
                              }),

                            );
                            scopeDecisionRef.current = { key: scopeKeyOf(scopeChoice.scopeName), decision: "pro" };
                            setScopeChoice(null);
                            void navigate({ to: "/pro" });
                          }}
                          className="rounded-xl bg-foreground px-4 py-2 text-sm font-semibold text-background"
                        >
                          {copy.proLabel}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            scopeDecisionRef.current = { key: scopeKeyOf(scopeChoice.scopeName), decision: "city" };
                            const base = scopeChoice.basePrompt;
                            setScopeChoice(null);
                            setClarification({ missing: "destination", question: copy.cityLabel, basePrompt: base });
                            setClarifyAnswer("");
                          }}
                          className="rounded-xl border border-border bg-background px-4 py-2 text-sm font-semibold text-foreground transition hover:border-foreground/40"
                        >
                          {copy.cityLabel}
                        </button>
                      </div>
                      <p className="mt-2 text-[11px] text-muted-foreground">{copy.hint}</p>
                    </div>
                  );
                })()}

                {stayClarify && (

                  <div className="mt-2 rounded-2xl border border-border/70 bg-muted/40 p-4">
                    <p className="text-sm font-semibold text-foreground">
                      {accommodationClarificationCopy(lang).question}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {(["overnight", "rest"] as const).map((choice) => (
                        <button
                          key={choice}
                          type="button"
                          onClick={() => {
                            stayDecisionRef.current = choice;
                            const base = stayClarify.basePrompt;
                            setStayClarify(null);
                            void submitAiPrompt(base, { continueSession: true, trigger: "clarification_answer" });
                          }}
                          className="rounded-xl border border-border bg-background px-4 py-2 text-sm font-semibold text-foreground transition hover:border-foreground/40"
                        >
                          {choice === "overnight"
                            ? accommodationClarificationCopy(lang).overnight
                            : accommodationClarificationCopy(lang).rest}
                        </button>
                      ))}
                    </div>
                    <p className="mt-2 text-[11px] text-muted-foreground">
                      {accommodationClarificationCopy(lang).note}
                    </p>
                  </div>
                )}

                {homonymChoice && (
                  <div className="mt-2 rounded-2xl border border-border/70 bg-muted/40 p-4">
                    <p id="decision-title-homonym-choice" tabIndex={-1} className="text-sm font-semibold text-foreground outline-none">
                      {homonymCopy(lang).question(homonymChoice.name)}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {homonymChoice.options.map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => {
                            // A escolha começa uma tentativa geográfica nova: nenhum
                            // resultado, mapa ou alternativa do Lagos anterior sobrevive.
                            reqIdRef.current += 1;
                            generationInFlightRef.current = false;
                            setAiStops(null);
                            setResolvedMap({});
                            setRouteLegs([]);
                            setRoutePolyline(null);
                            setNavigationLeg(null);
                            setRestaurants({});
                            setHotels({});
                            setStopDetailsMap({});
                            setLegMinutes([]);
                            aiPromptAlternativesRef.current = [];
                            homonymResolvedRef.current = homonymChoice.name.toLowerCase();
                            resolvedDestinationRef.current = option;
                            const merged = applyDestinationChoice(
                              homonymChoice.basePrompt,
                              homonymChoice.name,
                              option,
                            );
                            setHomonymChoice(null);
                            setAiPrompt(merged);
                            void submitAiPrompt(merged, { continueSession: true, trigger: "clarification_answer" });
                          }}
                          className="rounded-xl border border-border bg-background px-4 py-2 text-sm font-semibold text-foreground transition hover:border-foreground/40"
                        >
                          {option.label}
                          {option.countryCode ? (
                            <span className="ml-1 text-[11px] text-muted-foreground">
                              {option.countryCode}
                            </span>
                          ) : null}
                        </button>
                      ))}
                    </div>
                    <p className="mt-2 text-[11px] text-muted-foreground">{homonymCopy(lang).note}</p>
                  </div>
                )}

                {discovery && (
                  <div className="mt-2 rounded-2xl border border-border/70 bg-muted/40 p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground">{discovery.title}</p>
                        <p className="mt-1 text-[11px] text-muted-foreground">{discovery.disclaimer}</p>
                        {discoveryInRoute.size > 0 && (
                          <p className="mt-2 inline-flex rounded-full bg-brand/10 px-2 py-0.5 text-[11px] font-semibold text-brand">
                            {discoveryCopy.routeCounter} · {discoveryInRoute.size}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setDiscovery(null)}
                        className="shrink-0 rounded-full border border-border px-2 py-1 text-[11px] text-muted-foreground"
                      >
                        ✕
                      </button>
                    </div>

                    {discovery.loading ? (
                      <p className="mt-3 text-sm text-muted-foreground">{t.suggesting}</p>
                    ) : discovery.places.length === 0 ? (
                      <p className="mt-3 text-sm text-muted-foreground">{discoveryCopy.noData}</p>
                    ) : (
                      <>
                        <ol className="mt-3 space-y-2">
                          {discovery.places.map((place, idx) => (
                            <li
                              key={place.placeId}
                              className="rounded-xl border border-border/60 bg-background/70 p-3"
                            >
                              <div className="flex items-start gap-2">
                                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                                  {idx + 1}
                                </span>
                                <div className="min-w-0 flex-1">
                                  <p className="truncate text-sm font-semibold text-foreground">{place.name}</p>
                                  {(place.zone || place.address) && (
                                    <p className="truncate text-[11px] text-muted-foreground">
                                      {place.zone ?? place.address}
                                    </p>
                                  )}
                                  <p className="mt-1 text-[11px] text-muted-foreground">
                                    {place.reasons.join(" · ")}
                                  </p>
                                  <DiscoveryCardActions
                                    mapsUrl={place.googleMapsUri}
                                    saved={savedDiscovery.has(place.placeId)}
                                    inRoute={discoveryInRoute.has(place.placeId)}
                                    onToggleSave={() => toggleSavedDiscoveryPlace(place)}
                                    onToggleRoute={() => toggleDiscoveryPlaceInRoute(place)}
                                    copy={{
                                      openMaps: discoveryCopy.openMaps,
                                      opening: discoveryCopy.opening,
                                      openedMaps: discoveryCopy.openedMaps,
                                      save: discoveryCopy.save,
                                      saved: discoveryCopy.saved,
                                      addToRoute: discoveryCopy.addToRoute,
                                      inRoute: discoveryCopy.inRoute,
                                      error: discoveryCopy.actionError,
                                    }}
                                  />

                                </div>
                              </div>
                            </li>
                          ))}
                        </ol>
                        <button
                          type="button"
                          onClick={createRouteFromDiscovery}
                          className="mt-3 w-full rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
                        >
                          {discoveryCopy.buildRoute}
                        </button>
                      </>
                    )}
                  </div>
                )}

                {inspiration && (
                  <div className="mt-2 rounded-2xl border border-border/70 bg-muted/40 p-4">
                    {(() => {
                      const copy = inspirationCopy(lang);
                      return (
                        <>
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-foreground">{copy.title}</p>
                              <p className="mt-1 text-[11px] text-muted-foreground">{copy.subtitle}</p>
                            </div>
                            <button
                              type="button"
                              aria-label={copy.dismiss}
                              onClick={() => setInspiration(null)}
                              className="shrink-0 rounded-full border border-border px-2 py-1 text-[11px] text-muted-foreground"
                            >
                              ✕
                            </button>
                          </div>

                          {inspiration.phase !== "results" && (
                            <div className="mt-3 space-y-3">
                              <div>
                                <p className="text-[11px] font-semibold text-muted-foreground">{copy.daysQuestion}</p>
                                <div className="mt-1 flex flex-wrap gap-1.5">
                                  {copy.daysOptions.map((o) => (
                                    <button
                                      key={o.days}
                                      type="button"
                                      onClick={() => setInspiration({ ...inspiration, days: o.days })}
                                      className={cn(
                                        "rounded-full px-3 py-1.5 text-xs font-semibold transition",
                                        inspiration.days === o.days
                                          ? "bg-primary text-primary-foreground"
                                          : "border border-border bg-background text-foreground hover:border-foreground/40",
                                      )}
                                    >
                                      {o.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <div>
                                <p className="text-[11px] font-semibold text-muted-foreground">{copy.vibeQuestion}</p>
                                <div className="mt-1 flex flex-wrap gap-1.5">
                                  {copy.vibeOptions.map((o) => (
                                    <button
                                      key={o.vibe}
                                      type="button"
                                      aria-pressed={inspiration.vibe.includes(o.vibe)}
                                      onClick={() =>
                                        setInspiration({
                                          ...inspiration,
                                          vibe: inspiration.vibe.includes(o.vibe)
                                            ? inspiration.vibe.filter((v) => v !== o.vibe)
                                            : [...inspiration.vibe, o.vibe],
                                        })
                                      }
                                      className={cn(
                                        "rounded-full px-3 py-1.5 text-xs font-semibold transition",
                                        inspiration.vibe.includes(o.vibe)
                                          ? "bg-primary text-primary-foreground"
                                          : "border border-border bg-background text-foreground hover:border-foreground/40",
                                      )}
                                    >
                                      {o.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <div>
                                <p className="text-[11px] font-semibold text-muted-foreground">{copy.budgetQuestion}</p>
                                <div className="mt-1 flex flex-wrap gap-1.5">
                                  {copy.budgetOptions.map((o) => (
                                    <button
                                      key={o.budget}
                                      type="button"
                                      onClick={() =>
                                        setInspiration({
                                          ...inspiration,
                                          budget: inspiration.budget === o.budget ? null : o.budget,
                                        })
                                      }
                                      className={cn(
                                        "rounded-full px-3 py-1.5 text-xs font-semibold transition",
                                        inspiration.budget === o.budget
                                          ? "bg-primary text-primary-foreground"
                                          : "border border-border bg-background text-foreground hover:border-foreground/40",
                                      )}
                                    >
                                      {o.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <button
                                type="button"
                                disabled={inspiration.phase === "loading" || !inspiration.days}
                                onClick={() => void runInspiration()}
                                className="w-full rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
                              >
                                {inspiration.phase === "loading" ? copy.loading : copy.suggestButton}
                              </button>
                            </div>
                          )}

                          {inspiration.phase === "results" && (
                            <InspirationCards
                              destinations={inspiration.destinations}
                              copy={inspirationCardsCopy(lang)}
                              onCreateRoute={createRouteFromInspiration}
                            />
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}

                {homonymLoading && !homonymChoice && (
                  <p className="mt-2 text-[11px] text-muted-foreground">…</p>
                )}

                {clarification && (

                  <div className="mt-2 rounded-2xl border border-border/70 bg-muted/40 p-4">
                    {durationSuggestion ? (
                      <>
                        <p id="decision-title-clarification" tabIndex={-1} className="text-sm font-semibold text-foreground outline-none">{durationSuggestion.headline}</p>
                        <div className="mt-2 flex min-w-0 flex-col gap-2">
                          {durationSuggestion.options.map((option) => (
                            <button
                              key={option.days}
                              type="button"
                              onClick={() => {
                                const merged = mergeClarificationAnswer(
                                  clarification.basePrompt,
                                  "duration",
                                  String(option.days),
                                );
                                setAiPrompt(merged);
                                void submitAiPrompt(merged, { continueSession: true, trigger: "clarification_answer" });
                              }}
                              className="min-w-0 rounded-xl border border-border bg-background px-3 py-2 text-left text-sm hover:border-foreground/40"
                            >
                              {option.detail}
                            </button>
                          ))}
                        </div>
                      </>
                    ) : (
                      <p id="decision-title-clarification" tabIndex={-1} className="text-sm font-semibold text-foreground outline-none">{clarification.question}</p>
                    )}
                    {clarification.missing === "logistics_arrival" || clarification.missing === "logistics_departure" ? (
                      <LogisticsClarification
                        key={`${clarification.basePrompt}:${clarification.missing}`}
                        missing={clarification.missing}
                        logistics={clarification.logistics ?? {}}
                        lang={lang}
                        destination={locale.displayCity || locale.city}
                        hotelSelected={hotelIsSelected}
                        onAnswer={(answer) => {
                          const merged = mergeClarificationAnswer(
                            clarification.basePrompt,
                            clarification.missing,
                            answer,
                          );
                          setAiPrompt(merged);
                          void submitAiPrompt(merged, { continueSession: true, trigger: "clarification_answer" });
                        }}
                        onSkipLogistics={() => {
                          const merged = `${clarification.basePrompt} ${LOGISTICS_SKIP_MARKER}`.trim();
                          setAiPrompt(merged);
                          void submitAiPrompt(merged, { continueSession: true, trigger: "clarification_answer" });
                        }}
                      />
                    ) : clarification.missing === "time_window" ? (

                      <div className="mt-2 flex min-w-0 flex-col gap-3">
                        <div className="flex flex-wrap gap-2">
                          {[
                            { label: { pt: "Hoje", en: "Today", es: "Hoy", fr: "Aujourd'hui", it: "Oggi", de: "Heute", ja: "今日" }[lang], value: 0 },
                            { label: { pt: "Amanhã", en: "Tomorrow", es: "Mañana", fr: "Demain", it: "Domani", de: "Morgen", ja: "明日" }[lang], value: 1 },
                            { label: { pt: "Depois de amanhã", en: "Day after", es: "Pasado", fr: "Après-demain", it: "Dopodomani", de: "Übermorgen", ja: "明後日" }[lang], value: 2 },
                          ].map((btn) => (
                            <button
                              key={btn.value}
                              type="button"
                              onClick={() => setClarifyDayOffset(btn.value)}
                              className={cn(
                                "rounded-full px-3 py-1.5 text-xs font-semibold transition",
                                clarifyDayOffset === btn.value
                                  ? "bg-primary text-primary-foreground"
                                  : "border border-border bg-background text-foreground hover:border-foreground/40",
                              )}
                            >
                              {btn.label}
                            </button>
                          ))}
                        </div>
                        <div className="flex items-center gap-2">
                          <TimeInput
                            value={clarifyStartTime}
                            onChange={setClarifyStartTime}
                            label={{ pt: "Início", en: "Start", es: "Inicio", fr: "Début", it: "Inizio", de: "Beginn", ja: "開始" }[lang]}
                          />
                          <span className="text-sm text-muted-foreground">—</span>
                          <TimeInput
                            value={clarifyEndTime}
                            onChange={setClarifyEndTime}
                            label={{ pt: "Fim", en: "End", es: "Fin", fr: "Fin", it: "Fine", de: "Ende", ja: "終了" }[lang]}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const merged = mergeClarificationAnswer(
                              clarification.basePrompt,
                              "time_window",
                              buildTimeClarificationAnswer(),
                            );
                            setAiPrompt(merged);
                            void submitAiPrompt(merged, { continueSession: true, trigger: "clarification_answer" });
                          }}
                          className="w-full shrink-0 rounded-xl bg-foreground px-4 py-2 text-sm font-semibold text-background min-[420px]:w-auto"
                        >
                          {{ pt: "Continuar", en: "Continue", it: "Continua", es: "Continuar", fr: "Continuer", de: "Weiter", ja: "続ける" }[lang]}
                        </button>
                      </div>
                    ) : (
                      <div className="mt-2 flex min-w-0 flex-col gap-2 min-[420px]:flex-row">
                        <input
                          autoFocus
                          value={clarifyAnswer}
                          onChange={(e) => setClarifyAnswer(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              const answer = clarifyAnswer;
                              const merged = mergeClarificationAnswer(clarification.basePrompt, clarification.missing, answer);
                              setAiPrompt(merged);
                              void submitAiPrompt(merged, { continueSession: true, trigger: "clarification_answer" });
                            }
                          }}
                          inputMode={clarification.missing === "duration" ? "numeric" : undefined}
                          placeholder={clarification.missing === "duration" ? "3" : (clarification.missing as string) === "logistics_arrival" || (clarification.missing as string) === "logistics_departure" ? "14:30" : "Milão"}
                          className="min-w-0 flex-1 rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground/40"
                        />
                        <button
                          type="button"
                          disabled={!clarifyAnswer.trim()}
                          onClick={() => {
                            const answer = clarifyAnswer;
                            const merged = mergeClarificationAnswer(clarification.basePrompt, clarification.missing, answer);
                            setAiPrompt(merged);
                            void submitAiPrompt(merged, { continueSession: true, trigger: "clarification_answer" });
                          }}
                          className="w-full shrink-0 rounded-xl bg-foreground px-4 py-2 text-sm font-semibold text-background disabled:opacity-50 min-[420px]:w-auto"
                        >
                          {{ pt: "Continuar", en: "Continue", it: "Continua", es: "Continuar", fr: "Continuer", de: "Weiter", ja: "続ける" }[lang]}
                        </button>
                      </div>
                    )}
                    <p className="mt-2 text-[11px] text-muted-foreground">
                      {{
                        pt: "Ainda não usámos nenhum crédito — só usamos ao carregares no botão final “Criar itinerário”.",
                        en: "No credit used yet — credit is only used on the final “Create itinerary” button.", it: "Nessun credito usato — si usa solo con il pulsante finale “Crea itinerario”.",
                        es: "Aún no usamos crédito — solo al pulsar el botón final “Crear itinerario”.",
                        fr: "Aucun crédit utilisé — seulement au bouton final « Créer l'itinéraire ».",
                        de: "Noch kein Guthaben verbraucht — erst mit „Reiseplan erstellen“.",
                        ja: "クレジットはまだ使っていません。最後の「旅程を作成」でのみ使います。",
                      }[lang]}
                    </p>
                  </div>
                )}

                {/*
                  Pedido completo à espera de decisão explícita. Este é o ÚNICO
                  ponto de entrada de geração paga vindo da clarificação.
                */}
                {pendingGeneration && !clarification && !homonymChoice && !stayClarify && !cityConfirm && !localityChoice && (
                  <div className="mt-2 rounded-2xl border border-brand/40 bg-brand/5 p-4" data-testid="ready-to-generate">
                    <p className="text-sm font-semibold text-foreground">{readyToGenerateCopy(lang).title}</p>
                    <button
                      type="button"
                      data-testid="create-itinerary"
                      disabled={aiPromptLoading}
                      onClick={() => {
                        const pending = pendingGeneration.text;
                        void submitAiPrompt(pending, {
                          continueSession: true,
                          trigger: "explicit_generate",
                        });
                      }}
                      className="mt-2 w-full rounded-xl bg-foreground px-4 py-2 text-sm font-semibold text-background disabled:opacity-50 min-[420px]:w-auto"
                    >
                      {readyToGenerateCopy(lang).cta}
                    </button>
                    <p className="mt-2 text-[11px] text-muted-foreground">{readyToGenerateCopy(lang).credit}</p>
                  </div>
                )}



                {localityChoice && (
                  <div className="mt-4 rounded-2xl border border-border bg-card p-4">
                    <p id="decision-title-locality-choice" tabIndex={-1} className="text-sm font-semibold text-foreground outline-none">
                      {{
                        pt: `Qual destes locais queres para “${localityChoice.requested}”?`,
                        en: `Which of these places do you mean by “${localityChoice.requested}”?`,
                        es: `¿Cuál de estos lugares quieres para “${localityChoice.requested}”?`,
                        fr: `Lequel de ces lieux veux-tu pour « ${localityChoice.requested} » ?`,
                        it: `Quale di questi luoghi intendi per “${localityChoice.requested}”?`,
                        de: `Welchen dieser Orte meinst du mit „${localityChoice.requested}“?`,
                        ja: `「${localityChoice.requested}」はどの場所ですか？`,
                      }[lang]}
                    </p>
                    <div className="mt-3 flex flex-col gap-2">
                      {localityChoice.options.map((opt) => (
                        <button
                          key={`${opt.id ?? opt.label}-${opt.lat},${opt.lng}`}
                          type="button"
                          onClick={() => {
                            chosenLocalityRef.current = opt;
                            const base = localityChoice.basePrompt;
                            setLocalityChoice(null);
                            void submitAiPrompt(base, { continueSession: true, trigger: "clarification_answer" });
                          }}
                          className="rounded-xl border border-border bg-background px-4 py-2 text-left text-sm font-semibold hover:border-foreground/40"
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {pinnedConfirmation && (
                  <div className="mt-2 rounded-2xl border border-border/70 bg-muted/40 p-4">
                    <p id="decision-title-pinned-confirmation" tabIndex={-1} className="text-sm font-semibold text-foreground outline-none">
                      {{
                        pt: `Vais incluir ${pinnedConfirmation.name}`,
                        en: `You'll include ${pinnedConfirmation.name}`,
                        es: `Vas a incluir ${pinnedConfirmation.name}`,
                        fr: `Tu vas inclure ${pinnedConfirmation.name}`,
                        it: `Includerai ${pinnedConfirmation.name}`,
                        de: `Du nimmst ${pinnedConfirmation.name} auf`,
                        ja: `${pinnedConfirmation.name}を追加します`,
                      }[lang]}
                    </p>
                    {pinnedConfirmation.address && (
                      <p className="mt-0.5 text-[11px] text-muted-foreground">
                        {pinnedConfirmation.address}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-muted-foreground">
                      {{
                        pt: `Fica como paragem obrigatória para “${pinnedConfirmation.constraintLabel}”, na zona alargada que confirmaste.`,
                        en: `It becomes a required stop for “${pinnedConfirmation.constraintLabel}”, in the widened area you confirmed.`,
                        es: `Queda como parada obligatoria para “${pinnedConfirmation.constraintLabel}”, en la zona ampliada que confirmaste.`,
                        fr: `Il devient une étape obligatoire pour « ${pinnedConfirmation.constraintLabel} », dans la zone élargie confirmée.`,
                        it: `Diventa una tappa obbligatoria per “${pinnedConfirmation.constraintLabel}”, nella zona ampliata confermata.`,
                        de: `Er wird Pflichtstopp für „${pinnedConfirmation.constraintLabel}“ im bestätigten erweiterten Bereich.`,
                        ja: `確認済みの拡大エリア内で「${pinnedConfirmation.constraintLabel}」の必須地点になります。`,
                      }[lang]}
                    </p>
                    <div className="mt-3 flex flex-col gap-2 min-[420px]:flex-row">
                      <button
                        type="button"
                        onClick={continueWithPinned}
                        className="rounded-xl bg-foreground px-4 py-2 text-sm font-semibold text-background"
                      >
                        {{
                          pt: "Continuar a criar rota",
                          en: "Continue creating the route",
                          es: "Continuar creando la ruta",
                          fr: "Continuer la création de l'itinéraire",
                          it: "Continua a creare l'itinerario",
                          de: "Route weiter erstellen",
                          ja: "ルート作成を続ける",
                        }[lang]}
                      </button>
                      <button
                        type="button"
                        onClick={changePinnedChoice}
                        className="rounded-xl border border-border bg-background px-4 py-2 text-sm font-semibold"
                      >
                        {{
                          pt: "Alterar escolha",
                          en: "Change choice",
                          es: "Cambiar elección",
                          fr: "Modifier le choix",
                          it: "Cambia scelta",
                          de: "Auswahl ändern",
                          ja: "選択を変更",
                        }[lang]}
                      </button>
                    </div>
                  </div>
                )}

                {pinnedRejected && (
                  <div className="mt-2 rounded-2xl border border-destructive/40 bg-destructive/10 p-4">
                    <p id="decision-title-pinned-rejected" tabIndex={-1} className="text-sm font-semibold text-foreground outline-none">
                      {pinnedRejected.reason === "technical_failure"
                        ? ({
                            pt: "Não consegui confirmar este local agora",
                            en: "I couldn't confirm this place right now",
                            es: "No pude confirmar este lugar ahora",
                            fr: "Je n'ai pas pu confirmer ce lieu maintenant",
                            it: "Non sono riuscito a confermare questo luogo ora",
                            de: "Dieser Ort konnte gerade nicht bestätigt werden",
                            ja: "現在この場所を確認できませんでした",
                          }[lang])
                        : ({
                            pt: "A escolha deixou de ser válida",
                            en: "That choice is no longer valid",
                            es: "La elección dejó de ser válida",
                            fr: "Ce choix n'est plus valide",
                            it: "La scelta non è più valida",
                            de: "Die Auswahl ist nicht mehr gültig",
                            ja: "その選択は無効になりました",
                          }[lang])}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {lang === "pt"
                        ? pinnedRejected.reason === "signature_mismatch"
                          ? "O pedido mudou desde que escolheste este local."
                          : pinnedRejected.reason === "place_not_found"
                            ? "O local escolhido já não foi confirmado pelo Google Places."
                            : pinnedRejected.reason === "invalid_selection_ref"
                              ? "A escolha não pôde ser verificada com segurança. Volta a escolher no cartão abaixo."
                              : pinnedRejected.reason === "type_mismatch"
                                ? "O local escolhido deixou de corresponder ao que pediste."
                                : pinnedRejected.reason === "outside_area"
                                  ? "O local escolhido ficou fora da zona alargada confirmada."
                                  : "Não consegui reconfirmar a escolha neste momento."
                        : pinnedRejected.reason === "signature_mismatch"
                          ? "The request changed since you picked this place."
                          : pinnedRejected.reason === "place_not_found"
                            ? "The chosen place could no longer be confirmed by Google Places."
                            : pinnedRejected.reason === "invalid_selection_ref"
                              ? "The choice could not be verified securely. Pick again in the card below."
                              : pinnedRejected.reason === "type_mismatch"
                                ? "The chosen place no longer matches what you asked for."
                                : pinnedRejected.reason === "outside_area"
                                  ? "The chosen place lies outside the widened area you confirmed."
                                  : "I couldn't reconfirm the choice right now."}
                    </p>
                    <div className="mt-3 flex flex-col gap-2 min-[420px]:flex-row">
                      {pinnedRejected.reason === "technical_failure" && (
                        <button
                          type="button"
                          onClick={() => {
                            const ctx = pinnedContinuationRef.current;
                            if (!ctx) return;
                            setPinnedRejected(null);
                            void submitAiPrompt(ctx.basePrompt, {
                              continueSession: true,
                              continuation: { pinned: ctx.pinned, area: ctx.area },
                            });
                          }}
                          className="rounded-xl bg-foreground px-4 py-2 text-sm font-semibold text-background"
                        >
                          {{ pt: "Tentar novamente", en: "Try again", es: "Reintentar", fr: "Réessayer", it: "Riprova", de: "Erneut versuchen", ja: "再試行" }[lang]}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          // "Alterar escolha": volta ao MESMO cartão de
                          // candidatos guardado — sem nova pesquisa.
                          const saved = lastAvailabilityBlockRef.current;
                          setPinnedRejected(null);
                          if (saved && saved.basePrompt === pinnedRejected.basePrompt) {
                            setAvailabilityBlock(saved);
                          }
                        }}
                        className={cn(
                          "rounded-xl px-4 py-2 text-sm font-semibold",
                          pinnedRejected.reason === "technical_failure"
                            ? "border border-border bg-background text-foreground"
                            : "bg-foreground text-background",
                        )}
                      >
                        {{
                          pt: "Alterar escolha",
                          en: "Change choice",
                          es: "Cambiar elección",
                          fr: "Modifier le choix",
                          it: "Cambia scelta",
                          de: "Auswahl ändern",
                          ja: "選択を変更",
                        }[lang]}
                      </button>
                    </div>
                  </div>
                )}

                {availabilityBlock && !pinnedConfirmation && !pinnedRejected && (() => {

                  const block = availabilityBlock;
                  const groups = new Set(block.entries.map((e) => failureReasonGroup(e.reason)));
                  // As ações são decididas pelo MOTIVO: um problema de zona
                  // nunca abre o seletor de data/hora.
                  const hasSchedule = groups.has("schedule");
                  const actions = availabilityCardActions({
                    reasons: block.entries.map((e) => e.reason),
                    hasSuggestedArea: !!block.suggestedArea,
                    ...(block.keepStrict ? { keepStrict: true } : {}),
                  });
                  const titleKey = block.keepStrict
                    ? "strict"
                    : hasSchedule
                      ? groups.size > 1
                        ? "mixed"
                        : "schedule"
                      : groups.has("area")
                        ? "area"
                        : groups.has("not_found")
                          ? "not_found"
                          : "mixed";
                  const titles: Record<string, Record<string, string>> = {
                    schedule: {
                      pt: "Os locais que pediste não cabem nesse horário",
                      en: "The places you asked for don't fit that time window",
                      es: "Los lugares que pediste no caben en ese horario",
                      fr: "Les lieux demandés n'entrent pas dans ce créneau",
                      it: "I luoghi richiesti non rientrano in quell'orario",
                      de: "Die gewünschten Orte passen nicht in dieses Zeitfenster",
                      ja: "希望の場所はその時間帯に収まりません",
                    },
                    area: {
                      pt: "Encontrámos os locais, mas não na localidade exata",
                      en: "We found the places, but not in the exact locality",
                      es: "Encontramos los lugares, pero no en la localidad exacta",
                      fr: "Nous avons trouvé les lieux, mais pas dans la localité exacte",
                      it: "Abbiamo trovato i luoghi, ma non nella località esatta",
                      de: "Wir haben die Orte gefunden, aber nicht im genauen Ort",
                      ja: "場所は見つかりましたが、指定の地区内ではありません",
                    },
                    not_found: {
                      pt: "Não encontrámos alguns locais que pediste",
                      en: "We couldn't find some of the places you asked for",
                      es: "No encontramos algunos lugares que pediste",
                      fr: "Nous n'avons pas trouvé certains lieux demandés",
                      it: "Non abbiamo trovato alcuni luoghi richiesti",
                      de: "Einige gewünschte Orte wurden nicht gefunden",
                      ja: "ご希望の場所の一部が見つかりませんでした",
                    },
                    mixed: {
                      pt: "Há problemas com os locais que pediste",
                      en: "There are issues with the places you asked for",
                      es: "Hay problemas con los lugares que pediste",
                      fr: "Il y a des problèmes avec les lieux demandés",
                      it: "Ci sono problemi con i luoghi richiesti",
                      de: "Es gibt Probleme mit den gewünschten Orten",
                      ja: "ご希望の場所に問題があります",
                    },
                    strict: {
                      pt: `Só em ${block.area}: continua a faltar o que pediste`,
                      en: `Only in ${block.area}: what you asked for is still missing`,
                      es: `Solo en ${block.area}: sigue faltando lo que pediste`,
                      fr: `Uniquement à ${block.area} : il manque toujours ce que tu as demandé`,
                      it: `Solo a ${block.area}: manca ancora quello che hai chiesto`,
                      de: `Nur in ${block.area}: das Gewünschte fehlt weiterhin`,
                      ja: `${block.area}のみ：ご希望の場所はまだ見つかりません`,
                    },
                  };
                  return (
                  <div className="mt-2 rounded-2xl border border-border/70 bg-muted/40 p-4">
                    <p id="decision-title-availability" tabIndex={-1} className="text-sm font-semibold text-foreground outline-none">
                      {titles[titleKey]![lang] ?? titles[titleKey]!.en!}
                    </p>
                    {block.keepStrict && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {{
                          pt: `A pesquisa fica limitada a ${block.area}. Podes reformular o pedido, alargar a zona ou escolher outro dia.`,
                          en: `The search stays limited to ${block.area}. You can rewrite the request, widen the area or pick another day.`,
                          es: `La búsqueda se limita a ${block.area}. Puedes reformular, ampliar la zona o elegir otro día.`,
                          fr: `La recherche reste limitée à ${block.area}. Tu peux reformuler, élargir la zone ou choisir un autre jour.`,
                          it: `La ricerca resta limitata a ${block.area}. Puoi riformulare, ampliare la zona o scegliere un altro giorno.`,
                          de: `Die Suche bleibt auf ${block.area} begrenzt. Du kannst die Anfrage ändern, das Gebiet erweitern oder einen anderen Tag wählen.`,
                          ja: `検索は${block.area}に限定されます。依頼を書き直すか、範囲を広げるか、別の日を選べます。`,
                        }[lang]}
                      </p>
                    )}
                    <div className="mt-3 flex flex-col gap-3">
                      {block.entries.map((entry) => (
                        <div key={entry.label} className="rounded-xl border border-border bg-background p-3">
                          <p className="text-sm font-medium text-foreground">{entry.label}</p>
                          {entry.address && (
                            <p className="text-[11px] text-muted-foreground">{entry.address}</p>
                          )}
                          <p className="mt-1 text-xs text-muted-foreground">{entry.message}</p>
                          {entry.candidates.length > 0 && (
                            <div className="mt-2 flex flex-col gap-2">
                              {entry.candidates.map((cand) => (
                                <div
                                  key={`${entry.label}-${cand.name}`}
                                  className="rounded-lg border border-border/70 bg-muted/30 p-2"
                                >
                                  <p className="text-xs font-semibold text-foreground">{cand.name}</p>
                                  <p className="text-[11px] text-muted-foreground">
                                    {cand.nearby
                                      ? nearbyLocalityText(cand.locality, block.area, lang)
                                      : (cand.address ?? cand.locality ?? "")}
                                  </p>
                                  {cand.nearby && !block.keepStrict && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        // Seleção FIXA: guarda a identidade e
                                        // mostra confirmação — nunca revalida
                                        // como se a zona exata ainda mandasse.
                                        if (block.requestSignature && entry.constraintId && cand.placeId) {
                                          choosePinnedCandidate(block, entry, cand);
                                          return;
                                        }
                                        const area = buildExpandedArea({
                                          requestedLocality: block.area,
                                          candidates: [
                                            {
                                              locality: cand.locality,
                                              distanceMeters: cand.distanceMeters,
                                            },
                                          ],
                                          language: lang,
                                        });
                                        setAvailabilityBlock(null);
                                        if (area) applyExpandedArea(area);
                                        void submitAiPrompt(block.basePrompt, {
                                          continueSession: true,
                                        });
                                      }}
                                      className="mt-2 rounded-full border border-border bg-background px-3 py-1 text-[11px] font-semibold hover:border-foreground/40"
                                    >
                                      {{
                                        pt: "Escolher este local",
                                        en: "Choose this place",
                                        es: "Elegir este lugar",
                                        fr: "Choisir ce lieu",
                                        it: "Scegli questo luogo",
                                        de: "Diesen Ort wählen",
                                        ja: "この場所を選ぶ",
                                      }[lang]}
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          {hasSchedule &&
                            failureReasonGroup(entry.reason) === "schedule" &&
                            entry.alternatives.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {entry.alternatives.map((alt) => (
                                <button
                                  key={`${entry.label}-${alt.label}`}
                                  type="button"
                                  onClick={() => {
                                    const dayWord =
                                      alt.dayOffset === 0
                                        ? "hoje"
                                        : alt.dayOffset === 1
                                          ? "amanhã"
                                          : alt.dayOffset === 2
                                            ? "depois de amanhã"
                                            : `daqui a ${alt.dayOffset} dias`;
                                    const merged = mergeClarificationAnswer(
                                      block.basePrompt,
                                      "time_window",
                                      `${dayWord} das ${alt.start} às ${alt.end}`,
                                    );
                                    setAvailabilityBlock(null);
                                    // O pedido continua visível e completo.
                                    setAiPrompt(merged);
                                    void submitAiPrompt(merged, { continueSession: true });
                                  }}
                                  className="rounded-full border border-border bg-muted/40 px-3 py-1.5 text-xs font-semibold hover:border-foreground/40"
                                >
                                  {alt.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 flex flex-col gap-2 min-[420px]:flex-row">
                      {actions.time && (
                        <button
                          type="button"
                          onClick={() => {
                            setClarification({
                              missing: "time_window",
                              question: {
                                pt: "Que dia e que horas queres?",
                                en: "Which day and time do you want?",
                                es: "¿Qué día y hora quieres?",
                                fr: "Quel jour et quelle heure ?",
                                it: "Che giorno e che ora vuoi?",
                                de: "Welcher Tag und welche Uhrzeit?",
                                ja: "希望の日時は？",
                              }[lang],
                              basePrompt: block.basePrompt,
                            });
                            resetClarifyInputs();
                            setClarifyDayOffset(block.dayOffset);
                            // O pedido continua no formulário.
                            setAiPrompt(block.basePrompt);
                            setAvailabilityBlock(null);
                          }}
                          className="rounded-xl border border-border bg-background px-4 py-2 text-sm font-semibold"
                        >
                          {{
                            pt: "Escolher outro dia ou hora",
                            en: "Pick another day or time",
                            es: "Elegir otro día u hora",
                            fr: "Choisir un autre jour ou horaire",
                            it: "Scegli un altro giorno o orario",
                            de: "Anderen Tag oder Zeit wählen",
                            ja: "別の日時を選ぶ",
                          }[lang]}
                        </button>
                      )}
                      {actions.widen && block.suggestedArea && (
                        <button
                          type="button"
                          onClick={() => {
                            const base = block.basePrompt;
                            const area = block.suggestedArea!;
                            setAvailabilityBlock(null);
                            // Alargar é procurar numa ÁREA concreta e limitada,
                            // mantendo todas as restrições obrigatórias.
                            applyExpandedArea(area);
                            setAiPrompt(base);
                            void submitAiPrompt(base, { continueSession: true });
                          }}
                          className="rounded-xl bg-foreground px-4 py-2 text-left text-sm font-semibold text-background"
                        >
                          {{
                            pt: `Alargar para ${block.suggestedArea.label}`,
                            en: `Widen to ${block.suggestedArea.label}`,
                            es: `Ampliar a ${block.suggestedArea.label}`,
                            fr: `Élargir à ${block.suggestedArea.label}`,
                            it: `Amplia a ${block.suggestedArea.label}`,
                            de: `Erweitern auf ${block.suggestedArea.label}`,
                            ja: `${block.suggestedArea.label}まで範囲を広げる`,
                          }[lang]}
                        </button>
                      )}
                      {actions.keepStrict && (
                        <button
                          type="button"
                          onClick={() => {
                            // Manter a zona exata NÃO repete o mesmo pedido:
                            // fixa a área e mostra o que falta nela.
                            applyExpandedArea(null);
                            setAiPrompt(block.basePrompt);
                            setAvailabilityBlock({ ...block, keepStrict: true });
                          }}
                          className="rounded-xl border border-border bg-background px-4 py-2 text-sm font-semibold"
                        >
                          {{
                            pt: `Manter apenas ${block.area}`,
                            en: `Keep only ${block.area}`,
                            es: `Mantener solo ${block.area}`,
                            fr: `Garder uniquement ${block.area}`,
                            it: `Mantieni solo ${block.area}`,
                            de: `Nur ${block.area} behalten`,
                            ja: `${block.area}のみにする`,
                          }[lang]}
                        </button>
                      )}
                      {actions.rewrite && (
                        <button
                          type="button"
                          onClick={() => {
                            setAvailabilityBlock(null);
                            setAiPrompt(block.basePrompt);
                          }}

                          className="rounded-xl border border-border bg-background px-4 py-2 text-sm font-semibold"
                        >
                          {{
                            pt: "Reformular o pedido",
                            en: "Rewrite the request",
                            es: "Reformular la petición",
                            fr: "Reformuler la demande",
                            it: "Riformula la richiesta",
                            de: "Anfrage neu formulieren",
                            ja: "依頼を書き直す",
                          }[lang]}
                        </button>
                      )}
                    </div>
                    <p className="mt-2 text-[11px] text-muted-foreground">
                      {{
                        pt: "Horários habituais devolvidos pelo Google Places. Não consome créditos Routify nem inicia geração por IA antes da tua confirmação.",
                        en: "Usual opening hours returned by Google Places. No Routify credits are used and no AI generation starts before you confirm.",
                        es: "Horarios habituales devueltos por Google Places. No consume créditos ni inicia generación por IA antes de tu confirmación.",
                        fr: "Horaires habituels renvoyés par Google Places. Aucun crédit utilisé, aucune génération IA avant votre confirmation.",
                        it: "Orari abituali restituiti da Google Places. Nessun credito usato e nessuna generazione IA prima della conferma.",
                        de: "Übliche Öffnungszeiten von Google Places. Kein Guthaben und keine KI-Generierung vor deiner Bestätigung.",
                        ja: "Google Places の通常営業時間です。確認前にクレジット消費やAI生成は行いません。",
                      }[lang]}
                    </p>
                  </div>
                  );
                })()}


                {availabilityTechnical && (
                  <div className="mt-2 rounded-2xl border border-border/70 bg-muted/40 p-4">
                    <p id="decision-title-availability-technical" tabIndex={-1} className="text-sm font-semibold text-foreground outline-none">
                      {{
                        pt: "Não consegui confirmar os horários destes locais neste momento.",
                        en: "I couldn't confirm these places' opening hours right now.",
                        es: "No pude confirmar los horarios de estos lugares en este momento.",
                        fr: "Impossible de confirmer les horaires de ces lieux pour le moment.",
                        it: "Non sono riuscito a confermare gli orari di questi luoghi al momento.",
                        de: "Die Öffnungszeiten dieser Orte konnten gerade nicht bestätigt werden.",
                        ja: "現在これらの場所の営業時間を確認できませんでした。",
                      }[lang]}
                    </p>
                    <div className="mt-3 flex flex-col gap-2 min-[420px]:flex-row">
                      <button
                        type="button"
                        onClick={() => {
                          const base = availabilityTechnical.basePrompt;
                          setAvailabilityTechnical(null);
                          void submitAiPrompt(base, { continueSession: true });
                        }}
                        className="rounded-xl bg-foreground px-4 py-2 text-sm font-semibold text-background"
                      >
                        {{
                          pt: "Tentar novamente",
                          en: "Try again",
                          es: "Intentar de nuevo",
                          fr: "Réessayer",
                          it: "Riprova",
                          de: "Erneut versuchen",
                          ja: "再試行",
                        }[lang]}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const base = availabilityTechnical.basePrompt;
                          setAvailabilityTechnical(null);
                          void submitAiPrompt(base, {
                            continueSession: true,
                            skipAvailabilityCheck: true,
                          });
                        }}
                        className="rounded-xl border border-border bg-background px-4 py-2 text-sm font-semibold"
                      >
                        {{
                          pt: "Continuar sem confirmar horários",
                          en: "Continue without confirming hours",
                          es: "Continuar sin confirmar horarios",
                          fr: "Continuer sans confirmer les horaires",
                          it: "Continua senza confermare gli orari",
                          de: "Ohne Bestätigung fortfahren",
                          ja: "営業時間を確認せずに続ける",
                        }[lang]}
                      </button>
                    </div>
                    <p className="mt-2 text-[11px] text-muted-foreground">
                      {{
                        pt: "Não consome créditos Routify nem IA antes da confirmação; pode consultar Google Places e reutiliza cache quando disponível.",
                        en: "No Routify credits or AI are used before confirmation; it may query Google Places and reuses cache when available.",
                        es: "No consume créditos Routify ni IA antes de la confirmación; puede consultar Google Places y reutiliza la caché cuando está disponible.",
                        fr: "Aucun crédit Routify ni IA avant confirmation ; peut consulter Google Places et réutilise le cache si disponible.",
                        it: "Nessun credito Routify né IA prima della conferma; può consultare Google Places e riutilizza la cache quando disponibile.",
                        de: "Vor der Bestätigung werden weder Routify-Guthaben noch KI verbraucht; Google Places kann abgefragt und Cache wiederverwendet werden.",
                        ja: "確認前に Routify クレジットや AI は消費されません。Google Places を参照し、利用可能な場合はキャッシュを再利用します。",
                      }[lang]}
                    </p>
                  </div>
                )}

                {durationAdjustedDays !== null && (
                  <div
                    data-testid="duration-adjusted-notice"
                    className="mt-2 rounded-2xl border border-border/70 bg-background p-3 text-xs font-semibold text-foreground"
                  >
                    {lang === "pt"
                      ? `Roteiro ajustado para ${durationAdjustedDays} dia${durationAdjustedDays === 1 ? "" : "s"}.`
                      : `Itinerary adjusted to ${durationAdjustedDays} day${durationAdjustedDays === 1 ? "" : "s"}.`}
                  </div>
                )}

                {scarcityAction && (
                  <div
                    data-testid="scarcity-action-status"
                    className="mt-2 rounded-2xl border border-border/70 bg-muted/40 p-4"
                  >
                    <p className="text-sm font-semibold text-foreground">
                      {scarcityAction.phase === "running"
                        ? scarcityAction.kind === "widen_area"
                          ? lang === "pt"
                            ? `A alargar a zona para ${Math.round((scarcityAction.areaRadiusMeters ?? 0) / 1000)} km e a procurar de novo…`
                            : `Widening the area to ${Math.round((scarcityAction.areaRadiusMeters ?? 0) / 1000)} km and searching again…`
                          : lang === "pt"
                            ? `A recriar o roteiro para ${scarcityAction.days} dia${scarcityAction.days === 1 ? "" : "s"}…`
                            : `Rebuilding the itinerary for ${scarcityAction.days} day${scarcityAction.days === 1 ? "" : "s"}…`
                        : lang === "pt"
                          ? "A ação falhou por um problema técnico. O teu pedido foi mantido."
                          : "The action failed for a technical reason. Your request was kept."}
                    </p>
                    {scarcityAction.phase === "error" && (
                      <button
                        type="button"
                        data-testid="scarcity-action-retry"
                        onClick={() => {
                          const action = scarcityAction;
                          setScarcityAction(null);
                          if (action.kind === "widen_area") widenScarcityArea(action.areaRadiusMeters);
                          else if (action.days) applyShorterDuration(action.days);
                        }}
                        className="mt-3 rounded-xl bg-foreground px-4 py-2 text-sm font-semibold text-background"
                      >
                        {lang === "pt" ? "Tentar novamente" : "Try again"}
                      </button>
                    )}
                  </div>
                )}

                {/* Falha técnica estruturada: destino já resolvido, pedido
                    mantido e sem crédito consumido. Nunca sugere outra cidade. */}
                {technicalFailure && (
                  <div className="mt-4 rounded-2xl border border-border bg-card p-4">
                    <p className="text-sm text-foreground">
                      {lang === "pt"
                        ? "Não consegui criar esta rota agora. O teu pedido foi mantido e não consumiu crédito."
                        : "I couldn't create this route right now. Your request was kept and no credit was used."}
                    </p>
                    {import.meta.env.DEV && (
                      <p className="mt-1 font-mono text-[10px] text-muted-foreground" data-testid="build-marker">
                        build {__ROUTIFY_BUILD_COMMIT__} · {__ROUTIFY_BUILD_VERSION__}
                      </p>
                    )}
                    <button
                      type="button"
                      disabled={technicalRetryBusy || aiLoading}
                      onClick={() => {
                        const ctx = generationRetryContextRef.current;
                        if (!ctx || technicalRetryBusy || aiLoading) return;
                        setTechnicalRetryBusy(true);
                        setTechnicalFailure(null);
                        ctx.run();
                      }}
                      className="mt-3 rounded-xl bg-foreground px-4 py-2 text-sm font-semibold text-background disabled:opacity-50"
                    >
                      {lang === "pt" ? "Tentar novamente" : "Try again"}
                    </button>
                  </div>
                )}


                {visitScarcity && !scarcityAction && (() => {
                  // Cartão de escassez de visita: opções limitadas e concretas.
                  const scarcityCity = visitScarcity.city || (locale.displayCity || locale.city);
                  const usedKm = Math.round(visitScarcity.areaRadiusMeters / 1000);
                  const nextRadius = nextWidenRadiusMeters(visitScarcity.areaRadiusMeters);
                  const canWiden = nextRadius !== null;
                  const currentDays = visitScarcity.durationDays;
                  const canShorten = currentDays > 1;
                  return (
                    <div className="mt-2 rounded-2xl border border-border/70 bg-muted/40 p-4" data-testid="visit-scarcity-card">
                      <p id="decision-title-visit-scarcity" tabIndex={-1} className="text-sm font-semibold text-foreground outline-none">
                        {visitScarcity.cause === "area_scarcity"
                          ? {
                              pt: `Não encontrei locais de visita suficientes nesta zona para este roteiro.`,
                              en: `Not enough places to visit in this area for this itinerary.`,
                              es: `No encontramos suficientes lugares para visitar en esta zona para este itinerario.`,
                              fr: `Nous n'avons pas trouvé assez de lieux à visiter dans cette zone pour cet itinéraire.`,
                              it: `Non abbiamo trovato abbastanza luoghi da visitare in questa zona per questo itinerario.`,
                              de: `In dieser Gegend gibt es nicht genug Orte für diese Route.`,
                              ja: `このエリアにはこの旅程に十分な観光スポットが見つかりませんでした。`,
                            }[lang]
                          : visitScarcity.cause === "schedule_unavailable"
                            ? lang === "pt"
                              ? "Encontrámos locais suficientes, mas poucos estão abertos nas horas deste roteiro. O teu pedido foi mantido e não consumiu crédito."
                              : "We found enough places, but few are open during this itinerary's hours. Your request was kept and no credit was used."
                            : visitScarcity.cause === "route_assembly_failure"
                              ? lang === "pt"
                                ? "Encontrámos locais suficientes nesta zona, mas a montagem do roteiro falhou por um problema técnico. O teu pedido foi mantido e não consumiu crédito."
                                : "We found enough places in this area, but assembling the itinerary failed due to a technical issue. Your request was kept and no credit was used."
                              : lang === "pt"
                                ? "A procura de locais falhou por um problema técnico. O teu pedido foi mantido e não consumiu crédito."
                                : "The place search failed due to a technical issue. Your request was kept and no credit was used."}
                      </p>
                      {visitScarcity.cause === "route_assembly_failure" && (visitScarcity.eligibleCount ?? 0) > visitScarcity.found && (
                        <p className="mt-1 text-xs text-muted-foreground" data-testid="scarcity-eligible-context">
                          {lang === "pt"
                            ? `O motor encontrou ${visitScarcity.eligibleCount} candidatos elegíveis na área — a falha foi na montagem do roteiro, não na procura.`
                            : `The engine found ${visitScarcity.eligibleCount} eligible candidates in the area — the failure was in itinerary assembly, not in the search.`}
                        </p>
                      )}
                      <p className="mt-1 text-xs text-muted-foreground">
                        {lang === "pt"
                          ? `Encontrámos ${visitScarcity.found} visita(s) válida(s)${usedKm ? ` na zona usada (${visitScarcity.areaLabel}, ~${usedKm} km)` : ` em ${scarcityCity}`}; este roteiro de ${currentDays} dia(s) precisa de pelo menos ${visitScarcity.minimum} paragem(ns). Não apresentámos nem cobrámos uma rota parcial — escolhe uma opção:`
                          : `We found ${visitScarcity.found} valid visit(s)${usedKm ? ` in the area used (${visitScarcity.areaLabel}, ~${usedKm} km)` : ` in ${scarcityCity}`}; this ${currentDays}-day itinerary needs at least ${visitScarcity.minimum} stop(s). We did not present or charge a partial route — choose an option:`}
                      </p>
                      {/* Mínimos de segurança vs ritmo real: paragens finais
                          por dia, com o dia que falhou bem visível. */}
                      {(() => {
                        const perDay = visitScarcity.stopsPerDay;
                        if (!perDay || perDay.length === 0) return null;
                        const minima = visitScarcity.minimumViableStopsPerDay ?? [];
                        const failing = perDay
                          .map((stops, i) => ({ i, stops, min: minima[i] ?? 1 }))
                          .filter((entry) => entry.stops < entry.min);
                        return (
                          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="scarcity-per-day">
                            {perDay.map((stops, i) => {
                              const min = minima[i] ?? 1;
                              const isFailing = stops < min;
                              return (
                                <span
                                  key={i}
                                  className={`rounded-full border px-2.5 py-1 text-xs font-medium ${
                                    isFailing
                                      ? "border-destructive/40 bg-destructive/10 text-destructive"
                                      : "border-border bg-background text-muted-foreground"
                                  }`}
                                >
                                  {lang === "pt"
                                    ? `Dia ${i + 1}: ${stops} paragem${stops === 1 ? "" : "s"} (mínimo ${min})`
                                    : `Day ${i + 1}: ${stops} stop${stops === 1 ? "" : "s"} (minimum ${min})`}
                                </span>
                              );
                            })}
                          </div>
                        );
                      })()}
                      {durationChoice && (
                        <div className="mt-3 rounded-xl border border-border bg-background p-3" data-testid="duration-choice">
                          <p className="text-xs text-muted-foreground">
                            {lang === "pt"
                              ? `Reduzir de ${durationChoice.fromDays} dias para ${durationChoice.toDays} dia${durationChoice.toDays === 1 ? "" : "s"}?`
                              : `Shorten from ${durationChoice.fromDays} days to ${durationChoice.toDays} day${durationChoice.toDays === 1 ? "" : "s"}?`}
                          </p>
                          <div className="mt-2 flex gap-2">
                            <button
                              type="button"
                              data-testid="duration-choice-confirm"
                              onClick={() => {
                                const toDays = durationChoice.toDays;
                                setDurationChoice(null);
                                applyShorterDuration(toDays);
                              }}
                              className="rounded-xl bg-foreground px-3 py-2 text-xs font-semibold text-background"
                            >
                              {lang === "pt"
                                ? `Reduzir de ${durationChoice.fromDays} dias para ${durationChoice.toDays} dia${durationChoice.toDays === 1 ? "" : "s"}`
                                : `Shorten to ${durationChoice.toDays} day${durationChoice.toDays === 1 ? "" : "s"}`}
                            </button>
                            <button
                              type="button"
                              onClick={() => setDurationChoice(null)}
                              className="rounded-xl border border-border px-3 py-2 text-xs font-semibold"
                            >
                              {lang === "pt" ? "Cancelar" : "Cancel"}
                            </button>
                          </div>
                        </div>
                      )}
                      {visitScarcity.cause !== "area_scarcity" && (
                      <div className="mt-3">
                        <button
                          type="button"
                          data-testid="scarcity-retry"
                          onClick={() => {
                            const args = visitScarcityCtxRef.current?.generateArgs;
                            setVisitScarcity(null);
                            // Falha técnica: nova execução com o mesmo pedido
                            // guardado — nunca cartão de escassez da área.
                            if (args) void runGenerate({ ...args, continuation: "retry" });
                          }}
                          className="rounded-xl bg-foreground px-4 py-2 text-sm font-semibold text-background"
                        >
                          {{ pt: "Tentar novamente", en: "Try again", es: "Reintentar", fr: "Réessayer", it: "Riprova", de: "Erneut versuchen", ja: "再試行" }[lang]}
                        </button>
                      </div>
                      )}
                      {visitScarcity.cause === "schedule_unavailable" && canShorten && (
                      <div className="mt-3">
                        <button
                          type="button"
                          data-testid="scarcity-shorten-schedule"
                          onClick={() => setDurationChoice({ fromDays: currentDays, toDays: Math.max(1, currentDays - 1) })}
                          className="rounded-xl border border-border bg-background px-4 py-2 text-sm font-semibold"
                        >
                          {lang === "pt" ? "Reduzir a duração" : "Shorten the duration"}
                        </button>
                      </div>
                      )}
                      {visitScarcity.cause === "area_scarcity" && (
                      <div className="mt-3 flex flex-col gap-2 min-[420px]:flex-row min-[420px]:flex-wrap">
                        <button
                          type="button"
                          data-testid="scarcity-widen"
                          onClick={() => widenScarcityArea(nextRadius ?? undefined)}
                          disabled={!canWiden}
                          className="rounded-xl bg-foreground px-4 py-2 text-sm font-semibold text-background disabled:opacity-50"
                        >
                          {canWiden
                            ? lang === "pt"
                              ? `Alargar a zona (~${Math.round((nextRadius ?? 0) / 1000)} km)`
                              : `Widen the area (~${Math.round((nextRadius ?? 0) / 1000)} km)`
                            : lang === "pt"
                              ? "Zona já no limite"
                              : "Area already at the limit"}
                        </button>
                        <button
                          type="button"
                          data-testid="scarcity-shorten"
                          disabled={!canShorten}
                          onClick={() => setDurationChoice({ fromDays: currentDays, toDays: Math.max(1, currentDays - 1) })}
                          className="rounded-xl border border-border bg-background px-4 py-2 text-sm font-semibold disabled:opacity-50"
                        >
                          {{
                            pt: "Reduzir duração",
                            en: "Shorten duration",
                            es: "Reducir duración",
                            fr: "Réduire la durée",
                            it: "Riduci la durata",
                            de: "Dauer verkürzen",
                            ja: "時間を短くする",
                          }[lang]}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setVisitScarcity(null);
                            document.getElementById("interests-chips")?.scrollIntoView({ behavior: "smooth", block: "center" });
                          }}
                          className="rounded-xl border border-border bg-background px-4 py-2 text-sm font-semibold"
                        >
                          {{
                            pt: "Escolher um interesse",
                            en: "Choose an interest",
                            es: "Elegir un interés",
                            fr: "Choisir un intérêt",
                            it: "Scegli un interesse",
                            de: "Interesse wählen",
                            ja: "興味を選ぶ",
                          }[lang]}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const args = visitScarcityCtxRef.current?.generateArgs;
                            setVisitScarcity(null);
                            // "Incluir gastronomia" é a única ação que ativa
                            // comida — ação explícita do utilizador (Garantia A).
                            if (args) {
                              void runGenerate({
                                ...args,
                                explicitInterests: Array.from(new Set([...(args.explicitInterests ?? []), "food" as InterestId])),
                              });
                            }
                          }}
                          className="rounded-xl border border-border bg-background px-4 py-2 text-sm font-semibold"
                        >
                          {{
                            pt: "Incluir gastronomia (opcional)",
                            en: "Include food (optional)",
                            es: "Incluir gastronomía (opcional)",
                            fr: "Inclure la gastronomie (optionnel)",
                            it: "Includere gastronomia (opzionale)",
                            de: "Gastronomie einbeziehen (optional)",
                            ja: "グルメを含める（任意）",
                          }[lang]}
                        </button>
                      </div>
                      )}
                    </div>
                  );
                })()}


                {constraintBlock && (
                  <div className="mt-2 rounded-2xl border border-border/70 bg-muted/40 p-4">
                    <p id="decision-title-constraint-block" tabIndex={-1} className="text-sm font-semibold text-foreground outline-none">
                      {{
                        pt: `Não encontrámos em ${constraintBlock.area}: ${constraintBlock.labels.join(", ")}.`,
                        en: `We couldn't find in ${constraintBlock.area}: ${constraintBlock.labels.join(", ")}.`,
                        es: `No encontramos en ${constraintBlock.area}: ${constraintBlock.labels.join(", ")}.`,
                        fr: `Introuvable à ${constraintBlock.area} : ${constraintBlock.labels.join(", ")}.`,
                        it: `Non trovato a ${constraintBlock.area}: ${constraintBlock.labels.join(", ")}.`,
                        de: `In ${constraintBlock.area} nicht gefunden: ${constraintBlock.labels.join(", ")}.`,
                        ja: `${constraintBlock.area}で見つかりませんでした：${constraintBlock.labels.join("、")}。`,
                      }[lang]}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {{
                        pt: "Não criámos a rota sem esses locais e não gastámos crédito. Queres alargar a zona ou reescrever o pedido?",
                        en: "We didn't create the route without those places and no credit was used. Widen the area or rewrite the request?",
                        es: "No creamos la ruta sin esos lugares y no se usó crédito. ¿Ampliar la zona o reescribir la petición?",
                        fr: "Nous n'avons pas créé l'itinéraire sans ces lieux et aucun crédit n'a été utilisé. Élargir la zone ou réécrire la demande ?",
                        it: "Non abbiamo creato l'itinerario senza quei luoghi e nessun credito è stato usato. Ampliare la zona o riscrivere la richiesta?",
                        de: "Die Route wurde ohne diese Orte nicht erstellt und kein Guthaben verbraucht. Bereich erweitern oder Anfrage neu schreiben?",
                        ja: "その場所なしではルートを作成せず、クレジットも消費していません。範囲を広げますか、依頼を書き直しますか？",
                      }[lang]}
                    </p>
                    {constraintBlock.expandedLabel && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {{
                          pt: `Já procurámos também em ${constraintBlock.expandedLabel}.`,
                          en: `We also searched ${constraintBlock.expandedLabel}.`,
                          es: `También buscamos en ${constraintBlock.expandedLabel}.`,
                          fr: `Nous avons aussi cherché dans ${constraintBlock.expandedLabel}.`,
                          it: `Abbiamo cercato anche in ${constraintBlock.expandedLabel}.`,
                          de: `Wir haben auch in ${constraintBlock.expandedLabel} gesucht.`,
                          ja: `${constraintBlock.expandedLabel}でも検索しました。`,
                        }[lang]}
                      </p>
                    )}
                    <div className="mt-3 flex flex-col gap-2 min-[420px]:flex-row">
                      {constraintBlock.onWiden && (
                      <button
                        type="button"
                        onClick={constraintBlock.onWiden}
                        className="rounded-xl bg-foreground px-4 py-2 text-sm font-semibold text-background"
                      >
                        {{
                          pt: "Alargar a zona e continuar",
                          en: "Widen the area and continue",
                          es: "Ampliar la zona y continuar",
                          fr: "Élargir la zone et continuer",
                          it: "Amplia la zona e continua",
                          de: "Bereich erweitern und fortfahren",
                          ja: "範囲を広げて続ける",
                        }[lang]}
                      </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setConstraintBlock(null)}
                        className="rounded-xl border border-border bg-background px-4 py-2 text-sm font-semibold"
                      >
                        {{
                          pt: "Reescrever o pedido",
                          en: "Rewrite the request",
                          es: "Reescribir la petición",
                          fr: "Réécrire la demande",
                          it: "Riscrivi la richiesta",
                          de: "Anfrage neu schreiben",
                          ja: "依頼を書き直す",
                        }[lang]}
                      </button>
                    </div>
                  </div>
                )}



              </div>


              {/* Manual destination search */}
              <div className="space-y-2">
                <label className="ml-1 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                  {{ pt: "Ou escolhe um destino", en: "Or pick a destination", it: "Oppure scegli una destinazione", es: "O elige un destino", fr: "Ou choisis une destination", de: "Oder wähle ein Ziel", ja: "または目的地を選ぶ" }[lang]}
                </label>
                <div className="group relative rounded-2xl bg-muted/50 transition focus-within:bg-muted">
                  <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground transition-colors group-focus-within:text-foreground" />
                  <PlacesAutocompleteInput
                    value={query}
                    onChange={setQuery}
                    onSelect={pickFromPlace}
                    placeholder={t.searchPh}
                    language={lang}
                    className="w-full rounded-2xl bg-transparent py-4 pl-12 pr-4 text-[15px] font-medium text-foreground outline-none placeholder:font-normal placeholder:text-muted-foreground/70"
                  />
                </div>
                <button
                  type="button"
                  onClick={useGPS}
                  disabled={locating}
                  aria-label={t.useLocation}
                  data-testid="use-gps"
                  className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-border bg-background py-4 text-[15px] font-semibold text-foreground transition-all hover:bg-muted active:scale-[0.99] disabled:opacity-60"
                >
                  {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
                  {locating ? t.locating : t.useLocation}
                </button>
                {error && (
                  <div className="mt-1 rounded-xl bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive">
                    {error}
                  </div>
                )}
                {geoNotice && (
                  <div className="mt-1 flex flex-wrap items-center gap-2 rounded-xl border border-amber-400/50 bg-amber-400/10 px-3 py-2 text-sm text-foreground">
                    <span className="font-medium">{geoNotice}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setGeoNotice(null);
                        confirmActiveDestination();
                      }}

                      className="rounded-full border border-input bg-background px-3 py-1 text-xs font-semibold hover:bg-accent"
                    >
                      {lang === "pt" ? "Sim, é aqui" : "Yes, that's right"}
                    </button>
                    <button
                      type="button"
                      onClick={() => { manualLocationRef.current = true; setGeoNotice(null); }}
                      className="rounded-full border border-input bg-background px-3 py-1 text-xs font-semibold hover:bg-accent"
                    >
                      {lang === "pt" ? "Escolher cidade" : "Choose city"}
                    </button>
                  </div>
                )}

                {locale.city && (
                  <div className="mt-1 inline-flex max-w-full items-center gap-2 rounded-full border border-border bg-background px-3.5 py-2 text-sm">
                    <span className="text-base leading-none" aria-hidden>
                      {locale.countryCode && locale.countryCode.length === 2
                        ? String.fromCodePoint(
                            ...[...locale.countryCode.toUpperCase()].map(
                              (c) => 0x1f1e6 + c.charCodeAt(0) - 65,
                            ),
                          )
                        : "📍"}
                    </span>
                    <span className="truncate">
                      <span className="text-muted-foreground">
                        {{ pt: "Roteiro em", en: "Route in", it: "Itinerario a", es: "Ruta en", fr: "Itinéraire à", de: "Route in", ja: "Itinerario in" }[lang]}
                      </span>{" "}
                      <span className="font-bold text-foreground">{locale.displayCity || locale.city}</span>
                      {locale.country ? <span className="text-muted-foreground">, {locale.country}</span> : null}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* Layout: stacked on all sizes — map below content on desktop.
          To restore the previous side-by-side desktop layout, swap the
          <main> class for: "mx-auto grid max-w-6xl gap-6 px-4 py-8 lg:grid-cols-[1.2fr_1fr]"
          and re-add "lg:order-1" / "lg:order-2" on the map/aside wrappers. */}
      <main className="mx-auto grid max-w-5xl gap-6 px-4 py-8">
        <div ref={mapSectionRef} className={`${navigationActive ? "order-1 scroll-mt-20" : "order-2"} min-w-0`}>
          <div className={`${navigationActive && navFullscreen ? "fixed inset-0 z-[60] rounded-none border-0" : `relative w-full overflow-hidden rounded-3xl border border-border ${navigationActive ? "h-[64vh] min-h-[390px]" : "aspect-[4/3] sm:aspect-[16/11]"}`} bg-[oklch(0.96_0.02_220)] shadow-[var(--shadow-soft)] overflow-hidden`}>
            <GoogleMapView
              center={itineraryAnchor}
              stops={guidedStops.map((s) => ({ name: s.name, lat: s.lat, lng: s.lng, placeId: s.placeId, photoUrl: s.photoUrl, queryHint: locale.city }))}
              stopDays={guidedStops.map((s) => s.day ?? 1)}
              optimizeOrder={!navigationActive && planMode === "manual" ? optimizeManualOrder : false}
              departureTime={mapDepartureTime}
              mode={mode}
              language={lang}
              userPosition={userPos}
              userHeading={userHeading}
              navigationActive={navigationActive}
              activeStopIndex={activeStopIdx}
              venueMarkers={navigationActive ? [] : venueMarkers}
              originOverride={routeStart}
              onDurations={handleMapDurations}
              onResolved={handleResolved}
              onOrder={handleOrder}
              onRouteLegs={handleMapRouteLegs}
              onRoutePolyline={setRoutePolyline}
              onNavigationLeg={setNavigationLeg}
              selectMode={pickMode}
              onPoiPicked={handlePoiPicked}
              routeRevision={routeSnapshot?.routeRevision ?? 0}


            />


            <div className="pointer-events-none absolute left-4 top-4 z-20 flex items-center gap-2 rounded-full bg-background/90 px-3 py-1.5 text-xs font-medium shadow">
              <span className="h-2 w-2 animate-pulse rounded-full bg-brand" />
              {locale.displayCity || locale.city}, {locale.country}
            </div>

            {!navigationActive && (
              <button
                type="button"
                onClick={() => { if (pickMode) { setPickMode(false); } else { setPickMode(true); setPickCount(0); toast.info(lang === "pt" ? "Toca nos pontos de interesse no mapa para os adicionares." : lang === "fr" ? "Touchez les points d'intérêt sur la carte pour les ajouter." : lang === "es" ? "Toca los puntos de interés en el mapa para añadirlos." : lang === "de" ? "Tippe auf Sehenswürdigkeiten auf der Karte, um sie hinzuzufügen." : lang === "ja" ? "地図上の観光スポットをタップして追加してください。" : "Tap points of interest on the map to add them."); } }}
                aria-pressed={pickMode}
                className={`absolute right-4 top-4 z-30 inline-flex h-9 items-center gap-1.5 rounded-full px-3 text-xs font-semibold shadow ring-1 ring-border ${pickMode ? "bg-brand text-brand-foreground" : "bg-background/95 text-foreground hover:bg-background"}`}
              >
                {pickMode
                  ? <><X className="h-4 w-4" /> {lang === "pt" ? "Concluir" : lang === "fr" ? "Terminer" : lang === "es" ? "Finalizar" : lang === "de" ? "Fertig" : lang === "ja" ? "完了" : "Done"}</>
                  : <><MapPin className="h-4 w-4" /> {lang === "pt" ? "Escolher no mapa" : lang === "fr" ? "Choisir sur la carte" : lang === "es" ? "Elegir en el mapa" : lang === "de" ? "Auf Karte wählen" : lang === "ja" ? "地図で選択" : "Pick on map"}</>}
              </button>
            )}

            {pickMode && !navigationActive && (
              <div className="absolute left-4 right-20 top-4 z-20 rounded-2xl border border-brand/30 bg-background/95 px-3 py-2 text-xs shadow-[var(--shadow-soft)] backdrop-blur">
                <div className="font-semibold text-foreground">
                  {lang === "pt" ? "A escolher paragens no mapa" : lang === "fr" ? "Sélection d'arrêts sur la carte" : lang === "es" ? "Eligiendo paradas en el mapa" : lang === "de" ? "Stopps auf der Karte auswählen" : lang === "ja" ? "地図で立ち寄り地を選択中" : "Picking stops on the map"}
                </div>
                <div className="text-muted-foreground">
                  {lang === "pt" ? "Toca nos ícones de monumentos, museus, restaurantes…" : lang === "fr" ? "Touchez les icônes de monuments, musées, restaurants…" : lang === "es" ? "Toca los iconos de monumentos, museos, restaurantes…" : lang === "de" ? "Tippe auf Symbole von Sehenswürdigkeiten, Museen, Restaurants…" : lang === "ja" ? "モニュメント、博物館、レストランのアイコンをタップ…" : "Tap monument, museum, or restaurant icons…"}
                  {" "}
                  {pickCount > 0
                    ? (lang === "pt" ? `${pickCount} adicionada${pickCount === 1 ? "" : "s"}.` : lang === "fr" ? `${pickCount} ajouté${pickCount === 1 ? "" : "s"}.` : lang === "es" ? `${pickCount} añadida${pickCount === 1 ? "" : "s"}.` : lang === "de" ? `${pickCount} hinzugefügt.` : lang === "ja" ? `${pickCount} 件追加。` : `${pickCount} added.`)
                    : (lang === "pt" ? "Nenhuma ainda." : lang === "fr" ? "Aucun pour l'instant." : lang === "es" ? "Ninguna todavía." : lang === "de" ? "Noch keine." : lang === "ja" ? "まだありません。" : "None yet.")}
                </div>
              </div>
            )}

            {navigationActive && (
              <div className="absolute right-4 top-4 z-30 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setVoiceEnabled((v) => !v)}
                  aria-label={voiceEnabled ? "Desativar voz" : "Ativar voz"}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-background/95 text-foreground shadow ring-1 ring-border hover:bg-background"
                >
                  {voiceEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4 text-muted-foreground" />}
                </button>
                <button
                  type="button"
                  onClick={() => setNavFullscreen((v) => !v)}
                  aria-label={navFullscreen ? "Sair de ecrã inteiro" : "Ecrã inteiro"}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-background/95 text-foreground shadow ring-1 ring-border hover:bg-background"
                >
                  {navFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </button>
                <button
                  type="button"
                  onClick={() => { setNavigationActive(false); setNavFullscreen(false); }}
                  aria-label="Terminar navegação"
                  className="inline-flex h-9 items-center gap-1.5 rounded-full bg-foreground px-3 text-xs font-semibold text-background shadow hover:opacity-90"
                >
                  <X className="h-4 w-4" /> Sair
                </button>
              </div>
            )}
            {navigationActive && activeStop && (
              <div className="absolute left-4 right-4 top-16 z-30 rounded-2xl border border-brand/30 bg-background/95 p-3 shadow-[var(--shadow-soft)] backdrop-blur">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand text-brand-foreground">
                    <Navigation className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-brand">{t.goToNow}</div>
                    <div className="truncate text-sm font-semibold text-foreground">{activeStop.name}</div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {navigationLeg?.steps?.[0] || t.followBlueLine}
                    </div>
                  </div>
                  {navigationLeg?.distance && <div className="shrink-0 text-xs font-semibold text-foreground">{navigationLeg.distance}</div>}
                </div>
              </div>
            )}
          </div>
          {!navigationActive && Number.isFinite(guidedStops[0]?.lat) && Number.isFinite(guidedStops[0]?.lng) && (
            <NavigationReadinessBar
              className="mt-4"
              startPoint={{ lat: guidedStops[0]!.lat as number, lng: guidedStops[0]!.lng as number }}
              startPointName={guidedStops[0]!.name}
              routeStops={guidedStops.flatMap((stop) =>
                Number.isFinite(stop.lat) && Number.isFinite(stop.lng)
                  ? [{ lat: stop.lat as number, lng: stop.lng as number, name: stop.name }]
                  : [],
              )}
              mode={mode}
            />
          )}
        </div>

        <aside className={`${navigationActive ? "order-2" : "order-1"} min-w-0 space-y-5`}>
          <div className="rounded-2xl border border-border bg-card p-4 shadow-[var(--shadow-soft)]">
            <div className="flex items-center gap-2">
              <Wand2 className="h-4 w-4 text-brand" />
              <h2 className="text-sm font-semibold tracking-tight">{t.preferences}</h2>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-1 rounded-xl bg-muted p-1">
              <button
                type="button"
                onClick={() => { setPlanMode("ai"); setLegMinutes([]); setRouteLegs([]); setRoutePolyline(null); setNavigationLeg(null); }}
                className={`inline-flex items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-semibold transition ${planMode === "ai" ? "bg-background text-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}
              >
                <Sparkles className="h-3.5 w-3.5" /> {MM.ai[lang]}
              </button>
              <button
                type="button"
                onClick={() => { setPlanMode("manual"); setLegMinutes([]); setRouteLegs([]); setRoutePolyline(null); setNavigationLeg(null); }}
                className={`inline-flex items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-semibold transition ${planMode === "manual" ? "bg-background text-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}
              >
                <MapPin className="h-3.5 w-3.5" /> {MM.manual[lang]}
              </button>
            </div>

            {planMode === "ai" && (<div className="mt-4">
              <div className="flex items-center justify-between text-xs font-medium text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" /> {t.timeAvailable}
                </span>
                <span className="inline-flex items-center gap-1 font-semibold text-foreground">
                  {durationLabel}
                  {isMultiDay && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-brand/10 px-1.5 py-0.5 text-[10px] font-semibold text-brand">
                      <Crown className="h-2.5 w-2.5" /> Premium
                    </span>
                  )}
                </span>
              </div>
              <input
                type="range"
                min={0}
                max={DURATIONS.length - 1}
                step={1}
                value={durationIdx}
                onChange={(e) => {
                  const next = Number(e.target.value);
                  if (next >= FIRST_MULTIDAY_IDX && !isPremium) {
                    if (!isAuthenticated) {
                      toast.info("Faz login para desbloquear itinerários multi-dia 🗓️");
                      navigate({ to: "/login" });
                      return;
                    }
                    setPremiumOpen(true);
                    setDurationIdx(FIRST_MULTIDAY_IDX - 1);
                    return;
                  }
                  setDurationIdx(next);
                }}
                className="mt-2 w-full accent-[var(--brand)]"
                aria-label={t.timeAvailable}
              />
              {isMultiDay && (
                <p className="mt-1.5 text-[11px] text-muted-foreground">
                  Itinerário de {numDays} dias — agrupado por dia.
                </p>
              )}
            </div>)}

            <div className="mt-4">
              <label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <CalendarIcon className="h-3.5 w-3.5" />
                <Clock className="h-3.5 w-3.5" />
                {lang === "pt" ? "Data e hora" : "Date & time"}
              </label>
              <div className="mt-1.5 flex items-stretch overflow-hidden rounded-xl border border-border bg-background">
                <input
                  id="rt-date"
                  type="date"
                  value={startDate}
                  min={(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; })()}
                  onChange={(e) => { autoNowStartRef.current = false; setStartDate(e.target.value); }}
                  className="flex-1 appearance-none border-0 bg-transparent px-3 py-2.5 text-base font-medium text-foreground outline-none"
                  aria-label={lang === "pt" ? "Data de início" : "Start date"}
                />
                <div className="w-px self-stretch bg-border" />
                <input
                  id="rt-start"
                  type="time"
                  value={startTime || ""}
                  onChange={(e) => { autoNowStartRef.current = false; setStartTime(e.target.value); }}
                  aria-label={lang === "pt" ? "Hora de início" : "Start time"}
                  className="flex-1 appearance-none border-0 bg-transparent px-3 py-2.5 text-base font-medium text-foreground outline-none transition focus:ring-0"
                />
              </div>
            </div>

            {/* Meal preferences — windows + eating duration */}
            {(() => {
              const dirty = JSON.stringify(pendingPrefs) !== JSON.stringify(activePrefs);
              const setField = (k: keyof MealPrefs) => (v: string) =>
                setPendingPrefs((p) => ({ ...p, [k]: k === "eatMinutes" ? (Number.isFinite(Number(v)) ? Number(v) : 0) : v } as MealPrefs));
              const applyPending = () => {
                setActivePrefs(pendingPrefs);
                safeLocalStorageSet("routify-meal-prefs", JSON.stringify(pendingPrefs));
                toast.success(lang === "pt" ? "Itinerário recalculado" : "Itinerary recalculated");
              };
              const resetDefaults = async () => {
                safeLocalStorageRemove("routify-meal-prefs");
                try {
                  const remote = await getMealDefaultsFn();
                  const base = { ...DEFAULT_MEAL_PREFS, ...(remote || {}) } as MealPrefs;
                  setPendingPrefs(base);
                  setActivePrefs(base);
                  toast.success(lang === "pt" ? "Defaults repostos" : "Defaults restored");
                } catch { /* ignore */ }
              };
              return (
                <div className="mt-4 rounded-xl border border-border bg-card/40 p-3">
                  <button
                    type="button"
                    onClick={() => setMealPrefsOpen((v) => !v)}
                    className="flex w-full items-center justify-between text-xs font-medium text-muted-foreground"
                    aria-expanded={mealPrefsOpen}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <UtensilsCrossed className="h-3.5 w-3.5" />
                      {lang === "pt" ? "Refeições" : "Meals"}
                      <span className="ml-1 text-[10px] text-muted-foreground/70">
                        {mealPreference === "none"
                          ? (MEAL_CHOICE_LABELS.none[lang] ?? MEAL_CHOICE_LABELS.none.en)
                          : mealPreference === "free_break"
                          ? `${MEAL_CHOICE_LABELS.free_break[lang] ?? MEAL_CHOICE_LABELS.free_break.en} · ${activePrefs.eatMinutes}m`
                          : `${activePrefs.lunchStart}-${activePrefs.lunchEnd} · ${activePrefs.dinnerStart}-${activePrefs.dinnerEnd} · ${activePrefs.eatMinutes}m`}
                      </span>
                    </span>
                    {mealPrefsOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  </button>

                  {mealPrefsOpen && (
                    <div className="mt-3 space-y-2">
                      <div className="space-y-1.5 rounded-lg border border-border bg-background/60 p-2">
                        {(["restaurant", "free_break", "none"] as const).map((opt) => (
                          <label key={opt} className="flex cursor-pointer items-start gap-2 text-xs text-foreground">
                            <input
                              type="radio"
                              name="meal-preference"
                              className="mt-0.5"
                              checked={mealChoice === opt}
                              onChange={() => {
                                setMealChoice(opt);
                                // A escolha manual vence para o pedido em vigor.
                                setMealChoiceForText(lastRequestText);
                                if (opt === "restaurant") {
                                  // Limpa qualquer exclusão herdada de pedidos
                                  // anteriores e devolve tempo real à refeição.
                                  const cleaned = stripFoodExclusions(notes);
                                  if (cleaned !== notes) setNotes(cleaned);
                                  const cleanedRequest = stripFoodExclusions(lastRequestText);
                                  if (cleanedRequest !== lastRequestText) {
                                    setLastRequestText(cleanedRequest);
                                    setMealChoiceForText(cleanedRequest);
                                  }
                                  if (activePrefs.eatMinutes <= 0) {
                                    setActivePrefs((p) => ({ ...p, eatMinutes: DEFAULT_MEAL_PREFS.eatMinutes }));
                                    setPendingPrefs((p) => ({ ...p, eatMinutes: DEFAULT_MEAL_PREFS.eatMinutes }));
                                  }
                                }
                              }}

                            />
                            <span>{MEAL_CHOICE_LABELS[opt][lang] ?? MEAL_CHOICE_LABELS[opt].en}</span>
                          </label>
                        ))}
                      </div>
                      {mealPreference !== "none" && (
                      <div className="grid grid-cols-2 gap-2">
                        <label className="flex flex-col gap-1">
                          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{lang === "pt" ? "Almoço de" : "Lunch from"}</span>
                          <input type="time" value={pendingPrefs.lunchStart} onChange={(e) => setField("lunchStart")(e.target.value)} className="rounded-md border border-border bg-background px-2 py-1 text-sm" />
                        </label>
                        <label className="flex flex-col gap-1">
                          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{lang === "pt" ? "Almoço até" : "Lunch to"}</span>
                          <input type="time" value={pendingPrefs.lunchEnd} onChange={(e) => setField("lunchEnd")(e.target.value)} className="rounded-md border border-border bg-background px-2 py-1 text-sm" />
                        </label>
                        <label className="flex flex-col gap-1">
                          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{lang === "pt" ? "Jantar de" : "Dinner from"}</span>
                          <input type="time" value={pendingPrefs.dinnerStart} onChange={(e) => setField("dinnerStart")(e.target.value)} className="rounded-md border border-border bg-background px-2 py-1 text-sm" />
                        </label>
                        <label className="flex flex-col gap-1">
                          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{lang === "pt" ? "Jantar até" : "Dinner to"}</span>
                          <input type="time" value={pendingPrefs.dinnerEnd} onChange={(e) => setField("dinnerEnd")(e.target.value)} className="rounded-md border border-border bg-background px-2 py-1 text-sm" />
                        </label>
                      </div>
                      )}
                      {mealPreference !== "none" && (
                      <label className="flex flex-col gap-1">
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{lang === "pt" ? "Tempo a comer (min)" : "Eating time (min)"}</span>
                        <input type="number" min={0} max={240} step={5} value={pendingPrefs.eatMinutes} onChange={(e) => setField("eatMinutes")(e.target.value)} className="rounded-md border border-border bg-background px-2 py-1 text-sm" />
                      </label>
                      )}
                      <button
                        type="button"
                        onClick={resetDefaults}
                        className="text-[11px] text-muted-foreground underline-offset-2 hover:underline"
                      >
                        {lang === "pt" ? "Repor defaults" : "Restore defaults"}
                      </button>
                    </div>
                  )}

                  {dirty && (
                    <div className="mt-3 flex items-center justify-between gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-2.5 py-2">
                      <p className="text-[11px] text-foreground">
                        {lang === "pt" ? "Alterações pendentes nas refeições." : "Pending meal changes."}
                      </p>
                      <button
                        type="button"
                        onClick={applyPending}
                        className="rounded-md bg-brand px-2.5 py-1 text-[11px] font-semibold text-white hover:brightness-110"
                      >
                        {lang === "pt" ? "Recalcular agora" : "Recalculate now"}
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}


            {Number.isFinite(locale.lat) && Number.isFinite(locale.lng) && (
              <WeatherBadge lat={locale.lat} lng={locale.lng} numDays={isMultiDay ? numDays : 1} lang={lang} />
            )}

            {isPremium && !isMultiDay && (
              <div className="mt-4 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3">
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-foreground">
                    <Crown className="h-3.5 w-3.5 text-amber-500" /> {MM.arriveBy[lang]}…
                  </span>
                  {!isPremium && <Lock className="h-3.5 w-3.5 text-muted-foreground" />}
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">{MM.arriveByHint[lang]}</p>
                {isPremium ? (
                  <>
                    <TimeInput
                      value={arriveBy}
                      onChange={setArriveBy}
                      className="mt-2"
                      ariaLabel={MM.arriveBy[lang]}
                    />
                    {arriveBy && totalRouteMinutes == null && (
                      <p className="mt-2 text-[11px] text-muted-foreground">{MM.arriveByNeedsRoute[lang]}</p>
                    )}
                    {arriveBy && totalRouteMinutes != null && (() => {
                      const target = buildStartDateTime(startDate, arriveBy) ?? new Date();
                      if (Number.isNaN(target.getTime())) return null;
                      const leave = new Date(target.getTime() - (totalRouteMinutes + 10) * 60000);
                      if (Number.isNaN(leave.getTime())) return null;
                      const leaveStr = `${String(leave.getHours()).padStart(2, "0")}:${String(leave.getMinutes()).padStart(2, "0")}`;
                      // Only "impossible" if the route is longer than the available window
                      // from start time to arrival time. We compare in minutes, not against `now`,
                      // so users can plan for a future day without being blocked.
                      const start = buildStartDateTime(startDate, startTime || "08:00");
                      if (!start || Number.isNaN(start.getTime())) return null;
                      const startMin = start.getHours() * 60 + start.getMinutes();
                      const arriveMin = target.getHours() * 60 + target.getMinutes();
                      const windowMin = arriveMin - startMin;
                      const impossible = windowMin > 0 && totalRouteMinutes + 10 > windowMin;
                      if (impossible) {
                        return (
                          <p className="mt-2 rounded-lg bg-destructive/10 px-2.5 py-2 text-[11px] font-medium text-destructive">
                            {MM.notEnough[lang]}
                          </p>
                        );
                      }
                      // Re-compute schedule shifted to the suggested leave time so we can
                      // show a per-stop backwards plan ("depart 09:14 → arrive Belém 09:38…").
                      const visit = planVisitMinutes ?? (mode === "walk" ? 35 : mode === "car" || mode === "transit" ? 45 : 20);
                      const shifted = buildSchedule({
                        startTime: leaveStr,
                        visitMinutes: visit,
                        stops: displayStops.map((s) => ({ name: s.name, transitMinutes: s.minutes, day: s.day })),
                        mealPrefs: activePrefs,
                        mealPreference,
                        breakfast: breakfastBlock,
                      });

                      const shiftedStops = new Map<number, { arriveAt: Date; leaveAt: Date }>();
                      for (const e of shifted) if (e.type === "stop") shiftedStops.set(e.idx, { arriveAt: e.arriveAt, leaveAt: e.leaveAt });
                      return (
                        <div className="mt-2 rounded-lg bg-background px-3 py-2.5">
                          <p className="text-xs text-foreground">
                            {MM.leaveBy[lang]} <span className="font-bold text-brand">{leaveStr}</span>
                          </p>
                          <p className="mt-0.5 text-[10px] text-muted-foreground">{MM.marginNote[lang]}</p>

                          <div className="mt-3 border-t border-border pt-2.5">
                            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                              {MM.planTitle[lang]}
                            </p>
                            <ol className="space-y-1.5">
                              <li className="flex items-baseline gap-2 text-[11px]">
                                <span className="w-12 shrink-0 font-mono font-bold text-brand">{leaveStr}</span>
                                <span className="text-foreground">{MM.depart[lang]}</span>
                              </li>
                              {displayStops.map((s, i) => {
                                const t = shiftedStops.get(i);
                                if (!t) return null;
                                const transitMin = s.minutes;
                                return (
                                  <li key={`plan-${i}`} className="space-y-0.5">
                                    {typeof transitMin === "number" && transitMin > 0 && (
                                      <div className="flex items-baseline gap-2 text-[10px] text-muted-foreground">
                                        <span className="w-12 shrink-0" />
                                        <span>↓ {MM.transit[lang]} {transitMin} min</span>
                                      </div>
                                    )}
                                    <div className="flex items-baseline gap-2 text-[11px]">
                                      <span className="w-12 shrink-0 font-mono font-bold text-brand">{formatTime(t.arriveAt, lang)}</span>
                                      <span className="min-w-0 flex-1 truncate text-foreground">
                                        <span className="font-semibold">{i + 1}.</span> {s.name}
                                      </span>
                                      <span className="shrink-0 text-[10px] text-muted-foreground">
                                        → {formatTime(t.leaveAt, lang)}
                                      </span>
                                    </div>
                                  </li>
                                );
                              })}
                              <li className="flex items-baseline gap-2 border-t border-border pt-1.5 text-[11px]">
                                <span className="w-12 shrink-0 font-mono font-bold text-emerald-600 dark:text-emerald-400">{arriveBy}</span>
                                <span className="font-semibold text-foreground">🎯 {MM.finalArrival[lang]}</span>
                              </li>
                            </ol>
                          </div>

                          {startTime !== leaveStr && (
                            <button
                              type="button"
                              onClick={() => setStartTime(leaveStr)}
                              className="mt-3 w-full rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-white transition hover:brightness-110"
                            >
                              {MM.useAsStart[lang]}
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      if (!isAuthenticated) {
                        toast.info("Faz login para desbloquear o planeador ⏰");
                        return navigate({ to: "/login" });
                      }
                      setPremiumOpen(true);
                    }}
                    className="mt-2 w-full rounded-xl bg-foreground px-3 py-2 text-xs font-semibold text-background transition hover:opacity-90"
                  >
                    {MM.arriveByLocked[lang]}
                  </button>
                )}
              </div>
            )}

            {isPremium && mealPreference === "restaurant" && (
              <div className="mt-4 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3">
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-foreground">
                    <Crown className="h-3.5 w-3.5 text-amber-500" /> {MM.mealBudget[lang]}
                  </span>
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">{MM.mealBudgetHint[lang]}</p>
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="number"
                    inputMode="decimal"
                    min={1}
                    step={1}
                    placeholder="20"
                    value={maxMealBudget}
                    onChange={(e) => setMaxMealBudget(e.target.value.replace(/[^\d.]/g, ""))}
                    className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none transition focus:border-brand"
                    aria-label={MM.mealBudget[lang]}
                  />
                  <span className="text-sm font-semibold text-muted-foreground">{locale.currency}</span>
                </div>
              </div>
            )}

            {!isPremium && (
              <div className="mt-4 rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent p-4">
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground">
                    <Crown className="h-4 w-4 text-amber-500" /> {MM.premiumTitle[lang]}
                  </span>
                  <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                </div>
                <ul className="mt-2.5 space-y-1.5 text-[12px] text-foreground/80">
                  <li className="flex items-start gap-2"><span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-amber-500" /> {MM.premiumFeatArrive[lang]}</li>
                  <li className="flex items-start gap-2"><span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-amber-500" /> {MM.premiumFeatBudget[lang]}</li>
                  <li className="flex items-start gap-2"><span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-amber-500" /> {MM.premiumFeatModes[lang]}</li>
                  <li className="flex items-start gap-2"><span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-amber-500" /> {MM.premiumFeatResto[lang]}</li>
                </ul>
                <button
                  type="button"
                  onClick={() => {
                    if (!isAuthenticated) {
                      toast.info("Faz login para desbloquear o Premium ✨");
                      return navigate({ to: "/login" });
                    }
                    setPremiumOpen(true);
                  }}
                  className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 text-xs font-semibold text-brand-foreground transition hover:opacity-90"
                  style={{ background: "var(--gradient-hero)" }}
                >
                  <Crown className="h-3.5 w-3.5" /> {MM.premiumUnlock[lang]}
                </button>
              </div>
            )}

            <div className="mt-4">
              <div className="text-xs font-medium text-muted-foreground">{t.transportMode ?? "Modo de transporte"}</div>
              <div className="mt-2 flex flex-wrap gap-2">
                <ModeBtn active={mode === "walk"} onClick={() => changeTravelMode("walk")} icon={<Footprints className="h-4 w-4" />} label={t.walking} />
                <ModeBtn active={mode === "scooter"} onClick={() => changeTravelMode("scooter")} icon={<Zap className="h-4 w-4" />} label={t.scooter} />
                <ModeBtn active={mode === "bike"} onClick={() => changeTravelMode("bike")} icon={<Bike className="h-4 w-4" />} label={t.bike} />
                {/* Carro e transportes existem sempre: sem Premium ficam
                    visíveis e bloqueados (abrem o modal), nunca escondidos. */}
                <ModeBtn
                  active={mode === "car"}
                  locked={!isPremium}
                  onClick={() => (isPremium ? changeTravelMode("car") : setPremiumOpen(true))}
                  icon={<Car className="h-4 w-4" />}
                  label={t.car ?? "Car"}
                />
                <ModeBtn
                  active={mode === "transit"}
                  locked={!isPremium}
                  onClick={() => (isPremium ? changeTravelMode("transit") : setPremiumOpen(true))}
                  icon={<Bus className="h-4 w-4" />}
                  label={t.transit ?? "Transit"}
                />

              </div>
            </div>

            {planMode === "ai" && (<>
            <div className="mt-4">
              <div className="text-xs font-medium text-muted-foreground">{t.interests}</div>
              <div id="interests-chips" className="mt-2 flex flex-wrap gap-1.5">
                {INTERESTS.map((it) => {
                  const active = interests.includes(it.id);
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => toggleInterest(it.id)}
                      className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                        active
                          ? "border-brand bg-brand text-brand-foreground"
                          : "border-border bg-background text-foreground hover:border-brand/60"
                      }`}
                    >
                      {t[it.key]}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-4">
              <label className="text-xs font-medium text-muted-foreground" htmlFor="rt-notes">{t.customNote}</label>
              <input
                id="rt-notes"
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t.customNotePh}
                maxLength={300}
                className="mt-1.5 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none transition focus:border-brand"
              />
            </div>

            {cityConfirmTarget === "generate" &&
              cityConfirmPanel(() => {
                confirmActiveDestination();
                void runGenerate();
              })}

            <button
              type="button"
              onClick={() => requestGenerate()}

              disabled={aiLoading}
              data-testid="generate-itinerary"
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-brand-foreground transition hover:opacity-90 disabled:opacity-60"
              style={{ background: "var(--gradient-hero)" }}
            >
              {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {aiLoading ? t.regenerating : t.regenerate}
            </button>
            </>)}

            {planMode === "manual" && (
              <div className="mt-4">
                <div className="text-xs font-medium text-muted-foreground">{MM.addPlaces[lang]}</div>
                <div className="mt-2 flex items-center gap-2 rounded-xl border border-border bg-background px-3">
                  <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <StopSearchInput
                    center={{ lat: locale.lat, lng: locale.lng }}
                    language={lang}
                    placeholder={MM.poiPh[lang]}
                    onAdd={addManualStop}
                    className="w-full bg-transparent py-2.5 text-sm outline-none placeholder:text-muted-foreground"
                  />
                </div>
                {manualStops.length > 0 && (
                  <ul className="mt-3 space-y-1.5">
                    {manualStops.map((s, i) => (
                      <li
                        key={`${s.name}-${i}`}
                        data-route-stop="true"
                        data-stop-idx={i}
                        draggable={false}
                        onDragStart={(e) => {
                          if (optimizeManualOrder) { e.preventDefault(); return; }
                          stopDragStart(e, i, s.name);
                        }}
                        onDragOver={(e) => { stopDragOver(e); setDragOverStopIdx(i); }}
                        onDrop={(e) => stopDrop(e, i)}
                        onDragEnd={stopDragEnd}
                        className={`flex items-center gap-1.5 rounded-xl border bg-background px-2.5 py-2 text-sm transition ${draggingStopName === s.name ? "z-30 -translate-y-0.5 scale-[1.02] border-brand opacity-90 shadow-[0_8px_24px_-8px_rgba(0,0,0,0.35)] ring-2 ring-brand/40" : dragOverStopIdx === i ? "border-brand shadow-[var(--shadow-soft)]" : "border-border"}`}
                      >
                        <button
                          type="button"
                          onPointerDown={(e) => handleGripPointerDown(e, i, s.name)}
                          onPointerMove={handleGripPointerMove}
                          onPointerUp={handleGripPointerUp}
                          onPointerCancel={handleGripPointerUp}
                          className="rounded p-1 text-muted-foreground transition hover:text-brand disabled:cursor-not-allowed disabled:opacity-30 cursor-grab active:cursor-grabbing touch-none"
                          aria-label={lang === "pt" ? "Arrastar paragem" : "Drag stop"}
                          title={lang === "pt" ? "Arrastar paragem" : "Drag stop"}
                        >
                          <GripVertical className="h-4 w-4" />
                        </button>
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand/10 text-xs font-semibold text-brand">{i + 1}</span>
                        <span className="min-w-0 flex-1 truncate font-medium">{s.name}</span>
                        <button
                          type="button"
                          onClick={() => moveManualStop(i, -1)}
                          disabled={i === 0 || optimizeManualOrder}
                          className="rounded p-1 text-muted-foreground transition hover:text-foreground disabled:opacity-30"
                          aria-label="Mover para cima"
                        >
                          <ChevronUp className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveManualStop(i, 1)}
                          disabled={i === manualStops.length - 1 || optimizeManualOrder}
                          className="rounded p-1 text-muted-foreground transition hover:text-foreground disabled:opacity-30"
                          aria-label="Mover para baixo"
                        >
                          <ChevronDown className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeStop(s.name)}
                          className="rounded p-1 text-muted-foreground transition hover:text-destructive"
                          aria-label={t.removeStop}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <label className="mt-3 flex cursor-pointer items-center gap-2 text-xs font-medium text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={optimizeManualOrder}
                    onChange={(e) => { setOptimizeManualOrder(e.target.checked); setLegMinutes([]); setRouteLegs([]); setRoutePolyline(null); }}
                    className="accent-[var(--brand)]"
                  />
                  {MM.optimize[lang]}
                </label>
              </div>
            )}
          </div>

          <div ref={itineraryRef} className="flex items-baseline justify-between scroll-mt-20">
            <h2 className="text-xl font-semibold tracking-tight">{t.itinerary}</h2>
            <span className="text-xs text-muted-foreground">{displayStops.length} {t.stops}</span>
          </div>
          <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
            {aiLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : planMode === "manual" ? <MapPin className="h-3 w-3 text-brand" /> : <Sparkles className="h-3 w-3 text-brand" />}
            {planMode === "manual" ? MM.yourStops[lang] : t.suggested} <span className="font-medium text-foreground">{locale.displayCity || locale.city}</span>
          </p>

          {displayStops.length > 0 && (
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              <button
                type="button"
                onClick={startInAppNavigation}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-brand bg-brand px-4 py-3 text-sm font-semibold text-brand-foreground shadow-[var(--shadow-soft)] transition hover:opacity-90"
              >
                <Navigation className="h-4 w-4" />
                {t.startNav}
              </button>
              <a
                href={navigationUrlFor(displayStops[0])}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-foreground px-4 py-3 text-sm font-semibold text-background shadow-[var(--shadow-soft)] transition hover:opacity-90"
              >
                <Navigation className="h-4 w-4" />
                {t.openGoogleNav}
              </a>
            </div>
          )}

          {displayStops.length > 0 && logisticsSkipped && (
            <div className="mt-3 flex items-start gap-2 rounded-2xl border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-200">
              <span aria-hidden>ℹ️</span>
              <span>{noLogisticsNotice(lang)}</span>
            </div>
          )}

          {displayStops.length > 0 && routeTotals && (
            <div className="mt-3 rounded-2xl border border-border bg-card p-3">
              <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <RouteIcon className="h-3.5 w-3.5" />
                {lang === "pt" ? "Resumo da rota" : lang === "es" ? "Resumen de la ruta" : lang === "fr" ? "Résumé de l'itinéraire" : lang === "de" ? "Routen-Übersicht" : "Route summary"}
                {routeUpdating && (
                  <span className="ml-auto font-normal normal-case tracking-normal text-[10px] text-muted-foreground">
                    {lang === "pt" ? "a atualizar…" : "updating…"}
                  </span>
                )}
              </div>
              <div
                className={`grid grid-cols-3 gap-2 text-center transition-opacity ${routeUpdating ? "opacity-70" : ""}`}
              >
                <div className="rounded-lg bg-muted/40 px-2 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{lang === "pt" ? "Distância" : lang === "es" ? "Distancia" : lang === "fr" ? "Distance" : lang === "de" ? "Strecke" : "Distance"}</div>
                  <div className="mt-0.5 text-sm font-semibold text-foreground">{formatKm(routeTotals.meters)}</div>
                </div>
                <div className="rounded-lg bg-muted/40 px-2 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{lang === "pt" ? "Em deslocação" : lang === "es" ? "En trayecto" : lang === "fr" ? "En trajet" : lang === "de" ? "Unterwegs" : "Travel time"}</div>
                  <div className="mt-0.5 text-sm font-semibold text-foreground">{formatDurationMin(routeTotals.min)}</div>
                </div>
                <div className="rounded-lg bg-brand/10 px-2 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-brand">{lang === "pt" ? "Custo est." : lang === "es" ? "Coste est." : lang === "fr" ? "Coût est." : lang === "de" ? "Geschätzt" : "Est. cost"}</div>
                  <div className="mt-0.5 text-sm font-semibold text-brand">
                    {routeTotals.cost === 0
                      ? (lang === "pt" ? "Grátis" : lang === "es" ? "Gratis" : lang === "fr" ? "Gratuit" : lang === "de" ? "Gratis" : "Free")
                      : `€${routeTotals.cost.toFixed(2)}`}
                  </div>
                </div>
              </div>
              <p className="mt-1.5 text-[10px] leading-tight text-muted-foreground">
                {mode === "car"
                  ? (lang === "pt" ? "Estimativa de combustível (~0,10 €/km)." : "Fuel estimate (~€0.10/km).")
                  : mode === "transit"
                  ? (lang === "pt" ? "Estimativa de ~1,50 €/bilhete por troço." : "~€1.50 per transit leg.")
                  : mode === "scooter"
                  ? (lang === "pt" ? "Estimativa de aluguer de trotinete." : "Scooter rental estimate.")
                  : (lang === "pt" ? "Sem custos diretos." : "No direct costs.")}
              </p>
            </div>
          )}

          {displayStops.length > 0 && (
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {isAuthenticated && (
                <button
                  type="button"
                  onClick={handleSaveItinerary}
                  className="relative inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-semibold text-foreground transition hover:border-brand"
                >
                  <Bookmark className="h-4 w-4" />
                  {t.saveItinerary ?? "Guardar itinerário"}
                </button>
              )}
              {isAuthenticated && (
                <button
                  type="button"
                  onClick={handleShareRoute}
                  disabled={sharing}
                  className="relative inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-semibold text-foreground transition hover:border-brand disabled:opacity-60"
                >
                  {sharing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
                  {lang === "pt" ? "Partilhar com quem viaja comigo" : "Share with travel companions"}
                </button>
              )}
              <button
                type="button"
                onClick={handleExportIcs}
                className="relative inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-semibold text-foreground transition hover:border-brand"
              >
                <CalendarPlus className="h-4 w-4" />
                {lang === "pt" ? "Calendário (.ics)" : lang === "es" ? "Calendario (.ics)" : lang === "fr" ? "Calendrier (.ics)" : lang === "de" ? "Kalender (.ics)" : "Calendar (.ics)"}
              </button>
              <button
                type="button"
                onClick={() => setPdfDialogOpen(true)}
                className="relative inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-semibold text-foreground transition hover:border-brand"
              >
                <FileDown className="h-4 w-4" />
                {lang === "pt" ? "Exportar PDF" : lang === "es" ? "Exportar PDF" : lang === "fr" ? "Exporter PDF" : lang === "de" ? "PDF exportieren" : "Export PDF"}
              </button>
              {isAuthenticated && (
                <button
                  type="button"
                  onClick={handleAudioGuideAll}
                  className="relative inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-semibold text-foreground transition hover:border-brand sm:col-span-2"
                >
                  <Headphones className="h-4 w-4" />
                  {speakingKey === "__all__" ? (t.stopAudioGuide ?? "Parar áudio") : (t.audioGuide ?? "Audio guide")}
                </button>
              )}
            </div>
          )}


          {displayStops.length > 0 && (
            <p className="mt-1.5 text-[11px] text-muted-foreground">{t.navHint}</p>
          )}





          {navigationActive && activeStop && (
            <section className="mt-3 hidden overflow-hidden rounded-2xl border border-brand/30 bg-card shadow-[var(--shadow-soft)] lg:mt-4 lg:block">
              <div className="border-b border-border bg-brand/10 p-3 lg:p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs font-semibold uppercase tracking-wider text-brand">{t.inAppNav}</div>
                    <h3 className="mt-1 truncate text-lg font-semibold">{activeStopIdx + 1}. {activeStop.name}</h3>
                  </div>
                  <button
                    type="button"
                    onClick={() => setNavigationActive(false)}
                    className="rounded-full p-2 text-muted-foreground transition hover:bg-background hover:text-foreground"
                    aria-label={t.exitNav}
                    title={t.exitNav}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="mt-3 h-2 overflow-hidden rounded-full bg-background">
                  <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${progressPct}%` }} />
                </div>
                <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                  <span>{t.stopProgress} {activeStopIdx + 1}/{guidedStops.length}</span>
                  {typeof activeStop.minutes === "number" && <span>{activeStop.minutes} {t.minShort}</span>}
                </div>
              </div>
              <div className="p-3 lg:p-4">
                <div className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-[11px] font-medium ${userPos ? "bg-brand/10 text-brand" : "bg-muted text-muted-foreground"}`}>
                  <span className={`h-2 w-2 rounded-full ${userPos ? "animate-pulse bg-brand" : "bg-muted-foreground/60"}`} />
                  {userPos ? t.gpsActive : t.awaitingGPS}
                  {distanceToActive != null && (
                    <span className="ml-auto font-semibold text-foreground">
                      {distanceToActive < 1000
                        ? `${Math.round(distanceToActive)} m`
                        : `${(distanceToActive / 1000).toFixed(1)} km`}
                    </span>
                  )}
                </div>
                {autoArrived && (
                  <div className="mt-2 rounded-lg bg-brand px-3 py-2 text-xs font-semibold text-brand-foreground">
                    {t.arrivedAuto}
                  </div>
                )}
                <p className="mt-3 line-clamp-2 text-sm text-foreground/80">{activeStop.blurb}</p>
                {nextStop && (
                  <div className="mt-3 rounded-xl bg-muted p-3 text-sm">
                    <span className="font-medium text-muted-foreground">{t.nextStop}: </span>
                    <span className="font-semibold">{nextStop.name}</span>
                  </div>
                )}
                {(() => {
                  const navLeg = navigationLeg ?? (activeStopIdx > 0 ? navigationRouteLegs[activeStopIdx - 1] : navigationRouteLegs[0]);
                  if (!navLeg) return null;
                  return (
                    <div className="mt-3 rounded-xl border border-border bg-background p-3">
                      <div className="flex items-center justify-between gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        <span>{t.directions}</span>
                        <span>{[navLeg.distance, `${navLeg.minutes} ${t.minShort}`].filter(Boolean).join(" · ")}</span>
                      </div>
                      {navLeg.steps.length > 0 ? (
                        <ol className="mt-2 max-h-32 space-y-1.5 overflow-y-auto pr-1 text-sm text-foreground/85 lg:max-h-64">
                          {navLeg.steps.slice(0, 3).map((step, idx) => (
                            <li key={`${step}-${idx}`} className="flex gap-2">
                              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand/10 text-[11px] font-semibold text-brand">{idx + 1}</span>
                              <span>{step}</span>
                            </li>
                          ))}
                          {navLeg.steps.length > 3 && <li className="text-xs font-medium text-muted-foreground">+{navLeg.steps.length - 3} indicações seguintes</li>}
                        </ol>
                      ) : (
                        <div className="mt-2 flex gap-2 text-sm font-medium text-foreground/85">
                          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand/10 text-[11px] font-semibold text-brand">1</span>
                          <span>{t.followBlueLine}</span>
                        </div>
                      )}
                    </div>
                  );
                })()}
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setActiveStopIdx((cur) => Math.max(cur - 1, 0))}
                    disabled={activeStopIdx === 0}
                    className="rounded-xl border border-border bg-background px-3 py-2 text-sm font-semibold transition hover:border-brand disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {t.previousStop}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (activeStopIdx >= guidedStops.length - 1) setNavigationActive(false);
                      else setActiveStopIdx((cur) => cur + 1);
                    }}
                    className="rounded-xl bg-foreground px-3 py-2 text-sm font-semibold text-background transition hover:opacity-90"
                  >
                    {activeStopIdx >= guidedStops.length - 1 ? t.finishRoute : t.arrivedStop}
                  </button>
                </div>
              </div>
            </section>
          )}

          {schedule.length > 0 && (() => {
            const firstStop = schedule.find((e) => e.type === "stop");
            const last = [...schedule].reverse()[0];
            if (!firstStop || !last) return null;
            const startAt = firstStop.type === "stop" ? firstStop.arriveAt : new Date();
            const endAt = last.type === "stop" ? last.leaveAt : last.endAt;
            // Total ativo = soma das janelas de cada dia. Nunca fim menos
            // início, porque isso incluiria as noites.
            const spans = new Map<string, { start: number; end: number }>();
            for (const e of schedule) {
              const at = e.type === "stop" ? e.arriveAt : e.startAt;
              const dayKey = `${at.getFullYear()}-${at.getMonth()}-${at.getDate()}`;

              const from = (e.type === "stop" ? e.arriveAt : e.startAt).getTime();
              const to = (e.type === "stop" ? e.leaveAt : e.endAt).getTime();
              const cur = spans.get(dayKey);
              spans.set(dayKey, {
                start: cur ? Math.min(cur.start, from) : from,
                end: cur ? Math.max(cur.end, to) : to,
              });
            }
            const totalMin = Math.max(
              0,
              Math.round(
                [...spans.values()].reduce((sum, s) => sum + Math.max(0, s.end - s.start), 0) / 60000,
              ),
            );

            const h = Math.floor(totalMin / 60);
            const m = totalMin % 60;
            const totalLabel = h > 0 ? (m > 0 ? `${h}h ${m}min` : `${h}h`) : `${m}min`;
            const budgetMin = Math.round(activeRouteHours * 60);
            const overMin = totalMin - budgetMin;
            const overThreshold = 15; // tolerance for transit rounding
            const showOver = !isMultiDay && overMin > overThreshold;
            return (
              <>
                <div className="mt-4 flex flex-wrap items-center gap-2 rounded-xl border border-border bg-muted/40 px-3 py-2 text-xs">
                  <Clock className="h-3.5 w-3.5 text-brand" />
                  <span className="font-semibold text-foreground">
                    {formatTime(startAt, lang)} → {formatTime(endAt, lang)}
                  </span>
                  <span className="text-muted-foreground">•</span>
                  <span className="text-muted-foreground">Total {totalLabel}</span>
                  {breaks.length > 0 && (
                    <>
                      <span className="text-muted-foreground">•</span>
                      <span className="text-muted-foreground">{breaks.length} {breaks.length === 1 ? "pausa" : "pausas"}</span>
                    </>
                  )}
                </div>
                {showOver && (
                  <div className="mt-2 flex items-start gap-2 rounded-xl border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-200">
                    <span aria-hidden>⚠️</span>
                    <span>
                      {lang === "pt"
                        ? `Itinerário excede o tempo disponível em ${overMin >= 60 ? `${Math.floor(overMin / 60)}h ${overMin % 60}min` : `${overMin}min`}. Remove uma paragem ou aumenta o tempo disponível.`
                        : `Itinerary exceeds your available time by ${overMin >= 60 ? `${Math.floor(overMin / 60)}h ${overMin % 60}min` : `${overMin}min`}. Remove a stop or extend the available time.`}
                    </span>
                  </div>
                )}
              </>
            );
          })()}


          {displayStops.length > 0 && (() => {
            // Orçamento vem do snapshot canónico (mesma `routeRevision` da
            // agenda, mapa, PDF e ICS). Nunca é recalculado durante o render.
            const budget = routeSnapshot?.budget ?? routeBudget;
            const amountOf = (key: "attractions" | "meals" | "transport" | "accommodation") =>
              budget.categories.find((c) => c.key === key)?.amount ?? 0;
            const attractions = amountOf("attractions");
            const meals = amountOf("meals");
            const transport = amountOf("transport");
            const hasTransport = mode === "car" || mode === "transit";
            const total = budget.total;
            const transportLabel =
              mode === "car"
                ? (lang === "pt" ? "Estacionamento" : "Parking")
                : mode === "transit"
                ? (lang === "pt" ? "Passes diários" : "Day passes")
                : (lang === "pt" ? "Transporte" : "Transport");
            const notNeeded = lang === "pt" ? "Não necessário" : "Not needed";

            return (
              <div className="mt-3 overflow-hidden rounded-2xl border border-border bg-card">
                <div className="flex items-center justify-between gap-2 border-b border-border bg-muted/30 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Wallet className="h-4 w-4 text-brand" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-foreground">
                      {lang === "pt" ? "Orçamento estimado" : "Estimated budget"}
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-brand/10 px-1.5 py-0.5 text-[10px] font-bold text-brand">
                      <Crown className="h-2.5 w-2.5" /> PRO
                    </span>
                  </div>
                  {isPremium && (
                    <span className="text-sm font-bold text-foreground">
                      ≈ {formatPriceDual(total, locale.currency, lang)}
                    </span>
                  )}
                </div>
                {isPremium ? (
                  <>
                  <div className={`grid ${budget.accommodationIncluded ? "grid-cols-4" : "grid-cols-3"} divide-x divide-border text-center text-xs`}>

                    <a
                      href={`https://www.google.com/search?q=${encodeURIComponent(`bilhetes atrações ${locale.city} tickets`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-2 py-2.5 transition hover:bg-muted/40 active:bg-muted/60"
                      title={lang === "pt" ? "Ver onde comprar bilhetes" : "Find where to buy tickets"}
                    >
                      <div className="text-muted-foreground">{lang === "pt" ? "Atrações" : "Attractions"}</div>
                      <div className="mt-0.5 font-semibold text-foreground underline-offset-2 hover:underline">{formatPriceDual(attractions, locale.currency, lang)}</div>
                    </a>
                    <div className="px-2 py-2.5">
                      <div className="text-muted-foreground">{lang === "pt" ? "Refeições" : "Meals"}</div>
                      <div className="mt-0.5 font-semibold text-foreground">{formatPriceDual(meals, locale.currency, lang)}</div>
                    </div>
                    {hasTransport ? (() => {
                      // Direct purchase links for transit passes by city — see
                      // src/lib/transit-passes.ts for the curated list (~140
                      // cities). Falls back to Omio for unknown cities; static
                      // data, no API/AI cost per route.
                      const officialUrl = getTransitPassUrl(locale.city);
                      const buyUrl = mode === "transit"
                        ? (officialUrl ?? `https://www.omio.com/search-frontend/results?destinationCity=${encodeURIComponent(locale.city)}`)
                        : `https://www.google.com/search?q=${encodeURIComponent(`${locale.city} parking app pay`)}`;
                      return (
                        <a
                          href={buyUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-2 py-2.5 transition hover:bg-muted/40 active:bg-muted/60"
                          title={lang === "pt" ? "Comprar passe" : "Buy pass"}
                        >
                          <div className="text-muted-foreground">{transportLabel}</div>
                          <div className="mt-0.5 font-semibold text-foreground underline-offset-2 hover:underline">
                            {formatPriceDual(transport, locale.currency, lang)}
                          </div>
                        </a>
                      );
                    })() : (
                      <div className="px-2 py-2.5">
                        <div className="text-muted-foreground">{transportLabel}</div>
                        <div className="mt-0.5 font-semibold text-foreground">{notNeeded}</div>
                      </div>
                    )}
                    {budget.accommodationIncluded && (
                      <div className="px-2 py-2.5">
                        <div className="text-muted-foreground">{lang === "pt" ? "Alojamento" : "Accommodation"}</div>
                        <div className="mt-0.5 font-semibold text-foreground">
                          {formatPriceDual(amountOf("accommodation"), locale.currency, lang)}
                        </div>
                      </div>
                    )}
                  </div>
                  {!budget.accommodationIncluded && (
                    <p className="border-t border-border px-3 py-2 text-[11px] text-muted-foreground">
                      {lang === "pt" ? "Alojamento não incluído" : "Accommodation not included"}
                    </p>
                  )}
                  </>


                ) : (
                  <button
                    type="button"
                    onClick={() => requirePremium(() => {})}
                    className="flex w-full items-center justify-center gap-2 px-3 py-3 text-xs font-medium text-muted-foreground transition hover:bg-muted/40"
                  >
                    <Lock className="h-3 w-3" />
                    {lang === "pt" ? "Desbloquear orçamento da viagem" : "Unlock trip budget"}
                  </button>
                )}
              </div>
            );
          })()}


          {/* Cartão compacto único: recomendação de bilhete + mobilidade
              oficial. Free/Premium veem o teaser bloqueado com CTA para o Pro;
              os avisos essenciais (ZTL, portagens…) ficam sempre visíveis. */}
          {displayStops.length > 0 && (
            <MobilityTicketCard
              city={locale.city}
              transport={mode}
              lang={lang}
              locked={effectiveTier !== "pro"}
              title={
                mode === "transit"
                  ? lang === "pt"
                    ? `Bilhete recomendado: passe ${numDays > 1 ? `${numDays} dias` : "diário"}`
                    : `Recommended ticket: ${numDays > 1 ? `${numDays}-day` : "day"} pass`
                  : undefined
              }
              subtitle={
                mode === "transit"
                  ? lang === "pt"
                    ? `Compensa face a bilhetes avulsos para ${displayStops.length} paragens em ${locale.city}.`
                    : `Cheaper than single tickets for ${displayStops.length} stops in ${locale.city}.`
                  : undefined
              }
            />
          )}


          {displayStops.length === 0 && !aiLoading && (
            <div className="mt-4 flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border bg-card px-6 py-10 text-center">
              {planMode === "manual" ? <MapPin className="h-6 w-6 text-brand" /> : <Sparkles className="h-6 w-6 text-brand" />}
              <p className="text-sm font-medium text-foreground">
                {planMode === "manual"
                  ? MM.emptyTitle[lang]
                  : lang === "pt" ? "Ainda não geraste um itinerário" : lang === "fr" ? "Aucun itinéraire généré" : lang === "es" ? "Aún no has generado un itinerario" : lang === "de" ? "Noch keine Route erstellt" : lang === "ja" ? "まだ旅程が生成されていません" : "No itinerary generated yet"}
              </p>
              <p className="text-xs text-muted-foreground">
                {planMode === "manual"
                  ? MM.emptyHint[lang]
                  : lang === "pt" ? "Escolhe a cidade, duração e interesses acima e carrega em gerar." : lang === "fr" ? "Choisissez la ville, la durée et les centres d'intérêt ci-dessus, puis générez." : lang === "es" ? "Elige la ciudad, duración e intereses arriba y pulsa generar." : lang === "de" ? "Wähle oben Stadt, Dauer und Interessen und tippe auf Generieren." : lang === "ja" ? "上で都市・時間・興味を選んで生成してください。" : "Pick the city, duration and interests above, then tap generate."}
              </p>
            </div>
          )}

          {planMode === "ai" && displayStops.length > 0 && (
            <div data-testid="recommended-stops" className="mt-4 rounded-2xl border border-border bg-card p-3 space-y-3">
              <div>
                <div className="text-xs font-medium text-muted-foreground">{MM.addStop[lang]}</div>
                <div className="mt-2 flex items-center gap-2 rounded-xl border border-border bg-background px-3">
                  <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <StopSearchInput
                    center={{ lat: locale.lat, lng: locale.lng }}
                    language={lang}
                    placeholder={MM.poiPh[lang]}
                    onAdd={addAiStop}
                    className="w-full bg-transparent py-2.5 text-sm outline-none placeholder:text-muted-foreground"
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={openSuggestMore}
                  disabled={extraLoading}
                  className="inline-flex items-center gap-1.5 rounded-full border border-brand/30 bg-brand/10 px-3 py-1.5 text-xs font-semibold text-brand transition hover:bg-brand/20 disabled:opacity-60"
                >
                  {extraLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                  {extraLoading ? MM.suggesting[lang] : MM.suggestMore[lang]}
                </button>
                <button
                  type="button"
                  onClick={optimizeOrder}
                  disabled={displayStops.length < 3}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-semibold text-foreground transition hover:border-brand hover:text-brand disabled:opacity-40"
                >
                  <Wand2 className="h-3.5 w-3.5" />
                  {MM.optimizeNow[lang]}
                </button>
              </div>
            </div>
          )}

          {stayRequested && stayHotels && (
            <div className="mt-4 rounded-2xl border border-border/70 bg-muted/30 p-4">
              <p className="text-sm font-bold text-foreground">{accommodationSectionTitle(lang)}</p>
              {stayHotels === "loading" && (
                <p className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> …
                </p>
              )}
              {stayHotels === "none" && (
                <p className="mt-2 text-sm text-muted-foreground">
                  {accommodationUnavailableCopy(lang, locale.city)}
                </p>
              )}
              {hotelSelection.status === "selected" && (
                <div className="mt-2 flex flex-wrap items-center gap-2 rounded-xl border border-brand/40 bg-brand/5 px-3 py-2">
                  <span className="text-sm font-semibold text-foreground">
                    {lang === "pt" ? "Hotel selecionado" : "Selected hotel"}: {hotelSelection.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => setHotelSelection(clearHotelSelection())}
                    className="rounded-lg border border-border px-2 py-1 text-xs font-semibold hover:border-foreground/40"
                  >
                    {lang === "pt" ? "Remover seleção" : "Remove selection"}
                  </button>
                </div>
              )}
              {Array.isArray(stayHotels) && (
                <ul className="mt-2 space-y-2">
                  {stayHotels.map((hotel) => {
                    const isActive =
                      hotelSelection.status === "selected" && hotelSelection.placeId === hotel.placeId;
                    return (
                    <li key={hotel.placeId ?? hotel.name} className="rounded-xl border border-border/60 bg-background px-3 py-2">
                      <p className="text-sm font-semibold text-foreground">
                        {hotel.name}
                        {!isActive && hotelSelection.status === "selected" && (
                          <span className="ml-2 text-[11px] font-medium text-muted-foreground">
                            {lang === "pt" ? "alternativa" : "alternative"}
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">{hotel.blurb}</p>
                      <button
                        type="button"
                        onClick={() => {
                          // Usa exclusivamente os dados já sugeridos: sem IA,
                          // sem nova recolha e sem consumo de créditos.
                          const result = selectSuggestedHotel({
                            name: hotel.name,
                            placeId: hotel.placeId,
                            lat: hotel.lat,
                            lng: hotel.lng,
                            priceLocal: hotel.priceLocal,
                          });
                          if (result.status === "selected") setHotelSelection(result.selection);
                        }}
                        disabled={isActive || !hotel.placeId || typeof hotel.lat !== "number" || typeof hotel.lng !== "number"}
                        className="mt-2 rounded-lg border border-brand/50 px-2 py-1 text-xs font-semibold text-brand disabled:opacity-50"
                      >
                        {isActive
                          ? (lang === "pt" ? "Hotel selecionado" : "Hotel selected")
                          : hotelSelection.status === "selected"
                            ? (lang === "pt" ? "Alterar para este hotel" : "Switch to this hotel")
                            : (lang === "pt" ? "Escolher este hotel" : "Choose this hotel")}
                      </button>
                      {hotel.googleMapsUri && (
                        <a
                          href={hotel.googleMapsUri}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-1 inline-block text-xs font-semibold text-brand underline"
                        >
                          Google Maps
                        </a>
                      )}
                    </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {routeConstraintWarning && (
            <div
              role="alert"
              className="mt-4 flex items-start gap-2 rounded-xl border border-amber-300/70 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-700/70 dark:bg-amber-950/40 dark:text-amber-100"
            >
              <span aria-hidden="true">⚠️</span>
              <span>{routeConstraintWarning}</span>
            </div>
          )}

          {/* Roteiro mais leve: rota válida abaixo do alvo de ritmo — nota de
              tempo livre, sem bloquear nem recolher a rota. */}
          {interestFillNotice && (
            <div
              data-testid="interest-fill-notice"
              className="mt-3 flex items-start gap-2 rounded-xl border border-border/70 bg-muted/50 px-3 py-2 text-sm text-muted-foreground"
            >
              <span aria-hidden="true">✨</span>
              <span>
                {lang === "pt"
                  ? "Os interesses escolhidos deram poucos locais nesta zona, por isso completámos o teu dia com os melhores destaques da cidade."
                  : "Your chosen interests had few places in this area, so we completed your day with the city's top highlights."}
              </span>
            </div>
          )}

          {calmSmallPlaceNotice && (
            <div
              data-testid="calm-small-place-notice"
              className="mt-3 flex flex-col gap-2 rounded-xl border border-border/70 bg-muted/50 px-3 py-2 text-sm text-muted-foreground"
            >
              <div className="flex items-start gap-2">
                <span aria-hidden="true">🌿</span>
                <span>
                  {lang === "pt"
                    ? `Roteiro calmo em ${calmSmallPlaceNotice.area}: ${calmSmallPlaceNotice.stops} locais para viveres o sítio ao teu ritmo, sem pressas nem deslocações forçadas.`
                    : `Calm itinerary in ${calmSmallPlaceNotice.area}: ${calmSmallPlaceNotice.stops} places to enjoy at your own pace, with no rush or forced trips.`}
                </span>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="self-start"
                disabled={aiLoading}
                onClick={() => {
                  const radius = Math.min(
                    WIDEN_MAX_RADIUS_M,
                    Math.max(15000, calmSmallPlaceNotice.areaRadiusMeters * 2),
                  );
                  setCalmSmallPlaceNotice(null);
                  widenScarcityArea(radius);
                }}
              >
                {lang === "pt" ? "Queres ver mais? Alargar aos arredores" : "Want more? Include the surroundings"}
              </Button>
            </div>
          )}

          {itineraryPaceNotice && !calmSmallPlaceNotice && (
            <div
              data-testid="itinerary-pace-notice"
              className="mt-3 flex items-start gap-2 rounded-xl border border-border/70 bg-muted/50 px-3 py-2 text-sm text-muted-foreground"
            >
              <span aria-hidden="true">🕐</span>
              <span>
                {lang === "pt"
                  ? `Roteiro mais leve: ${itineraryPaceNotice.stops} de ~${itineraryPaceNotice.target} visitas típicas para este ritmo. Fica tempo livre — podes alargar a zona ou escolher um interesse para mais paragens.`
                  : `Lighter itinerary: ${itineraryPaceNotice.stops} of ~${itineraryPaceNotice.target} typical stops for this pace. You'll have free time — widen the area or pick an interest for more stops.`}
              </span>
            </div>
          )}

          <ol className="mt-4 space-y-3">
            {routeStart && (
              <li className="flex items-center gap-3 rounded-2xl border border-brand/35 bg-brand/5 px-4 py-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand text-sm font-bold text-brand-foreground shadow">1</span>
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wide text-brand">
                    {lang === "pt" ? "Ponto de partida" : "Route start"}
                  </p>
                  <p className="truncate font-semibold">
                    {(("name" in routeStart ? routeStart.name : "") as string) || (lang === "pt" ? "Localização atual" : "Current location")}
                  </p>
                </div>
              </li>
            )}

            {/* Sem rota publicada: skeleton. NUNCA se renderiza o rascunho. */}
            {agendaPending && (
              <li data-testid="agenda-skeleton" className="space-y-3" aria-busy="true">
                {[0, 1, 2].map((k) => (
                  <div key={k} className="h-24 animate-pulse rounded-2xl border border-border bg-muted/50" />
                ))}
              </li>
            )}

            {/* Fonte única da agenda: os dias e horas da rota publicada. */}
            {agendaItems.map((item) => {
              const { stop: s, index: i, times, breaksBefore, breaksAfter } = item;
              const showDayHeader = isMultiDay && item.showDayHeader;
              const dayHeader = showDayHeader ? (
                <div className="mb-2 mt-1 flex items-center gap-2">
                  <div className="flex h-7 items-center justify-center rounded-full bg-brand px-3 text-xs font-semibold text-brand-foreground shadow">
                    {lang === "pt" ? "Dia" : "Day"} {item.dayIndex}
                  </div>
                  <div className="h-px flex-1 bg-border" />
                </div>
              ) : null;
              if (!s || !times) {
                return (
                  <div key={item.key}>
                    {dayHeader}
                    <p className="mb-3 text-xs text-muted-foreground">
                      {lang === "pt" ? "Sem visitas neste dia." : "No visits on this day."}
                    </p>
                  </div>
                );
              }
              const speakKey = `stop-${i}`;
              const isSpeaking = speakingKey === speakKey;
              const breakAfter = breaksAfter[0]; // back-compat reference; rendering iterates breaksAfter
              return (
              <div key={item.key}>
              {dayHeader}
              {breaksBefore.map((bk) => renderBreakCard(bk, "mb-3"))}

              <li
                data-route-stop="true"
                data-stop-idx={i}
                draggable={false}
                onDragStart={(e) => stopDragStart(e, i, s.name)}
                onDragOver={(e) => { stopDragOver(e); setDragOverStopIdx(i); }}
                onDrop={(e) => stopDrop(e, i)}
                onDragEnd={stopDragEnd}
                className={`group overflow-hidden rounded-2xl border bg-card transition hover:border-brand hover:shadow-[var(--shadow-soft)] ${draggingStopName === s.name ? "z-30 -translate-y-1 scale-[1.02] border-brand opacity-90 shadow-[0_10px_30px_-8px_rgba(0,0,0,0.35)] ring-2 ring-brand/40" : dragOverStopIdx === i ? "border-brand shadow-[var(--shadow-soft)]" : navigationActive && activeStop?.name === s.name ? "border-brand shadow-[var(--shadow-soft)]" : "border-border"}`}
              >
                <button
                  type="button"
                  onClick={() => setDetailStop(s)}
                  className="block w-full text-left"
                >
                <div className="flex flex-col sm:flex-row">
                  <div className="relative h-40 w-full shrink-0 bg-muted sm:h-36 sm:w-36">

                    {s.photoUrl && !failedPhotoKeys.has(`${s.placeId ?? s.name}|${s.photoUrl}`) ? (
                      <img
                        src={s.photoUrl}
                        alt={s.name}
                        loading={i < 3 ? "eager" : "lazy"}
                        fetchPriority={i < 2 ? "high" : "auto"}
                        decoding="async"
                        onError={() => setFailedPhotoKeys((current) => {
                          const key = `${s.placeId ?? s.name}|${s.photoUrl}`;
                          if (current.has(key)) return current;
                          const next = new Set(current);
                          next.add(key);
                          return next;
                        })}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      // Sem foto disponível: marcador discreto, nunca um ícone partido.
                      <div className="flex h-full w-full flex-col items-center justify-center gap-1 bg-muted text-muted-foreground">
                        <span className="text-2xl font-semibold text-brand/70">
                          {s.name.trim().charAt(0).toUpperCase()}
                        </span>
                        <span className="px-2 text-center text-[10px] uppercase tracking-wider opacity-70">
                          {s.category}
                        </span>
                      </div>
                    )}
                    <div className="absolute left-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-background/90 text-xs font-semibold text-brand shadow">
                      {i + 1 + routeNumberOffset}
                    </div>
                    {times && (
                      <div className="absolute bottom-2 left-2 rounded-full bg-foreground/85 px-2 py-0.5 text-[10px] font-semibold text-background shadow">
                        {formatTime(times.arriveAt, lang)}
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1 p-4">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="truncate font-semibold">{s.name}</h3>
                      <div className="flex items-center gap-1">
                        {s.rating > 0 && (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Star className="h-3 w-3 fill-[var(--sun)] text-[var(--sun)]" />{s.rating.toFixed(1)}
                          </span>
                        )}
                        {isAuthenticated && (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => { e.stopPropagation(); handleListenStop(s, speakKey); }}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); e.preventDefault(); handleListenStop(s, speakKey); } }}
                            aria-label="Listen AI Guide"
                            title="Listen AI Guide"
                            className={`relative rounded-full p-1 transition cursor-pointer ${isSpeaking ? "bg-brand text-brand-foreground" : "text-muted-foreground hover:bg-brand/10 hover:text-brand"}`}
                          >
                            <Headphones className="h-4 w-4" />
                          </span>
                        )}
                        {(planMode === "ai" || planMode === "manual") && (
                          <>
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={(e) => e.stopPropagation()}
                              onPointerDown={(e) => { e.stopPropagation(); handleGripPointerDown(e, i, s.name); }}
                              onPointerMove={(e) => { e.stopPropagation(); handleGripPointerMove(e); }}
                              onPointerUp={(e) => { e.stopPropagation(); handleGripPointerUp(e); }}
                              onPointerCancel={(e) => { e.stopPropagation(); handleGripPointerUp(e); }}
                              onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && i < displayStops.length - 1) { e.stopPropagation(); e.preventDefault(); if (planMode === "manual") { moveManualStop(i, 1); } else { moveAiStop(i, 1); } } }}
                              aria-label={lang === "pt" ? "Arrastar paragem" : "Drag stop"}
                              title={lang === "pt" ? "Arrastar paragem" : "Drag stop"}
                              className="rounded-full p-1 text-muted-foreground transition hover:bg-brand/10 hover:text-brand cursor-grab active:cursor-grabbing touch-none"
                            >
                              <GripVertical className="h-4 w-4" />
                            </span>
                            {planMode === "ai" && (
                              <>
                                <span
                                  role="button"
                                  tabIndex={0}
                                  onClick={(e) => { e.stopPropagation(); moveAiStop(i, -1); }}
                                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); e.preventDefault(); moveAiStop(i, -1); } }}
                                  aria-label={lang === "pt" ? "Mover para cima" : "Move up"}
                                  title={lang === "pt" ? "Mover para cima" : "Move up"}
                                  className={`rounded-full p-1 transition cursor-pointer ${i === 0 ? "pointer-events-none opacity-30" : "text-muted-foreground hover:bg-brand/10 hover:text-brand"}`}
                                >
                                  <ChevronUp className="h-4 w-4" />
                                </span>
                                <span
                                  role="button"
                                  tabIndex={0}
                                  onClick={(e) => { e.stopPropagation(); moveAiStop(i, 1); }}
                                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); e.preventDefault(); moveAiStop(i, 1); } }}
                                  aria-label={lang === "pt" ? "Mover para baixo" : "Move down"}
                                  title={lang === "pt" ? "Mover para baixo" : "Move down"}
                                  className={`rounded-full p-1 transition cursor-pointer ${i === displayStops.length - 1 ? "pointer-events-none opacity-30" : "text-muted-foreground hover:bg-brand/10 hover:text-brand"}`}
                                >
                                  <ChevronDown className="h-4 w-4" />
                                </span>
                              </>
                            )}
                          </>
                        )}
                        {planMode !== "manual" && spareStops.length > 0 && (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => { e.stopPropagation(); setSwapFor((cur) => (cur === s.name ? null : s.name)); }}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); e.preventDefault(); setSwapFor((cur) => (cur === s.name ? null : s.name)); } }}
                            aria-label={lang === "pt" ? "Trocar sítio" : "Swap place"}
                            title={lang === "pt" ? "Trocar sítio" : "Swap place"}
                            className="rounded-full p-1 text-muted-foreground transition hover:bg-brand/10 hover:text-brand cursor-pointer"
                          >
                            <Shuffle className="h-4 w-4" />
                          </span>
                        )}
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => { e.stopPropagation(); removeStop(s.name); }}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); e.preventDefault(); removeStop(s.name); } }}
                          aria-label={t.removeStop}
                          title={t.removeStop}
                          className="rounded-full p-1 text-muted-foreground transition hover:bg-destructive/10 hover:text-destructive cursor-pointer"
                        >
                          <X className="h-4 w-4" />
                        </span>
                      </div>
                    </div>
                    <div className="mt-0.5 text-xs uppercase tracking-wider text-muted-foreground">{s.category}</div>
                    {swapFor === s.name && (
                      <div className="mt-2 rounded-xl border border-border bg-muted/30 p-2" onClick={(e) => e.stopPropagation()}>
                        <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                          {lang === "pt" ? "Alternativas perto daqui" : "Nearby alternatives"}
                        </div>
                        {swapOptionsFor(s).length === 0 ? (
                          <p className="text-xs text-muted-foreground">{lang === "pt" ? "Sem mais alternativas preparadas." : "No more alternatives prepared."}</p>
                        ) : (
                          <div className="flex flex-col gap-1.5">
                            {swapOptionsFor(s).map((alt) => (
                              <button
                                key={alt.placeId ?? alt.name}
                                type="button"
                                onClick={() => swapStop(s.name, alt)}
                                className="flex items-center justify-between gap-2 rounded-lg border border-border bg-background px-2.5 py-1.5 text-left transition hover:border-brand"
                              >
                                <span className="min-w-0 flex-1">
                                  <span className="block truncate text-xs font-medium text-foreground">{alt.name}</span>
                                  <span className="block truncate text-[11px] text-muted-foreground">{alt.category}</span>
                                </span>
                                <span className="shrink-0 text-[11px] font-semibold text-brand">{lang === "pt" ? "Usar" : "Use"}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {specialtyLineFor(s, lang) && (
                      <div className="mt-1 text-xs font-medium text-brand">
                        {specialtyLineFor(s, lang)}
                        {s.specialtyEvidence && (
                          <span className="ml-1 font-normal text-muted-foreground">
                            · {s.specialtyEvidence}
                          </span>
                        )}
                      </div>
                    )}
                    <p className="mt-2 line-clamp-2 text-sm text-foreground/80">{s.blurb}</p>
                    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                      {times && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-foreground/10 px-2.5 py-1 font-semibold text-foreground">
                          <Clock className="h-3 w-3" />
                          {formatTime(times.arriveAt, lang)} – {formatTime(times.leaveAt, lang)}
                        </span>
                      )}
                      {typeof s.minutes === "number" && s.minutes > 0 && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 font-medium">
                          {mode === "walk" && <Footprints className="h-3 w-3" />}
                          {mode === "scooter" && <Zap className="h-3 w-3" />}
                          {mode === "bike" && <Bike className="h-3 w-3" />}
                          {mode === "car" && <Car className="h-3 w-3" />}
                          {mode === "transit" && <Bus className="h-3 w-3" />}
                          {s.minutes} {t.minShort}
                        </span>
                      )}
                      {planMode === "ai" && s.priceLocal > 0 && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-brand/10 px-2.5 py-1 font-medium text-brand">
                          {formatPriceDual(s.priceLocal, locale.currency, lang)}
                        </span>
                      )}
                      {mode === "car" && (
                        isPremium ? (
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`parking near ${s.name} ${locale.city}`)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2.5 py-1 font-medium text-amber-700 transition hover:bg-amber-500/25 dark:text-amber-400"
                          >
                            <ParkingCircle className="h-3 w-3" />
                            {t.parkingNearby ?? "Parking nearby"}
                          </a>
                        ) : (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); requirePremium(() => {}); }}
                            className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 font-medium text-muted-foreground transition hover:bg-amber-500/10 hover:text-amber-600"
                          >
                            <ParkingCircle className="h-3 w-3" />
                            {t.parkingNearby ?? "Parking"}
                            <Lock className="h-2.5 w-2.5" />
                          </button>
                        )
                      )}
                      <a
                        href={mapsUrlFor(s)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 rounded-full bg-foreground px-2.5 py-1 font-medium text-background transition hover:opacity-90 sm:ml-auto"
                      >
                        <Navigation className="h-3 w-3" />
                        {t.openInMaps}
                      </a>
                    </div>
                  </div>
                </div>
                </button>
                {/* Personal notes per stop (session-only) */}
                {(openNoteFor === s.name || stopNotes[s.name]) ? (
                  <div className="border-t border-dashed border-border bg-muted/20 px-4 py-2.5">
                    <div className="mb-1 flex items-center justify-between">
                      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        <StickyNote className="h-3 w-3" />
                        {lang === "pt" ? "Nota pessoal" : lang === "es" ? "Nota personal" : lang === "fr" ? "Note personnelle" : lang === "de" ? "Persönliche Notiz" : "Personal note"}
                      </span>
                      {stopNotes[s.name] && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setStopNotes((cur) => { const n = { ...cur }; delete n[s.name]; return n; }); setOpenNoteFor(null); }}
                          className="text-[11px] text-muted-foreground hover:text-destructive"
                        >
                          {lang === "pt" ? "Apagar" : "Clear"}
                        </button>
                      )}
                    </div>
                    <textarea
                      value={stopNotes[s.name] ?? ""}
                      onChange={(e) => setStopNotes((cur) => ({ ...cur, [s.name]: e.target.value }))}
                      onBlur={() => setOpenNoteFor(null)}
                      autoFocus={openNoteFor === s.name}
                      placeholder={lang === "pt" ? "ex.: Levar bilhete antecipado" : "e.g. Bring tickets in advance"}
                      rows={2}
                      className="w-full resize-none rounded-lg border border-border bg-background px-2.5 py-1.5 text-xs outline-none transition focus:border-brand"
                    />
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setOpenNoteFor(s.name); }}
                    className="flex w-full items-center gap-1.5 border-t border-dashed border-border px-4 py-1.5 text-[11px] font-medium text-muted-foreground transition hover:bg-muted/30 hover:text-brand"
                  >
                    <StickyNote className="h-3 w-3" />
                    {lang === "pt" ? "Adicionar nota" : lang === "es" ? "Añadir nota" : lang === "fr" ? "Ajouter une note" : lang === "de" ? "Notiz hinzufügen" : "Add a note"}
                  </button>
                )}
              </li>

              {breaksAfter.map((bk) => renderBreakCard(bk, "mt-3"))}


              </div>
              );
            })}
          </ol>

        </aside>
      </main>

      <footer className="mx-auto max-w-6xl px-4 pb-10 text-center text-xs text-muted-foreground">
        Routify
      </footer>

      <Dialog open={!!detailStop} onOpenChange={(open) => { if (!open) setDetailStop(null); }}>
        <DialogContent className="max-w-lg p-0 max-h-[90vh] overflow-y-auto overscroll-contain">
          {detailStop && (
            <>
              <div className="relative h-56 w-full bg-muted sm:h-72">
                {detailStop.photoUrl ? (
                  <img
                    src={detailStop.photoUrl}
                    alt={detailStop.name}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full flex-col items-center justify-center gap-1 bg-muted text-muted-foreground">
                    <span className="text-3xl font-semibold text-brand/70">
                      {detailStop.name.trim().charAt(0).toUpperCase()}
                    </span>
                    <span className="text-[11px] uppercase tracking-wider opacity-70">
                      {detailStop.category}
                    </span>
                  </div>
                )}
              </div>
              <div className="p-5">
                <DialogHeader>
                  <DialogTitle className="text-xl">{detailStop.name}</DialogTitle>
                  <DialogDescription className="text-xs uppercase tracking-wider">
                    {detailStop.category}
                  </DialogDescription>
                </DialogHeader>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                  {detailStop.rating > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 font-medium">
                      <Star className="h-3 w-3 fill-[var(--sun)] text-[var(--sun)]" />
                      {detailStop.rating.toFixed(1)}
                    </span>
                  )}
                  {typeof detailStop.minutes === "number" && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 font-medium">
                      {mode === "walk" && <Footprints className="h-3 w-3" />}
                      {mode === "scooter" && <Zap className="h-3 w-3" />}
                      {mode === "bike" && <Bike className="h-3 w-3" />}
                      {mode === "car" && <Car className="h-3 w-3" />}
                      {mode === "transit" && <Bus className="h-3 w-3" />}
                      {detailStop.minutes} {t.minShort}
                    </span>
                  )}
                  {planMode === "ai" && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-brand/10 px-2.5 py-1 font-medium text-brand">
                      {formatPriceDual(detailStop.priceLocal, locale.currency, lang)}
                    </span>
                  )}
                </div>
                <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-foreground/85">
                  {detailStop.blurb}
                </p>

                {(() => {
                  const det = stopDetailsMap[detailStop.name];
                  if (!det) {
                    return (
                      <button
                        type="button"
                        onClick={() => handleSeeMore(detailStop)}
                        className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full border border-brand/40 bg-brand/5 px-4 py-2.5 text-sm font-semibold text-brand transition hover:bg-brand/10"
                      >
                        {isPremium ? <Sparkles className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                        {lang === "pt" ? "Ver mais (história, curiosidades…)" : lang === "fr" ? "Voir plus (histoire, anecdotes…)" : lang === "es" ? "Ver más (historia, curiosidades…)" : lang === "de" ? "Mehr anzeigen (Geschichte, Wissenswertes…)" : lang === "ja" ? "もっと見る（歴史、豆知識…）" : "See more (history, fun facts…)"}
                        {!isPremium && <Crown className="h-4 w-4" />}
                      </button>
                    );
                  }
                  if (det === "loading") {
                    return (
                      <div className="mt-4 flex items-center justify-center gap-2 rounded-2xl border border-dashed border-border p-4 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {lang === "pt" ? "A carregar contexto histórico…" : "Loading history…"}
                      </div>
                    );
                  }
                  if (det === "error") {
                    return (
                      <div className="mt-4 rounded-2xl border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                        {lang === "pt" ? "Não foi possível carregar. Tenta de novo." : "Couldn't load. Try again."}
                        <button onClick={() => handleSeeMore(detailStop)} className="ml-2 underline">↻</button>
                      </div>
                    );
                  }
                  return (
                    <div className="mt-5 space-y-4">
                      <section>
                        <h4 className="mb-1.5 text-xs font-bold uppercase tracking-wider text-brand">
                          {lang === "pt" ? "História" : lang === "fr" ? "Histoire" : lang === "es" ? "Historia" : lang === "de" ? "Geschichte" : lang === "ja" ? "歴史" : "History"}
                        </h4>
                        <p className="whitespace-pre-line text-sm leading-relaxed text-foreground/85">{det.history}</p>
                      </section>
                      {det.funFacts.length > 0 && (
                        <section>
                          <h4 className="mb-1.5 text-xs font-bold uppercase tracking-wider text-brand">
                            {lang === "pt" ? "Curiosidades" : lang === "fr" ? "Anecdotes" : lang === "es" ? "Curiosidades" : lang === "de" ? "Wissenswertes" : lang === "ja" ? "豆知識" : "Fun facts"}
                          </h4>
                          <ul className="space-y-1.5 text-sm text-foreground/85">
                            {det.funFacts.map((f, i) => (
                              <li key={i} className="flex gap-2"><span className="text-brand">•</span><span>{f}</span></li>
                            ))}
                          </ul>
                        </section>
                      )}
                      {det.historicalFigures.length > 0 && (
                        <section>
                          <h4 className="mb-1.5 text-xs font-bold uppercase tracking-wider text-brand">
                            {lang === "pt" ? "Figuras históricas" : lang === "fr" ? "Personnages historiques" : lang === "es" ? "Figuras históricas" : lang === "de" ? "Historische Persönlichkeiten" : lang === "ja" ? "歴史上の人物" : "Historical figures"}
                          </h4>
                          <ul className="space-y-2 text-sm text-foreground/85">
                            {det.historicalFigures.map((p, i) => (
                              <li key={i}><span className="font-semibold">{p.name}</span> — <span className="text-foreground/70">{p.note}</span></li>
                            ))}
                          </ul>
                        </section>
                      )}
                      <section>
                        <h4 className="mb-1.5 text-xs font-bold uppercase tracking-wider text-brand">
                          {lang === "pt" ? "Melhor altura para visitar" : lang === "fr" ? "Meilleur moment" : lang === "es" ? "Mejor momento" : lang === "de" ? "Beste Besuchszeit" : lang === "ja" ? "おすすめの時間" : "Best time to visit"}
                        </h4>
                        <p className="text-sm leading-relaxed text-foreground/85">{det.bestTimeToVisit}</p>
                      </section>
                    </div>
                  );
                })()}

                {(() => {
                  if (!detailStop.placeId) return null;
                  const info = placeContactMap[detailStop.placeId];
                  if (!info || info === "loading") return null;
                  const types = [info.primaryType ?? "", ...(info.types ?? [])].map((t) => t.toLowerCase());
                  const isLodging = types.some((t) => /lodging|hotel|motel|hostel|guest_house|bed_and_breakfast|resort|inn|extended_stay/.test(t));
                  const isFood = types.some((t) => /restaurant|cafe|bar|food|bakery|meal_|coffee|pub|brewery|winery|ice_cream/.test(t));
                  const phone = info.phone;
                  const website = info.website;
                  const showSection = isLodging || isFood || phone || website;
                  if (!showSection) return null;

                  const labels = {
                    pt: { call: "Ligar", website: "Site oficial", booking: "Procurar no Booking", airbnb: "Procurar no Airbnb", priceLevel: "Preço médio", book: "Reservar" },
                    en: { call: "Call", website: "Official site", booking: "Search on Booking", airbnb: "Search on Airbnb", priceLevel: "Price level", book: "Book" },
                    es: { call: "Llamar", website: "Sitio oficial", booking: "Buscar en Booking", airbnb: "Buscar en Airbnb", priceLevel: "Nivel de precio", book: "Reservar" },
                    fr: { call: "Appeler", website: "Site officiel", booking: "Chercher sur Booking", airbnb: "Chercher sur Airbnb", priceLevel: "Niveau de prix", book: "Réserver" },
                    de: { call: "Anrufen", website: "Offizielle Seite", booking: "Auf Booking suchen", airbnb: "Auf Airbnb suchen", priceLevel: "Preisniveau", book: "Buchen" },
                    it: { call: "Chiama", website: "Sito ufficiale", booking: "Cerca su Booking", airbnb: "Cerca su Airbnb", priceLevel: "Fascia di prezzo", book: "Prenota" },
                    ja: { call: "電話", website: "公式サイト", booking: "Bookingで検索", airbnb: "Airbnbで検索", priceLevel: "価格帯", book: "予約" },

                  }[lang] ?? { call: "Call", website: "Official site", booking: "Search on Booking", airbnb: "Search on Airbnb", priceLevel: "Price level", book: "Book" };

                  const query = encodeURIComponent(`${detailStop.name} ${locale.city}`);
                  const bookingUrl = `https://www.booking.com/searchresults.html?ss=${query}`;
                  const airbnbUrl = `https://www.airbnb.com/s/${query}/homes`;

                  return (
                    <div className="mt-5 space-y-3 rounded-2xl border border-border bg-muted/40 p-3">
                      {isFood && info.priceLevel != null && info.priceLevel > 0 && (
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">{labels.priceLevel}</span>
                          <span className="font-bold text-foreground">{"€".repeat(Math.max(1, Math.min(4, info.priceLevel)))}</span>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-2">
                        {phone && (
                          <a
                            href={`tel:${phone.replace(/\s+/g, "")}`}
                            className="inline-flex items-center justify-center gap-1.5 rounded-full border border-border bg-background px-3 py-2 text-xs font-semibold transition hover:bg-muted"
                          >
                            <Phone className="h-3.5 w-3.5" /> {labels.call}
                          </a>
                        )}
                        {website && (
                          <a
                            href={website}
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                            className="inline-flex items-center justify-center gap-1.5 rounded-full border border-border bg-background px-3 py-2 text-xs font-semibold transition hover:bg-muted"
                          >
                            <Globe className="h-3.5 w-3.5" /> {labels.website}
                          </a>
                        )}
                      </div>
                      {isLodging && (
                        <div className="grid grid-cols-2 gap-2 border-t border-border/60 pt-3">
                          <a
                            href={bookingUrl}
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                            className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#003580] px-3 py-2 text-xs font-semibold text-white transition hover:opacity-90"
                          >
                            <ExternalLink className="h-3.5 w-3.5" /> Booking
                          </a>
                          <a
                            href={airbnbUrl}
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                            className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#FF5A5F] px-3 py-2 text-xs font-semibold text-white transition hover:opacity-90"
                          >
                            <ExternalLink className="h-3.5 w-3.5" /> Airbnb
                          </a>
                        </div>
                      )}
                    </div>
                  );
                })()}



                <a
                  href={mapsUrlFor(detailStop)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-foreground px-4 py-2.5 text-sm font-medium text-background transition hover:opacity-90"
                >
                  <Navigation className="h-4 w-4" />
                  {t.openInMaps}
                </a>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <PremiumModal
        open={premiumOpen}
        onClose={() => {
          setPremiumOpen(false);
          // If user later upgrades, the realtime subscription in usePremium will
          // flip isPremium; apply pending mode then.
          if (pendingMode && isPremium) {
            setMode(pendingMode);
            setPendingMode(null);
          }
        }}
      />

      <QuotaLimitModal
        open={quotaModal.open}
        tier={quotaModal.tier}
        reason={quotaModal.reason}
        resetInMinutes={quotaModal.resetInMinutes}
        lang={lang}
        onClose={() => setQuotaModal((q) => ({ ...q, open: false }))}
        onOpenPremium={() => setPremiumOpen(true)}
      />

      <Dialog open={gpsPromptOpen} onOpenChange={(o) => { if (!o) { setGpsPromptOpen(false); pendingGenerateRef.current = null; } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {lang === "pt" ? "Partilhar a tua localização?" : lang === "es" ? "¿Compartir tu ubicación?" : lang === "fr" ? "Partager votre position ?" : lang === "de" ? "Standort teilen?" : lang === "ja" ? "現在地を共有しますか？" : "Share your location?"}
            </DialogTitle>
            <DialogDescription>
              {lang === "pt"
                ? "Disseste que estás no destino — queres que o roteiro comece exatamente onde estás? Precisamos de aceder ao GPS."
                : lang === "es"
                ? "Dijiste que estás en el destino — ¿quieres que la ruta empiece justo donde estás? Necesitamos acceder al GPS."
                : lang === "fr"
                ? "Vous avez dit être sur place — voulez-vous que l'itinéraire commence là où vous êtes ? Nous avons besoin de votre GPS."
                : lang === "de"
                ? "Du hast gesagt, du bist am Ziel — soll die Route dort starten, wo du bist? Wir brauchen GPS-Zugriff."
                : lang === "ja"
                ? "現在地から出発するには位置情報が必要です。"
                : "You mentioned you're at the destination — start the route from where you are? We need GPS access."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setGpsPromptOpen(false);
                setUseGpsAsStart(false);
                const args = pendingGenerateRef.current;
                pendingGenerateRef.current = null;
                if (args) setTimeout(() => { void runGenerate(args); }, 50);
              }}
              disabled={gpsPromptLocating}
            >
              {lang === "pt" ? "Usar centro da cidade" : lang === "es" ? "Usar centro" : lang === "fr" ? "Utiliser le centre-ville" : lang === "de" ? "Stadtzentrum verwenden" : lang === "ja" ? "市の中心から" : "Use city centre"}
            </Button>
            <Button
              onClick={() => {
                if (!("geolocation" in navigator)) {
                  toast.error(lang === "pt" ? "GPS não disponível neste dispositivo." : "GPS not available on this device.");
                  return;
                }
                setGpsPromptLocating(true);
                navigator.geolocation.getCurrentPosition(
                  async (p) => {
                    const gpsPos = { lat: p.coords.latitude, lng: p.coords.longitude };
                    setUserPos(gpsPos);
                    setUseGpsAsStart(true);
                    const gpsLocale = await localeFromCoordsValue(gpsPos.lat, gpsPos.lng);
                    setLocale(gpsLocale);
                    setGpsPromptLocating(false);
                    setGpsPromptOpen(false);
                    const args = pendingGenerateRef.current;
                    pendingGenerateRef.current = null;
                    if (args) void runGenerate({ ...args, locale: gpsLocale, useGpsAsStart: true, userPosition: gpsPos });
                  },
                  () => {
                    setGpsPromptLocating(false);
                    toast.error(lang === "pt" ? "Não consegui aceder ao GPS. A usar centro da cidade." : "Couldn't access GPS. Using city centre.");
                    setUseGpsAsStart(false);
                    setGpsPromptOpen(false);
                    const args = pendingGenerateRef.current;
                    pendingGenerateRef.current = null;
                    if (args) setTimeout(() => { void runGenerate(args); }, 50);
                  },
                  { enableHighAccuracy: true, timeout: 10000 },
                );
              }}
              disabled={gpsPromptLocating}
            >
              {gpsPromptLocating
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{lang === "pt" ? "A localizar…" : "Locating…"}</>
                : (lang === "pt" ? "Ativar GPS" : lang === "es" ? "Activar GPS" : lang === "fr" ? "Activer le GPS" : lang === "de" ? "GPS aktivieren" : lang === "ja" ? "GPSを有効化" : "Enable GPS")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={extraOpen} onOpenChange={(o) => setExtraOpen(o)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-brand" />
              {MM.suggestModalTitle[lang]}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-2 py-2">
            {extraLoading && (
              <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {MM.suggesting[lang]}
              </div>
            )}
            {!extraLoading && extraSuggestions && extraSuggestions.length === 0 && (
              <p className="px-1 py-4 text-center text-sm text-muted-foreground">{MM.suggestModalEmpty[lang]}</p>
            )}
            {!extraLoading && extraSuggestions && extraSuggestions.map((s) => {
              const isAdded = addedExtra.has(s.name);
              return (
                <div key={s.name} className="flex items-start gap-3 rounded-xl border border-border bg-card p-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="truncate font-semibold text-sm">{s.name}</h4>
                      {s.rating > 0 && (
                        <span className="flex items-center gap-0.5 text-xs text-muted-foreground">
                          <Star className="h-3 w-3 fill-[var(--sun)] text-[var(--sun)]" />{s.rating.toFixed(1)}
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{s.category}</div>
                    <p className="mt-1 line-clamp-2 text-xs text-foreground/80">{s.blurb}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => addExtraStop(s)}
                    disabled={isAdded}
                    className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold transition ${isAdded ? "bg-muted text-muted-foreground" : "bg-brand text-brand-foreground hover:opacity-90"}`}
                  >
                    {isAdded ? MM.added[lang] : MM.addBtn[lang]}
                  </button>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtraOpen(false)}>{lang === "pt" ? "Fechar" : lang === "es" ? "Cerrar" : lang === "fr" ? "Fermer" : lang === "de" ? "Schließen" : lang === "ja" ? "閉じる" : "Close"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={saveOpen} onOpenChange={(o) => !saving && setSaveOpen(o)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bookmark className="h-5 w-5 text-brand" />
              Guardar rota
            </DialogTitle>
            <DialogDescription>
              Dá um nome a esta rota para a encontrares depois em "As minhas rotas".
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <label htmlFor="route-name" className="text-xs font-medium text-muted-foreground">
              Nome da rota
            </label>
            <Input
              id="route-name"
              value={saveTitle}
              onChange={(e) => setSaveTitle(e.target.value)}
              placeholder="Ex.: Paris · fim de semana"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter" && saveTitle.trim() && !saving) confirmSaveItinerary();
              }}
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="ghost" onClick={() => setSaveOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={confirmSaveItinerary} disabled={saving || !saveTitle.trim()}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Bookmark className="mr-2 h-4 w-4" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pdfDialogOpen} onOpenChange={setPdfDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{lang === "pt" ? "Escolher modelo de PDF" : "Choose PDF template"}</DialogTitle>
            <DialogDescription>
              {lang === "pt" ? "Todos incluem o link da rota completa no Google Maps." : "All include the full route Google Maps link."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2.5">
            {([
              { id: "classic", label: lang === "pt" ? "Clássico" : "Classic", swatch: "from-slate-900 to-slate-700", desc: lang === "pt" ? "Sóbrio, atual" : "Clean, current" },
              { id: "colorful", label: lang === "pt" ? "Colorido" : "Colorful", swatch: "from-violet-600 to-pink-500", desc: lang === "pt" ? "Vivo & moderno" : "Vivid & modern" },
              { id: "magazine", label: "Magazine", swatch: "from-sky-900 to-teal-500", desc: lang === "pt" ? "Editorial" : "Editorial" },
              { id: "minimal", label: "Minimal", swatch: "from-zinc-100 to-zinc-300", desc: lang === "pt" ? "Preto & branco" : "Black & white" },
            ] as { id: PdfTemplate; label: string; swatch: string; desc: string }[]).map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => handleExportPdf(t.id)}
                className="group flex flex-col gap-2 rounded-xl border border-border bg-card p-3 text-left transition hover:border-brand hover:shadow-[var(--shadow-soft)]"
              >
                <div className={`h-16 w-full rounded-lg bg-gradient-to-br ${t.swatch}`} />
                <div>
                  <div className="text-sm font-semibold text-foreground">{t.label}</div>
                  <div className="text-xs text-muted-foreground">{t.desc}</div>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Nearby restaurants modal (Google Places) */}
      <Dialog open={!!restoModalKey} onOpenChange={(o) => { if (!o) setRestoModalKey(null); }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{lang === "pt" ? "Restaurantes perto" : "Restaurants nearby"}</DialogTitle>
            <DialogDescription>
              {lang === "pt"
                ? "Sugestões reais do Google, ordenadas por avaliação."
                : "Real Google suggestions, ranked by rating."}
              {maxMealBudget ? (lang === "pt" ? ` Filtrado por orçamento ≤ €${maxMealBudget}.` : ` Filtered by budget ≤ €${maxMealBudget}.`) : null}
            </DialogDescription>
          </DialogHeader>
          {restoModalLoading ? (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> {lang === "pt" ? "A procurar…" : "Searching…"}
            </div>
          ) : restoModalResults.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              {lang === "pt" ? "Sem resultados nesta zona." : "No results in this area."}
            </div>
          ) : (
            <ul className="space-y-2">
              {restoModalResults.map((r) => {
                const priceLabel = r.priceLevel ? "€".repeat(r.priceLevel) : null;
                const avgRange = r.priceLevel
                  ? ({ 1: "≤ €15", 2: "€15–30", 3: "€30–60", 4: "€60+" } as Record<number, string>)[r.priceLevel]
                  : null;
                const mapsHref = r.googleMapsUri ?? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(r.name + " " + locale.city)}&query_place_id=${r.placeId}`;
                return (
                  <li key={r.placeId} className="rounded-xl border border-border bg-background p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold">{r.name}</div>
                        {r.address ? <div className="truncate text-xs text-muted-foreground">{r.address}</div> : null}
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                          {typeof r.rating === "number" ? (
                            <span className="inline-flex items-center gap-1 font-medium text-amber-600">
                              ★ {r.rating.toFixed(1)}
                              {r.userRatingCount ? <span className="text-muted-foreground">({r.userRatingCount})</span> : null}
                            </span>
                          ) : null}
                          {priceLabel ? <span className="font-semibold text-foreground/80">{priceLabel}</span> : null}
                          {avgRange ? <span>· {lang === "pt" ? "média" : "avg"} {avgRange}</span> : null}
                        </div>
                      </div>
                      <a
                        href={mapsHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="shrink-0 inline-flex items-center gap-1 rounded-full bg-foreground px-3 py-1.5 text-xs font-semibold text-background hover:opacity-90"
                      >
                        <Navigation className="h-3 w-3" /> {lang === "pt" ? "Maps" : "Maps"}
                      </a>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </DialogContent>
      </Dialog>


      {dragGhost ? (
        <div
          className="pointer-events-none fixed z-[100] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-brand bg-background/95 px-3 py-2 text-sm font-medium text-foreground opacity-90 shadow-[0_12px_32px_-8px_rgba(0,0,0,0.45)] ring-2 ring-brand/40 backdrop-blur"
          style={{ left: dragGhost.x, top: dragGhost.y, maxWidth: "70vw" }}
        >
          <span className="block truncate">{dragGhost.name}</span>
        </div>
      ) : null}

      {(aiPromptLoading || aiLoading) ? (
        <AiCreatingOverlay lang={lang} />
      ) : null}

    </div>
  );
}

function AiCreatingOverlay({ lang }: { lang: string }) {
  const messages = useMemo(() => {
    const map: Record<string, string[]> = {
      pt: ["✈️ A analisar o destino…", "📍 A encontrar os melhores locais…", "🍽️ A escolher restaurantes…", "🧠 A otimizar o percurso…", "✅ Roteiro quase pronto…"],
      en: ["✈️ Analysing the destination…", "📍 Finding the best spots…", "🍽️ Picking restaurants…", "🧠 Optimising the route…", "✅ Itinerary almost ready…"],
      es: ["✈️ Analizando el destino…", "📍 Buscando los mejores lugares…", "🍽️ Eligiendo restaurantes…", "🧠 Optimizando la ruta…", "✅ Itinerario casi listo…"],
      fr: ["✈️ Analyse de la destination…", "📍 Recherche des meilleurs lieux…", "🍽️ Sélection de restaurants…", "🧠 Optimisation du parcours…", "✅ Itinéraire presque prêt…"],
      de: ["✈️ Reiseziel wird analysiert…", "📍 Beste Orte werden gesucht…", "🍽️ Restaurants werden ausgewählt…", "🧠 Route wird optimiert…", "✅ Route fast fertig…"],
      ja: ["✈️ 目的地を分析中…", "📍 最適なスポットを検索中…", "🍽️ レストランを選択中…", "🧠 ルートを最適化中…", "✅ 旅程はほぼ完成…"],
    };
    return map[lang] ?? map.en;
  }, [lang]);
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setIdx((i) => Math.min(i + 1, messages.length - 1)), 2200);
    return () => window.clearInterval(id);
  }, [messages]);
  // Keep the viewport stable while the route is being created. Avoid setting
  // body `position: fixed` with a negative top: on mobile browsers/PWAs that
  // itself can trigger the visible "page jump" the user reported.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    const body = document.body;
    const scrollY = window.scrollY;
    const prevRoot = {
      overflow: root.style.overflow,
      overscrollBehavior: root.style.overscrollBehavior,
    };
    const prev = {
      overflow: body.style.overflow,
      overscrollBehavior: body.style.overscrollBehavior,
      touchAction: body.style.touchAction,
    };
    root.style.overflow = "hidden";
    root.style.overscrollBehavior = "none";
    body.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";
    body.style.touchAction = "none";
    return () => {
      root.style.overflow = prevRoot.overflow;
      root.style.overscrollBehavior = prevRoot.overscrollBehavior;
      body.style.overflow = prev.overflow;
      body.style.overscrollBehavior = prev.overscrollBehavior;
      body.style.touchAction = prev.touchAction;
      window.requestAnimationFrame(() => window.scrollTo(0, scrollY));
    };
  }, []);
  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-background"
      role="status"
      aria-live="polite"
      style={{ height: "100svh", minHeight: "100svh", touchAction: "none", overscrollBehavior: "contain" }}
      onTouchMove={(e) => e.preventDefault()}
      onWheel={(e) => e.preventDefault()}
    >

      <div className="mx-4 flex max-w-sm flex-col items-center gap-4 rounded-2xl border border-brand/30 bg-background/95 px-6 py-6 text-center shadow-[0_20px_60px_-12px_rgba(0,0,0,0.45)]">
        <Loader2 className="h-7 w-7 animate-spin text-brand" />
        <div key={idx} className="min-h-[24px] animate-in fade-in slide-in-from-bottom-1 text-base font-semibold text-foreground duration-300">
          {messages[idx]}
        </div>
        <div className="flex gap-1.5">
          {messages.map((_, i) => (
            <span key={i} className={`h-1.5 w-1.5 rounded-full transition-colors ${i <= idx ? "bg-brand" : "bg-muted"}`} />
          ))}
        </div>
        <div className="text-xs text-muted-foreground">
          {{ pt: "Pode demorar alguns segundos. Não feches a página.", en: "This may take a few seconds. Please don't close the page.", it: "Potrebbe richiedere qualche secondo. Non chiudere la pagina.", es: "Puede tardar unos segundos. No cierres la página.", fr: "Cela peut prendre quelques secondes. Ne fermez pas la page.", de: "Das kann ein paar Sekunden dauern. Seite nicht schließen.", ja: "数秒かかることがあります。ページを閉じないでください。" }[lang as "pt"] ?? ""}
        </div>
      </div>
    </div>
  );
}


function ModeBtn({ active, onClick, icon, label, locked }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string; locked?: boolean }) {
  return (
    <button
      onClick={onClick}
      className={`relative inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition ${
        active ? "bg-foreground text-background shadow" : "bg-background/90 text-foreground hover:bg-background"
      }`}
    >
      {icon}
      {label}
      {locked && <Lock className="h-3 w-3 opacity-70" />}
    </button>
  );
}
